import AdminPage from './AdminPage';
import { createMatch } from './api';
import { Capacitor } from '@capacitor/core';
import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';
import './App.css';

const HOME_BRAND_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/dongnesai.logo.jpeg`;
const ADMIN_PATH = (process.env.REACT_APP_ADMIN_PATH || '/a9x7k-admin-4f2').trim();

/** 내부/베타 빌드(ADB 등): 유료 포인트 토스 결제 UI 비활성화. 정식 오픈 시 토스 블록 주석·이 안내 교체 */
const BETA_PAID_CHARGE_NOTICE =
  '베타 테스트 기간에는 포인트 구매가 제한됩니다.\n\n테스터에게는 테스트용 포인트가 지급됩니다.';

/** 강제 퇴장 안내 채팅 — `lib/chatPolicyCopy.ts`와 동일 값 유지 */
const GYEOL_CHAT_SYSTEM_USER_ID = 'a0000000-0000-4000-8000-000000000001';
const CHAT_POLICY_KICK_EXIT_MESSAGE =
  '운영 정책 위반 또는 신고 접수로 인해 관리자에 의해 채팅방에서 퇴장 처리되었습니다.\n\n'
  + '안전한 채팅 환경 유지를 위한 조치이며, 반복 위반 시 추가 이용 제한 또는 계정 정지 조치가 적용될 수 있습니다.\n\n'
  + '조치에 대한 문의 또는 재검토가 필요한 경우 카카오채널 @동네사이 로 문의해주세요.';
const USER_WARN_NOTICE_TITLE = '사용자 경고 안내';
/** 채팅 모더 invoke 팝업 직후 user_warnings_v2 중복 alert 방지 (ChatScreen ↔ App 공유) */
let gyeolModSanctionPopupShownAt = 0;
const markModSanctionPopupShown = () => { gyeolModSanctionPopupShownAt = Date.now(); };
const wasModSanctionPopupShownRecently = () => Date.now() - gyeolModSanctionPopupShownAt < 20000;
function parsePolicyKickChatText(raw) {
  const s = String(raw || '');
  const m = s.match(/^\[\[gyeol:kick:([0-9a-f-]{36})\]\]\s*\n?/i);
  if (!m) return { kickedUserId: null, displayText: s };
  const rest = s.slice(m[0].length).replace(/^\s+/, '');
  return { kickedUserId: m[1], displayText: rest || CHAT_POLICY_KICK_EXIT_MESSAGE };
}

/** 홈 지역 필·모임 카드 등 공통 위치 핀 (벡터 SVG) */
function LocationPinIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 22.2c.32 0 .62-.17.77-.45 1.85-3.2 7.93-10.62 7.93-12.55C20.7 5.7 16.78 2 12 2S3.3 5.7 3.3 9.2c0 1.93 6.08 9.35 7.93 12.55.15.28.45.45.77.45z"
        fill="#1B5E20"
        stroke="#0D3318"
        strokeWidth="1.05"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="9.35" r="2.5" fill="#ffffff" />
    </svg>
  );
}

/** 내부 오류 상세는 콘솔에만 (DB/스토리지/네트워크 원문은 사용자에게 비노출) */
function logClientError(tag, ...details) {
  try {
    const parts = details.filter((d) => d !== undefined && d !== null && d !== '');
    if (parts.length) console.warn(`[client:${tag}]`, ...parts);
  } catch { /* ignore */ }
}

/** Edge Function 응답 JSON의 error만 표시(서버에서 사용자용 문구로 통제). invoke 전송 오류는 로그만. */
function alertFromEdgeInvoke(tag, fallbackText, invokeResult) {
  const data = invokeResult?.data;
  const invokeError = invokeResult?.error;
  const bodyErr = typeof data?.error === 'string' ? data.error.trim() : '';
  if (bodyErr) {
    if (invokeError) logClientError(`${tag}:invoke`, invokeError.message, invokeError.context);
    alert(bodyErr);
    return;
  }
  logClientError(tag, invokeError?.message, invokeError?.context, data);
  alert(fallbackText);
}

function alertDbFailure(tag, userMessage, dbError) {
  logClientError(tag, dbError?.message, dbError?.code, dbError?.details);
  alert(userMessage);
}

/** prepareImageFileForUpload 등에서 던진 한국어 UX 문구만 표시, 그 외는 일반 안내 */
function alertImagePrepareFailure(tag, err, fallbackText) {
  const msg = err && typeof err.message === 'string' ? err.message.trim() : '';
  const fromOurPipeline =
    msg &&
    (/[가-힣]/.test(msg) &&
      (msg.includes('사진') || msg.includes('이미지') || msg.includes('JPEG') || msg.includes('jpg') ||
        msg.includes('용량') || msg.includes('선택') || msg.includes('지원') || msg.includes('압축') ||
        msg.includes('호환') || msg.includes('HEIC') || msg.includes('SVG')));
  logClientError(tag, msg || err);
  alert(fromOurPipeline ? msg : (fallbackText || '이미지를 처리하지 못했습니다. 형식·용량을 확인해주세요.'));
}

async function invokeVerifyOtp(body) {
  return supabase.functions.invoke('verify-otp', { body });
}

// ── Supabase 헬퍼들 ─────────────────────────────
// DB는 snake_case, 기존 App.js 컴포넌트는 camelCase를 참조하므로 로드 시 매핑
const mapClubRow = (c) => ({
  id:              c.id,
  title:           c.title || null,
  roomIntro:       c.room_intro || null,
  hostName:        c.host_name || null,
  hostProfileImageUrl: c.host_profile_image_url || null,
  hobby:           c.hobby,
  hobbies:         Array.isArray(c.hobbies) && c.hobbies.length > 0
    ? c.hobbies.filter(Boolean)
    : (c.hobby ? [c.hobby] : []),
  region:          c.region,
  ageGroup:        c.age_group,
  ratio:           c.ratio,
  times:           c.times || [],
  members:         c.members || 0,
  maxMembers:      c.max_members || 6,
  hostId:          c.host_id || null,
  isLocked:        c.is_locked || false,
  lastTitleUpdate: c.last_title_update || null,
  roomType:        c.room_type || 'age_limited',
  minAge:          c.min_age  ?? 0,
  maxAge:          c.max_age  ?? 120,
  isActive:        c.is_active !== false,
  status:          c.status || 'recruiting',
  createdAt:       c.created_at,
  coverImageUrl:   c.cover_image_url || null,
  isBoosted:       c.is_boosted === true,
  boostedAt:       c.boosted_at || null,
  boostExpiresAt:  c.boost_expires_at || null,
});

const parseTimestampMs = (v) => {
  if (!v) return NaN;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : NaN;
};
const isBoostActive = (club) => {
  if (!club?.isBoosted) return false;
  const expMs = parseTimestampMs(club.boostExpiresAt);
  if (!Number.isFinite(expMs)) return false;
  return expMs > Date.now();
};
const boostRemainingMs = (club) => {
  if (!isBoostActive(club)) return 0;
  const expMs = parseTimestampMs(club.boostExpiresAt);
  return Math.max(0, expMs - Date.now());
};
const formatBoostRemaining = (ms) => {
  const sec = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}시간 ${m}분`;
  return `${m}분`;
};

/** 기존 앱(인천 구 단위) DB 값 — 그대로 두고 필터만 광역으로 매핑 */
const METRO_LEGACY_INCHEON_DISTRICTS = ['계양구', '미추홀구', '남동구', '동구', '부평구', '서구', '연수구', '중구'];
/** 서울 특별시 자치구(구 단위로 저장된 기존·신규 데이터 매칭용) */
const METRO_SEOUL_DISTRICTS = new Set([
  '종로구', '중구', '용산구', '성동구', '광진구', '동대문구', '중랑구', '성북구', '강북구', '도봉구', '노원구',
  '은평구', '서대문구', '마포구', '양천구', '강서구', '구로구', '금천구', '영등포구', '동작구', '관악구',
  '서초구', '강남구', '송파구', '강동구',
]);
/** 경기도 시·군(자주 쓰이는 명칭 + 시·군 접미사 휴리스틱 보조) */
const METRO_GYEONGGI_DISTRICTS = new Set([
  '수원시', '성남시', '고양시', '용인시', '부천시', '안산시', '안양시', '남양주시', '화성시', '평택시', '의정부시',
  '시흥시', '파주시', '김포시', '광명시', '광주시', '군포시', '하남시', '오산시', '이천시', '안성시', '의왕시',
  '양평군', '여주시', '동두천시', '과천시', '가평군', '연천군', '구리시', '양주시', '포천시',
]);

/** 홈·모임 찾기: 광역(수도권 전체·인천·서울·경기) 필터. 레거시 구·시 값은 매핑만 하고 DB는 변경하지 않음. */
function clubMatchesMetroRegionFilter(clubRegion, filterVal) {
  const r = String(clubRegion || '').trim();
  if (!filterVal || filterVal === 'all') return true;
  if (r === '수도권 전체') return true;
  if (filterVal === '인천') return r === '인천' || METRO_LEGACY_INCHEON_DISTRICTS.includes(r);
  if (filterVal === '서울') {
    if (r === '서울') return true;
    if (METRO_LEGACY_INCHEON_DISTRICTS.includes(r)) return false;
    return METRO_SEOUL_DISTRICTS.has(r);
  }
  if (filterVal === '경기') {
    if (r === '경기') return true;
    if (METRO_LEGACY_INCHEON_DISTRICTS.includes(r)) return false;
    if (METRO_SEOUL_DISTRICTS.has(r)) return false;
    if (METRO_GYEONGGI_DISTRICTS.has(r)) return true;
    return false;
  }
  return true;
}

// 방 오픈 기준:
// - 생성 후 24시간 이내: 6명 이상이면 오픈
// - 생성 후 24시간 이후: 4명 이상
// - status가 active면 즉시 오픈으로 간주
const getRoomOpenMinMembers = (club) => {
  const createdAtRaw = club?.createdAt ?? club?.created_at;
  const createdAtMs = createdAtRaw ? new Date(createdAtRaw).getTime() : NaN;
  const validCreatedAt = Number.isFinite(createdAtMs);
  if (!validCreatedAt) return 6;
  const hoursSinceCreate = (Date.now() - createdAtMs) / 3600000;
  return hoursSinceCreate < 24 ? 6 : 4;
};

const isClubOpenForChat = (club) => {
  if (!club) return false;
  if (club.status === 'active') return true;
  const minMembers = getRoomOpenMinMembers(club);
  return (club.members || 0) >= minMembers;
};
const ROOM_OPEN_ALERT_KEY = '__room_open_alert__';
const readUnreadTotal = (counts) => Object.entries(counts || {})
  .reduce((sum, [k, v]) => (k === ROOM_OPEN_ALERT_KEY ? sum : (sum + (Number(v) || 0))), 0);

const sameUserId = (a, b) => String(a || '') === String(b || '');

/** 표시용 첫 글자(이모지·한글 음절 단위 우선) */
const firstDisplayChar = (s) => {
  const str = String(s || '');
  if (!str) return '';
  try {
    const seg = Intl.Segmenter && new Intl.Segmenter(undefined, { granularity: 'grapheme' });
    if (seg) {
      for (const { segment } of seg.segment(str)) return segment;
    }
  } catch { /* ignore */ }
  return [...str][0] || '';
};

/**
 * 채팅/모임에서 타인 이름 가림: 첫 글자 + '*'.
 * 본인·방 모임장(roomHostId 일치)는 닉네임/이름 풀 표시.
 * DB에는 원문 유지, 화면 표시만 적용.
 */
const maskMemberNameForViewer = (rawName, viewerUserId, memberUserId, roomHostId = null) => {
  const self = viewerUserId != null && memberUserId != null && sameUserId(viewerUserId, memberUserId);
  const memberIsRoomHost = roomHostId != null && memberUserId != null && sameUserId(roomHostId, memberUserId);
  const s = String(rawName ?? '').trim();
  if (self || memberIsRoomHost) {
    if (!s) return memberIsRoomHost ? '모임장' : '회원';
    return s;
  }
  if (!s) return '회*';
  const first = firstDisplayChar(s);
  if (!first) return '*';
  return `${first}*`;
};

/** 내가 클럽에 참여한 시점(ms). matches.createdAt은 club_members.joined_at 복원값과 동일 기준. */
const participationStartMs = (myMatch, club, userId) => {
  const rawCa = myMatch?.createdAt;
  if (rawCa != null && rawCa !== '') {
    if (typeof rawCa === 'number' && Number.isFinite(rawCa) && rawCa > 0) return rawCa;
    const tm = new Date(rawCa).getTime();
    if (Number.isFinite(tm) && tm > 0) return tm;
  }
  if (club && userId && sameUserId(club.hostId, userId)) {
    const raw = club.createdAt ?? club.created_at;
    if (!raw) return null;
    const t = new Date(raw).getTime();
    return Number.isFinite(t) ? t : null;
  }
  return null;
};

/** 취소·환급 정책 카운트다운 기준 시각(ms) — 홈/채팅방/모임찾기 공통 */
const cancelPolicyAnchorMs = (match, club, userId) =>
  participationStartMs(match || {}, club, userId);

/** MatchDetail·취소 처리에서 hoursPassed 계산용 (기준 없을 때 모임장이면 방 생성일). */
function parseCancelPolicyAnchorMs(match, club, userId) {
  let ms = cancelPolicyAnchorMs(match, club, userId);
  if (Number.isFinite(ms) && ms > 0) return ms;
  if (club?.hostId && userId && sameUserId(club.hostId, userId)) {
    const tHost = new Date(club.createdAt ?? club.created_at).getTime();
    if (Number.isFinite(tHost) && tHost > 0) return tHost;
  }
  const r = match?.createdAt;
  if (typeof r === 'number' && Number.isFinite(r) && r > 0) return r;
  if (r) {
    const t = new Date(r).getTime();
    if (Number.isFinite(t) && t > 0) return t;
  }
  return Date.now();
}

const isUuidLike = (v) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(v || '').trim());
const DEFAULT_PROFILE_ICON = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 128 128'%3E%3Crect width='128' height='128' rx='14' fill='%23f2f4f7'/%3E%3Ccircle cx='64' cy='50' r='18' fill='%23d1d5db'/%3E%3Cpath d='M24 108c4-24 22-36 40-36s36 12 40 36' fill='%23d1d5db'/%3E%3C/svg%3E";
const isDefaultProfileAvatar = (url) => !url || url === DEFAULT_PROFILE_ICON;
const rankHostProfile = (p) => {
  if (!p) return 0;
  const hasNamed = !!String(p.name || '').trim() && String(p.name || '').trim() !== '모임장';
  const hasAvatar = !isDefaultProfileAvatar(p.avatar);
  return (hasNamed ? 2 : 0) + (hasAvatar ? 1 : 0);
};

const mergeHostDetailProfile = (prev, next) => {
  if (!next) return prev;
  if (!sameUserId(prev?.id, next.id)) return next;
  // 동일 점수면 DB에서 온 next(최신)를 택해 프로필 수정 후에도 반영되게 함
  return rankHostProfile(prev) > rankHostProfile(next) ? prev : next;
};

/** img/src·썸네일용: http(s) 또는 동일 출처 상대 경로만 허용 (data/javascript 등 차단) */
function sanitizeUserImageUrl(url) {
  if (url == null) return null;
  const s = String(url).trim();
  if (!s) return null;
  try {
    const u = new URL(s);
    if (u.protocol === 'https:' || u.protocol === 'http:') return s;
    return null;
  } catch {
    if (s.startsWith('/') && !s.startsWith('//')) return s;
    return null;
  }
}

/** Storage 공개 URL + 캐시 무력화용 쿼리 */
const withUrlCacheBust = (url) => {
  if (!url) return url;
  const safe = sanitizeUserImageUrl(url);
  if (!safe) return null;
  const sep = safe.includes('?') ? '&' : '?';
  return `${safe}${sep}v=${Date.now()}`;
};

/** clubs.cover_image_url 갱신 (컬럼 없으면 조용히 스킵) */
async function updateClubCoverImageUrl(clubId, publicUrl) {
  if (!clubId || !publicUrl) return false;
  const bust = withUrlCacheBust(publicUrl);
  const { error } = await supabase.from('clubs').update({ cover_image_url: bust }).eq('id', clubId);
  if (error) {
    if (error.message?.includes('column') || error.code === 'PGRST204') return false;
    console.warn('cover_image_url DB 갱신 실패:', error.message);
    return false;
  }
  return true;
}

/** 클라이언트 압축·JPEG 변환 후 업로드 (원본 그대로 업로드하지 않음). 목표 ~1MB, HEIC 지원 */
const IMAGE_UPLOAD_MAX_EDGE_PX = 1080;
const IMAGE_UPLOAD_QUALITY_START = 0.78;
const IMAGE_UPLOAD_QUALITY_MIN = 0.58;
const IMAGE_UPLOAD_TARGET_MAX_BYTES = 1024 * 1024;
const IMAGE_UPLOAD_MAX_INPUT_BYTES = 30 * 1024 * 1024;
const CLUB_COVER_MAX_BYTES = IMAGE_UPLOAD_TARGET_MAX_BYTES;
const CHAT_IMAGE_MAX_UPLOAD_BYTES = IMAGE_UPLOAD_TARGET_MAX_BYTES;

/** 모임방 생성 직후 대표 사진 업로드 → Storage URL을 clubs.cover_image_url에 저장 */
async function uploadClubCoverPublicUrl(userId, coverKey, dataUrl) {
  if (!userId || !coverKey || !dataUrl || !String(dataUrl).startsWith('data:')) return null;
  try {
    const res = await fetch(dataUrl);
    let blob = await res.blob();
    if (blob.size > CLUB_COVER_MAX_BYTES) {
      const tmp = new File([blob], 'cover.jpg', { type: blob.type || 'image/jpeg' });
      try {
        blob = await prepareImageFileForUpload(tmp);
      } catch {
        return null;
      }
    }
    const mime = blob.type || 'image/jpeg';
    const ext = mime.includes('png') ? 'png' : mime.includes('webp') ? 'webp' : 'jpg';
    const pathsByBucket = [
      { bucket: 'club-covers', path: `${userId}/${coverKey}/cover.${ext}` },
      { bucket: 'chat-images', path: `${coverKey}/room_cover.${ext}` },
    ];
    for (const { bucket, path } of pathsByBucket) {
      const { error: upErr } = await supabase.storage.from(bucket).upload(path, blob, {
        upsert: true,
        contentType: mime,
      });
      if (upErr) continue;
      const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
      if (pub?.publicUrl) return pub.publicUrl;
    }
    return null;
  } catch {
    return null;
  }
}

async function persistClubCoverFromDataUrl(userId, clubId, dataUrl) {
  if (!userId || !clubId || !dataUrl || !String(dataUrl).startsWith('data:')) return;
  try {
    const url = await uploadClubCoverPublicUrl(userId, String(clubId), dataUrl);
    if (!url) {
      console.warn('모임 대표 사진 Storage 업로드 실패(버킷/정책 확인)');
      return;
    }
    const bust = withUrlCacheBust(url);
    const { error: rpcErr } = await supabase.rpc('set_my_club_cover_image_url', {
      p_club_id: clubId,
      p_url: bust,
    });
    if (!rpcErr) return;
    const ok = await updateClubCoverImageUrl(clubId, url);
    if (!ok) console.warn('모임 대표 사진 DB 갱신 실패(RLS·함수 미배포 여부 확인)');
  } catch (e) {
    console.warn('persistClubCoverFromDataUrl', e);
  }
}

const hydrateClubMembersFromMembership = async (clubRows) => {
  const rows = clubRows || [];
  if (rows.length === 0) return rows;
  const clubIds = rows.map(r => String(r?.id || '').trim()).filter(Boolean);
  if (clubIds.length === 0) return rows;
  const { data: memberRows, error } = await supabase
    .from('club_members')
    .select('club_id')
    .in('club_id', clubIds);
  if (error) {
    console.warn('club_members 실측 인원 조회 실패:', error.message);
    return rows;
  }
  const countByClubId = {};
  (memberRows || []).forEach(m => {
    const key = String(m?.club_id || '').trim();
    if (!key) return;
    countByClubId[key] = (countByClubId[key] || 0) + 1;
  });
  return rows.map(r => ({
    ...r,
    members: Number.isFinite(countByClubId[String(r?.id || '').trim()])
      ? countByClubId[String(r?.id || '').trim()]
      : (r.members || 0),
  }));
};

const fetchClubs = async () => {
  // 만료된 상단 노출(Boost)은 목록 조회 전에 정리(함수 없으면 조용히 스킵)
  try {
    await supabase.rpc('expire_club_boosts');
  } catch {}

  const isMissingColumn = (err) =>
    !!(err?.code === 'PGRST204' || err?.message?.includes('column') || err?.message?.includes('schema cache'));

  const baseQuery = () =>
    supabase
      .from('clubs')
      .select('*')
      .eq('is_active', true)
      .in('status', ['recruiting', 'active'])
      .not('host_id', 'is', null);

  // 1) boost 정렬 포함 쿼리 먼저 시도
  let { data, error } = await baseQuery()
    .order('is_boosted', { ascending: false })
    .order('boosted_at', { ascending: false })
    .order('created_at', { ascending: false });

  // 2) 컬럼 미반영이면(마이그레이션 미적용) boost 정렬 제거 후 재시도
  if (error && isMissingColumn(error)) {
    console.warn('fetchClubs: boost 컬럼 미반영 → 기존 정렬로 재시도:', error.message);
    ({ data, error } = await baseQuery().order('created_at', { ascending: false }));
  }

  // 3) 그래도 실패하면 최소 폴백(구 스키마 대응)으로 한 번 더
  if (error) {
    console.warn('fetchClubs 폴백 (SQL migration 필요):', error.message);
    let fallback = null;
    let e2 = null;
    ({ data: fallback, error: e2 } = await supabase.from('clubs').select('*').order('created_at', { ascending: false }));
    if (e2) { console.error(e2); return []; }
    const hydratedFallback = await hydrateClubMembersFromMembership(fallback || []);
    return hydratedFallback.map(mapClubRow);
  }

  const hydrated = await hydrateClubMembersFromMembership(data || []);
  return hydrated.map(mapClubRow);
};

// 로그인 후 club_members 기반으로 기존 참여 정보 복원
// Join 방식 대신 2-step 쿼리: Join 시 clubs RLS/스키마 문제로 null 반환되는 버그 방지
const loadUserMemberships = async (userId) => {
  // Step 1: 내 club_members 행만 조회 (단순 필터, join 없음)
  const { data: memberRows, error } = await supabase
    .from('club_members')
    .select('club_id, joined_at')
    .eq('user_id', userId);
  if (error) { console.warn('club_members 조회 오류:', error.message); return []; }
  if (!memberRows || memberRows.length === 0) return [];

  // Step 2: 해당 club_id 목록으로 clubs 직접 조회 (fetchClubs와 동일한 방식)
  const clubIds = memberRows.map(r => r.club_id);
  const { data: clubRows } = await supabase
    .from('clubs')
    .select('id, hobby, hobbies, region, ratio, times, host_id, is_active, status, title, room_intro, room_type, min_age, max_age, created_at')
    .in('id', clubIds);

  const clubMap = {};
  (clubRows || []).forEach(c => { clubMap[c.id] = c; });

  return memberRows
    .filter(r => {
      const c = clubMap[r.club_id];
      if (!c) return false;
      if (c.is_active === false) return false;
      const st = c.status || 'recruiting';
      return st === 'recruiting' || st === 'active';
    })
    .map(r => {
      const c = clubMap[r.club_id];
      const joinedAtMs = new Date(r.joined_at).getTime();
      const createdAtMs = new Date(c.created_at).getTime();
      const isCurrentHost = c.host_id === userId;
      const isCreatorHost = isCurrentHost && (
        !Number.isFinite(createdAtMs) ||
        !Number.isFinite(joinedAtMs) ||
        Math.abs(joinedAtMs - createdAtMs) <= (30 * 60 * 1000)
      );
      return {
        id:         'restored_' + r.club_id,
        clubId:     r.club_id,
        hobby:      c.hobby || null,
        region:     c.region || null,
        ratio:      c.ratio || null,
        times:      c.times || [],
        title:      c.title || null,
        roomIntro:  c.room_intro || null,
        roomType:   c.room_type || 'age_limited',
        minAge:     c.min_age ?? null,
        maxAge:     c.max_age ?? null,
        isHost:     isCreatorHost,
        isCurrentHost,
        status:     'waiting',
        createdAt:  new Date(r.joined_at).getTime(),
        coinType:   'paid',
        spentCoins: isCreatorHost ? 50 : 30,
      };
    });
};

// 포인트트 잔액을 DB에 원자적으로 기록 (프론트 state와 항상 동기화)
const appendCoinHistory = async (userId, amount, reason) => {
  if (!userId || !amount) return;
  try {
    await supabase.from('coin_adjustments').insert({
      user_id: userId,
      amount,
      reason: reason || '포인트 지급',
    });
  } catch (e) {
    console.warn('coin_adjustments 기록 실패:', e?.message || e);
  }
};

const updateUserCoins = async (userId, { freeDelta = 0, paidDelta = 0, freeReason = '', paidReason = '' }) => {
  if (!userId) return;
  const { error } = await supabase.rpc('update_coins', {
    p_user_id:    userId,
    p_free_delta: freeDelta,
    p_paid_delta: paidDelta,
  });
  if (error) {
    // RPC 미생성 시 폴백: 직접 read-modify-write
    console.warn('update_coins RPC 없음, 폴백:', error.message);
    const { data: p } = await supabase
      .from('profiles').select('free_coins,paid_coins').eq('user_id', userId).maybeSingle();
    await supabase.from('profiles').update({
      free_coins: Math.max(0, (p?.free_coins ?? 0) + freeDelta),
      paid_coins: Math.max(0, (p?.paid_coins ?? 0)  + paidDelta),
    }).eq('user_id', userId);
  }
  if (freeDelta) await appendCoinHistory(userId, freeDelta, freeReason || (freeDelta > 0 ? '무료 포인트 지급' : '무료 포인트 사용'));
  if (paidDelta) await appendCoinHistory(userId, paidDelta, paidReason || (paidDelta > 0 ? '유료 포인트 지급' : '유료 포인트 사용'));
};

// 본인 포인트 조정 + 지갑 내역 기록 (환급/차감 사유 노출용)
const updateMyPaidCoinsWithHistory = async (paidDelta, reason) => {
  if (!paidDelta) return;
  const { error } = await supabase.rpc('apply_my_paid_coin_adjustment', {
    p_delta: paidDelta,
    p_reason: reason || '포인트 지급',
  });
  if (error) {
    console.warn('apply_my_paid_coin_adjustment RPC 실패, 폴백:', error.message);
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user?.id) {
      await updateUserCoins(user.id, { paidDelta, paidReason: reason || '포인트 지급' });
    }
  }
};

const MONTHLY_FREE_COINS = 30;
const SIGNUP_PAID_BONUS_COINS = 50;
const SIGNUP_PAID_BONUS_REASON_LEGACY = `신규 가입 유료 포인트 지급 (${SIGNUP_PAID_BONUS_COINS})`;
const signupPaidBonusReason = () => `오픈 이벤트 지급 (${SIGNUP_PAID_BONUS_COINS})`;
const isSignupOpenEventReason = (reasonText) => {
  const r = String(reasonText || '').trim();
  return /^오픈 이벤트 지급\s*\(\d+\)/.test(r)
    || /^신규 가입 유료 포인트 지급\s*\(\d+\)/.test(r);
};
const monthlyFreeGrantReason = (ym) => `월간 무료 포인트 지급 (${ym})`;
/** 한국(Asia/Seoul) 달력 기준 YYYY-MM — 월간 무료 지급·초기화 판별에 사용 */
const kstYearMonthKey = (d = new Date()) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
    }).formatToParts(d);
    const y = parts.find((p) => p.type === 'year')?.value;
    const m = parts.find((p) => p.type === 'month')?.value;
    if (y && m) return `${y}-${m}`;
  } catch { /* fall through */ }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
/** 이번 달 월간 지급 내역이 없을 때 목록용 가상 행 정렬 시각 (KST 매월 1일 00:00) */
const kstMonthStartIso = (d = new Date()) => {
  const ym = kstYearMonthKey(d);
  return new Date(`${ym}-01T00:00:00+09:00`).toISOString();
};
/** 전월 분을 안 쓴 채로 넘긴 경우(무료 ≥30) 신규 달 1일 지급 직전 30 차감 */
const monthlyFreeUnusedResetReason = (ym) => `월간 무료 포인트 미사용 분 초기화 (${ym})`;
const hasCoinAdjustmentReason = async (userId, reason) => {
  if (!userId || !reason) return false;
  const { data, error } = await supabase
    .from('coin_adjustments')
    .select('id')
    .eq('user_id', userId)
    .eq('reason', reason)
    .limit(1)
    .maybeSingle();
  if (error) return false;
  return !!data?.id;
};
const grantSignupPaidBonusOnce = async (userId) => {
  if (!userId) return false;
  const reason = signupPaidBonusReason();
  if (await hasCoinAdjustmentReason(userId, reason)) return false;
  if (await hasCoinAdjustmentReason(userId, SIGNUP_PAID_BONUS_REASON_LEGACY)) return false;
  await updateUserCoins(userId, {
    paidDelta: SIGNUP_PAID_BONUS_COINS,
    paidReason: reason,
  });
  return true;
};
const ensureMonthlyFreeCoins = async (userId) => {
  if (!userId) return false;
  const ym = kstYearMonthKey();
  const grantReason = monthlyFreeGrantReason(ym);
  if (await hasCoinAdjustmentReason(userId, grantReason)) return false;

  const { data: p } = await supabase
    .from('profiles')
    .select('free_coins')
    .eq('user_id', userId)
    .maybeSingle();
  const free = Number(p?.free_coins ?? 0);
  if (free >= MONTHLY_FREE_COINS) {
    await updateUserCoins(userId, {
      freeDelta: -MONTHLY_FREE_COINS,
      freeReason: monthlyFreeUnusedResetReason(ym),
    });
  }
  await updateUserCoins(userId, {
    freeDelta: MONTHLY_FREE_COINS,
    freeReason: grantReason,
  });
  return true;
};

// 조건 충족 시 방 status를 active로 고정 (모임장/참여자 모두 호출 가능)
const maybeActivateClubForChat = async (clubId) => {
  if (!clubId) return false;
  try {
    const { data, error } = await supabase.rpc('maybe_activate_club_for_chat', {
      p_club_id: clubId,
    });
    if (error) throw error;
    return !!data;
  } catch (e) {
    console.warn('maybe_activate_club_for_chat RPC 실패, 폴백 시도:', e?.message || e);
    try {
      const { data: club, error: clubErr } = await supabase
        .from('clubs')
        .select('id, status, created_at, members')
        .eq('id', clubId)
        .maybeSingle();
      if (clubErr || !club) return false;
      if (club.status === 'active') return true;
      const minMembers = getRoomOpenMinMembers(club);
      const memberCount = Number(club.members || 0);
      if (memberCount < minMembers) return false;
      const { error: updateErr } = await supabase
        .from('clubs')
        .update({ status: 'active' })
        .eq('id', clubId);
      if (updateErr) {
        console.warn('오픈 상태 폴백 업데이트 실패:', updateErr.message);
        return false;
      }
      return true;
    } catch (fallbackErr) {
      console.warn('오픈 상태 폴백 실패:', fallbackErr?.message || fallbackErr);
      return false;
    }
  }
};

// 한번 오픈된 방은 status를 active로 고정해 다시 대기 상태로 내려가지 않게 한다.
const pinClubAsActive = async (clubId) => {
  if (!clubId) return false;
  try {
    const { error } = await supabase
      .from('clubs')
      .update({ status: 'active', is_active: true })
      .eq('id', clubId);
    if (!error) return true;
    // RLS 등으로 직접 업데이트가 막히는 경우, 멤버용 RPC로 재시도
    const activated = await maybeActivateClubForChat(clubId);
    if (!activated) {
      console.warn('pinClubAsActive 실패:', error.message);
      return false;
    }
    return true;
  } catch (e) {
    console.warn('pinClubAsActive 예외:', e?.message || e);
    return false;
  }
};

// club_members 실제 행 수를 기준으로 clubs.members 갱신 (증감 대신 재계산)
const syncClubMembersCount = async (clubId) => {
  const { count, error } = await supabase
    .from('club_members')
    .select('*', { count: 'exact', head: true })
    .eq('club_id', clubId);
  if (error) { console.warn('syncClubMembersCount:', error.message); return; }
  const n = count ?? 0;
  const { error: rpcErr } = await supabase.rpc('sync_club_members_count', { p_club_id: clubId });
  if (!rpcErr) return;
  const { error: upErr } = await supabase.from('clubs').update({ members: n }).eq('id', clubId);
  if (upErr) console.warn('syncClubMembersCount(update):', upErr.message);
};

// 모임장 이탈·취소 시 방을 비활성화 (소프트 삭제)
const deactivateClub = async (clubId) => {
  if (!clubId) return false;
  const { data: rpcOk, error: rpcErr } = await supabase.rpc('close_my_club_if_host', { p_club_id: clubId });
  if (!rpcErr && rpcOk === true) return true;
  if (!rpcErr && rpcOk === false) {
    console.warn('close_my_club_if_host: 모임장이 아니거나 방이 없습니다.');
    return false;
  }
  if (rpcErr) {
    console.warn('close_my_club_if_host RPC(폴백 시도):', rpcErr.message);
  }
  const { error } = await supabase
    .from('clubs')
    .update({ is_active: false, status: 'cancelled' })
    .eq('id', clubId);
  if (error) {
    console.warn('deactivateClub 직접 업데이트 실패:', error.message);
    return false;
  }
  return true;
};

// 멤버십 등록 + 카운트 재계산 (방 생성/참여 시 호출)
const insertClubMember = async (clubId, userId, memberMeta = null) => {
  if (!clubId || !userId) return false;
  const joinedAt = new Date().toISOString();
  const displayName = String(memberMeta?.name || '').trim() || null;
  const profileImageUrl = String(memberMeta?.avatar || '').trim() || null;
  const { error } = await supabase.from('club_members').upsert(
    {
      club_id: clubId,
      user_id: userId,
      joined_at: joinedAt,
      member_name: displayName,
      member_profile_image_url: profileImageUrl,
    },
    { onConflict: 'club_id,user_id', ignoreDuplicates: true }
  );
  if (error) {
    console.warn('insertClubMember 실패:', error.message);
    return false;
  }
  await rememberClubMemberHistory(clubId, userId, joinedAt);
  await syncClubMembersCount(clubId);
  // 24시간 내 6명 / 이후 4명 이상이면 자동 오픈 → status를 active로 고정
  const activated = await maybeActivateClubForChat(clubId);
  if (activated) {
    try {
      const { data } = await supabase.from('clubs').select('members').eq('id', clubId).maybeSingle();
      const mem = data?.members ?? null;
      alert(`🎉 모임방이 열렸습니다!\n현재 ${mem ?? ''}명 참여중 · 지금부터 채팅이 가능합니다.`);
    } catch { /* noop */ }
  }
  return true;
};

const rememberClubMemberHistory = async (clubId, userId, joinedAt = null) => {
  if (!clubId || !userId) return;
  const { error } = await supabase.from('club_member_history').upsert(
    { club_id: String(clubId), user_id: String(userId), first_joined_at: joinedAt || new Date().toISOString() },
    { onConflict: 'club_id,user_id', ignoreDuplicates: true }
  );
  if (error && !(error.message?.includes('schema') || error.message?.includes('relation'))) {
    console.warn('club_member_history 기록 실패:', error.message);
  }
};

const clearClubMemberHistory = async (clubId, userId) => {
  if (!clubId || !userId) return;
  const { error } = await supabase
    .from('club_member_history')
    .delete()
    .eq('club_id', String(clubId))
    .eq('user_id', String(userId));
  if (error && !(error.message?.includes('schema') || error.message?.includes('relation'))) {
    console.warn('club_member_history 삭제 실패:', error.message);
  }
};

// 채팅 조회 기준이 되는 joined_at을 보장한다. 기존 행은 절대 덮어쓰지 않는다.
const ensureClubMemberJoinedAt = async (clubId, userId, fallbackJoinedAt = null) => {
  if (!clubId || !userId) return null;
  const [{ data: existing, error: selectErr }, { data: history }] = await Promise.all([
    supabase
      .from('club_members')
      .select('joined_at')
      .eq('club_id', clubId)
      .eq('user_id', userId)
      .maybeSingle(),
    supabase
      .from('club_member_history')
      .select('first_joined_at')
      .eq('club_id', String(clubId))
      .eq('user_id', String(userId))
      .maybeSingle(),
  ]);
  if (selectErr) {
    console.warn('club_members joined_at 조회 실패:', selectErr.message);
    return null;
  }
  if (existing?.joined_at) {
    await rememberClubMemberHistory(clubId, userId, existing.joined_at);
    return existing.joined_at;
  }

  const joinedAt = history?.first_joined_at || fallbackJoinedAt || new Date().toISOString();
  const { data: inserted, error: insertErr } = await supabase
    .from('club_members')
    .insert({ club_id: clubId, user_id: userId, joined_at: joinedAt })
    .select('joined_at')
    .maybeSingle();
  if (insertErr) {
    console.warn('club_members joined_at 생성 실패:', insertErr.message);
    const { data: retry } = await supabase
      .from('club_members')
      .select('joined_at')
      .eq('club_id', clubId)
      .eq('user_id', userId)
      .maybeSingle();
    return retry?.joined_at || null;
  }
  await rememberClubMemberHistory(clubId, userId, joinedAt);
  await syncClubMembersCount(clubId);
  return inserted?.joined_at || joinedAt;
};

// 강퇴 이력이 있으면 해당 방 재참여 차단
const wasKickedFromClub = async (clubId, userId) => {
  if (!clubId || !userId) return false;
  const { data, error } = await supabase
    .from('club_kicks_v2')
    .select('id')
    .eq('club_id', clubId)
    .eq('user_id', userId)
    .limit(1)
    .maybeSingle();
  if (!error && data?.id) return true;
  // 레거시 호환: 일부 경로(host-kick-member 등)가 club_kicks(legacy)에 기록하는 경우가 있어 폴백 체크
  const { data: legacy, error: legacyErr } = await supabase
    .from('club_kicks')
    .select('id')
    .eq('club_id', clubId)
    .eq('user_id', userId)
    .limit(1)
    .maybeSingle();
  if (legacyErr) return false;
  return !!legacy?.id;
};

// 모임장 승계: joined_at 가장 빠른 비(非)모임장에게 host_id 이전
// 후계자 없음(모임장 단독) → 멤버 제거 후 방 비활성화만 수행·승계 없음
// 반환: { ok: true, newHostId } | { ok: true, roomClosed: true } | { ok: false }
const succeedHost = async (clubId, currentHostId) => {
  // 현재 모임장 제외하고 가장 먼저 입장한 멤버 조회
  const { data: successor, error: successorErr } = await supabase
    .from('club_members')
    .select('user_id')
    .eq('club_id', clubId)
    .neq('user_id', currentHostId)
    .order('joined_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (successorErr) {
    console.warn('모임장 승계 후보 조회 실패:', successorErr.message);
    return { ok: false };
  }

  if (successor?.user_id) {
    // 1) 먼저 host_id를 후계자에게 변경 (실패 시 나가기 중단 가능)
    const { error: hostUpdateErr } = await supabase.from('clubs')
      .update({ host_id: successor.user_id })
      .eq('id', clubId);
    if (hostUpdateErr) {
      console.warn('모임장 승계 실패:', hostUpdateErr.message);
      return { ok: false };
    }
    // 2) 성공 후 기존 모임장 멤버십 제거
    await supabase.from('club_members').delete()
      .eq('club_id', clubId).eq('user_id', currentHostId);
    await syncClubMembersCount(clubId);
    return { ok: true, newHostId: successor.user_id };
  }
  // 후계자 없음 → 모임장만 남은 경우: 승계 없이 멤버 제거 후 방 종료(비활성)
  const { error: soloDelErr } = await supabase.from('club_members').delete()
    .eq('club_id', clubId).eq('user_id', currentHostId);
  if (soloDelErr) {
    console.warn('모임장 단독 멤버십 삭제 실패:', soloDelErr.message);
    return { ok: false };
  }
  await syncClubMembersCount(clubId);
  const closed = await deactivateClub(clubId);
  if (!closed) return { ok: false };
  return { ok: true, roomClosed: true };
};

const fetchActiveNotice = async () => {
  const { data, error } = await supabase
    .from('notices').select('*').eq('active', true)
    .order('created_at', { ascending: false }).limit(1).maybeSingle();
  if (error) { console.error(error); return null; }
  return data;
};

const NOTICE_DISMISS_KEY = 'gyeol_dismissed_notice_ids';
const getDismissedNoticeIds = () => {
  try {
    const raw = localStorage.getItem(NOTICE_DISMISS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};
const saveDismissedNoticeId = (noticeId) => {
  if (!noticeId) return;
  const prev = getDismissedNoticeIds();
  if (prev.includes(noticeId)) return;
  localStorage.setItem(NOTICE_DISMISS_KEY, JSON.stringify([...prev, noticeId]));
};

// 여러 식별자 중 하나라도 banned_users에 있으면 true
const areIdsBanned = async (ids) => {
  const filtered = ids.filter(Boolean);
  if (!filtered.length) return false;
  const nowIso = new Date().toISOString();
  let { data, error } = await supabase
    .from('banned_users')
    .select('identifier')
    .in('identifier', filtered)
    .or(`expires_at.is.null,expires_at.gt.${nowIso}`);
  if (error && (error.message?.includes('column') || error.code === 'PGRST204')) {
    ({ data, error } = await supabase
      .from('banned_users')
      .select('identifier')
      .in('identifier', filtered));
  }
  if (error) { console.error(error); return false; }
  return (data || []).length > 0;
};

// 로그인 유저의 phone/email/id 중 하나라도 banned_users에 있는지
// name은 동명이인 오차단 방지를 위해 제외
const checkBanned = async (sessionUser) =>
  areIdsBanned([sessionUser.phone, sessionUser.user_metadata?.phone, sessionUser.email, sessionUser.id]);

// ── 계정 정지 안내(로그아웃 직전 1회) ─────────────────────────────

const fetchActiveAccountSanctionForUserId = async (userId) => {
  if (!userId) return null;
  const nowIso = new Date().toISOString();
  const { data, error } = await supabase
    .from('user_sanctions_v2')
    .select('id,sanction_type,end_at,is_active')
    .eq('user_id', userId)
    .eq('is_active', true)
    .or(`end_at.is.null,end_at.gt.${nowIso}`)
    .in('sanction_type', ['ACCOUNT_SUSPEND_1D', 'ACCOUNT_SUSPEND_7D', 'PERMANENT_BAN'])
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) return null;
  return data || null;
};

const buildAccountSuspendPopup = (row) => {
  const st = String(row?.sanction_type || '');
  if (st === 'ACCOUNT_SUSPEND_1D') {
    return {
      title: '1일 계정 정지 안내',
      message:
        '운영 정책에 위반될 수 있는 활동이 확인되어 계정 이용이 1일 동안 제한되었습니다.\n\n'
        + '안전한 채팅 환경 유지를 위한 조치이며, 반복 위반 시 추가 이용 제한 또는 장기 정지 조치가 적용될 수 있습니다.\n\n'
        + '조치에 대한 문의 또는 재검토가 필요한 경우 카카오채널 @동네사이 로 문의해주세요.',
    };
  }
  if (st === 'ACCOUNT_SUSPEND_7D') {
    return {
      title: '7일 계정 정지 안내',
      message:
        '운영 정책에 위반될 수 있는 활동이 반복적으로 확인되어 계정 이용이 7일 동안 제한되었습니다.\n\n'
        + '안전한 서비스 환경 유지를 위한 조치이며, 추가 위반 시 영구 정지 조치가 적용될 수 있습니다.\n\n'
        + '조치에 대한 문의 또는 재검토가 필요한 경우 카카오채널 @동네사이 로 문의해주세요.',
    };
  }
  if (st === 'PERMANENT_BAN') {
    return {
      title: '영구 정지 안내',
      message:
        '운영 정책에 위반되는 활동이 반복적으로 확인되어 계정 이용이 영구적으로 제한되었습니다.\n\n'
        + '안전한 서비스 환경 유지를 위한 조치이며, 서비스 이용이 더 이상 불가능할 수 있습니다.\n\n'
        + '조치에 대한 문의 또는 재검토가 필요한 경우 카카오채널 @동네사이 로 문의해주세요.',
    };
  }
  return null;
};

// ── 유저 프로필 (출생년도/성별 등 1회만 설정, DB 영구 저장) ──
const fetchProfile = async (userId) => {
  const { data, error } = await supabase
    .from('profiles').select('*').eq('user_id', userId).maybeSingle();
  if (error) { console.error(error); return null; }
  return data;
};

const NICKNAME_MIN_LEN = 2;
const NICKNAME_MAX_LEN = 12;
const NICKNAME_COOLDOWN_DAYS = 30;
const NICKNAME_REGEX = /^[가-힣a-zA-Z0-9_]+$/;
const normalizeNickname = (v) => String(v || '').trim();
const isValidNicknameFormat = (v) => {
  const n = normalizeNickname(v);
  return n.length >= NICKNAME_MIN_LEN && n.length <= NICKNAME_MAX_LEN && NICKNAME_REGEX.test(n);
};
const nicknameCooldown = (lastUpdatedAt) => {
  if (!lastUpdatedAt) return { canChange: true, leftDays: 0 };
  const elapsedMs = Date.now() - new Date(lastUpdatedAt).getTime();
  const cooldownMs = NICKNAME_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
  if (elapsedMs >= cooldownMs) return { canChange: true, leftDays: 0 };
  const leftDays = Math.ceil((cooldownMs - elapsedMs) / (24 * 60 * 60 * 1000));
  return { canChange: false, leftDays };
};
const nicknameNextLabel = (lastUpdatedAt) => {
  const { canChange, leftDays } = nicknameCooldown(lastUpdatedAt);
  return canChange ? '지금 변경 가능' : `${leftDays}일 후 변경 가능`;
};
const isMissingProfileColumnError = (error) =>
  !!(error?.code === 'PGRST204' || error?.message?.includes('column'));
const SEARCH_HISTORY_LIMIT = 5;
const searchHistoryStorageKey = (userId) => (userId ? `gyeol_search_history_${userId}` : null);
const loadSearchHistory = (userId) => {
  const key = searchHistoryStorageKey(userId);
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(Boolean).map(v => String(v).trim()).filter(Boolean) : [];
  } catch {
    return [];
  }
};
const saveSearchHistory = (userId, history) => {
  const key = searchHistoryStorageKey(userId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify((history || []).slice(0, SEARCH_HISTORY_LIMIT)));
  } catch {}
};
const pushSearchHistory = (history, nextKeyword) => {
  const keyword = String(nextKeyword || '').trim();
  if (!keyword) return history || [];
  const deduped = [keyword, ...(history || []).filter(v => String(v).trim() !== keyword)];
  return deduped.slice(0, SEARCH_HISTORY_LIMIT);
};
const isNicknameAvailable = async (nickname, currentUserId = null) => {
  const nick = normalizeNickname(nickname);
  if (!isValidNicknameFormat(nick)) return false;
  const { data, error } = await supabase
    .from('profiles')
    .select('user_id')
    .eq('nickname', nick)
    .maybeSingle();
  if (error) {
    if (isMissingProfileColumnError(error)) return null;
    throw error;
  }
  if (!data) return true;
  return data.user_id === currentUserId;
};

const isLikelyHeicFile = (file) => {
  const mime = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '').toLowerCase();
  return mime.includes('heic') || mime.includes('heif') || name.endsWith('.heic') || name.endsWith('.heif');
};

async function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function loadImageFromDataURL(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('이미지를 불러올 수 없습니다.'));
    img.src = dataUrl;
  });
}

/** iPhone HEIC/HEIF → JPEG File (heic2any) */
async function convertHeicIfNeeded(file) {
  if (!isLikelyHeicFile(file)) return file;
  try {
    const mod = await import('heic2any');
    const heic2any = mod.default;
    const converted = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.92,
    });
    const outBlob = Array.isArray(converted) ? converted[0] : converted;
    const base = String(file.name || 'photo').replace(/\.[^.]+$/, '') || 'photo';
    return new File([outBlob], `${base}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } catch (e) {
    console.warn('HEIC 변환 실패', e);
    throw new Error('HEIC 사진을 JPEG로 바꾸지 못했습니다. 사진 앱에서「가장 호환되는 형식」으로 저장하거나 JPG를 선택해주세요.');
  }
}

/** 최장 변 maxEdge 이하로 맞춘 뒤 JPEG로 인코딩 */
async function encodeImageFileToJpegFile(file, maxEdge, quality) {
  const dataUrl = await readFileAsDataURL(file);
  const img = await loadImageFromDataURL(dataUrl);
  const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  const blob = await new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', quality);
  });
  if (!blob) throw new Error('이미지 인코딩에 실패했습니다.');
  const base = String(file.name || 'img').replace(/\.[^.]+$/, '') || 'img';
  return new File([blob], `${base}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
}

/**
 * 업로드 직전 공통 처리: HEIC→JPEG, 최대 1080px(옵션), 품질·해상도 단계 조정으로 목표 용량 이하
 */
async function prepareImageFileForUpload(file, opts = {}) {
  if (!file) return file;
  const maxEdge = opts.maxEdge ?? IMAGE_UPLOAD_MAX_EDGE_PX;
  const targetBytes = opts.targetBytes ?? IMAGE_UPLOAD_TARGET_MAX_BYTES;
  let q = opts.qualityStart ?? IMAGE_UPLOAD_QUALITY_START;
  const qMin = opts.qualityMin ?? IMAGE_UPLOAD_QUALITY_MIN;
  if (file.size > IMAGE_UPLOAD_MAX_INPUT_BYTES) {
    throw new Error('원본 파일이 너무 큽니다. 다른 사진을 선택해주세요.');
  }
  let f = await convertHeicIfNeeded(file);
  const mime = String(f.type || '').toLowerCase();
  if (!mime.startsWith('image/') && !isLikelyHeicFile(file)) {
    throw new Error('이미지 파일만 선택할 수 있어요.');
  }
  if (mime === 'image/svg+xml') {
    throw new Error('SVG는 지원하지 않아요.');
  }
  let edge = maxEdge;
  for (let i = 0; i < 22; i += 1) {
    const out = await encodeImageFileToJpegFile(f, edge, q);
    if (out.size <= targetBytes) return out;
    if (q > qMin + 0.02) {
      q = Math.max(qMin, q - 0.07);
      continue;
    }
    if (edge > 520) {
      edge = Math.round(edge * 0.86);
      q = opts.qualityStart ?? IMAGE_UPLOAD_QUALITY_START;
      continue;
    }
    q = Math.max(0.52, q - 0.05);
  }
  const last = await encodeImageFileToJpegFile(f, 480, qMin);
  if (last.size > targetBytes * 1.15) {
    throw new Error('용량을 목표 이하로 줄이기 어렵습니다. 다른 이미지를 선택해주세요.');
  }
  return last;
}

async function uploadProfileImageFromDataUrl(userId, dataUrl) {
  if (!userId || !dataUrl || !String(dataUrl).startsWith('data:')) return null;
  const res = await fetch(dataUrl);
  let blob = await res.blob();
  if (blob.size > IMAGE_UPLOAD_TARGET_MAX_BYTES) {
    const tmp = new File([blob], 'avatar.jpg', { type: blob.type || 'image/jpeg' });
    const prepared = await prepareImageFileForUpload(tmp);
    blob = prepared;
  }
  if (blob.size > IMAGE_UPLOAD_TARGET_MAX_BYTES * 1.15) {
    throw new Error('프로필 사진은 압축 후에도 용량이 큽니다. 다른 사진을 선택해주세요.');
  }
  const mime = blob.type || 'image/jpeg';
  const ext = mime.includes('png') ? 'png' : 'jpg';
  const path = `${userId}/avatar.${ext}`;
  for (const bucket of ['profile-images', 'chat-images']) {
    const { error: upErr } = await supabase.storage.from(bucket).upload(path, blob, {
      upsert: true,
      contentType: mime,
    });
    if (upErr) continue;
    const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
    if (pub?.publicUrl) return withUrlCacheBust(pub.publicUrl);
  }
  throw new Error('프로필 사진 업로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
}

function dataUrlToFile(dataUrl, filename = 'image.jpg') {
  const arr = String(dataUrl || '').split(',');
  const mimeMatch = arr[0]?.match(/:(.*?);/);
  const mime = mimeMatch?.[1] || 'image/jpeg';
  const bstr = atob(arr[1] || '');
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], filename, { type: mime });
}


// 가입: 법정 만 나이(생년월일) 만 16 이상만 허용.
// 모임·모임장 연령대·참여 가능 여부: 출생연도만으로 나이(올해 − 생년 + 1, 생일 무시) — 일상에서 말하는 몇 살과 맞춤
const MIN_SIGNUP_MAN_AGE = 16;
/** 가입 단계·UI에서 청소년(만 나이) 구분용 */
const MINOR_MAX_MAN_AGE = 19;
const MINOR_ROOM_MIN = 16;
const MINOR_ROOM_MAX = 19;
/** 성인 방 연령대 산정 하한(연도 기준 나이 기준) */
const ADULT_ROOM_POLICY_BASE = 20;

const calcManAgeFromYmd = (y, m, d) => {
  const t = new Date();
  let age = t.getFullYear() - y;
  if (t.getMonth() + 1 < m || (t.getMonth() + 1 === m && t.getDate() < d)) age -= 1;
  return age;
};
const signupAgeRuleLine = () =>
  `만 ${MIN_SIGNUP_MAN_AGE}세 이상(생년월일 기준 법정 연령)`;
const signupAgeRuleFootnote = () =>
  '※ 가입은 생년월일 기준 법정 만 나이(만 16 이상)로만 검증합니다. 모임 연령·내 방 나이대는 출생연도만으로 계산합니다(올해 − 생년 + 1).';

const isSignupEligibleFullDate = (birthYear, birthMonth, birthDay) => {
  const y = Number(birthYear);
  const m = Number(birthMonth);
  const d = Number(birthDay);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return false;
  if (String(birthYear).length !== 4) return false;
  return calcManAgeFromYmd(y, m, d) >= MIN_SIGNUP_MAN_AGE;
};

/** 모임·연령대: (올해 − 출생연도) + 1, 생일 무시 */
const calcYearDiffAgeFromBirthYear = (birthYear) => {
  const y = Number(birthYear);
  if (!Number.isFinite(y) || String(birthYear).length !== 4) return null;
  return new Date().getFullYear() - y + 1;
};

const resolveYearDiffAge = (user) => {
  if (!user?.birthYear || String(user.birthYear).length !== 4) return null;
  return calcYearDiffAgeFromBirthYear(user.birthYear);
};

const deriveAgeGroupFromBirth = (birthYear, _birthMonth, _birthDay) => {
  const y = Number(birthYear);
  if (!Number.isFinite(y) || String(birthYear).length !== 4) return 'all';
  const age = calcYearDiffAgeFromBirthYear(String(birthYear));
  if (age == null || !Number.isFinite(age)) return 'all';
  if (age >= MIN_SIGNUP_MAN_AGE && age <= MINOR_MAX_MAN_AGE) return '16-19';
  if (age >= 30 && age <= 34) return '30-34';
  if (age >= 35 && age <= 42) return '35-42';
  if (age >= 43 && age <= 50) return '43-50';
  return 'all';
};

const calcAgeRange = (birthYear, _birthMonth, _birthDay) => {
  const age = calcYearDiffAgeFromBirthYear(birthYear);
  if (age == null || !Number.isFinite(age) || age < MIN_SIGNUP_MAN_AGE) return null;
  if (age <= MINOR_MAX_MAN_AGE) {
    return { minAge: MINOR_ROOM_MIN, maxAge: MINOR_ROOM_MAX };
  }
  const minAge = Math.max(ADULT_ROOM_POLICY_BASE, age - 4);
  const maxAge = age < ADULT_ROOM_POLICY_BASE + 4
    ? ADULT_ROOM_POLICY_BASE * 2 + 8 - age
    : age + 4;
  return { minAge, maxAge };
};
const calcAgeRangeFromAge = (age) => {
  const n = Number(age);
  if (!Number.isFinite(n) || n < MIN_SIGNUP_MAN_AGE) return null;
  if (n <= MINOR_MAX_MAN_AGE) {
    return { minAge: MINOR_ROOM_MIN, maxAge: MINOR_ROOM_MAX };
  }
  const minAge = Math.max(ADULT_ROOM_POLICY_BASE, n - 4);
  const maxAge = n < ADULT_ROOM_POLICY_BASE + 4
    ? ADULT_ROOM_POLICY_BASE * 2 + 8 - n
    : n + 4;
  return { minAge, maxAge };
};
const parseBirthYearFromAgeText = (ageText) => {
  const text = String(ageText || '');
  const m = text.match(/(19|20)\d{2}/);
  return m ? Number(m[0]) : null;
};
const resolveUserBirthYear = (user) => {
  if (user?.birthYear && String(user.birthYear).length === 4) return Number(user.birthYear);
  const fromAgeText = parseBirthYearFromAgeText(user?.age);
  if (fromAgeText) return fromAgeText;
  return null;
};
const resolveHostAgeRange = (user) => {
  const birthYear = resolveUserBirthYear(user);
  return birthYear ? calcAgeRange(birthYear, user?.birthMonth, user?.birthDay) : null;
};
const canJoinClub = (user, club) => {
  if (!user?.birthYear) return true;
  if (!club || club.roomType !== 'age_limited') return true;
  const age = resolveYearDiffAge(user);
  if (age == null) return true;
  const minN = Number(club.minAge);
  const maxN = Number(club.maxAge);
  const lo = Number.isFinite(minN) ? minN : 0;
  const hi = Number.isFinite(maxN) ? maxN : 120;
  return age >= lo && age <= hi;
};

const HOBBY_MAP = {
  wine: '와인·맛집',
  brunch: '브런치·카페',
  book: '책·독서',
  talk: '책·독서',
  culture: '전시·문화생활',
  walk: '산책·등산',
  game: '게임·오락',
  sports: '운동·스포츠',
  other: '기타',
};
const hobbyCategoryLabel = (h) => HOBBY_MAP[h]?.replace(/^[^\s]+\s/, '').trim() || h;
const normalizeClubHobbies = (club) => {
  if (!club) return [];
  const fromArray = Array.isArray(club.hobbies) ? club.hobbies.filter(Boolean) : [];
  const source = fromArray.length > 0 ? fromArray : (club.hobby ? [club.hobby] : []);
  return Array.from(new Set(source.map(v => String(v || '').trim()).filter(Boolean)));
};
const clubHobbyCategoryText = (club) => normalizeClubHobbies(club).map(hobbyCategoryLabel).join(' · ');
const clubHobbyFullText = (club) => normalizeClubHobbies(club).map(h => HOBBY_MAP[h] || h).join(' · ');
const HOBBIES = [
  { id: 'wine', label: HOBBY_MAP.wine },
  { id: 'brunch', label: HOBBY_MAP.brunch },
  { id: 'book', label: HOBBY_MAP.book },
  { id: 'culture', label: HOBBY_MAP.culture },
  { id: 'walk', label: HOBBY_MAP.walk },
  { id: 'game', label: HOBBY_MAP.game },
  { id: 'sports', label: HOBBY_MAP.sports },
  { id: 'other', label: HOBBY_MAP.other },
];
const DEFAULT_CLUB_COVER_KEYS = new Set(['wine', 'brunch', 'book', 'culture', 'walk', 'game', 'sports', 'other']);
/** `public/default-covers` 이미지 교체 후 브라우저 캐시 갱신이 필요하면 숫자만 올려주세요. */
const DEFAULT_COVER_ASSET_V = '4';
/** public/default-covers/{key}.jpeg — 주제별 기본 배경(대표 사진 미업로드 시). 실패 시 /chatroom.png */
function defaultClubCoverKey(hobby) {
  const h = String(hobby || '').trim();
  if (h === 'talk') return 'book';
  if (DEFAULT_CLUB_COVER_KEYS.has(h)) return h;
  return 'other';
}
function defaultClubCoverSrc(hobby) {
  const k = defaultClubCoverKey(hobby);
  const pre = process.env.PUBLIC_URL || '';
  return `${pre}/default-covers/${k}.jpeg?v=${DEFAULT_COVER_ASSET_V}`;
}
function defaultClubCoverFallbackSrc() {
  const pre = process.env.PUBLIC_URL || '';
  return `${pre}/chatroom.png?v=${DEFAULT_COVER_ASSET_V}`;
}
const RATIO_MAP = { mixed: '혼합 모임', female: '여성전용 모임', male: '남성전용 모임' };
/** profiles.gender — '남'/'여' 외 레거시·영문 값 정규화 */
const normalizeProfileGender = (raw) => {
  const g = String(raw || '').trim().toLowerCase();
  if (!g) return null;
  if (g === '남' || g === '남성' || g === 'male' || g === 'm' || g === 'man') return 'male';
  if (g === '여' || g === '여성' || g === 'female' || g === 'f' || g === 'woman') return 'female';
  return null;
};
const enrichMembersForGenderCount = (members, { viewer, hostId, hostProfile }) => (
  (members || []).map((m) => {
    let gender = normalizeProfileGender(m?.gender) ? m.gender : null;
    if (!gender && sameUserId(m?.id, viewer?.userId) && viewer?.gender) {
      gender = viewer.gender;
    }
    if (!gender && sameUserId(m?.id, hostId)) {
      gender = hostProfile?.gender
        || (sameUserId(hostId, viewer?.userId) ? viewer?.gender : null);
    }
    return { ...m, gender };
  })
);
/** 참여 멤버 성별 집계 — 항상 0명 포함, 예: "👨 남성 2명  👩 여성 2명" */
const formatGenderMemberBreakdown = (members) => {
  let male = 0;
  let female = 0;
  (members || []).forEach((m) => {
    const g = normalizeProfileGender(m?.gender);
    if (g === 'male') male += 1;
    else if (g === 'female') female += 1;
  });
  return ` 남성 ${male}명  여성 ${female}명`;
};
const isRatioAllowedForGender = (ratio, gender) => {
  if (ratio === 'female') return gender === '여';
  if (ratio === 'male') return gender === '남';
  return true;
};
const getRatioDenyMessage = (ratio) => {
  if (ratio === 'female') return '여성전용 모임은 여성 회원만 신청할 수 있어요.';
  if (ratio === 'male') return '남성전용 모임은 남성 회원만 신청할 수 있어요.';
  return '해당 모임 타입은 신청할 수 없습니다.';
};
/** 카드·버튼에서 연령 불가 / 성별 불가 구분 (연령은 생년 기준 나이) */
const joinEligibilityBlockLabel = (user, club) => {
  if (!user) return '';
  const genderOk = isRatioAllowedForGender(club?.ratio, user?.gender);
  const ageOk = !user?.birthYear || canJoinClub(user, club);
  if (genderOk && ageOk) return '';
  if (!genderOk && !ageOk) return '성별·연령';
  if (!genderOk) return '성별';
  return '연령';
};
/** 연령 제한 모임 — 모달·알림용 한 줄 안내 */
function clubAgeApplyOnlyMessage(club) {
  if (!club || club.roomType !== 'age_limited') {
    return '이 모임의 연령 조건에 맞는 분만 신청할 수 있어요.';
  }
  const minN = Number(club.minAge);
  const maxN = Number(club.maxAge);
  if (Number.isFinite(minN) && Number.isFinite(maxN)) {
    return `${minN}세부터 ${maxN}세까지만 신청 가능합니다.`;
  }
  return '이 모임은 모임장 나이대에 따라 신청 가능한 연령이 정해져 있어요.';
}

function parseNotifTimestamp(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return new Date(value);
  if (typeof value === 'string') {
    const t = new Date(value);
    return Number.isNaN(t.getTime()) ? null : t;
  }
  return null;
}

/** 알림 목록 시각 — 기기 로컬 기준(절대 날짜·시각이 헷갈리지 않게) */
function formatNotifListTime(value) {
  const d = parseNotifTimestamp(value);
  if (!d) return '방금 전';
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  if (diffMs >= 0 && diffMs < 45_000) return '방금 전';
  if (diffMs >= 0 && diffMs < 60 * 60_000) {
    const m = Math.max(1, Math.floor(diffMs / 60_000));
    return `${m}분 전`;
  }
  const pad = (n) => String(n).padStart(2, '0');
  const sameYmd = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (sameYmd(d, now)) return `오늘 ${hm}`;
  const yest = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (sameYmd(d, yest)) return `어제 ${hm}`;
  if (d.getFullYear() === now.getFullYear()) return `${d.getMonth() + 1}월 ${d.getDate()}일 ${hm}`;
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${hm}`;
}

const HOST_COST = 50;
const JOIN_COST = 30;

/** 매칭 취소·이탈 (채팅방 매칭 상세와 동일). 홈/모임찾기/채팅 탭 공통 */
async function executeMembershipWithdrawal({
  match,
  user,
  appState,
  clubs,
  setUser,
  updateState,
  setClubs,
}) {
  if (!match?.clubId || !user?.userId) return;

  let clubForPolicy = clubs?.find(cl => cl.id === match.clubId);
  if (!clubForPolicy) {
    const { data: row, error: clubFetchErr } = await supabase
      .from('clubs')
      .select('*')
      .eq('id', match.clubId)
      .maybeSingle();
    if (!clubFetchErr && row) {
      const hydrated = await hydrateClubMembersFromMembership([row]);
      clubForPolicy = mapClubRow(hydrated[0] || row);
    }
  }
  const anchorMs = parseCancelPolicyAnchorMs(match, clubForPolicy, user?.userId);
  const hoursPassed = Math.floor((Date.now() - anchorMs) / 3600000);
  const coinType = match.coinType || 'free';
  const applicantWasCreatorHost = !!match.applicantWasHost;
  const currentClub = clubForPolicy;
  const isMatched = currentClub?.status === 'matched';
  const isCurrentHost = sameUserId(currentClub?.hostId, user?.userId);
  const spentCoins = match.spentCoins || (applicantWasCreatorHost ? HOST_COST : JOIN_COST);

  let refundCoins = 0;
  let refundReason = '';
  if (isMatched) {
    refundCoins = 0;
    refundReason = '매칭 완료 후 취소 (환급 없음)';
  } else if (applicantWasCreatorHost) {
    if (hoursPassed >= 48 && !isMatched && coinType === 'paid') {
      refundCoins = spentCoins;
      refundReason = '매칭 취소 환급 (모임장 48시간 경과 전액)';
    }
  } else {
    const freeCancel = hoursPassed >= 24;
    if (freeCancel) {
      refundCoins = coinType === 'paid' ? spentCoins : 0;
      refundReason = '매칭 취소 환급 (24시간 경과 전액)';
    } else {
      refundCoins = coinType === 'paid' ? Math.max(0, spentCoins - 10) : 0;
      refundReason = '매칭 취소 환급 (10포인트 패널티 적용)';
    }
  }

  if (match.clubId && isClubOpenForChat(currentClub)) {
    await pinClubAsActive(match.clubId);
  }

  if (isCurrentHost && match.clubId) {
    const hostRes = await succeedHost(match.clubId, user?.userId);
    if (!hostRes?.ok) {
      alert('방 종료·모임장 처리에 실패했습니다. 잠시 후 다시 시도해주세요.');
      return;
    }
    await clearClubMemberHistory(match.clubId, user?.userId);
  } else if (!isCurrentHost && match.clubId) {
    await supabase.from('club_members').delete()
      .eq('club_id', match.clubId).eq('user_id', user?.userId);
    await clearClubMemberHistory(match.clubId, user?.userId);
    await syncClubMembersCount(match.clubId);
  }

  await supabase.from('matches')
    .update({ status: 'cancelled' })
    .eq('user_id', user?.userId)
    .eq('status', 'waiting');

  setUser(prev => refundCoins > 0 && coinType === 'paid'
    ? { ...prev, paidCoins: prev.paidCoins + refundCoins }
    : prev);
  if (refundCoins > 0 && coinType === 'paid') {
    await updateMyPaidCoinsWithHistory(refundCoins, refundReason);
  }
  updateState('matches', (appState.matches || []).filter(m => m.clubId !== match.clubId));
  fetchClubs().then(setClubs);

  if (isMatched) {
    alert('취소 완료: 매칭 완료된 방은 환급되지 않습니다.');
  } else if (applicantWasCreatorHost) {
    alert(refundCoins > 0
      ? `완료: 유료포인트 ${spentCoins}포인트가 환급되었습니다.`
      : '방에서 나갔습니다.\n(방 생성 48시간 이내 나가기 — 포인트 환급 없음)');
  } else if (coinType !== 'paid') {
    alert('취소 완료: 무료 포인트는 환급되지 않습니다.');
  } else if (refundCoins === 20) {
    alert('취소 완료: 10포인트 패널티가 적용되어 유료포인트 20포인트가 환급되었습니다.');
  } else if (refundCoins === spentCoins) {
    alert(`취소 완료: 유료포인트 ${spentCoins}포인트가 전액 환급되었습니다.`);
  } else {
    alert('취소 완료: 10포인트 패널티가 적용되어 20포인트가 환급되었습니다.');
  }
}

const TIME_LABELS = {
  weekday_morning: '평일 오전',
  weekday_evening: '평일 저녁',
  weekend_day: '주말 낮',
  weekend_evening: '주말 저녁',
};
// 포인트 아이콘(앱 전역 공통)
// point.png는 배경(체커)이 포함되어 있어, 배경 제거 후 생성한 point_coin_only.png를 사용
const COIN_WALLET_ICON_URL = `${process.env.PUBLIC_URL || ''}/point.png`;
function CoinOnlyIcon({ size = 20, style = {} }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: '999px',
        overflow: 'hidden',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        background: 'transparent',
        ...style,
      }}
    >
      <img
        src={COIN_WALLET_ICON_URL}
        alt=""
        aria-hidden="true"
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          objectFit: 'contain',
          objectPosition: 'center',
          background: 'transparent',
          // 아이콘 원형이 꽉 차 보이도록 약간 확대(이미지 여백 보정)
          transform: 'scale(1.08)',
        }}
      />
    </span>
  );
}
const AGE_GROUP_LABELS = { 'all': '전체연령', '16-19': '16~19세', '30-34': '30~34세', '35-42': '35~42세', '43-50': '43~50세' };
const KAKAO_CHANNEL_URL = 'https://pf.kakao.com/_LBGTX';
const LOGIN_ID_REGEX = /^[a-z0-9_]{4,20}$/;
const normalizeLoginId = (v) => String(v || '').trim().toLowerCase();
const loginIdToEmail = (loginId) => `${normalizeLoginId(loginId)}@id.dongnesai.local`;
const isValidLoginId = (loginId) => LOGIN_ID_REGEX.test(normalizeLoginId(loginId));
const OTP_RESEND_COOLDOWN_SEC = 60;
const CHAT_FETCH_LIMIT = 200;

// getSession + onAuthStateChange 가 동시에 SIGNED_IN 을 쏠 때 포인트 지급/프로필 로드가 이중 실행되지 않도록 직렬화
const authUserInitLocks = new Map();

export default function App() {
  const [screen, setScreen]     = useState('home');
  /** 뒤로 스와이프용: 앞으로 갈 때마다 이전 screen 문자열을 쌓아 둔다 */
  const screenHistoryRef      = useRef([]);
  const [user, setUser]         = useState(null);
  const [clubs, setClubs]       = useState([]);
  const [appNotice, setAppNotice] = useState(null);
  // (구) 유료결제 준비중 모달 제거 — 토스 결제위젯으로 바로 연결
  const maintenanceTapCountRef = useRef(0);
  const maintenanceTapTimerRef = useRef(null);
  const [maintenanceConfig, setMaintenanceConfig] = useState({
    enabled: false,
    message: '현재 웹앱 업데이트 중입니다. 잠시 후 다시 이용해주세요.',
    blocked: false,
    loaded: false,
  });
  const clubsRefreshTimerRef     = useRef(null);
  const activatingClubIdsRef = useRef(new Set());
  const prevHostByClubRef = useRef(new Map());
  const hostChangeInitRef = useRef(false);
  const suppressHostNoticeUntilRef = useRef({});
  const suppressHostNoticeForClub = (clubId, ms = 15000) => {
    if (!clubId) return;
    suppressHostNoticeUntilRef.current[clubId] = Date.now() + ms;
  };

  // ── 알림 시스템 ──────────────────────────────────────────────
  const [unreadCounts, setUnreadCounts] = useState({});
  const [mutedRooms, setMutedRooms] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('gyeol_muted') || '[]')); }
    catch { return new Set(); }
  });
  const [chatNotif, setChatNotif] = useState(null); // { clubId, clubTitle, senderName, text }
  const [chatNotifDragY, setChatNotifDragY] = useState(0);
  const [chatNotifIsDragging, setChatNotifIsDragging] = useState(false);
  const chatNotifTimerRef = useRef(null);
  const chatNotifDragYRef = useRef(0);
  const chatNotifPointerStartYRef = useRef(0);
  const chatNotifPointerActiveRef = useRef(false);
  const chatNotifMovedRef = useRef(false);
  const notifiedMessageIdsRef = useRef(new Set());
  /** user_warnings_v2: 모든 화면에서 경고팝업(관리자 챗밴·자동 단계별). ChatScreen에만 두면 채팅 탭 외에서는 구독이 없음 */
  const userWarnV2ConsumeIdsRef = useRef(new Set());
  const prevOpenByClubRef = useRef(new Map());
  const roomOpenInitRef = useRef(false);
  const roomOpenStorageKey = user?.userId ? `gyeol_room_open_notifs_${user.userId}` : null;
  const roomOpenSeenKey = user?.userId ? `gyeol_room_open_seen_${user.userId}` : null;
  const [roomOpenNotifs, setRoomOpenNotifs] = useState([]);
  const roomOpenSeenRef = useRef(new Set());
  const roomOpenSeenLoadedRef = useRef(false);
  const screenRef = useRef(screen);
  const currentClubIdRef = useRef(null);
  // club_user_chat_blocks: { clubId -> { blockedUserId -> [{ start, end? }, ...] } }
  const blockedSinceByClubRef = useRef(new Map());
  const [chatBlocksRev, setChatBlocksRev] = useState(0);
  const unreadStorageKey = user?.userId ? `gyeol_unread_${user.userId}` : null;
  const chatLastReadKey = user?.userId ? `gyeol_chat_last_read_${user.userId}` : null;
  const readChatLastReadMap = () => {
    if (!chatLastReadKey) return {};
    try {
      const parsed = JSON.parse(localStorage.getItem(chatLastReadKey) || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  };
  const writeChatLastRead = (clubId, iso = new Date().toISOString()) => {
    if (!chatLastReadKey || !clubId) return;
    try {
      const prev = readChatLastReadMap();
      const prevMs = prev[clubId] ? new Date(prev[clubId]).getTime() : 0;
      const nextMs = iso ? new Date(iso).getTime() : Date.now();
      const nextIso = Number.isFinite(nextMs) && nextMs > prevMs
        ? new Date(nextMs + 1000).toISOString()
        : new Date(Math.max(Date.now(), prevMs) + 1000).toISOString();
      localStorage.setItem(chatLastReadKey, JSON.stringify({ ...prev, [clubId]: nextIso }));
    } catch {}
  };
  const getBlockWindows = (clubId, otherUserId) => {
    const cid = String(clubId || '').trim();
    const ouid = String(otherUserId || '').trim();
    if (!cid || !ouid) return null;
    const m = blockedSinceByClubRef.current.get(cid);
    if (!m) return null;
    return m.get(ouid) || null;
  };
  const isBlockedMessageForMe = (clubId, senderUserId, createdAtIso) => {
    const windows = getBlockWindows(clubId, senderUserId);
    if (!windows || !Array.isArray(windows) || windows.length === 0) return false;
    const msgMs = createdAtIso ? new Date(createdAtIso).getTime() : NaN;
    if (!Number.isFinite(msgMs) || msgMs <= 0) return false;
    for (const w of windows) {
      const sMs = w?.start ? new Date(w.start).getTime() : NaN;
      const eMs = w?.end ? new Date(w.end).getTime() : Infinity;
      if (!Number.isFinite(sMs) || sMs <= 0) continue;
      const end = Number.isFinite(eMs) && eMs > 0 ? eMs : Infinity;
      if (msgMs >= sMs && msgMs <= end) return true;
    }
    return false;
  };

  // 전역(홈/탭/알림/미읽음)에서 쓰는 차단 목록을 항상 최신으로 유지
  useEffect(() => {
    const uid = user?.userId;
    if (!uid) {
      blockedSinceByClubRef.current = new Map();
      return undefined;
    }
    let alive = true;
    const loadBlocks = async () => {
      const { data, error } = await supabase
        .from('club_user_chat_blocks')
        .select('club_id, blocked_user_id, created_at, ended_at')
        .eq('blocker_user_id', uid);
      if (!alive) return;
      if (error) {
        logClientError('loadAllClubChatBlocks', error?.message, error);
        return;
      }
      const next = new Map();
      (data || []).forEach((r) => {
        const clubId = String(r.club_id || '').trim();
        const bid = String(r.blocked_user_id || '').trim();
        if (!clubId || !bid) return;
        if (!next.has(clubId)) next.set(clubId, new Map());
        const byUser = next.get(clubId);
        const prev = byUser.get(bid) || [];
        byUser.set(bid, [...prev, { start: r.created_at, end: r.ended_at || null }]);
      });
      blockedSinceByClubRef.current = next;
      setChatBlocksRev(v => (v + 1) % 1000000);
    };
    loadBlocks();
    const channel = supabase.channel(`club_user_chat_blocks_all_${uid}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'club_user_chat_blocks',
        filter: `blocker_user_id=eq.${uid}`,
      }, () => { loadBlocks(); })
      .subscribe();
    return () => {
      alive = false;
      supabase.removeChannel(channel);
    };
  }, [user?.userId]);
  const refreshUnreadFromDbForUser = async (targetUserId, { countOpenRoom = false } = {}) => {
    if (!targetUserId) return;
    const lastReadStorageKey = `gyeol_chat_last_read_${targetUserId}`;
    let lastReadMap = {};
    try {
      const parsed = JSON.parse(localStorage.getItem(lastReadStorageKey) || '{}');
      lastReadMap = parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      lastReadMap = {};
    }

    const { data: memberRows, error } = await supabase
      .from('club_members')
      .select('club_id, joined_at')
      .eq('user_id', targetUserId);
    if (error || !memberRows) {
      setUnreadCounts(prev => ({ [ROOM_OPEN_ALERT_KEY]: (prev || {})[ROOM_OPEN_ALERT_KEY] || 0 }));
      return;
    }

    const memberClubIds = [...new Set((memberRows || [])
      .map(row => String(row?.club_id || '').trim())
      .filter(Boolean))];
    if (memberClubIds.length === 0) {
      setUnreadCounts(prev => ({ [ROOM_OPEN_ALERT_KEY]: (prev || {})[ROOM_OPEN_ALERT_KEY] || 0 }));
      return;
    }

    const { data: liveClubs } = await supabase
      .from('clubs')
      .select('id,is_active,status,host_id')
      .in('id', memberClubIds);
    const liveClubIds = new Set((liveClubs || [])
      .filter(c => c?.id && c.is_active !== false && c.host_id && ['recruiting', 'active'].includes(c.status || 'recruiting'))
      .map(c => String(c.id)));

    const nextCounts = {};
    await Promise.all((memberRows || []).map(async (row) => {
      const clubId = String(row?.club_id || '').trim();
      if (!clubId) return;
      if (!liveClubIds.has(clubId)) return;
      if (!countOpenRoom && screenRef.current === 'chat' && currentClubIdRef.current === clubId) {
        nextCounts[clubId] = 0;
        return;
      }
      const rawSince = lastReadMap[clubId] || row?.joined_at || new Date(0).toISOString();
      const sinceMs = new Date(rawSince).getTime();
      const joinedMs = row?.joined_at ? new Date(row.joined_at).getTime() : 0;
      const since = Number.isFinite(sinceMs) && sinceMs > 0
        ? new Date(Math.max(sinceMs, joinedMs || 0)).toISOString()
        : (row?.joined_at || new Date(0).toISOString());
      // 차단한 사용자의 메시지는(차단 시점부터) 미읽음/뱃지/전역 알림에서 제외한다.
      const { data: cnt, error: countError } = await supabase
        .rpc('count_unread_chat_messages_excluding_blocks', {
          p_club_id: clubId,
          p_since: since,
        });
      if (!countError) nextCounts[clubId] = Number(cnt || 0) || 0;
    }));

    setUnreadCounts(prev => ({
      ...nextCounts,
      // 채팅 미읽음은 DB 재계산 결과만 신뢰한다. 홈 알림(방 오픈)은 별도 보존.
      [ROOM_OPEN_ALERT_KEY]: (prev || {})[ROOM_OPEN_ALERT_KEY] || 0,
    }));
  };
  // screenRef는 useEffect보다 렌더에서 먼저 맞춘다 (나가는 직후 한 틱 동안 'chat'으로 남아
  // 미읽음 재계산·전역 알림이 잘못 동작하는 것을 방지).
  screenRef.current = screen;
  useEffect(() => {
    if (!user?.userId) {
      setUnreadCounts({});
      return;
    }
    try {
      const raw = localStorage.getItem(`gyeol_unread_${user.userId}`) || '{}';
      const parsed = JSON.parse(raw);
      // 채팅 미읽음은 DB에서 즉시 재계산한다. 로컬에서는 홈 알림 카운트만 복원한다.
      setUnreadCounts(parsed && typeof parsed === 'object'
        ? { [ROOM_OPEN_ALERT_KEY]: Number(parsed[ROOM_OPEN_ALERT_KEY] || 0) }
        : {});
    } catch {
      setUnreadCounts({});
    }
  }, [user?.userId]);
  useEffect(() => {
    if (!unreadStorageKey) return;
    try { localStorage.setItem(unreadStorageKey, JSON.stringify(unreadCounts || {})); } catch {}
  }, [unreadStorageKey, unreadCounts]);

  useEffect(() => {
    if (!user?.userId || !chatLastReadKey) return;
    let cancelled = false;

    const refreshUnreadFromDb = async () => {
      if (cancelled) return;
      await refreshUnreadFromDbForUser(user.userId);
    };

    refreshUnreadFromDb();
    window.addEventListener('focus', refreshUnreadFromDb);
    document.addEventListener('visibilitychange', refreshUnreadFromDb);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', refreshUnreadFromDb);
      document.removeEventListener('visibilitychange', refreshUnreadFromDb);
    };
  }, [user?.userId, chatLastReadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user?.userId) return;
    refreshUnreadFromDbForUser(user.userId);
  }, [user?.userId, clubs]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user?.userId) return;
    refreshUnreadFromDbForUser(user.userId);
  }, [user?.userId, chatBlocksRev]); // eslint-disable-line react-hooks/exhaustive-deps
  // 채팅 화면 이탈 직후 DB·last_read 기준으로 탭 배지 재동기화 (숨김 마운트 구독과 상태 불일치 보정)
  useEffect(() => {
    if (!user?.userId) return;
    if (screen !== 'chat') return;
    return () => {
      void refreshUnreadFromDbForUser(user.userId);
    };
  }, [screen, user?.userId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!chatNotif) return;
    chatNotifDragYRef.current = 0;
    setChatNotifDragY(0);
    setChatNotifIsDragging(false);
    chatNotifMovedRef.current = false;
  }, [chatNotif]);
  useEffect(() => {
    if (!roomOpenStorageKey) {
      setRoomOpenNotifs([]);
      return;
    }
    try {
      const raw = localStorage.getItem(roomOpenStorageKey) || '[]';
      const parsed = JSON.parse(raw);
      setRoomOpenNotifs(Array.isArray(parsed) ? parsed : []);
    } catch {
      setRoomOpenNotifs([]);
    }
  }, [roomOpenStorageKey]);
  useEffect(() => {
    if (!roomOpenSeenKey) {
      roomOpenSeenRef.current = new Set();
      roomOpenSeenLoadedRef.current = false;
      return;
    }
    try {
      const raw = localStorage.getItem(roomOpenSeenKey) || '[]';
      const parsed = JSON.parse(raw);
      roomOpenSeenRef.current = new Set(Array.isArray(parsed) ? parsed.map(String) : []);
      roomOpenSeenLoadedRef.current = true;
    } catch {
      roomOpenSeenRef.current = new Set();
      roomOpenSeenLoadedRef.current = true;
    }
  }, [roomOpenSeenKey]);
  useEffect(() => {
    if (!roomOpenStorageKey) return;
    try { localStorage.setItem(roomOpenStorageKey, JSON.stringify(roomOpenNotifs || [])); } catch {}
  }, [roomOpenStorageKey, roomOpenNotifs]);
  // onNewMsgRef: keep-mounted ChatScreen에서 항상 최신 상태를 읽어 알림 처리
  const onNewMsgRef = useRef(null);
  onNewMsgRef.current = (clubId, msg, clubTitle) => {
    if (isBlockedMessageForMe(clubId, msg?.userId, msg?.createdAt)) return;
    if (msg?.id) {
      if (notifiedMessageIdsRef.current.has(msg.id)) return;
      notifiedMessageIdsRef.current.add(msg.id);
      if (notifiedMessageIdsRef.current.size > 300) {
        const first = notifiedMessageIdsRef.current.values().next().value;
        notifiedMessageIdsRef.current.delete(first);
      }
    }
    if (screenRef.current === 'chat' && currentClubIdRef.current === clubId) {
      writeChatLastRead(clubId);
      return;
    }
    // 뮤트 여부와 상관없이 카운트는 항상 증가
    setUnreadCounts(prev => ({ ...prev, [clubId]: (prev[clubId] || 0) + 1 }));
    // 뮤트된 방은 토스트 알림만 생략
    if (mutedRooms.has(clubId)) return;
    setChatNotif({ clubId, clubTitle, senderName: msg.name, text: msg.text || '📷 사진' });
    if (chatNotifTimerRef.current) clearTimeout(chatNotifTimerRef.current);
    chatNotifTimerRef.current = setTimeout(() => {
      chatNotifTimerRef.current = null;
      chatNotifDragYRef.current = 0;
      setChatNotifDragY(0);
      setChatNotif(null);
    }, 3500);
  };
  const onResetUnread = (clubId, readAt = new Date().toISOString()) => {
    writeChatLastRead(clubId, readAt);
    setUnreadCounts(prev => ({ ...prev, [clubId]: 0 }));
  };
  const onToggleMute = (clubId) => {
    setMutedRooms(prev => {
      const next = new Set(prev);
      if (next.has(clubId)) next.delete(clubId); else next.add(clubId);
      try { localStorage.setItem('gyeol_muted', JSON.stringify([...next])); } catch {}
      return next;
    });
  };
  const [appState, setAppState] = useState({
    hobby: null, hobbies: [], region: '', ratio: null, times: [],
    matches: [], fromWallet: false, fromClub: false,
    hostingTitle: '', hostingIntro: '', hostingCoverDataUrl: null, currentClubId: null, roomType: 'age_limited',
    tossChargeDraft: null,
  });
  const resetSessionScopedAppState = () => {
    setAppState(prev => ({
      ...prev,
      hobbies: [],
      matches: [],
      fromClub: false,
      fromWallet: false,
      hostingTitle: '',
      hostingIntro: '',
      hostingCoverDataUrl: null,
      currentClubId: null,
      tossChargeDraft: null,
    }));
  };
  const notifyClubIdsKey = Array.from(new Set([
    ...appState.matches.map(m => m.clubId).filter(Boolean),
    ...clubs.filter(c => sameUserId(c.hostId, user?.userId)).map(c => c.id).filter(Boolean),
  ])).sort().join(',');
  const isMaintenanceBlocked = maintenanceConfig.loaded && maintenanceConfig.blocked;
  const readOnlyLoginRequiredScreens = new Set([
    'hobby', 'time', 'form', 'ratio', 'confirm', 'host_title',
    'match', 'chat', 'wallet', 'charge', 'profile', 'notif',
    // welcome_profile: 가입 직후 setUser와 같은 틱에 onNav 할 때 user가 아직 null인 경우가 있어
    // 여기 넣으면 window.confirm(로그인)이 뜬다. 화면에서 user 없으면 막음.
  ]);
  const navigate = (nextScreen, navOpts = {}) => {
    const replace = !!(navOpts && navOpts.replace);
    const root = !!(navOpts && navOpts.root);
    const resetHistory = !!(navOpts && navOpts.resetHistory);
    if (isMaintenanceBlocked && nextScreen !== 'login') {
      screenHistoryRef.current = [];
      setScreen('home');
      return;
    }
    if (!user && readOnlyLoginRequiredScreens.has(nextScreen)) {
      const shouldLogin = window.confirm('로그인하시겠습니까?');
      if (!shouldLogin) return;
      screenHistoryRef.current = [];
      setScreen('login');
      return;
    }
    if (nextScreen === 'notif') {
      setUnreadCounts(prev => {
        if (!prev || !Object.prototype.hasOwnProperty.call(prev, ROOM_OPEN_ALERT_KEY)) return prev;
        const next = { ...prev };
        delete next[ROOM_OPEN_ALERT_KEY];
        return next;
      });
      setRoomOpenNotifs(prev => (prev || []).map(item => ({ ...item, isRead: true })));
    }
    if (resetHistory || root) {
      screenHistoryRef.current = [];
      setScreen(nextScreen);
      return;
    }
    if (replace) {
      if (screenHistoryRef.current.length > 0) screenHistoryRef.current.pop();
      setScreen(nextScreen);
      return;
    }
    if (nextScreen !== screen) {
      screenHistoryRef.current = [...screenHistoryRef.current, screen].slice(-40);
      setScreen(nextScreen);
    }
  };

  // [베타] 토스페이먼츠 결제 리다이렉트 비활성화 — 남은 /pay URL만 정리 후 지갑으로
  useEffect(() => {
    const path = String(window.location.pathname || '');
    if (path === '/pay/success' || path === '/pay/fail') {
      window.history.replaceState(null, '', '/');
      screenHistoryRef.current = [];
      setScreen('wallet');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const swipeBackGestureRef = useRef(null);
  const trySwipeNavigateBack = () => {
    if (appNotice || chatNotif) return false;
    const noHist = new Set(['login', 'setup', 'welcome_profile']);
    if (screen === 'chat') {
      navigate('match', { replace: true });
      return true;
    }
    if (noHist.has(screen)) return false;
    const h = screenHistoryRef.current;
    if (h.length === 0) return false;
    const prev = h.pop();
    if (prev) setScreen(prev);
    return true;
  };
  const handleMaintenanceLogoTap = () => {
    if (maintenanceTapTimerRef.current) clearTimeout(maintenanceTapTimerRef.current);
    maintenanceTapCountRef.current += 1;
    if (maintenanceTapCountRef.current >= 5) {
      maintenanceTapCountRef.current = 0;
      navigate('login', { resetHistory: true });
      return;
    }
    maintenanceTapTimerRef.current = setTimeout(() => {
      maintenanceTapCountRef.current = 0;
      maintenanceTapTimerRef.current = null;
    }, 1800);
  };

  useEffect(() => () => {
    if (maintenanceTapTimerRef.current) clearTimeout(maintenanceTapTimerRef.current);
  }, []);

  useEffect(() => {
    const loadMaintenanceConfig = async () => {
      const { data, error } = await supabase.rpc('get_maintenance_state');
      if (error) {
        // app_config 미생성 상태에서는 점검모드를 비활성으로 간주
        setMaintenanceConfig(prev => ({ ...prev, enabled: false, blocked: false, loaded: true }));
        return;
      }
      const row = Array.isArray(data) ? data[0] : data;
      setMaintenanceConfig({
        enabled: !!row?.maintenance_mode,
        message: row?.maintenance_message || '현재 웹앱 업데이트 중입니다. 잠시 후 다시 이용해주세요.',
        blocked: !!row?.blocked,
        loaded: true,
      });
    };
    loadMaintenanceConfig();
    const poll = setInterval(loadMaintenanceConfig, 10000);
    const onFocus = () => { loadMaintenanceConfig(); };
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(poll);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  useEffect(() => {
    if (!isMaintenanceBlocked) return;
    if (screen !== 'home' && screen !== 'login') {
      screenHistoryRef.current = [];
      setScreen('home');
    }
  }, [isMaintenanceBlocked, screen]);

  useEffect(() => {
    if (!user?.userId) {
      prevHostByClubRef.current = new Map();
      hostChangeInitRef.current = false;
      return;
    }
    const nextMap = new Map((clubs || []).map(c => [c.id, c.hostId || null]));
    if (!hostChangeInitRef.current) {
      prevHostByClubRef.current = nextMap;
      hostChangeInitRef.current = true;
      return;
    }
    for (const c of (clubs || [])) {
      const prevHost = prevHostByClubRef.current.get(c.id) || null;
      const nextHost = c.hostId || null;
      const becameNewHost = prevHost && nextHost && prevHost !== nextHost
        && sameUserId(nextHost, user.userId) && !sameUserId(prevHost, user.userId);
      const suppressUntil = suppressHostNoticeUntilRef.current[c.id] || 0;
      if (Date.now() < suppressUntil) continue;
      if (!becameNewHost) continue;
      const clubName = c.title || HOBBY_MAP[c.hobby] || '모임방';
      if (isClubOpenForChat(c)) {
        setAppNotice(`안내: 이전 모임장이 방을 떠나 회원님이 "${clubName}"의 모임장이 되었습니다.`);
      } else {
        setAppNotice(`안내: 이전 모임장이 방을 떠나 회원님이 "${clubName}"의 모임장이 되었습니다.`);
      }
    }
    prevHostByClubRef.current = nextMap;
  }, [clubs, user?.userId]);

  // 내가 신청/참여한 방이 매칭중 -> 채팅중으로 열릴 때 홈 알림 카운트 증가
  useEffect(() => {
    if (!user?.userId) {
      prevOpenByClubRef.current = new Map();
      roomOpenInitRef.current = false;
      return;
    }
    if (!roomOpenSeenLoadedRef.current) return;
    const myClubIdSet = new Set((appState.matches || []).map(m => m.clubId).filter(Boolean));
    if (myClubIdSet.size === 0) return;
    const currentMap = new Map(
      (clubs || [])
        .filter(c => myClubIdSet.has(c.id))
        .map(c => [c.id, isClubOpenForChat(c)])
    );

    if (!roomOpenInitRef.current) {
      prevOpenByClubRef.current = currentMap;
      roomOpenInitRef.current = true;
      return;
    }

    const openedClubs = [];
    const seen = new Set(roomOpenSeenRef.current);
    // 닫힌 방은 seen에서 제거해서 추후 재오픈 시 다시 알림 가능
    for (const id of Array.from(seen)) {
      const openNow = currentMap.get(id) || false;
      if (!openNow) seen.delete(id);
    }
    for (const [clubId, isOpenNow] of currentMap.entries()) {
      const wasOpen = prevOpenByClubRef.current.get(clubId) || false;
      if (!wasOpen && isOpenNow && !seen.has(clubId)) {
        const club = (clubs || []).find(c => c.id === clubId);
        openedClubs.push({
          id: `room_open_${clubId}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          clubId,
          title: club?.title || HOBBY_MAP[club?.hobby] || '모임',
          createdAt: new Date().toISOString(),
          isRead: false,
        });
        seen.add(clubId);
      }
    }
    roomOpenSeenRef.current = seen;
    if (roomOpenSeenKey) {
      try { localStorage.setItem(roomOpenSeenKey, JSON.stringify([...seen])); } catch {}
    }
    if (openedClubs.length > 0) {
      setUnreadCounts(prev => ({
        ...(prev || {}),
        [ROOM_OPEN_ALERT_KEY]: ((prev && prev[ROOM_OPEN_ALERT_KEY]) || 0) + openedClubs.length,
      }));
      setRoomOpenNotifs(prev => [...openedClubs, ...(prev || [])].slice(0, 50));
    }
    prevOpenByClubRef.current = currentMap;
  }, [clubs, appState.matches, user?.userId, roomOpenSeenKey]);

  // 한번 오픈 조건을 만족한 방은 status를 active로 고정해서,
  // 인원 변동(누군가 이탈) 후에도 다시 "매칭중"으로 내려가지 않도록 보장한다.
  useEffect(() => {
    const candidates = (clubs || []).filter(c => c?.id && c.status !== 'active' && isClubOpenForChat(c));
    candidates.forEach((club) => {
      if (activatingClubIdsRef.current.has(club.id)) return;
      activatingClubIdsRef.current.add(club.id);
      supabase
        .from('clubs')
        .update({ status: 'active' })
        .eq('id', club.id)
        .then(({ error }) => {
          if (error) console.warn('active 고정 업데이트 실패:', error.message);
        })
        .finally(() => {
          activatingClubIdsRef.current.delete(club.id);
        });
    });
  }, [clubs]);

  // 클럽/멤버십 로드 + 실시간 구독 (사용자 화면 전체의 기준 데이터)
  useEffect(() => {
    // DB를 단일 진실 공급원으로 사용: clubs + 내 멤버십을 동시에 갱신
    const load = async () => {
      setClubs(await fetchClubs());
      const uid = userIdRef.current;
      if (uid) {
        const memberships = await loadUserMemberships(uid);
        setAppState(prev => ({ ...prev, matches: memberships }));
      }
    };
    load();
    window.addEventListener('focus', load);

    const scheduleLoad = (delay = 120) => {
      if (clubsRefreshTimerRef.current) clearTimeout(clubsRefreshTimerRef.current);
      clubsRefreshTimerRef.current = setTimeout(() => {
        load();
        clubsRefreshTimerRef.current = null;
      }, delay);
    };

    // Realtime: 사용자 화면 핵심 데이터 즉시 반영
    const channel = supabase.channel('clubs-watch')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'clubs' }, () => scheduleLoad())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'club_members' }, () => scheduleLoad())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'club_kicks_v2' }, () => scheduleLoad())
      .subscribe();

    // Realtime 누락/네트워크 흔들림 대비 안전 폴백
    const poll = setInterval(load, 15 * 1000);

    return () => {
      window.removeEventListener('focus', load);
      if (clubsRefreshTimerRef.current) clearTimeout(clubsRefreshTimerRef.current);
      clearInterval(poll);
      supabase.removeChannel(channel);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 앱 전체 채팅 알림: ChatScreen이 열려 있지 않아도 내 방 메시지를 감지한다.
  useEffect(() => {
    if (!user?.userId || !notifyClubIdsKey) return;
    const notifyClubIds = new Set(notifyClubIdsKey.split(',').filter(Boolean));
    const titleByClubId = new Map((clubs || []).map(c => [c.id, c.title || HOBBY_MAP[c.hobby] || '모임']));
    const channel = supabase.channel('chat_global_' + user.userId)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messages' }, (payload) => {
        const row = payload.new;
        if (!row?.club_id || !notifyClubIds.has(row.club_id)) return;
        if (String(row.user_id || '') === GYEOL_CHAT_SYSTEM_USER_ID) return;
        if (sameUserId(row.user_id, user.userId)) return;
        if (isBlockedMessageForMe(row.club_id, row.user_id, row.created_at)) return;
        onNewMsgRef.current?.(row.club_id, {
          id: row.id,
          name: String(row.user_name || '회원').trim() || '회원',
          text: row.text || null,
          image: sanitizeUserImageUrl(row.image_url) || null,
          userId: row.user_id,
          createdAt: row.created_at || null,
        }, titleByClubId.get(row.club_id) || '모임');
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.userId, notifyClubIdsKey, clubs]); // eslint-disable-line react-hooks/exhaustive-deps

  // user_warnings_v2: 로그인 시 어느 화면(홈·매칭·지갑 등)에 있어도 경고 즉시/폴링 처리
  useEffect(() => {
    let alive = true;
    if (!user?.userId) return undefined;
    if (window.location.pathname === ADMIN_PATH) return undefined;

    const seenKey = `gyeol_seen_warning_ids_v1_${user.userId}`;

    const consumeUserWarningV2 = async (data) => {
      if (!alive || !data?.id) return;
      if (data.is_read === true) return;
      const idStr = String(data.id);
      if (userWarnV2ConsumeIdsRef.current.has(idStr)) return;
      userWarnV2ConsumeIdsRef.current.add(idStr);
      try {
        const title = String(data?.title || '관리자로부터 경고를 받았습니다.');
        const msg = String(data?.message || '신고 또는 채팅 이용 규칙 위반으로 인해 경고가 부여되었습니다.');
        // 채팅 모더 invoke가 이미 같은 제재/경고 팝업을 띄운 경우 DB 알림은 읽음만 처리
        if (wasModSanctionPopupShownRecently()) {
          await supabase.from('user_warnings_v2').update({ is_read: true }).eq('id', data.id).catch(() => {});
          return;
        }
        const alreadySeen = (() => {
          try {
            const raw = localStorage.getItem(seenKey);
            const arr = raw ? JSON.parse(raw) : [];
            return Array.isArray(arr) && arr.includes(idStr);
          } catch { return false; }
        })();
        if (alreadySeen) {
          await supabase.from('user_warnings_v2').update({ is_read: true }).eq('id', data.id).catch(() => {});
          return;
        }
        try {
          const raw = localStorage.getItem(seenKey);
          const arr = raw ? JSON.parse(raw) : [];
          const next = Array.isArray(arr) ? arr.filter(Boolean) : [];
          next.push(idStr);
          const sliced = next.slice(-200);
          localStorage.setItem(seenKey, JSON.stringify(sliced));
        } catch {}

        alert(`${title}\n\n${msg}`);
        await supabase.from('user_warnings_v2').update({ is_read: true }).eq('id', data.id).catch(() => {});
      } finally {
        userWarnV2ConsumeIdsRef.current.delete(idStr);
      }
    };

    const checkWarnings = async () => {
      const { data, error } = await supabase
        .from('user_warnings_v2')
        .select('id,title,message,created_at,is_read,warning_type')
        .eq('user_id', user.userId)
        .eq('is_read', false)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (!alive || error || !data?.id) return;
      await consumeUserWarningV2(data);
    };

    checkWarnings();
    const timer = setInterval(checkWarnings, 15000);

    const channel = supabase.channel('user-warnings-v2-global-' + user.userId)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'user_warnings_v2',
        filter: `user_id=eq.${user.userId}`,
      }, async (payload) => {
        const row = payload?.new;
        if (!row?.id || row?.is_read === true) return;
        await consumeUserWarningV2(row);
      })
      .subscribe();

    return () => {
      alive = false;
      clearInterval(timer);
      supabase.removeChannel(channel);
    };
  }, [user?.userId]);

  // 모바일 앱처럼: 뷰포트 고정
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      document.documentElement.classList.add('capacitor-native');
      document.body.classList.add('capacitor-native');
    }
    const setVh = () =>
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    setVh();
    window.addEventListener('resize', setVh);
    window.addEventListener('orientationchange', setVh);

    document.body.style.overflow  = 'hidden';
    document.body.style.position  = 'fixed';
    document.body.style.width     = '100%';
    document.body.style.height    = '100%';

    const style = document.createElement('style');
    style.textContent = `
      * { -webkit-tap-highlight-color: transparent; touch-action: manipulation; }
      input, textarea, select { touch-action: auto; }
      .app-wrap   { height: calc(var(--vh,1vh)*100); overflow: hidden; }
      .screen     { height: calc(var(--vh,1vh)*100); max-height: calc(var(--vh,1vh)*100); }
      .page-fixed { height: calc(var(--vh,1vh)*100); }
      input, button, select, textarea { font-size: 16px !important; }
      ::-webkit-scrollbar { display: none; }
      * { scrollbar-width: none; }
    `;
    document.head.appendChild(style);
    return () => {
      window.removeEventListener('resize', setVh);
      window.removeEventListener('orientationchange', setVh);
      try { document.head.removeChild(style); } catch {}
    };
  }, []);

  // Supabase 인증
  useEffect(() => {
    // 관리자 페이지에서는 유저 세션/정지 체크 스킵 (PIN 인증만 사용)
    if (window.location.pathname === ADMIN_PATH) return;

    const runCheckAndSetUser = async (sessionUser) => {
      if (!sessionUser) return;

      const identifier = sessionUser.phone || sessionUser.user_metadata?.phone || sessionUser.email || sessionUser.id;
      const profile = await fetchProfile(sessionUser.id);
      const name = profile?.name || sessionUser.user_metadata?.full_name || sessionUser.user_metadata?.name || sessionUser.phone || sessionUser.email || '회원';
  // 무료 포인트는 월 1회(30) 보장 지급
      await ensureMonthlyFreeCoins(sessionUser.id);
      const refreshedProfile = await fetchProfile(sessionUser.id);

      // 정지 체크 (DB 기반) - phone/email/id 중 하나라도 매치되면 차단
      // name은 동명이인 오차단 방지를 위해 체크 대상에서 제외
      if (await checkBanned(sessionUser)) {
        try {
          const sanction = await fetchActiveAccountSanctionForUserId(sessionUser.id);
          const popup = buildAccountSuspendPopup(sanction);
          if (popup?.title && popup?.message) alert(`${popup.title}\n\n${popup.message}`);
        } catch {}
        await supabase.auth.signOut();
        return;
      }

      // 사용자 전환(재로그인/신규가입) 시 이전 세션의 방 상태가 섞이지 않도록 초기화
      resetSessionScopedAppState();
      setChatNotif(null);

      setUser({
        name,
        nickname:   refreshedProfile?.nickname || profile?.nickname || null,
        email:      sessionUser.email || null,
        phone:      refreshedProfile?.phone || profile?.phone || sessionUser.phone || sessionUser.user_metadata?.phone || null,
        age:        refreshedProfile?.age_display || profile?.age_display || '인증됨',
        type:       sessionUser.app_metadata?.provider || 'phone',
        freeCoins:  refreshedProfile?.free_coins ?? profile?.free_coins ?? 0,
        paidCoins:  refreshedProfile?.paid_coins ?? profile?.paid_coins ?? 0,
        profileImageUrl: refreshedProfile?.profile_image_url || profile?.profile_image_url || null,
        nicknameUpdatedAt: refreshedProfile?.nickname_updated_at || profile?.nickname_updated_at || null,
        identifier,
        userId:     sessionUser.id,
        ...((refreshedProfile || profile) ? (() => {
          const p = refreshedProfile || profile;
          const by = p.birth_year;
          const ageGroupFromBirth = by && String(by).length === 4
            ? deriveAgeGroupFromBirth(by, p.birth_month, p.birth_day)
            : null;
          return {
            birthYear: by,
            birthMonth: p.birth_month || null,
            birthDay: p.birth_day || null,
            ageGroup: ageGroupFromBirth || p.age_group || 'all',
            gender: p.gender,
            setupDone: true,
          };
        })() : {}),
      });

      // club_members DB 기반으로 참여 중인 방 복원 (새로고침·재로그인 후 상태 유지)
      if (refreshedProfile || profile) {
        const memberships = await loadUserMemberships(sessionUser.id);
        setAppState(prev => {
          const myClubIdSet = new Set((memberships || []).map(m => m.clubId).filter(Boolean));
          return {
            ...prev,
            matches: memberships,
            currentClubId: myClubIdSet.has(prev.currentClubId) ? prev.currentClubId : null,
          };
        });
        // 로그인/재로그인 직후에는 Realtime이 놓친 채팅을 DB 기준으로 즉시 복원한다.
        setTimeout(() => refreshUnreadFromDbForUser(sessionUser.id, { countOpenRoom: true }), 0);
      }

      const p = refreshedProfile || profile;
      if (!p) {
        screenHistoryRef.current = [];
        setScreen('setup');
        return;
      }
      const needPublicProfile = !normalizeNickname(p.nickname);
      screenHistoryRef.current = [];
      setScreen(needPublicProfile ? 'welcome_profile' : 'home');
    };

    const checkAndSetUser = (sessionUser) => {
      if (!sessionUser?.id) return Promise.resolve();
      const existing = authUserInitLocks.get(sessionUser.id);
      if (existing) return existing;
      const task = (async () => {
        try {
          await runCheckAndSetUser(sessionUser);
        } finally {
          authUserInitLocks.delete(sessionUser.id);
        }
      })();
      authUserInitLocks.set(sessionUser.id, task);
      return task;
    };

    supabase.auth.getSession().then(({ data: { session } }) => { if (session?.user) void checkAndSetUser(session.user); });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) void checkAndSetUser(session.user);
      if (event === 'SIGNED_OUT') {
        authUserInitLocks.clear();
        setUser(null);
        resetSessionScopedAppState();
        setChatNotif(null);
        screenHistoryRef.current = [];
        setScreen('home');
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── 실시간 정지 감시 ─────────────────────────────────
  // 관리자가 정지시키면 즉시 튕겨냄 (3중 방어: Realtime + 폴링 + 포커스 재검사)
  // useRef로 컴포넌트 전역 플래그 관리 → StrictMode 더블마운트에도 1회만 실행
  const userIdRef     = useRef(null);
  useEffect(() => { userIdRef.current = user?.userId || null; }, [user?.userId]);

  const banHandledRef = useRef(false);
  const warnedIdsRef  = useRef(new Set());
  useEffect(() => {
    if (!user?.userId) return;
    // 관리자 페이지에서는 정지 감시 스킵
    if (window.location.pathname === ADMIN_PATH) return;

    // name은 동명이인 오차단 방지를 위해 ban 식별자에서 제외
    const myIds = [user.identifier, user.userId, user.phone, user.email].filter(Boolean);
    // 새 유저 세션 시작 시 플래그 리셋
    banHandledRef.current = false;

    // 동기적으로 플래그를 먼저 세팅 → 여러 이벤트가 동시에 와도 1회만 실행됨
    const triggerBan = async () => {
      if (banHandledRef.current) return;
      banHandledRef.current = true;
      try {
        const sanction = await fetchActiveAccountSanctionForUserId(user.userId);
        const popup = buildAccountSuspendPopup(sanction);
        if (popup?.title && popup?.message) alert(`${popup.title}\n\n${popup.message}`);
      } catch {}
      supabase.auth.signOut();
    };

    const kickIfBanned = async () => {
      if (banHandledRef.current) return;
      const banned = await areIdsBanned(myIds);
      if (!banned || banHandledRef.current) return;
      await triggerBan();
    };

    // 1) Realtime: banned_users 변경(insert/update) 즉시 반응
    //    payload.new.identifier가 내 ID 중 하나면 바로 triggerBan() (DB 재조회 불필요)
    //    여러 identifier가 동시에 INSERT되어도 동기 플래그로 1회만 처리
    const channel = supabase.channel('ban-watch-' + user.userId)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'banned_users' },
        (payload) => {
          const targetId = payload.new?.identifier || payload.old?.identifier;
          if (myIds.includes(targetId)) void triggerBan();
        })
      .subscribe();

    // 2) 폴링: 60초마다 DB 재조회 (Realtime 놓친 경우 대비)
    const poll = setInterval(kickIfBanned, 60 * 1000);

    // 3) 탭 포커스 복귀 시 즉시 재검사
    const onFocus = () => kickIfBanned();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(poll);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [user?.userId, user?.identifier, user?.phone, user?.email]);

  // ── 실시간 경고 팝업 ─────────────────────────────────
  // 어느 화면에 있든(user_warnings INSERT) 즉시 경고 문구 표시
  useEffect(() => {
    if (!user?.userId) return;
    if (window.location.pathname === ADMIN_PATH) return;
    warnedIdsRef.current = new Set();

    const defaultWarnBody = '운영정책 위반 행위가 확인되었습니다. 채팅 이용에 유의해주세요.';
    const looksLikeV2BackfillMessage = (msg) => {
      const firstLine = String(msg || '').split('\n')[0]?.trim();
      if (!firstLine) return false;
      // user_warnings_v2를 알림 탭에 노출시키기 위해 "제목+본문" 형태로 백필된 메시지는
      // 여기서 팝업을 띄우지 않는다(중복 팝업 방지).
      const knownTitles = new Set([
        '사용자 경고 안내',
        '채팅 경고',
        '10분 채팅 제한 안내',
        '24시간 채팅 제한 안내',
        '반복 위반 안내',
        '채팅 경고 안내',
        '계정 제재 위험 안내',
        '최종 경고 안내',
      ]);
      return knownTitles.has(firstLine);
    };
    const showWarningNow = async (row) => {
      if (!row?.id || warnedIdsRef.current.has(row.id)) return;
      warnedIdsRef.current.add(row.id);
      const body = String(row.message || '').trim() || defaultWarnBody;
      if (looksLikeV2BackfillMessage(body)) {
        // 알림 탭에서 확인하도록 unread 유지
        return;
      }
      alert(`${USER_WARN_NOTICE_TITLE}\n\n${body}`);
      await supabase.from('user_warnings').update({ is_read: true }).eq('id', row.id);
    };

    const bootstrapUnreadWarnings = async () => {
      const { data } = await supabase
        .from('user_warnings')
        .select('id, message, is_read, created_at')
        .eq('user_id', user.userId)
        .eq('is_read', false)
        .order('created_at', { ascending: true })
        .limit(10);
      for (const row of (data || [])) {
        await showWarningNow(row);
      }
    };

    bootstrapUnreadWarnings();

    const channel = supabase.channel('warning-popup-' + user.userId)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'user_warnings',
        filter: `user_id=eq.${user.userId}`,
      }, async (payload) => {
        await showWarningNow(payload?.new || null);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user?.userId]);

  // ── 내 프로필/포인트 실시간 반영 ───────────────────────────────
  // 관리자 조정, 타기기 변경 등도 사용자 화면에 즉시 동기화
  useEffect(() => {
    if (!user?.userId) return;
    if (window.location.pathname === ADMIN_PATH) return;

    const syncMyProfile = async () => {
      const p = await fetchProfile(user.userId);
      if (!p) return;
      setUser(prev => prev ? ({
        ...prev,
        name: p.name || prev.name,
        nickname: p.nickname || null,
        phone: p.phone || prev.phone || null,
        age: p.age_display || prev.age,
        freeCoins: p.free_coins ?? prev.freeCoins,
        paidCoins: p.paid_coins ?? prev.paidCoins,
        profileImageUrl: p.profile_image_url || null,
        nicknameUpdatedAt: p.nickname_updated_at || null,
      }) : prev);
    };

    const channel = supabase.channel('my-profile-watch-' + user.userId)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'profiles',
        filter: `user_id=eq.${user.userId}`,
      }, () => syncMyProfile())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user?.userId]);

  const updateState = (key, val) => setAppState(prev => ({ ...prev, [key]: val }));

  const submitApp = async () => {
    const isHost = !appState.fromClub;
    const cost   = isHost ? HOST_COST : JOIN_COST;

    if (appState.fromClub && appState.clubId) {
      // 신청 직전 DB 재검증: 잠금/마감/비활성 상태 변경을 즉시 반영
      const { data: latestClubRow } = await supabase
        .from('clubs')
        .select('id, is_active, status, is_locked, members, max_members, room_type, min_age, max_age, ratio')
        .eq('id', appState.clubId)
        .maybeSingle();
      const targetClub = latestClubRow
        ? {
            id: latestClubRow.id,
            isActive: latestClubRow.is_active,
            status: latestClubRow.status || 'recruiting',
            isLocked: !!latestClubRow.is_locked,
            members: latestClubRow.members || 0,
            maxMembers: latestClubRow.max_members || 6,
            roomType: latestClubRow.room_type || 'age_limited',
            minAge: latestClubRow.min_age ?? null,
            maxAge: latestClubRow.max_age ?? null,
            ratio: latestClubRow.ratio || 'mixed',
          }
        : clubs.find(c => c.id === appState.clubId);
      if (!targetClub || targetClub.isActive === false || targetClub.status === 'cancelled') {
        alert('이 방은 더 이상 신청할 수 없습니다.');
        screenHistoryRef.current = [];
        setScreen('home');
        return;
      }
      if (targetClub.isLocked) {
        alert('모임장이 방을 잠궈서 지금은 신청할 수 없습니다.');
        screenHistoryRef.current = [];
        setScreen('home');
        return;
      }
      if ((targetClub.members || 0) >= (targetClub.maxMembers || 6)) {
        alert('정원이 마감되어 신청할 수 없습니다.');
        screenHistoryRef.current = [];
        setScreen('home');
        return;
      }
      if (targetClub && user?.birthYear && !canJoinClub(user, targetClub)) {
        alert(clubAgeApplyOnlyMessage(targetClub));
        screenHistoryRef.current = [];
        setScreen('home');
        return;
      }
      if (!isRatioAllowedForGender(targetClub?.ratio, user?.gender)) {
        alert(getRatioDenyMessage(targetClub?.ratio));
        screenHistoryRef.current = [];
        setScreen('home');
        return;
      }
      if (await wasKickedFromClub(appState.clubId, user?.userId)) {
        alert('강퇴된 모임에는 다시 참여할 수 없습니다.');
        screenHistoryRef.current = [];
        setScreen('home');
        return;
      }
    }

    // 모임장은 항상 유료 포인트만 사용, 참가자는 무료 포인트 우선
    const usedCoinType = (!isHost && (user?.freeCoins || 0) >= cost) ? 'free' : 'paid';

    if (usedCoinType === 'paid' && (user?.paidCoins || 0) < cost) {
      updateState('fromWallet', true);
      navigate('charge');
      return;
    }

    const newMatch = {
      id: Date.now(), hobby: appState.hobby, region: appState.region,
      ratio: appState.ratio, times: appState.times, title: appState.hostingTitle || null, roomIntro: appState.hostingIntro || null,
      ageGroup: user?.ageGroup, gender: user?.gender, status: 'waiting', createdAt: Date.now(),
      coinType: usedCoinType, spentCoins: cost, isHost, clubId: null,
    };

    if (appState.fromClub && appState.clubId) {
      const joined = await insertClubMember(appState.clubId, user?.userId, {
        name: user?.nickname || user?.name || '회원',
        avatar: user?.profileImageUrl || null,
      });
      if (!joined) {
        alert('방 참여 처리에 실패했습니다. 포인트는 차감되지 않았어요. 잠시 후 다시 시도해주세요.');
        return;
      }
      newMatch.clubId = appState.clubId;
    } else {
      const hostRoomType = appState.roomType || 'age_limited';
      const ageRange = hostRoomType === 'age_limited'
        ? resolveHostAgeRange(user)
        : null;

      // 기본 payload (항상 존재하는 컬럼만)
      const selectedHobbies = Array.isArray(appState.hobbies) && appState.hobbies.length > 0
        ? appState.hobbies
        : (appState.hobby ? [appState.hobby] : []);
      const preUploadedCoverUrl = appState.hostingCoverDataUrl
        ? await uploadClubCoverPublicUrl(
            user?.userId,
            `pre_${user?.userId || 'guest'}_${Date.now()}`,
            appState.hostingCoverDataUrl
          )
        : null;
      const basePayload = {
        title:       appState.hostingTitle || null,
        room_intro:  appState.hostingIntro || null,
        cover_image_url: preUploadedCoverUrl ? withUrlCacheBust(preUploadedCoverUrl) : null,
        host_name:   user?.nickname || user?.name || '모임장',
        host_profile_image_url: user?.profileImageUrl || null,
        hobby:       appState.hobby,
        hobbies:     selectedHobbies,
        region:      appState.region,
        age_group:   user?.birthYear && String(user.birthYear).length === 4
          ? deriveAgeGroupFromBirth(user.birthYear, user?.birthMonth, user?.birthDay)
          : (user?.ageGroup || 'all'),
        ratio:       appState.ratio || 'mixed',
        times:       appState.times || [],
        max_members: 6,
        members:     1,
        host_id:     user?.userId || null,
      };
      // 신규 컬럼 payload (SQL migration 완료 후 자동 적용)
      const extPayload = {
        room_type: hostRoomType,
        is_active: true,
        status:    'recruiting',
        ...(ageRange ? { min_age: ageRange.minAge, max_age: ageRange.maxAge } : {}),
      };

      let result = await supabase.from('clubs')
        .insert({ ...basePayload, ...extPayload }).select().maybeSingle();

      // 신규 컬럼 미존재 시 폴백: 기본 payload만으로 재시도
      if (result.error && (
        result.error.message.includes('schema cache') ||
        result.error.message.includes('column') ||
        result.error.code === 'PGRST204'
      )) {
        console.warn('신규 컬럼 없음, 폴백으로 방 생성:', result.error.message);
        const legacyPayload = { ...basePayload };
        delete legacyPayload.hobbies;
        delete legacyPayload.cover_image_url;
        delete legacyPayload.room_intro;
        delete legacyPayload.host_name;
        delete legacyPayload.host_profile_image_url;
        result = await supabase.from('clubs')
          .insert(legacyPayload).select().maybeSingle();
      }

      if (result.error) {
        console.error('club insert error:', result.error);
        logClientError('clubInsert', result.error.message, result.error.code);
        alert('모임방 생성 중 오류가 발생했어요. 잠시 후 다시 시도해주세요.');
        return;
      }
      if (result.data) {
        newMatch.clubId = result.data.id;
        const joined = await insertClubMember(result.data.id, user?.userId, {
          name: user?.nickname || user?.name || '회원',
          avatar: user?.profileImageUrl || null,
        });
        if (!joined) {
          await supabase.from('clubs').update({ members: 0 }).eq('id', result.data.id);
          alert('방 생성은 되었지만 모임장 참여 처리에 실패했습니다. 포인트는 차감되지 않았어요. 잠시 후 다시 시도해주세요.');
          return;
        }
        if (appState.hostingCoverDataUrl && !preUploadedCoverUrl) {
          await persistClubCoverFromDataUrl(user?.userId, result.data.id, appState.hostingCoverDataUrl);
        }
        setAppNotice('안내: 이 모임방은 생성 후 24시간 내에 6명이 모이면 자동으로 오픈됩니다\n(24시간 이후에는 4명 이상)');
      }
    }

    try {
      await createMatch(user?.userId || null, { hobby: appState.hobby, region: appState.region,
        ratio: appState.ratio, times: appState.times, ageGroup: user?.ageGroup, gender: user?.gender });
    } catch (e) {
      // matches 생성 실패 시 멤버십/카운트를 롤백해 포인트트·참여 상태 불일치를 막는다.
      if (newMatch.clubId && user?.userId) {
        await supabase.from('club_members')
          .delete()
          .eq('club_id', newMatch.clubId)
          .eq('user_id', user.userId);
        await clearClubMemberHistory(newMatch.clubId, user.userId);
        await syncClubMembersCount(newMatch.clubId);
        if (!appState.fromClub) {
          await deactivateClub(newMatch.clubId);
        }
      }
      logClientError('createMatch', e?.message, e);
      const msg = e && typeof e.message === 'string' ? e.message.trim() : '';
      alert(msg ? `신청 처리 중 오류가 발생했어요.\n\n${msg}` : '신청 처리 중 오류가 발생했어요. 잠시 후 다시 시도해주세요.');
      return;
    }
    const [freshClubs, freshMemberships] = await Promise.all([
      fetchClubs(),
      loadUserMemberships(user?.userId),
    ]);
    setClubs(freshClubs);
    // DB 기반 멤버십으로 교체 (로컬 append 대신 DB 재조회로 단일 진실 공급원 유지)
    setAppState(prev => ({ ...prev, matches: freshMemberships, hostingTitle: '', hostingIntro: '', hostingCoverDataUrl: null }));
    setUser(prev => usedCoinType === 'free'
      ? { ...prev, freeCoins: prev.freeCoins - cost }
      : { ...prev, paidCoins: prev.paidCoins - cost });
    await updateUserCoins(
      user?.userId,
      usedCoinType === 'free'
        ? { freeDelta: -cost, freeReason: isHost ? `모임장 방 생성 포인트 사용 (${cost})` : `모임 참여 포인트 사용 (${cost})` }
        : { paidDelta: -cost, paidReason: isHost ? `모임장 방 생성 포인트 사용 (${cost})` : `모임 참여 포인트 사용 (${cost})` }
    );
    navigate('match');
  };

  // currentClubId ref 동기화 (알림 시스템에서 stale 방지)
  currentClubIdRef.current = appState.currentClubId;

  const dismissChatNotifToast = () => {
    if (chatNotifTimerRef.current) {
      clearTimeout(chatNotifTimerRef.current);
      chatNotifTimerRef.current = null;
    }
    chatNotifDragYRef.current = 0;
    setChatNotifDragY(0);
    setChatNotifIsDragging(false);
    setChatNotif(null);
  };

  const props = {
    user, setUser, screen, onNav: navigate, appState, updateState, submitApp, clubs, setClubs,
    unreadCounts, roomOpenNotifs, mutedRooms, onNewMsgRef, onResetUnread, onToggleMute, suppressHostNoticeForClub,
    showChargeInfo: () => { updateState('fromWallet', true); navigate('charge'); },
  };

  // 숨김 관리자 경로 → 관리자 페이지 (hooks 이후에 체크)
  if (window.location.pathname === ADMIN_PATH) return <AdminPage />;

  if (isMaintenanceBlocked && screen !== 'login') {
    return (
      <div className="app-wrap">
        <div className="phone-shell">
          <div className="phone-notch" aria-hidden="true" />
          <div className="phone-inner">
            <div className="screen" style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'0 22px'}}>
              <div style={{width:'100%',maxWidth:360,background:'#fff',border:'1px solid #E5E7EB',borderRadius:16,padding:'20px 16px'}}>
                <div
                  style={{fontSize:20,fontWeight:800,color:'#111827',marginBottom:10,textAlign:'center'}}
                  onClick={handleMaintenanceLogoTap}
                >
                  서비스 점검 중
                </div>
                <div style={{fontSize:14,color:'#4B5563',lineHeight:1.7,whiteSpace:'pre-line',textAlign:'center'}}>
                  {maintenanceConfig.message}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-wrap">
      {appNotice && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 24px' }}>
          <div style={{ background:'white', borderRadius:16, padding:24, width:'100%', maxWidth:360 }}>
            <div style={{ fontSize:18, fontWeight:800, color:'#111827', marginBottom:12 }}>안내</div>
            <div style={{ fontSize:14, color:'#374151', lineHeight:1.8, whiteSpace:'pre-line', marginBottom:20 }}>
              {appNotice}
            </div>
            <button
              type="button"
              onClick={() => setAppNotice(null)}
              style={{ width:'100%', padding:14, background:'var(--green)', border:'none', borderRadius:10, fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:'inherit', color:'white' }}
            >
              확인
            </button>
          </div>
        </div>
      )}
      {/* (구) 유료결제 준비중 모달 제거 */}
      {/* 인앱 채팅 알림 토스트 (탭=입장, 위로 밀어 닫기) */}
      {chatNotif && (
        <div
          className={`chat-notif-toast${chatNotifIsDragging ? ' chat-notif-toast--dragging' : ''}`}
          style={{
            touchAction: 'none',
            ...(chatNotifDragY > 0 || chatNotifIsDragging
              ? {
                transform: `translateX(-50%) translateY(-${chatNotifDragY}px)`,
                opacity: Math.max(0.28, 1 - chatNotifDragY / 130),
              }
              : {}),
          }}
          onPointerDown={(e) => {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            chatNotifPointerStartYRef.current = e.clientY;
            chatNotifPointerActiveRef.current = true;
            chatNotifMovedRef.current = false;
            setChatNotifIsDragging(true);
            try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
          }}
          onPointerMove={(e) => {
            if (!chatNotifPointerActiveRef.current) return;
            const dy = chatNotifPointerStartYRef.current - e.clientY;
            if (Math.abs(dy) > 10) chatNotifMovedRef.current = true;
            const clamped = Math.max(0, Math.min(dy, 160));
            chatNotifDragYRef.current = clamped;
            setChatNotifDragY(clamped);
          }}
          onPointerUp={(e) => {
            if (!chatNotifPointerActiveRef.current) return;
            chatNotifPointerActiveRef.current = false;
            setChatNotifIsDragging(false);
            try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* ignore */ }
            const y = chatNotifDragYRef.current;
            if (y >= 48) {
              dismissChatNotifToast();
            } else {
              chatNotifDragYRef.current = 0;
              setChatNotifDragY(0);
            }
          }}
          onPointerCancel={(e) => {
            if (!chatNotifPointerActiveRef.current) return;
            chatNotifPointerActiveRef.current = false;
            setChatNotifIsDragging(false);
            try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* ignore */ }
            chatNotifDragYRef.current = 0;
            setChatNotifDragY(0);
          }}
          onClick={() => {
            if (chatNotifMovedRef.current) {
              chatNotifMovedRef.current = false;
              return;
            }
            dismissChatNotifToast();
            updateState('currentClubId', chatNotif.clubId);
            navigate('chat');
          }}
        >
          <div className="chat-notif-drag-hint" aria-hidden="true" />
          <div className="chat-notif-room">💬 {chatNotif.clubTitle}</div>
          <div className="chat-notif-msg"><strong>{chatNotif.senderName}</strong>: {chatNotif.text}</div>
        </div>
      )}
      <div className="phone-shell">
        <div className="phone-notch" aria-hidden="true" />
        <div
          className="phone-inner"
          onPointerDownCapture={(e) => {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            if (swipeBackGestureRef.current) return;
            const el = e.currentTarget;
            const rect = el.getBoundingClientRect();
            const x = e.clientX - rect.left;
            if (x > 36) return;
            swipeBackGestureRef.current = {
              startX: e.clientX,
              startY: e.clientY,
              pointerId: e.pointerId,
              triggered: false,
            };
            try { el.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
          }}
          onPointerMoveCapture={(e) => {
            const g = swipeBackGestureRef.current;
            if (!g || g.pointerId !== e.pointerId || g.triggered) return;
            const dx = e.clientX - g.startX;
            const dy = e.clientY - g.startY;
            if (dx < 14) return;
            if (Math.abs(dy) > Math.abs(dx) + 22) return;
            if (dx >= 76) {
              g.triggered = true;
              trySwipeNavigateBack();
            }
          }}
          onPointerUpCapture={(e) => {
            const g = swipeBackGestureRef.current;
            if (!g || g.pointerId !== e.pointerId) return;
            if (!g.triggered) {
              const dx = e.clientX - g.startX;
              const dy = e.clientY - g.startY;
              if (dx >= 58 && Math.abs(dy) <= Math.abs(dx) + 28) trySwipeNavigateBack();
            }
            swipeBackGestureRef.current = null;
            try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* ignore */ }
          }}
          onPointerCancelCapture={(e) => {
            const g = swipeBackGestureRef.current;
            if (g && g.pointerId === e.pointerId) swipeBackGestureRef.current = null;
            try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* ignore */ }
          }}
        >
      {screen === 'login'   && <LoginScreen   {...props} />}
      {screen === 'find_id' && <FindIdScreen  {...props} />}
      {screen === 'reset_pw' && <ResetPasswordScreen {...props} />}
      {screen === 'signup'  && <SignupScreen  {...props} />}
      {screen === 'setup'   && <SetupScreen   {...props} />}
      {screen === 'welcome_profile' && <WelcomeProfileScreen {...props} />}
      {screen === 'home'    && <HomeScreen    {...props} />}
      {screen === 'hobby'   && <HobbyScreen   {...props} />}
      {screen === 'time'    && <TimeScreen    {...props} />}
      {screen === 'form'    && <FormScreen    {...props} />}
      {screen === 'ratio'   && <RatioScreen   {...props} />}
      {screen === 'confirm' && <ConfirmScreen {...props} />}
      {screen === 'host_title' && <TitleScreen  {...props} />}
      {screen === 'match'   && <MatchScreen   {...props} />}
      {/* ChatScreen: 같은 방이면 언마운트하지 않고 숨기기만 해서 메시지 상태 보존 */}
      {appState.currentClubId && (
        <div key={appState.currentClubId} className="chat-screen-holder" style={screen !== 'chat' ? {display:'none'} : undefined}>
          <ChatScreen {...props} />
        </div>
      )}
      {screen === 'wallet'  && <WalletScreen  {...props} />}
      {screen === 'charge'  && <ChargeScreen  {...props} />}
      {/* [베타] 토스페이먼츠 결제 화면 비활성화
      {screen === 'toss_checkout' && <TossCheckoutScreen {...props} />}
      {screen === 'toss_success'  && <TossPaySuccessScreen {...props} />}
      {screen === 'toss_fail'     && <TossPayFailScreen {...props} />}
      */}
      {screen === 'profile' && <ProfileScreen {...props} />}
      {screen === 'search'  && <SearchScreen  {...props} />}
      {screen === 'notif'   && <NotifScreen   {...props} />}
      {screen === 'terms'   && <TermsScreen   {...props} />}
      {screen === 'refund_policy' && <RefundPolicyScreen {...props} />}
      {screen === 'privacy' && <PrivacyScreen {...props} />}
        </div>
      </div>
    </div>
  );
}

/* ── 로그인 ─────────────────────────────── */
function LoginScreen({ onNav, user }) {
  const [loginId, setLoginId]   = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);

  const handleLogin = async () => {
    const normalizedId = normalizeLoginId(loginId);
    if (!normalizedId) { alert('아이디를 입력해주세요.'); return; }
    if (!password) { alert('비밀번호를 입력해주세요.'); return; }
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: loginIdToEmail(normalizedId),
      password,
    });
    if (error) alert('로그인 실패: 아이디 또는 비밀번호를 확인해주세요.');
    setLoading(false);
  };

  return (
    <div className="screen">
      <div className="login-hero">
        <div className="logo-text"><span style={{color:'white'}}>동네</span><span className="green">사이</span></div>
        <div className="logo-sub">DONGNE SAI</div>
        <div className="logo-desc">같은 취향, 같은 동네<br/>처음부터 함께 시작하는 취미 모임</div>
      </div>
      <div className="content">
        {!user && (
          <button className="btn-outline" style={{marginBottom:12}} onClick={() => onNav('home', { root: true })}>
            홈페이지 보기
          </button>
        )}
        <div className="notice blue" style={{fontSize:14,marginBottom:20}}>
          <strong>아이디 · 비밀번호 로그인</strong><br/>회원가입 시 등록한 계정으로 안전하게 로그인하세요.
        </div>
        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>아이디</label>
          <input className="form-input" style={{background:'white',color:'#111827',fontSize:16}}
            value={loginId}
            onChange={e => setLoginId(e.target.value.slice(0, 40))}
            placeholder="아이디 입력"
            autoComplete="username"/>
        </div>
        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>비밀번호</label>
          <input type="password" className="form-input" style={{background:'white',color:'#111827',fontSize:16}}
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="비밀번호 입력"
            name="login_password_manual"
            autoComplete="off"
            onKeyDown={e => e.key === 'Enter' && handleLogin()}/>
        </div>
        <button className="btn-primary" onClick={handleLogin} disabled={loading}>
          {loading ? '로그인 중...' : '로그인'}
        </button>
        <div style={{marginTop:16,textAlign:'center',fontSize:14,color:'var(--gray-600)'}}>
          처음이신가요?{' '}
          <span style={{color:'var(--green)',fontWeight:700,cursor:'pointer'}} onClick={() => onNav('signup')}>
            회원가입
          </span>
        </div>
        <div style={{marginTop:10,textAlign:'center',fontSize:13,color:'var(--gray-500)'}}>
          <span style={{cursor:'pointer'}} onClick={() => onNav('find_id')}>아이디 찾기</span>
          <span style={{margin:'0 8px',color:'var(--gray-300)'}}>|</span>
          <span style={{cursor:'pointer'}} onClick={() => onNav('reset_pw')}>비밀번호 찾기</span>
        </div>
        <div className="terms" style={{marginTop:20}}>
          <span onClick={() => onNav('terms')}>이용약관</span> 및{' '}
          <span onClick={() => onNav('privacy')}>개인정보처리방침</span>에 동의하게 됩니다
        </div>
      </div>
    </div>
  );
}

/* ── 아이디 찾기 ─────────────────────────── */
function FindIdScreen({ onNav }) {
  const [name, setName] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verifiedPhone, setVerifiedPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [foundId, setFoundId] = useState('');
  const [otpLastSentAt, setOtpLastSentAt] = useState(0);

  const formatPhone = (raw) => '+82' + raw.replace(/^0/, '');

  const sendPhoneOtp = async () => {
    if (phoneInput.length < 10) { alert('올바른 전화번호를 입력해주세요.'); return; }
    const diff = Date.now() - otpLastSentAt;
    if (diff < OTP_RESEND_COOLDOWN_SEC * 1000) {
      const left = Math.ceil((OTP_RESEND_COOLDOWN_SEC * 1000 - diff) / 1000);
      alert(`인증번호는 ${left}초 후 다시 전송할 수 있어요.`);
      return;
    }
    setLoading(true);
    try {
      const formatted = formatPhone(phoneInput);
      const { data, error } = await supabase.functions.invoke('send-otp', { body: { phone: formatted } });
      if (error || !data?.ok) { alertFromEdgeInvoke('send-otp', '인증번호를 보내지 못했어요. 잠시 후 다시 시도해주세요.', { data, error }); return; }
      setOtpSent(true);
      setOtpLastSentAt(Date.now());
      alert('인증번호를 전송했어요.');
    } finally {
      setLoading(false);
    }
  };

  const verifyPhoneOtp = async () => {
    if (otp.length < 4) { alert('인증번호를 입력해주세요.'); return; }
    setLoading(true);
    try {
      const formatted = formatPhone(phoneInput);
      const { data, error } = await invokeVerifyOtp({ phone: formatted, otp, mode: 'otp_only' });
      if (error || !data?.ok) { alertFromEdgeInvoke('verify-otp', '인증에 실패했어요. 코드를 확인하거나 잠시 후 다시 시도해주세요.', { data, error }); return; }
      setPhoneVerified(true);
      setVerifiedPhone(formatted);
      alert('전화번호 인증이 완료됐어요.');
    } finally {
      setLoading(false);
    }
  };

  const handleFindId = async () => {
    if (!name.trim()) { alert('이름을 입력해주세요.'); return; }
    if (!phoneVerified || !verifiedPhone) { alert('전화번호 인증을 먼저 완료해주세요.'); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('recover-account', {
        body: { action: 'find_login_id', name: name.trim(), phone: verifiedPhone },
      });
      if (error || !data?.ok) {
        alertFromEdgeInvoke('recover-account-find', '아이디를 찾지 못했어요. 입력 정보를 확인하거나 잠시 후 다시 시도해주세요.', { data, error });
        return;
      }
      setFoundId(data.loginId || '');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="screen">
      <div className="top-bar">
        <button className="back-btn" onClick={() => onNav('login', { replace: true })}>←</button>
        <div className="screen-title">아이디 찾기</div>
      </div>
      <div className="content" style={{paddingTop:12}}>
        <div className="notice blue" style={{fontSize:13,marginBottom:14}}>
          가입한 <strong>이름 + 전화번호 인증</strong> 후 아이디를 확인할 수 있어요.
        </div>
        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>이름</label>
          <input className="form-input" value={name} onChange={e => setName(e.target.value.slice(0, 20))} placeholder="가입 시 입력한 이름" />
        </div>
        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>전화번호</label>
          <div style={{display:'flex',gap:8}}>
            <input className="form-input" style={{flex:1}} value={phoneInput}
              onChange={e => setPhoneInput(e.target.value.replace(/\D/g,'').slice(0,11))}
              placeholder="010-0000-0000" inputMode="numeric" disabled={otpSent || loading || phoneVerified} />
            {!otpSent && !phoneVerified && (
              <button className="btn-primary" style={{width:'auto',padding:'0 16px',fontSize:14}} onClick={sendPhoneOtp} disabled={loading}>
                {loading ? '...' : '인증전송'}
              </button>
            )}
          </div>
          {otpSent && !phoneVerified && (
            <div style={{marginTop:10,display:'flex',gap:8}}>
              <input className="form-input" style={{flex:1,textAlign:'center',letterSpacing:2}}
                value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                placeholder="인증번호" inputMode="numeric" maxLength={6} />
              <button className="btn-primary" style={{width:'auto',padding:'0 16px',fontSize:14}} onClick={verifyPhoneOtp} disabled={loading}>
                {loading ? '...' : '확인'}
              </button>
            </div>
          )}
          {phoneVerified && <div style={{fontSize:12,color:'var(--green)',marginTop:6,fontWeight:600}}>✓ 전화번호 인증 완료</div>}
        </div>
        {foundId && (
          <div className="notice blue" style={{fontSize:14}}>
            회원님의 아이디는 <strong>{foundId}</strong> 입니다.
          </div>
        )}
      </div>
      <div className="bottom-area">
        <button className="btn-primary" style={{fontSize:16}} onClick={handleFindId} disabled={loading || !phoneVerified || !name.trim()}>
          {loading ? '확인 중...' : '아이디 확인'}
        </button>
      </div>
    </div>
  );
}

/* ── 비밀번호 찾기 ───────────────────────── */
function ResetPasswordScreen({ onNav }) {
  const [name, setName] = useState('');
  const [loginId, setLoginId] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verifiedPhone, setVerifiedPhone] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpLastSentAt, setOtpLastSentAt] = useState(0);

  const formatPhone = (raw) => '+82' + raw.replace(/^0/, '');

  const sendPhoneOtp = async () => {
    if (phoneInput.length < 10) { alert('올바른 전화번호를 입력해주세요.'); return; }
    const diff = Date.now() - otpLastSentAt;
    if (diff < OTP_RESEND_COOLDOWN_SEC * 1000) {
      const left = Math.ceil((OTP_RESEND_COOLDOWN_SEC * 1000 - diff) / 1000);
      alert(`인증번호는 ${left}초 후 다시 전송할 수 있어요.`);
      return;
    }
    setLoading(true);
    try {
      const formatted = formatPhone(phoneInput);
      const { data, error } = await supabase.functions.invoke('send-otp', { body: { phone: formatted } });
      if (error || !data?.ok) { alertFromEdgeInvoke('send-otp', '인증번호를 보내지 못했어요. 잠시 후 다시 시도해주세요.', { data, error }); return; }
      setOtpSent(true);
      setOtpLastSentAt(Date.now());
      alert('인증번호를 전송했어요.');
    } finally {
      setLoading(false);
    }
  };

  const verifyPhoneOtp = async () => {
    if (otp.length < 4) { alert('인증번호를 입력해주세요.'); return; }
    setLoading(true);
    try {
      const formatted = formatPhone(phoneInput);
      const { data, error } = await invokeVerifyOtp({ phone: formatted, otp, mode: 'otp_only' });
      if (error || !data?.ok) { alertFromEdgeInvoke('verify-otp', '인증에 실패했어요. 코드를 확인하거나 잠시 후 다시 시도해주세요.', { data, error }); return; }
      setPhoneVerified(true);
      setVerifiedPhone(formatted);
      alert('전화번호 인증이 완료됐어요.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    const normalizedId = normalizeLoginId(loginId);
    if (!isValidLoginId(normalizedId)) { alert('아이디 형식을 확인해주세요.'); return; }
    if (!name.trim()) { alert('이름을 입력해주세요.'); return; }
    if (!phoneVerified || !verifiedPhone) { alert('전화번호 인증을 먼저 완료해주세요.'); return; }
    if (newPassword.length < 8) { alert('새 비밀번호는 8자 이상 입력해주세요.'); return; }
    if (newPassword !== newPasswordConfirm) { alert('비밀번호 확인이 일치하지 않습니다.'); return; }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('recover-account', {
        body: {
          action: 'reset_password',
          loginId: normalizedId,
          name: name.trim(),
          phone: verifiedPhone,
          newPassword,
        },
      });
      if (error || !data?.ok) {
        alertFromEdgeInvoke('recover-account-pw', '비밀번호 변경에 실패했어요. 잠시 후 다시 시도해주세요.', { data, error });
        return;
      }
      alert('비밀번호가 변경되었습니다. 새 비밀번호로 로그인해주세요.');
      onNav('login', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="screen">
      <div className="top-bar">
        <button className="back-btn" onClick={() => onNav('login', { replace: true })}>←</button>
        <div className="screen-title">비밀번호 찾기</div>
      </div>
      <div className="content" style={{paddingTop:12}}>
        <div className="notice blue" style={{fontSize:13,marginBottom:14}}>
          가입한 정보 확인 후 새 비밀번호로 재설정할 수 있어요.
        </div>
        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>아이디</label>
          <input className="form-input" value={loginId}
            onChange={e => setLoginId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0,20))}
            placeholder="영문 소문자/숫자/_ (4~20자)" />
        </div>
        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>이름</label>
          <input className="form-input" value={name} onChange={e => setName(e.target.value.slice(0, 20))} placeholder="가입 시 입력한 이름" />
        </div>
        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>전화번호</label>
          <div style={{display:'flex',gap:8}}>
            <input className="form-input" style={{flex:1}} value={phoneInput}
              onChange={e => setPhoneInput(e.target.value.replace(/\D/g,'').slice(0,11))}
              placeholder="010-0000-0000" inputMode="numeric" disabled={otpSent || loading || phoneVerified} />
            {!otpSent && !phoneVerified && (
              <button className="btn-primary" style={{width:'auto',padding:'0 16px',fontSize:14}} onClick={sendPhoneOtp} disabled={loading}>
                {loading ? '...' : '인증전송'}
              </button>
            )}
          </div>
          {otpSent && !phoneVerified && (
            <div style={{marginTop:10,display:'flex',gap:8}}>
              <input className="form-input" style={{flex:1,textAlign:'center',letterSpacing:2}}
                value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                placeholder="인증번호" inputMode="numeric" maxLength={6} />
              <button className="btn-primary" style={{width:'auto',padding:'0 16px',fontSize:14}} onClick={verifyPhoneOtp} disabled={loading}>
                {loading ? '...' : '확인'}
              </button>
            </div>
          )}
          {phoneVerified && <div style={{fontSize:12,color:'var(--green)',marginTop:6,fontWeight:600}}>✓ 전화번호 인증 완료</div>}
        </div>
        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>새 비밀번호</label>
          <input type="password" className="form-input" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="8자 이상 입력" />
        </div>
        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>새 비밀번호 확인</label>
          <input type="password" className="form-input" value={newPasswordConfirm} onChange={e => setNewPasswordConfirm(e.target.value)} placeholder="비밀번호를 다시 입력" />
        </div>
      </div>
      <div className="bottom-area">
        <button className="btn-primary" style={{fontSize:16}} onClick={handleResetPassword} disabled={loading}>
          {loading ? '변경 중...' : '비밀번호 변경'}
        </button>
      </div>
    </div>
  );
}

/* ── 회원가입 ───────────────────────────── */
function SignupScreen({ onNav, setUser }) {
  const [name, setName]                     = useState('');
  const [birthYearInput, setBirthYearInput] = useState('');
  const [birthMonthInput, setBirthMonthInput] = useState('');
  const [birthDayInput, setBirthDayInput]   = useState('');
  const [gender, setGender]                 = useState('');
  const [phoneInput, setPhoneInput]         = useState('');
  const [otp, setOtp]                       = useState('');
  const [otpSent, setOtpSent]               = useState(false);
  const [phoneVerified, setPhoneVerified]   = useState(false);
  const [verifiedPhone, setVerifiedPhone]   = useState('');
  const [loginId, setLoginId]               = useState('');
  const [password, setPassword]             = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [otpLoading, setOtpLoading]         = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [otpLastSentAt, setOtpLastSentAt]   = useState(0);

  const formatPhone = (raw) => '+82' + raw.replace(/^0/, '');
  const birthYear = birthYearInput;
  const monthNum = Number(birthMonthInput);
  const dayNum = Number(birthDayInput);
  const validMonth = birthMonthInput.length === 2 && monthNum >= 1 && monthNum <= 12;
  const validDay = birthDayInput.length === 2 && dayNum >= 1 && dayNum <= 31;
  const birthFieldsValid = birthYear.length === 4 && validMonth && validDay;
  const ageGroup = birthFieldsValid ? deriveAgeGroupFromBirth(birthYear, birthMonthInput, birthDayInput) : null;
  const strongPassword = password.length >= 8;
  const idOk = isValidLoginId(loginId);
  const signupAgeEligible = birthFieldsValid && isSignupEligibleFullDate(birthYear, birthMonthInput, birthDayInput);
  const canSubmit = phoneVerified && name.trim().length >= 2 && birthFieldsValid && signupAgeEligible && gender && idOk && strongPassword && password === passwordConfirm;

  const sendPhoneOtp = async () => {
    if (phoneInput.length < 10) { alert('올바른 전화번호를 입력해주세요.'); return; }
    const diff = Date.now() - otpLastSentAt;
    if (diff < OTP_RESEND_COOLDOWN_SEC * 1000) {
      const left = Math.ceil((OTP_RESEND_COOLDOWN_SEC * 1000 - diff) / 1000);
      alert(`인증번호는 ${left}초 후 다시 전송할 수 있어요.`);
      return;
    }
    setOtpLoading(true);
    try {
      const formatted = formatPhone(phoneInput);
      const { data: existing } = await supabase.from('profiles').select('user_id').eq('phone', formatted).maybeSingle();
      if (existing) { alert('이미 가입된 전화번호입니다.'); return; }
      const { data, error } = await supabase.functions.invoke('send-otp', { body: { phone: formatted } });
      if (error || !data?.ok) { alertFromEdgeInvoke('send-otp', '인증번호를 보내지 못했어요. 잠시 후 다시 시도해주세요.', { data, error }); return; }
      setOtpSent(true);
      setOtpLastSentAt(Date.now());
    } finally { setOtpLoading(false); }
  };

  const verifyPhoneOtp = async () => {
    if (otp.length < 4) { alert('인증번호를 입력해주세요.'); return; }
    setOtpLoading(true);
    try {
      const formatted = formatPhone(phoneInput);
      const { data, error } = await invokeVerifyOtp({ phone: formatted, otp, mode: 'otp_only' });
      if (error || !data?.ok) { alertFromEdgeInvoke('verify-otp', '인증에 실패했어요. 코드를 확인하거나 잠시 후 다시 시도해주세요.', { data, error }); return; }
      setPhoneVerified(true);
      setVerifiedPhone(formatted);
    } finally { setOtpLoading(false); }
  };

  const handleSignup = async () => {
    if (!canSubmit) { alert('입력 항목을 다시 확인해주세요.'); return; }
    if (!birthFieldsValid) { alert('생년월일을 정확히 입력해주세요.'); return; }
    if (!signupAgeEligible) {
      alert(`가입할 수 없어요.\n${signupAgeRuleLine()}.\n${signupAgeRuleFootnote()}`);
      return;
    }
    setSaving(true);
    try {
      const normalizedId = normalizeLoginId(loginId);
      const email = loginIdToEmail(normalizedId);
      const finalName = name.trim();
      const man = calcManAgeFromYmd(parseInt(birthYear, 10), monthNum, dayNum);
      const ageDisplay = `만 ${man}세 (${birthYear}년생)`;

      // Supabase auth.signUp()는 이메일 전송 제한(rate limit)에 자주 걸려
      // 서버 함수(admin.createUser)로 가입 처리해 안정적으로 계정을 만든다.
      const { data: signRes, error: signInvokeError } = await supabase.functions.invoke('signup-id-password', {
        body: {
          loginId: normalizedId,
          password,
          name: finalName,
          phone: verifiedPhone,
          birthYear,
          birthMonth: birthMonthInput,
          birthDay: birthDayInput,
          ageGroup,
          gender,
          ageDisplay,
        },
      });
      if (signInvokeError || !signRes?.ok) {
        const raw = (signInvokeError?.message || signRes?.error || '').toLowerCase();
        // 간헐적 rate limit 상황에서 계정이 이미 생성된 경우 로그인으로 즉시 복구
        if (raw.includes('rate') || raw.includes('too many') || raw.includes('요청')) {
          const { error: retrySignInError } = await supabase.auth.signInWithPassword({ email, password });
          if (!retrySignInError) {
            const { data: userRes } = await supabase.auth.getUser();
            const retryUser = userRes?.user || null;
            if (retryUser?.id) {
              setUser(prev => ({ ...prev,
                userId: retryUser.id,
                name: finalName,
                phone: verifiedPhone,
                birthYear,
                birthMonth: birthMonthInput,
                birthDay: birthDayInput,
                ageGroup,
                gender,
                age: ageDisplay,
                setupDone: true,
              }));
              alert('회원가입이 완료되었습니다.');
              onNav('welcome_profile');
              return;
            }
          }
          alert('요청이 잠시 많아 처리 지연 중입니다. 30초 후 다시 시도해주세요.');
          return;
        }
        alertFromEdgeInvoke('signup-id-password', '회원가입에 실패했어요. 잠시 후 다시 시도해주세요.', { data: signRes, error: signInvokeError });
        return;
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) {
        logClientError('signInAfterSignup', signInError.message, signInError);
        alert('회원가입 후 로그인에 실패했어요. 잠시 후 다시 시도해주세요.');
        return;
      }
      const { data: userRes } = await supabase.auth.getUser();
      const authUser = userRes?.user || null;
      if (!authUser?.id) { alert('회원가입 처리 중 사용자 정보를 확인할 수 없습니다.'); return; }

      // 서버 함수에서 이미 저장했더라도 클라이언트에서 한 번 더 보정 저장해 누락 케이스를 방지한다.
      const { error: profileError } = await supabase.from('profiles').upsert({
        user_id: authUser.id,
        name: finalName,
        phone: verifiedPhone,
        birth_year: birthYear,
        birth_month: birthMonthInput,
        birth_day: birthDayInput,
        age_group: ageGroup,
        gender,
        age_display: ageDisplay,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id' });
      if (profileError) {
        if (!isMissingProfileColumnError(profileError)) {
          alertDbFailure('signupProfile', '프로필 저장에 실패했어요. 잠시 후 다시 시도해주세요.', profileError);
          return;
        }
        const { error: fallbackErr } = await supabase.from('profiles').upsert({
          user_id: authUser.id,
          name: finalName,
          phone: verifiedPhone,
          birth_year: birthYear,
          age_group: ageGroup,
          gender,
          age_display: ageDisplay,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' });
        if (fallbackErr) {
          alertDbFailure('signupProfileFallback', '프로필 저장에 실패했어요. 잠시 후 다시 시도해주세요.', fallbackErr);
          return;
        }
        alert('닉네임/프로필사진 기능을 위해 DB 업데이트가 필요합니다.');
      }
      const signupPaidGranted = await grantSignupPaidBonusOnce(authUser.id);

      setUser(prev => ({ ...prev,
        userId: authUser.id,
        name: finalName,
        phone: verifiedPhone,
        birthYear,
        birthMonth: birthMonthInput,
        birthDay: birthDayInput,
        ageGroup,
        gender,
        age: ageDisplay,
        paidCoins: (prev?.paidCoins ?? 0) + (signupPaidGranted ? SIGNUP_PAID_BONUS_COINS : 0),
        setupDone: true,
      }));
      alert('회원가입이 완료되었습니다.');
      onNav('welcome_profile');
    } finally { setSaving(false); }
  };

  return (
    <div className="screen">
      <div className="login-hero" style={{padding:'40px 22px 26px'}}>
        <div className="logo-text" style={{fontSize:38}}><span style={{color:'white'}}>동네</span><span className="green">사이</span></div>
        <div className="logo-desc" style={{marginTop:10}}>처음이신가요? 😊<br/>회원가입 정보를 입력해주세요</div>
      </div>
      <div className="content">
        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>이름 <span style={{color:'#991B1B',fontSize:12,fontWeight:700}}>* 필수</span></label>
          <input className="form-input" style={{fontSize:16}} value={name} onChange={e => setName(e.target.value.slice(0, 20))}
            placeholder="실명을 입력해주세요" maxLength={20}/>
        </div>
        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>생년월일 <span style={{color:'#991B1B',fontSize:12,fontWeight:700}}>* 필수</span></label>
          <div style={{display:'flex',gap:8}}>
            <input className="form-input" style={{background:'white',color:'#111827',fontSize:16,flex:1,textAlign:'center'}}
              value={birthYearInput}
              onChange={e => setBirthYearInput(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="YYYY"
              inputMode="numeric"
              maxLength={4}/>
            <input className="form-input" style={{background:'white',color:'#111827',fontSize:16,width:88,textAlign:'center'}}
              value={birthMonthInput}
              onChange={e => setBirthMonthInput(e.target.value.replace(/\D/g, '').slice(0, 2))}
              placeholder="MM"
              inputMode="numeric"
              maxLength={2}/>
            <input className="form-input" style={{background:'white',color:'#111827',fontSize:16,width:88,textAlign:'center'}}
              value={birthDayInput}
              onChange={e => setBirthDayInput(e.target.value.replace(/\D/g, '').slice(0, 2))}
              placeholder="DD"
              inputMode="numeric"
              maxLength={2}/>
          </div>
          <div style={{fontSize:12,color:'var(--gray-400)',marginTop:6}}>
            예: 1988 / 03 / 17 · {signupAgeRuleLine()}<br/>
            <span style={{fontSize:11}}>{signupAgeRuleFootnote()}</span>
          </div>
          {(birthYearInput || birthMonthInput || birthDayInput) && !birthFieldsValid && (
            <div style={{fontSize:13,color:'#991B1B',marginTop:6,fontWeight:600}}>⚠️ 연/월/일을 정확히 입력해주세요</div>
          )}
          {birthYear.length === 4 && birthFieldsValid && !signupAgeEligible && (
            <div style={{fontSize:13,color:'#991B1B',marginTop:6,fontWeight:600}}>
              ⚠️ {signupAgeRuleLine()}만 가입할 수 있어요.
            </div>
          )}
          {birthFieldsValid && signupAgeEligible && (
            <div style={{fontSize:13,color:'var(--green)',marginTop:6,fontWeight:600}}>
              ✓ 만 {calcManAgeFromYmd(parseInt(birthYear, 10), monthNum, dayNum)}세로 저장됩니다
            </div>
          )}
          {birthFieldsValid && signupAgeEligible && calcManAgeFromYmd(parseInt(birthYear, 10), monthNum, dayNum) <= MINOR_MAX_MAN_AGE && (
            <div style={{fontSize:12,color:'#92400E',marginTop:8,lineHeight:1.78,background:'#FFFBEB',borderRadius:10,padding:'12px 14px',border:'1px solid #F59E0B'}}>
              <div style={{fontWeight:800,marginBottom:6}}>⚠️ 청소년 회원(만 16세 이상 만 19세 이하) — 반드시 확인</div>
              · <strong>법정대리인(보호자) 동의·지도·감독</strong> 없이는 이용할 수 없습니다.<br/>
              · 연령 제한 모임은 <strong>{MINOR_ROOM_MIN}~{MINOR_ROOM_MAX}세</strong>(생년 기준)로만 개설·참여됩니다(성인 모임과 분리).<br/>
              · 허위 생년월일·성별 입력 시 <strong>영구 정지</strong>되며 민·형사상 책임이 따를 수 있습니다.<br/>
              · 오프라인 만남 전 보호자에게 일정·장소를 알리고, 위험 시 <strong>112</strong>·<strong>1372</strong>(디지털성범죄) 등에 신고하세요.
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>성별 <span style={{color:'#991B1B',fontSize:12,fontWeight:700}}>* 필수</span></label>
          <div className="gender-grid">
            <button className={`gender-card ${gender==='남'?'selected':''}`} onClick={() => setGender('남')}><div className="gender-label">남성</div></button>
            <button className={`gender-card ${gender==='여'?'selected':''}`} onClick={() => setGender('여')}><div className="gender-label">여성</div></button>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>전화번호 <span style={{color:'#991B1B',fontSize:12,fontWeight:700}}>* 필수</span></label>
          {phoneVerified ? (
            <input className="form-input" style={{fontSize:16,background:'#E8F5EE',color:'#065F46'}} value={verifiedPhone.replace('+82','0')} disabled/>
          ) : (
            <>
              <div style={{display:'flex',gap:8}}>
                <input className="form-input" style={{background:'white',color:'#111827',fontSize:16,flex:1}}
                  value={phoneInput} onChange={e => setPhoneInput(e.target.value.replace(/\D/g,'').slice(0,11))}
                  placeholder="010-0000-0000" inputMode="numeric" disabled={otpSent || otpLoading}/>
                {!otpSent && <button className="btn-primary" style={{width:'auto',padding:'0 16px',fontSize:14}} onClick={sendPhoneOtp} disabled={otpLoading}>{otpLoading ? '...' : '인증전송'}</button>}
              </div>
              {otpSent && (
                <div style={{marginTop:10}}>
                  <div style={{display:'flex',gap:8}}>
                    <input className="form-input" style={{background:'white',color:'#111827',fontSize:18,flex:1,letterSpacing:4,textAlign:'center'}}
                      value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                      placeholder="000000" inputMode="numeric" maxLength={6}/>
                    <button className="btn-primary" style={{width:'auto',padding:'0 16px',fontSize:14}} onClick={verifyPhoneOtp} disabled={otpLoading}>{otpLoading ? '...' : '확인'}</button>
                  </div>
                  <div style={{fontSize:12,color:'var(--gray-400)',marginTop:6}}>
                    인증번호가 오지 않으면 <span style={{color:'var(--green)',cursor:'pointer',fontWeight:600}} onClick={() => { setOtpSent(false); setOtp(''); }}>다시 전송</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>아이디 <span style={{color:'#991B1B',fontSize:12,fontWeight:700}}>* 필수</span></label>
          <input className="form-input" style={{background:'white',color:'#111827',fontSize:16}}
            value={loginId} onChange={e => setLoginId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0,20))}
            placeholder="영문 소문자/숫자/_ 4~20자"/>
        </div>

        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>비밀번호 <span style={{color:'#991B1B',fontSize:12,fontWeight:700}}>* 필수</span></label>
          <input type="password" className="form-input" style={{background:'white',color:'#111827',fontSize:16}}
            value={password} onChange={e => setPassword(e.target.value)} placeholder="8자 이상 입력"/>
        </div>

        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>비밀번호 확인 <span style={{color:'#991B1B',fontSize:12,fontWeight:700}}>* 필수</span></label>
          <input type="password" className="form-input" style={{background:'white',color:'#111827',fontSize:16}}
            value={passwordConfirm} onChange={e => setPasswordConfirm(e.target.value)} placeholder="비밀번호를 다시 입력"/>
        </div>

        <div className="notice blue" style={{fontSize:12}}>
          입력하신 정보는 서비스 제공 및 본인 확인 목적에만 사용되며 관련 법령 및 개인정보처리방침에 따라 보호됩니다.
        </div>
      </div>
      <div className="bottom-area">
        <button className="btn-primary" style={{fontSize:16}} disabled={!canSubmit || saving} onClick={handleSignup}>
          {saving ? '가입 처리 중...' : '확인하고 시작하기'}
        </button>
        <button className="btn-outline" style={{marginTop:10}} onClick={() => onNav('login', { replace: true })}>로그인으로 돌아가기</button>
      </div>
    </div>
  );
}

/* ── 최초 설정 ──────────────────────────── */
function SetupScreen({ user, setUser, onNav }) {
  const [name, setName]           = useState(user?.name && user.name !== '회원' ? user.name : '');
  const [birthYear, setBirthYear] = useState('');
  const [birthMonthInput, setBirthMonthInput] = useState('');
  const [birthDayInput, setBirthDayInput] = useState('');
  const [gender, setGender]       = useState('');
  const verifiedPhone = user?.phone || '';
  const [saving, setSaving]           = useState(false);

  const monthNum = Number(birthMonthInput);
  const dayNum = Number(birthDayInput);
  const validMonth = birthMonthInput.length === 2 && monthNum >= 1 && monthNum <= 12;
  const validDay = birthDayInput.length === 2 && dayNum >= 1 && dayNum <= 31;
  const birthFieldsValid = birthYear.length === 4 && validMonth && validDay;
  const ageGroup       = birthFieldsValid ? deriveAgeGroupFromBirth(birthYear, birthMonthInput, birthDayInput) : null;
  const setupAgeEligible = birthFieldsValid && isSignupEligibleFullDate(birthYear, birthMonthInput, birthDayInput);
  const canNext        = !!verifiedPhone && name.trim().length >= 2 && birthFieldsValid && setupAgeEligible && gender;

  const handleDone = async () => {
    if (!user?.userId) { alert('로그인 정보가 없어요. 다시 로그인해주세요.'); return; }
    if (!verifiedPhone)  { alert('전화번호 인증 정보를 불러오지 못했어요. 다시 로그인해주세요.'); return; }
    if (name.trim().length < 2) { alert('이름을 2자 이상 입력해주세요.'); return; }
    if (!birthFieldsValid) { alert('생년월일(연·월·일)을 정확히 입력해주세요.'); return; }
    if (!setupAgeEligible) {
      alert(`가입할 수 없어요.\n${signupAgeRuleLine()}.\n${signupAgeRuleFootnote()}`);
      return;
    }

    setSaving(true);
    try {
      const man = calcManAgeFromYmd(parseInt(birthYear, 10), monthNum, dayNum);
      const ageDisplay = `만 ${man}세 (${birthYear}년생)`;
      const finalPhone = verifiedPhone;
      const finalName = name.trim();

      const { error } = await supabase.from('profiles').upsert({
        user_id:     user.userId,
        name:        finalName,
        phone:       finalPhone,
        birth_year:  birthYear,
        birth_month: birthMonthInput,
        birth_day:   birthDayInput,
        age_group:   ageGroup,
        gender,
        age_display: ageDisplay,
        updated_at:  new Date().toISOString(),
      }, { onConflict: 'user_id' });

      if (error) {
        // 전화번호 UNIQUE 위반 (다른 계정이 같은 번호 선점)
        if (error.code === '23505') {
          alert('이미 이 전화번호로 가입된 계정이 있어요.\n한 사람당 하나의 계정만 사용할 수 있습니다.');
          await supabase.auth.signOut();
          return;
        }
        if (!isMissingProfileColumnError(error)) {
          alertDbFailure('welcomeProfile', '프로필 저장에 실패했어요. 잠시 후 다시 시도해주세요.', error);
          return;
        }
        const { error: fallbackErr } = await supabase.from('profiles').upsert({
          user_id:     user.userId,
          name:        finalName,
          phone:       finalPhone,
          birth_year:  birthYear,
          age_group:   ageGroup,
          gender,
          age_display: ageDisplay,
          updated_at:  new Date().toISOString(),
        }, { onConflict: 'user_id' });
        if (fallbackErr) {
          alertDbFailure('welcomeProfileFallback', '프로필 저장에 실패했어요. 잠시 후 다시 시도해주세요.', fallbackErr);
          return;
        }
        alert('닉네임/프로필사진 기능을 위해 DB 업데이트가 필요합니다.');
      }
      const signupPaidGranted = await grantSignupPaidBonusOnce(user.userId);

      setUser(prev => ({ ...prev,
        name: finalName, phone: finalPhone, birthYear, birthMonth: birthMonthInput, birthDay: birthDayInput, ageGroup, gender,
        age: ageDisplay,
        paidCoins: (prev?.paidCoins ?? 0) + (signupPaidGranted ? SIGNUP_PAID_BONUS_COINS : 0),
        setupDone: true,
      }));
      onNav('home', { resetHistory: true });
    } finally { setSaving(false); }
  };

  return (
    <div className="screen">
      <div className="login-hero" style={{padding:'44px 22px 28px'}}>
        <div className="logo-text" style={{fontSize:38}}><span style={{color:'white'}}>동네</span><span className="green">사이</span></div>
        <div className="logo-desc" style={{marginTop:10}}>처음 오셨군요! 😊<br/>간단한 정보를 알려주세요</div>
      </div>
      <div className="content">
        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>이름 <span style={{color:'#991B1B',fontSize:12,fontWeight:700}}>* 필수</span></label>
          <input className="form-input" style={{fontSize:16}}
            value={name}
            onChange={e => setName(e.target.value.slice(0, 20))}
            placeholder="실명을 입력해주세요 (2자 이상)"
            maxLength={20}/>
        </div>
        {/* 로그인에서 이미 인증된 전화번호 표시 */}
        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>
            전화번호 <span className="verified">✓ 인증 완료</span>
          </label>
          <input className="form-input" style={{fontSize:16,background:'#E8F5EE',color:'#065F46'}}
            value={verifiedPhone.replace('+82','0')} disabled/>
          <div style={{fontSize:12,color:'var(--gray-400)',marginTop:6}}>
            ※ 동일 전화번호로 <strong>중복 가입 불가</strong>합니다.
          </div>
        </div>

        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>생년월일 <span style={{color:'#991B1B',fontSize:12,fontWeight:700}}>* 필수</span></label>
          <div style={{display:'flex',gap:8}}>
            <input className="form-input" style={{background:'white',color:'#111827',fontSize:16,flex:1,textAlign:'center'}}
              value={birthYear} onChange={e => setBirthYear(e.target.value.replace(/\D/g,'').slice(0,4))}
              placeholder="YYYY" maxLength={4} inputMode="numeric"/>
            <input className="form-input" style={{background:'white',color:'#111827',fontSize:16,width:88,textAlign:'center'}}
              value={birthMonthInput} onChange={e => setBirthMonthInput(e.target.value.replace(/\D/g,'').slice(0,2))}
              placeholder="MM" maxLength={2} inputMode="numeric"/>
            <input className="form-input" style={{background:'white',color:'#111827',fontSize:16,width:88,textAlign:'center'}}
              value={birthDayInput} onChange={e => setBirthDayInput(e.target.value.replace(/\D/g,'').slice(0,2))}
              placeholder="DD" maxLength={2} inputMode="numeric"/>
          </div>
          <div style={{fontSize:12,color:'var(--gray-400)',marginTop:6}}>
            {signupAgeRuleLine()}<br/>
            <span style={{fontSize:11}}>{signupAgeRuleFootnote()}</span>
          </div>
          {(birthYear || birthMonthInput || birthDayInput) && !birthFieldsValid && birthYear.length === 4 && (
            <div style={{fontSize:13,color:'#991B1B',marginTop:6,fontWeight:600}}>⚠️ 연·월·일을 정확히 입력해주세요</div>
          )}
          {birthFieldsValid && !setupAgeEligible && (
            <div style={{fontSize:13,color:'#991B1B',marginTop:6,fontWeight:600}}>
              ⚠️ {signupAgeRuleLine()}만 가입할 수 있어요.
            </div>
          )}
          {birthFieldsValid && setupAgeEligible && (
            <div style={{fontSize:13,color:'var(--green)',marginTop:6,fontWeight:600}}>
              ✓ 만 {calcManAgeFromYmd(parseInt(birthYear, 10), monthNum, dayNum)}세로 저장됩니다
            </div>
          )}
          {birthFieldsValid && setupAgeEligible && calcManAgeFromYmd(parseInt(birthYear, 10), monthNum, dayNum) <= MINOR_MAX_MAN_AGE && (
            <div style={{fontSize:12,color:'#92400E',marginTop:8,lineHeight:1.78,background:'#FFFBEB',borderRadius:10,padding:'12px 14px',border:'1px solid #F59E0B'}}>
              <div style={{fontWeight:800,marginBottom:6}}>⚠️ 청소년 회원(만 16세 이상 만 19세 이하) — 반드시 확인</div>
              · <strong>법정대리인(보호자) 동의·지도·감독</strong> 하에만 이용하세요.<br/>
              · 연령 제한 모임은 <strong>{MINOR_ROOM_MIN}~{MINOR_ROOM_MAX}세</strong>(생년 기준)로만 이용 가능합니다.<br/>
              · 허위 정보 시 <strong>영구 정지</strong>·법적 책임이 따를 수 있습니다.
            </div>
          )}
        </div>
        <div className="form-group">
          <label className="form-label" style={{fontSize:15}}>성별</label>
          <div className="gender-grid">
            <button className={`gender-card ${gender==='남'?'selected':''}`} onClick={() => setGender('남')}><div className="gender-label">남성</div></button>
            <button className={`gender-card ${gender==='여'?'selected':''}`} onClick={() => setGender('여')}><div className="gender-label">여성</div></button>
          </div>
        </div>
        <div style={{background:'#FEF2F2',borderRadius:10,padding:'13px 15px',border:'0.5px solid #FECACA',marginBottom:12}}>
          <div style={{fontSize:14,fontWeight:700,color:'#991B1B',marginBottom:4}}>⚠️ 허위 정보 입력 금지</div>
          <div style={{fontSize:13,color:'#7F1D1D',lineHeight:1.7}}>나이 또는 성별 허위 입력 시 <strong>계정이 영구 정지</strong>됩니다.</div>
        </div>
        <div className="notice blue" style={{fontSize:13}}>나이·성별은 저장 후 <strong>변경이 불가</strong>합니다.</div>
      </div>
      <div className="bottom-area">
        <button className="btn-primary" style={{fontSize:16}} disabled={!canNext || saving} onClick={handleDone}>
          {saving ? '저장 중...' : '저장하고 시작하기 →'}
        </button>
      </div>
    </div>
  );
}

/* ── 가입 직후 닉네임·프로필 사진 (사진은 선택) ───────────────── */
function WelcomeProfileScreen({ user, setUser, onNav }) {
  const [nicknameInput, setNicknameInput] = useState(user?.nickname || '');
  const [profilePreviewDataUrl, setProfilePreviewDataUrl] = useState(user?.profileImageUrl || null);
  const [saving, setSaving] = useState(false);

  const profileChanged = (profilePreviewDataUrl || null) !== (user?.profileImageUrl || null);
  const nicknameChanged = normalizeNickname(nicknameInput) !== normalizeNickname(user?.nickname || '');

  const completeAndEnter = async () => {
    if (!user?.userId) { alert('로그인 정보가 없어요. 다시 로그인해주세요.'); return; }
    const nick = normalizeNickname(nicknameInput);
    if (!isValidNicknameFormat(nick)) {
      alert(`닉네임은 ${NICKNAME_MIN_LEN}~${NICKNAME_MAX_LEN}자, 한글/영문/숫자/_만 가능합니다.`);
      return;
    }
    const okNick = await isNicknameAvailable(nick, user.userId);
    const nicknameSupported = okNick !== null;
    if (nicknameSupported) {
      if (!okNick) { alert('이미 사용 중인 닉네임이에요. 다른 닉네임을 입력해주세요.'); return; }
    } else {
      alert('닉네임 컬럼이 아직 준비되지 않았어요. 내 정보에서 나중에 설정해주세요.');
    }

    setSaving(true);
    try {
      const payload = { updated_at: new Date().toISOString() };
      let uploadedProfileUrl = user?.profileImageUrl || null;
      if (profileChanged && profilePreviewDataUrl && String(profilePreviewDataUrl).startsWith('data:')) {
        uploadedProfileUrl = await uploadProfileImageFromDataUrl(user.userId, profilePreviewDataUrl);
        payload.profile_image_url = uploadedProfileUrl;
      }
      if (nicknameSupported && (nicknameChanged || normalizeNickname(user?.nickname || '') !== nick)) {
        payload.nickname = nick;
        payload.nickname_updated_at = new Date().toISOString();
      }
      const upsertPayload = {
        user_id: user.userId,
        name: user?.name || null,
        ...payload,
      };
      let { error } = await supabase.from('profiles').upsert(upsertPayload, { onConflict: 'user_id' });
      if (error && isMissingProfileColumnError(error)) {
        const fallbackPayload = { updated_at: payload.updated_at };
        ({ error } = await supabase
          .from('profiles')
          .upsert({ user_id: user.userId, name: user?.name || null, ...fallbackPayload }, { onConflict: 'user_id' }));
        if (!error) {
          alert('DB 업데이트가 필요해 닉네임/사진 저장은 적용되지 않았어요.');
          return;
        }
      }
      if (error) {
        alertDbFailure('welcomeNicknameProfile', '프로필 저장에 실패했어요. 잠시 후 다시 시도해주세요.', error);
        return;
      }

      const p = await fetchProfile(user.userId);
      if (p) {
        setUser(prev => prev ? ({
          ...prev,
          nickname: p.nickname || nick,
          profileImageUrl: p.profile_image_url || uploadedProfileUrl,
          nicknameUpdatedAt: p.nickname_updated_at || payload.nickname_updated_at || prev.nicknameUpdatedAt,
          freeCoins: p.free_coins ?? prev.freeCoins,
          paidCoins: p.paid_coins ?? prev.paidCoins,
        }) : prev);
      } else {
        setUser(prev => ({
          ...prev,
          nickname: nick,
          profileImageUrl: uploadedProfileUrl,
          nicknameUpdatedAt: payload.nickname_updated_at || prev?.nicknameUpdatedAt,
        }));
      }
      try { sessionStorage.setItem('gyeol_show_welcome_event', '1'); } catch {}
      onNav('home', { resetHistory: true });
    } catch (e) {
      logClientError('welcomeNicknameProfileCatch', e?.message, e);
      alert('프로필 저장에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-fixed">
      <div className="fixed-header" style={{ padding:'18px 16px 14px' }}>
        <div style={{ fontSize:20, fontWeight:800, color:'#111827' }}>프로필을 완성해주세요</div>
        <div style={{ fontSize:13, color:'var(--gray-500)', marginTop:6, lineHeight:1.55 }}>
          모임과 채팅에서 보일 <strong>닉네임</strong>을 설정해주세요. 프로필 사진은 원하실 때만 넣으시면 됩니다.
        </div>
      </div>
      <div className="fixed-scroll" style={{ padding:'12px 16px 110px' }}>
        <div className="card" style={{ marginBottom:14 }}>
          <div className="card-title" style={{ marginBottom:10 }}>프로필 사진</div>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <label style={{ width:86, height:86, borderRadius:'50%', overflow:'hidden', border:'1px solid var(--gray-200)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', background:'#F8FAFC' }}>
              {profilePreviewDataUrl
                ? <img src={profilePreviewDataUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                : <img src={DEFAULT_PROFILE_ICON} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>}
              <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" style={{ display:'none' }}
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  if (!f.type?.startsWith('image/') && !isLikelyHeicFile(f)) { alert('이미지 파일만 선택할 수 있어요.'); e.target.value=''; return; }
                  if (f.size > IMAGE_UPLOAD_MAX_INPUT_BYTES) { alert('원본이 너무 큽니다(최대 약 30MB). 다른 사진을 선택해주세요.'); e.target.value=''; return; }
                  try {
                    const cf = await prepareImageFileForUpload(f);
                    const reader = new FileReader();
                    reader.onload = () => setProfilePreviewDataUrl(reader.result);
                    reader.readAsDataURL(cf);
                  } catch (err) {
                    alertImagePrepareFailure('profilePreviewImage', err);
                  }
                  e.target.value = '';
                }}
              />
            </label>
            <div style={{ flex:1, fontSize:13, color:'var(--gray-600)', lineHeight:1.65 }}>
              <strong>지금은 설정하지 않아도 괜찮아요.</strong><br/>
              업로드한 이미지는 서비스 내 프로필로 표시됩니다.
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-title" style={{ marginBottom:10 }}>닉네임</div>
          <input className="form-input" style={{ fontSize:16 }} value={nicknameInput} maxLength={NICKNAME_MAX_LEN}
            onChange={e => setNicknameInput(e.target.value)}
            placeholder={`닉네임 ${NICKNAME_MIN_LEN}~${NICKNAME_MAX_LEN}자`} />
          <div style={{ fontSize:12, color:'var(--gray-400)', marginTop:8, lineHeight:1.55 }}>
            저장 시 자동으로 중복 여부를 확인합니다. (이후 변경은 {NICKNAME_COOLDOWN_DAYS}일에 한 번)
          </div>
        </div>
      </div>
      <div className="bottom-area">
        <button className="btn-primary" style={{ fontSize:16 }} disabled={saving} onClick={completeAndEnter}>
          {saving ? '저장 중...' : '저장하고 시작하기 →'}
        </button>
      </div>
    </div>
  );
}

function ClubListThumb({ club, compact, hostHighlight = false }) {
  const sizeCls = compact ? 'club-thumb--compact' : 'club-thumb--promo';
  const hostCls = hostHighlight ? 'club-thumb--host-ring' : '';
  if (club.coverImageUrl) {
    return (
      <div className={`club-thumb ${sizeCls} club-thumb--photo ${hostCls}`}>
        <img src={club.coverImageUrl} alt="" className="club-thumb-img" />
      </div>
    );
  }
  const defSrc = defaultClubCoverSrc(club.hobby);
  const fbSrc = defaultClubCoverFallbackSrc();
  return (
    <div className={`club-thumb ${sizeCls} club-thumb--photo club-thumb--default-cover ${hostCls}`}>
      <img
        src={defSrc}
        alt=""
        className="club-thumb-img club-thumb-img--default"
        onError={(e) => {
          const t = e.currentTarget;
          if (String(t.src || '').replace(/\\/g, '/').includes('/chatroom.png') && !String(t.src || '').includes('/default-covers/')) return;
          t.onerror = null;
          t.src = fbSrc;
        }}
      />
    </div>
  );
}

/* ── 홈 ─────────────────────────────────── */
function HomeScreen({ user, setUser, onNav, updateState, appState, clubs, setClubs, unreadCounts }) {
  const [activeCategory, setActiveCategory]   = useState('전체');
  const [selectedRegion, setSelectedRegion]   = useState('all');
  const [showRegionPicker, setShowRegionPicker] = useState(false);
  const [showAllClubs, setShowAllClubs]       = useState(false);
  const [showAllActiveRooms, setShowAllActiveRooms] = useState(false);
  const [ageWarnClub, setAgeWarnClub]         = useState(null);
  const [notice, setNotice]                   = useState(null);
  const [showWelcomeEvent, setShowWelcomeEvent] = useState(false);
  const [selectedDetailClub, setSelectedDetailClub] = useState(null);
  const [catTabDragging, setCatTabDragging] = useState(false);
  const categoryDragRef = useRef({ active: false, startX: 0, startScroll: 0 });
  const unreadTotal = readUnreadTotal(unreadCounts);
  const roomOpenUnread = Number(unreadCounts?.[ROOM_OPEN_ALERT_KEY] || 0);
  // 홈 상단 벨은 방 오픈/운영 알림만 표시하고, 채팅 미읽음은 하단 채팅 탭에만 표시한다.
  const homeNotifUnreadTotal = roomOpenUnread;
  const welcomeCoinTotal = SIGNUP_PAID_BONUS_COINS + MONTHLY_FREE_COINS;

  // 공지사항 체크 (Supabase) + 실시간 구독
  useEffect(() => {
    const load = async () => {
      const next = await fetchActiveNotice();
      if (!next?.id) { setNotice(next); return; }
      const dismissed = getDismissedNoticeIds();
      setNotice(dismissed.includes(next.id) ? null : next);
    };
    load();
    const channel = supabase.channel('notices-watch')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notices' }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  useEffect(() => {
    if (!user?.userId) return;
    try {
      if (sessionStorage.getItem('gyeol_show_welcome_event') === '1') {
        sessionStorage.removeItem('gyeol_show_welcome_event');
        setShowWelcomeEvent(true);
      }
    } catch {}
  }, [user?.userId]);

  const closeNotice = () => setNotice(null);
  const dismissNoticeForever = () => {
    if (notice?.id) saveDismissedNoticeId(notice.id);
    setNotice(null);
  };

  const categories = ['전체','와인·맛집','브런치·카페','책·독서','전시·문화','산책·등산','게임·오락','운동·스포츠','기타'];
  const categoryHobbyMap = {
    '와인·맛집': 'wine',
    '브런치·카페': 'brunch',
    '책·독서': 'book',
    '전시·문화': 'culture',
    '산책·등산': 'walk',
    '게임·오락': 'game',
    '운동·스포츠': 'sports',
    '기타': 'other',
  };
  const isCategoryMatch = (club, category) => {
    if (category === '전체') return true;
    const target = categoryHobbyMap[category];
    const hobbies = normalizeClubHobbies(club);
    if (target === 'book') return hobbies.includes('book') || hobbies.includes('talk');
    return hobbies.includes(target);
  };
  const regionOptions = [
    { val: 'all', label: '수도권 전체', sub: '인천·서울·경기' },
    { val: '인천', label: '인천', sub: '인천광역시' },
    { val: '서울', label: '서울', sub: '서울특별시' },
    { val: '경기', label: '경기', sub: '경기도' },
  ];
  const selectedRegionMeta = regionOptions.find((r) => r.val === selectedRegion);
  const regionShortLabel = selectedRegionMeta?.label || '수도권 전체';

  const isActiveRoom = (c) => isClubOpenForChat(c);
  const onCategoryMouseDown = (e) => {
    if (e.button !== 0) return;
    const el = e.currentTarget;
    categoryDragRef.current = { active: true, startX: e.clientX, startScroll: el.scrollLeft };
    setCatTabDragging(true);
  };
  const onCategoryMouseMove = (e) => {
    const drag = categoryDragRef.current;
    if (!drag.active) return;
    const el = e.currentTarget;
    const dx = e.clientX - drag.startX;
    el.scrollLeft = drag.startScroll - dx;
  };
  const stopCategoryDrag = () => {
    if (!categoryDragRef.current.active) return;
    categoryDragRef.current.active = false;
    setCatTabDragging(false);
  };

  const visibleClubs = clubs.filter(c => {
    if (isActiveRoom(c)) return false; // 채팅중인 방은 별도 섹션
    if (!clubMatchesMetroRegionFilter(c.region, selectedRegion)) return false;
    if (!isCategoryMatch(c, activeCategory)) return false;
    return true;
  });
  const sortedVisibleClubs = [...visibleClubs].sort((a, b) => {
    const aBoost = isBoostActive(a);
    const bBoost = isBoostActive(b);
    if (aBoost !== bBoost) return Number(bBoost) - Number(aBoost);
    if (aBoost && bBoost) {
      const at = parseTimestampMs(a.boostedAt);
      const bt = parseTimestampMs(b.boostedAt);
      if (Number.isFinite(at) || Number.isFinite(bt)) return (bt || 0) - (at || 0);
    }
    const aDenied = !isRatioAllowedForGender(a.ratio, user?.gender) || (user?.birthYear && !canJoinClub(user, a));
    const bDenied = !isRatioAllowedForGender(b.ratio, user?.gender) || (user?.birthYear && !canJoinClub(user, b));
    if (aDenied !== bDenied) return Number(aDenied) - Number(bDenied);
    const atc = parseTimestampMs(a.createdAt);
    const btc = parseTimestampMs(b.createdAt);
    return (btc || 0) - (atc || 0);
  });

  const activeRooms = clubs.filter(c => {
    if (!isActiveRoom(c)) return false;
    if ((c.members || 0) >= (c.maxMembers || 6)) return false; // 정원 마감 방은 공개 채팅중 목록에서 제외
    if (c.isLocked) return false; // 모임장이 잠근 방은 숨김
    if (!clubMatchesMetroRegionFilter(c.region, selectedRegion)) return false;
    if (!isCategoryMatch(c, activeCategory)) return false;
    return true;
  });
  const sortedActiveRooms = [...activeRooms].sort((a, b) => {
    const aBoost = isBoostActive(a);
    const bBoost = isBoostActive(b);
    if (aBoost !== bBoost) return Number(bBoost) - Number(aBoost);
    if (aBoost && bBoost) {
      const at = parseTimestampMs(a.boostedAt);
      const bt = parseTimestampMs(b.boostedAt);
      if (Number.isFinite(at) || Number.isFinite(bt)) return (bt || 0) - (at || 0);
    }
    const aDenied = !isRatioAllowedForGender(a.ratio, user?.gender);
    const bDenied = !isRatioAllowedForGender(b.ratio, user?.gender);
    if (aDenied !== bDenied) return Number(aDenied) - Number(bDenied);
    const atc = parseTimestampMs(a.createdAt);
    const btc = parseTimestampMs(b.createdAt);
    return (btc || 0) - (atc || 0);
  });

  const handleEnterActive = async (c) => {
    if (!user?.userId) { if (window.confirm('로그인하시겠습니까?')) onNav('login'); return; }
    if (!isRatioAllowedForGender(c.ratio, user?.gender)) {
      alert(getRatioDenyMessage(c.ratio));
      return;
    }
    if (user?.birthYear && !canJoinClub(user, c)) { setAgeWarnClub(c); return; }
    if (await wasKickedFromClub(c.id, user?.userId)) {
      alert('강퇴된 모임에는 다시 참여할 수 없습니다.');
      return;
    }
    const targetHobbies = normalizeClubHobbies(c);
    updateState('hobby', targetHobbies[0] || c.hobby || null); updateState('hobbies', targetHobbies); updateState('region', c.region);
    updateState('ratio', c.ratio); updateState('times', c.times);
    updateState('fromClub', true); updateState('clubId', c.id);
    onNav('confirm');
  };

  const clubAgeText = (c) => (
    c.minAge != null && c.maxAge != null
      ? `${c.minAge}~${c.maxAge}세`
      : null
  );

  const ActiveClubCard = ({ c }) => {
    const myMatch  = appState.matches.find(m => m.clubId === c.id);
    const isMember = !!myMatch || c.hostId === user?.userId;
    const isMyHost = c.hostId === user?.userId;
    const full     = !isMember && (c.members||0) >= (c.maxMembers||6);
    const genderOk = isRatioAllowedForGender(c.ratio, user?.gender);
    const mem      = c.members || 0;
    const maxM     = c.maxMembers || 6;
    const titleLine = c.title || clubHobbyCategoryText(c);
    return (
      <div className="mcard mcard--chat" onClick={() => setSelectedDetailClub(c)} style={{cursor:'pointer'}}>
        <ClubListThumb club={c} hostHighlight={isMyHost} />
        <div className="mcard-body mcard-body--chat-active">
          <div className="mcard-title-row">
            <div className="mcard-title-text">{titleLine}</div>
            <div className="mcard-title-badges">
              <span className="mcard-badge mcard-badge--chat">채팅중</span>
            </div>
          </div>
          <div className="mcard-cat mcard-cat--chat-active">
            {clubHobbyCategoryText(c)}{clubAgeText(c) ? ` · ${clubAgeText(c)}` : ''}
          </div>
          <div className="mcard-loc">
            <LocationPinIcon className="mcard-loc-pin" />
            <span>{c.region}</span>
          </div>
          <div className="mcard-time">{(c.times||[]).map(t=>TIME_LABELS[t]||t).join(' · ')}</div>
        </div>
        <div className="mcard-right">
          <div className="mcard-mem mcard-mem--chat">
            <img
              className="mcard-mem-icon"
              src={`${process.env.PUBLIC_URL || ''}/humanicon.png`}
              alt=""
              aria-hidden="true"
            />
            <span className="mcard-mem-num">{mem}</span>
            <span className="mcard-mem-slash">/</span>
            <span className="mcard-mem-total">{maxM}</span>
          </div>
          {isMember
            ? <button type="button" className="mcard-btn mcard-btn--blue" onClick={e=>{e.stopPropagation();updateState('currentClubId',c.id);onNav('chat');}}>입장</button>
            : !genderOk
              ? <span className="mcard-btn mcard-btn--warn">성별 불가</span>
            : full
              ? <span className="mcard-btn mcard-btn--muted">마감</span>
              : <button type="button" className="mcard-btn mcard-btn--blue" onClick={e=>{e.stopPropagation();handleEnterActive(c);}}>참여</button>}
        </div>
      </div>
    );
  };

  const handleApply = async (c) => {
    if (!user?.userId) { if (window.confirm('로그인하시겠습니까?')) onNav('login'); return; }
    if (!isRatioAllowedForGender(c.ratio, user?.gender)) {
      alert(getRatioDenyMessage(c.ratio));
      return;
    }
    if (user?.birthYear && !canJoinClub(user, c)) { setAgeWarnClub(c); return; }
    if (await wasKickedFromClub(c.id, user?.userId)) {
      alert('강퇴된 모임에는 다시 참여할 수 없습니다.');
      return;
    }
    const targetHobbies = normalizeClubHobbies(c);
    updateState('hobby', targetHobbies[0] || c.hobby || null); updateState('hobbies', targetHobbies); updateState('region', c.region);
    updateState('ratio', c.ratio); updateState('times', c.times);
    updateState('fromClub', true); updateState('clubId', c.id);
    onNav('confirm');
  };

  const ClubCard = ({ c }) => {
    const myMatch    = appState.matches.find(m => m.clubId === c.id);
    const isMember   = !!myMatch || c.hostId === user?.userId;
    const isActive   = isMember && isClubOpenForChat(c);
    const genderOk   = isRatioAllowedForGender(c.ratio, user?.gender);
    const full       = !isMember && (c.members||0) >= (c.maxMembers||6);
    const isMyHost   = c.hostId === user?.userId;
    const ageText = clubAgeText(c);
    const maxM       = c.maxMembers || 6;
    const mem        = c.members || 0;
    const titleLine  = c.title || clubHobbyCategoryText(c);
    return (
      <div className="mcard" onClick={() => setSelectedDetailClub(c)} style={{cursor:'pointer'}}>
        <ClubListThumb club={c} hostHighlight={isMyHost} />
        <div className="mcard-body">
          <div className="mcard-title-row">
            <div className="mcard-title-text mcard-title-text--match-home">{titleLine}</div>
            <div className="mcard-title-badges">
              <span className="mcard-badge mcard-badge--wait">매칭중</span>
            </div>
          </div>
          <div className="mcard-cat">{clubHobbyCategoryText(c)}{ageText ? ` · ${ageText}` : ''}</div>
          <div className="mcard-loc">
            <LocationPinIcon className="mcard-loc-pin" />
            <span>{c.region}</span>
          </div>
          <div className="mcard-time">{(c.times||[]).map(t=>TIME_LABELS[t]||t).join(' · ')}</div>
        </div>
        <div className="mcard-right">
          <div className={`mcard-mem${!genderOk && !isMember ? ' mcard-mem--warn' : ''}`}>
            <img className="mcard-mem-icon" src={`${process.env.PUBLIC_URL || ''}/humanicon.png`} alt="" aria-hidden="true" />
            <span className="mcard-mem-num">{mem}</span>
            <span className="mcard-mem-slash">/</span>
            <span className="mcard-mem-total">{maxM}</span>
          </div>
          {isMember
            ? isActive
              ? <button type="button" className="mcard-btn mcard-btn--green" onClick={e=>{e.stopPropagation();updateState('currentClubId',c.id);onNav('chat');}}>입장</button>
              : <span className="mcard-btn mcard-btn--wait">매칭중</span>
            : full
              ? <span className="mcard-btn mcard-btn--muted">마감</span>
              : !genderOk
                ? <span className="mcard-btn mcard-btn--warn">{joinEligibilityBlockLabel(user, c)} 불가</span>
                : <button type="button" className="mcard-btn mcard-btn--green" onClick={e=>{e.stopPropagation();handleApply(c);}}>신청</button>}
        </div>
      </div>
    );
  };

  if (selectedDetailClub) {
    const c = selectedDetailClub;
    const myMatch = appState.matches.find(m => m.clubId === c.id);
    const isMember = !!myMatch || sameUserId(c.hostId, user?.userId);
    const isHostViewer = sameUserId(c.hostId, user?.userId);
    const isActive = isClubOpenForChat(c);
    const ageOk = !user?.birthYear || canJoinClub(user, c);
    const genderOk = isRatioAllowedForGender(c.ratio, user?.gender);
    const full = !isMember && (c.members || 0) >= (c.maxMembers || 6);
    let primaryAction = null;
    if (isHostViewer && !isActive) {
      primaryAction = { label: '내가 만든 방 (매칭 진행중)', disabled: true, onClick: () => {}, background: '#9CA3AF' };
    } else if (isMember && isActive) {
      primaryAction = { label: '💬 채팅방 입장하기', onClick: () => { updateState('currentClubId', c.id); onNav('chat'); } };
    } else if (isMember && !isActive) {
      /** 이미 신청한 매칭 대기방 — 채팅 탭 매칭 상세와 동일(하단 신청 취소만) */
      primaryAction = null;
    } else if (!genderOk) {
      const lab = joinEligibilityBlockLabel(user, c);
      primaryAction = { label: lab ? `${lab} 조건` : '참여 불가', disabled: true, onClick: () => {}, background: '#9CA3AF' };
    } else if (!ageOk) {
      primaryAction = isActive
        ? { label: '참여하기', onClick: () => handleEnterActive(c), background: 'var(--green)' }
        : { label: '신청하기', onClick: () => handleApply(c), background: 'var(--green)' };
    } else if (full) {
      primaryAction = { label: '마감된 모임', disabled: true, onClick: () => {}, background: '#9CA3AF' };
    } else if (isActive) {
      primaryAction = { label: '참여하기', onClick: () => handleEnterActive(c) };
    } else {
      primaryAction = { label: '신청하기', onClick: () => handleApply(c), background: 'var(--green)' };
    }
    const anchorMs = cancelPolicyAnchorMs(
      myMatch || { clubId: c.id },
      c,
      user?.userId
    );
    const detailMatchPayload = {
      id: myMatch?.id || `home_${c.id}`,
      clubId: c.id,
      hobby: c.hobby,
      title: c.title || null,
      roomIntro: c.roomIntro || null,
      region: c.region,
      ratio: c.ratio,
      times: c.times || [],
      applicantWasHost: !!myMatch?.isHost,
      isViewerInRoom: isMember,
      isHost: sameUserId(c.hostId, user?.userId),
      status: isActive ? 'active' : 'waiting',
      createdAt: anchorMs ?? myMatch?.createdAt ?? Date.now(),
      coinType: myMatch?.coinType || 'paid',
      spentCoins: myMatch?.spentCoins ?? (!!myMatch?.isHost ? HOST_COST : JOIN_COST),
    };
    const waitingMyApplication = isMember && !isActive;
    return (
      <MatchDetail
        match={detailMatchPayload}
        user={user}
        clubs={clubs}
        onBack={() => setSelectedDetailClub(null)}
        onCancel={waitingMyApplication
          ? () => executeMembershipWithdrawal({
            match: detailMatchPayload,
            user,
            appState,
            clubs,
            setUser,
            updateState,
            setClubs,
          }).then(() => setSelectedDetailClub(null))
          : () => setSelectedDetailClub(null)}
        onNav={onNav}
        hideCancelAction={!waitingMyApplication}
        primaryAction={primaryAction}
        onEnterChat={(clubId) => { updateState('currentClubId', clubId); onNav('chat'); }}
        onClubUpdated={(updatedClub) => {
          if (!updatedClub?.id) return;
          setClubs(prev => (prev || []).map(item => item.id === updatedClub.id ? { ...item, ...updatedClub } : item));
          setSelectedDetailClub(prev => (prev?.id === updatedClub.id ? { ...prev, ...updatedClub } : prev));
        }}
        onAgeMismatchFromDetail={() => setAgeWarnClub(c)}
        onJoinActiveRoomFromDetail={() => handleEnterActive(c)}
        onBoostApplied={async (clubId) => {
          if (user?.userId) {
            const p = await fetchProfile(user.userId);
            if (p) {
              setUser(prev => prev ? ({
                ...prev,
                freeCoins: p.free_coins ?? prev.freeCoins,
                paidCoins: p.paid_coins ?? prev.paidCoins,
              }) : prev);
            }
          }
          const fresh = await fetchClubs();
          setClubs(fresh);
          setSelectedDetailClub(prev => {
            if (!prev?.id || String(prev.id) !== String(clubId)) return prev;
            const found = fresh.find(x => String(x.id) === String(clubId));
            return found ? { ...prev, ...found } : prev;
          });
        }}
      />
    );
  }

  if (showAllClubs) return (
    <div className="page-fixed">
      <div className="fixed-header" style={{padding:0}}>
        <div style={{display:'flex',alignItems:'center',gap:10,padding:'16px 16px 14px'}}>
          <button className="back-btn" onClick={() => setShowAllClubs(false)}>←</button>
          <div style={{fontSize:18,fontWeight:800,color:'var(--gray-900)'}}>지금 인기 있는 모임</div>
        </div>
        <div
          className={`category-tabs${catTabDragging ? ' is-dragging' : ''}`}
          onMouseDown={onCategoryMouseDown}
          onMouseMove={onCategoryMouseMove}
          onMouseUp={stopCategoryDrag}
          onMouseLeave={stopCategoryDrag}
        >
          {categories.map(c => <button key={c} className={`category-tab ${activeCategory===c?'active':''}`} onClick={() => setActiveCategory(c)}>{c}</button>)}
        </div>
      </div>
      <div className="fixed-scroll" style={{padding:'12px 16px'}}>
        {sortedVisibleClubs.length === 0
          ? <div style={{textAlign:'center',padding:'48px 0',color:'var(--gray-400)',fontSize:14}}>조건에 맞는 모임이 없어요</div>
          : <div className="mcard-list">{sortedVisibleClubs.map(c => <ClubCard key={c.id} c={c}/>)}</div>}
        <div style={{height:16}}/>
      </div>
      <TabBar active="home" onNav={onNav} updateState={updateState} unreadTotal={unreadTotal}/>
    </div>
  );

  if (showAllActiveRooms) return (
    <div className="page-fixed">
      <div className="fixed-header" style={{padding:0}}>
        <div style={{display:'flex',alignItems:'center',gap:10,padding:'16px 16px 14px'}}>
          <button className="back-btn" onClick={() => setShowAllActiveRooms(false)}>←</button>
          <div style={{fontSize:18,fontWeight:800,color:'var(--gray-900)'}}>💬 채팅중인 방</div>
        </div>
        <div
          className={`category-tabs${catTabDragging ? ' is-dragging' : ''}`}
          onMouseDown={onCategoryMouseDown}
          onMouseMove={onCategoryMouseMove}
          onMouseUp={stopCategoryDrag}
          onMouseLeave={stopCategoryDrag}
        >
          {categories.map(c => <button key={c} className={`category-tab ${activeCategory===c?'active':''}`} onClick={() => setActiveCategory(c)}>{c}</button>)}
        </div>
      </div>
      <div className="fixed-scroll" style={{padding:'12px 16px'}}>
        {sortedActiveRooms.length === 0
          ? <div style={{textAlign:'center',padding:'48px 0',color:'var(--gray-400)',fontSize:14}}>조건에 맞는 채팅중인 방이 없어요</div>
          : <div className="mcard-list">{sortedActiveRooms.map(c => <ActiveClubCard key={c.id} c={c}/>)}</div>}
        <div style={{height:16}}/>
      </div>
      <TabBar active="home" onNav={onNav} updateState={updateState} unreadTotal={unreadTotal}/>
    </div>
  );

  return (
    <div className="page-fixed">
      {showWelcomeEvent && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:210, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 18px' }}>
          <div style={{ background:'#fff', borderRadius:18, width:'100%', maxWidth:380, maxHeight:'min(92vh, 720px)', overflow:'auto', position:'relative', boxShadow:'0 18px 50px rgba(15,23,42,0.18)' }}>
            <button
              type="button"
              aria-label="닫기"
              onClick={() => setShowWelcomeEvent(false)}
              style={{ position:'absolute', top:10, right:10, width:34, height:34, borderRadius:10, border:'none', background:'#F3F4F6', cursor:'pointer', fontSize:18, color:'#6B7280', lineHeight:1 }}
            >
              ×
            </button>
            <div style={{ padding:'22px 18px 16px', textAlign:'center' }}>
              <div style={{ width:92, height:92, borderRadius:'50%', margin:'0 auto 12px', background:'linear-gradient(145deg,#DCFCE7,#ECFDF5)', display:'grid', placeItems:'center', position:'relative' }}>
                <div style={{ fontSize:44, lineHeight:1 }}>🎁</div>
                <div style={{ position:'absolute', inset:-6, borderRadius:'50%', border:'1px dashed rgba(34,197,94,0.35)' }} />
              </div>
              <div style={{ fontSize:20, fontWeight:900, color:'#111827', lineHeight:1.35, marginBottom:8 }}>
                환영합니다! 첫 가입을 축하드려요 🎉
              </div>
              <div style={{ fontSize:14, color:'#4B5563', lineHeight:1.65 }}>
                동네사이는 당신의 새로운 모임을 응원합니다!
              </div>
            </div>

            <div style={{ margin:'0 16px 16px', border:'1px solid #E5E7EB', borderRadius:14, padding:'14px 12px 12px', background:'#FAFAFA' }}>
              <div style={{ textAlign:'center', fontSize:14, fontWeight:900, color:'#111827', marginBottom:12 }}>
                ✨ 첫 가입 이벤트 혜택 ✨
              </div>
              <div style={{ display:'flex', alignItems:'stretch', justifyContent:'center', gap:8 }}>
                <div style={{ flex:1, background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:12, padding:'12px 10px', textAlign:'center' }}>
                  <div style={{ fontSize:22, marginBottom:6 }}>🪙</div>
                  <div style={{ fontSize:12, fontWeight:800, color:'#92400E' }}>유료 포인트</div>
                  <div style={{ fontSize:18, fontWeight:900, color:'#B45309', marginTop:4 }}>{SIGNUP_PAID_BONUS_COINS} 포인트</div>
                </div>
                <div style={{ display:'grid', placeItems:'center', fontSize:22, fontWeight:900, color:'#9CA3AF', padding:'0 2px' }}>+</div>
                <div style={{ flex:1, background:'#ECFDF5', border:'1px solid #BBF7D0', borderRadius:12, padding:'12px 10px', textAlign:'center' }}>
                  <div style={{ fontSize:22, marginBottom:6 }}>🪙</div>
                  <div style={{ fontSize:12, fontWeight:800, color:'#065F46' }}>무료 포인트</div>
                  <div style={{ fontSize:18, fontWeight:900, color:'#047857', marginTop:4 }}>{MONTHLY_FREE_COINS} 포인트</div>
                </div>
              </div>
              <div style={{ textAlign:'center', marginTop:12, fontSize:13, fontWeight:800, color:'#111827' }}>
                총 {welcomeCoinTotal}포인트가 지급되었습니다!
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:10, padding:'0 16px 14px' }}>
              <div style={{ display:'flex', gap:10, alignItems:'flex-start', background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:12, padding:'10px 10px' }}>
                <div style={{ width:34, height:34, borderRadius:10, background:'#DCFCE7', display:'grid', placeItems:'center', flexShrink:0 }}>
                  <img
                    src={`${process.env.PUBLIC_URL || ''}/humanicon.png`}
                    alt=""
                    aria-hidden="true"
                    style={{ width: 18, height: 18, objectFit: 'contain', display: 'block' }}
                  />
                </div>
                <div style={{ fontSize:12.5, color:'#374151', lineHeight:1.65, textAlign:'left' }}>
                  관심사 맞는 사람들과 새로운 모임을 만들어보세요
                </div>
              </div>
              <div style={{ display:'flex', gap:10, alignItems:'flex-start', background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:12, padding:'10px 10px' }}>
                <div style={{ width:34, height:34, borderRadius:10, background:'#EDE9FE', display:'grid', placeItems:'center', flexShrink:0, fontSize:18 }}>💬</div>
                <div style={{ fontSize:12.5, color:'#374151', lineHeight:1.65, textAlign:'left' }}>
                  다양한 주제로 소통하며 즐거운 시간을 보내세요
                </div>
              </div>
              <div style={{ display:'flex', gap:10, alignItems:'flex-start', background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:12, padding:'10px 10px' }}>
                <div style={{ width:34, height:34, borderRadius:10, background:'#FCE7F3', display:'grid', placeItems:'center', flexShrink:0, fontSize:18 }}>💖</div>
                <div style={{ fontSize:12.5, color:'#374151', lineHeight:1.65, textAlign:'left' }}>
                  특별한 인연과 추억을 만들어보세요
                </div>
              </div>
            </div>

            <div style={{ padding:'0 16px 18px' }}>
              <div style={{ fontSize:13, fontWeight:800, color:'#111827', marginBottom:10, display:'flex', alignItems:'center', gap:6 }}>
                지금 바로 나만의 모임을 만들어보세요! <span style={{ color:'var(--green)', fontSize:16 }}>→</span>
              </div>
              <button
                type="button"
                className="btn-primary"
                style={{ width:'100%', fontSize:16, borderRadius:12, padding:'14px 12px' }}
                onClick={() => {
                  setShowWelcomeEvent(false);
                  updateState('fromClub', false);
                  updateState('clubId', null);
                  updateState('hobby', null);
                  updateState('hobbies', []);
                  updateState('region', '');
                  updateState('times', []);
                  updateState('ratio', null);
                  updateState('hostingTitle', '');
                  updateState('hostingIntro', '');
                  updateState('hostingCoverDataUrl', null);
                  onNav('hobby');
                }}
              >
                + 모임 생성하기
              </button>
              <button
                type="button"
                onClick={() => setShowWelcomeEvent(false)}
                style={{ width:'100%', marginTop:10, background:'transparent', border:'none', color:'#6B7280', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}
              >
                나중에 하기
              </button>
            </div>
          </div>
        </div>
      )}
      {/* 나이대 경고 모달 */}
      {ageWarnClub && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 24px'}}>
          <div style={{background:'white',borderRadius:16,padding:24,width:'100%',maxWidth:340}}>
            <div style={{fontSize:18,fontWeight:800,color:'#111827',marginBottom:10}}>신청 가능 연령 안내</div>
            <div style={{fontSize:14,color:'var(--gray-600)',lineHeight:1.8,marginBottom:20}}>
              {ageWarnClub.roomType === 'age_limited'
                ? <div style={{fontWeight:800,color:'#111827'}}>{clubAgeApplyOnlyMessage(ageWarnClub)}</div>
                : <>{clubAgeApplyOnlyMessage(ageWarnClub)}<br />다른 모임을 찾아보세요!</>
              }
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              <button onClick={() => { const targetHobbies = normalizeClubHobbies(ageWarnClub); updateState('hobby',targetHobbies[0] || ageWarnClub.hobby || null); updateState('hobbies', targetHobbies); updateState('region',ageWarnClub.region); updateState('times',ageWarnClub.times); setAgeWarnClub(null); onNav('search'); }}
                style={{width:'100%',padding:14,background:'var(--green)',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'inherit',color:'white'}}>다른 모임 찾기</button>
              <button onClick={() => setAgeWarnClub(null)}
                style={{width:'100%',padding:14,background:'var(--gray-100)',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'inherit',color:'#6B7280'}}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* 지역 선택 바텀시트 */}
      {/* 지역 드롭다운 */}
      {showRegionPicker && (
        <div onClick={() => setShowRegionPicker(false)} style={{position:'fixed',inset:0,zIndex:190}}/>
      )}

      {/* 헤더 */}
      <div className="fixed-header" style={{padding:0}}>
        {/* 공지사항 배너 */}
        {notice && (
          <div style={{background:'#1A2B3C',padding:'12px 16px 14px'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
              <span style={{fontSize:12,background:'var(--green)',color:'white',padding:'2px 7px',borderRadius:4,fontWeight:700}}>공지</span>
              <div style={{display:'flex',gap:8}}>
                <button onClick={dismissNoticeForever}
                  style={{background:'rgba(255,255,255,0.15)',border:'none',color:'white',fontSize:12,cursor:'pointer',padding:'5px 9px',borderRadius:6,fontFamily:'inherit',fontWeight:700}}>
                  다시보지않기
                </button>
                <button onClick={closeNotice}
                  style={{background:'rgba(255,255,255,0.15)',border:'none',color:'white',fontSize:12,cursor:'pointer',padding:'5px 9px',borderRadius:6,fontFamily:'inherit',fontWeight:700}}>
                  닫기
                </button>
              </div>
            </div>
            <div style={{fontSize:14,color:'white',fontWeight:800,lineHeight:1.5,marginBottom:notice.content ? 6 : 0}}>
              {notice.title}
            </div>
            {!!notice.content && (
              <div style={{fontSize:13,color:'rgba(255,255,255,0.92)',lineHeight:1.6,whiteSpace:'pre-line'}}>
                {notice.content}
              </div>
            )}
          </div>
        )}
        <div className="app-header app-header--home">
          <div className="home-header-top">
            {/* 로고 */}
            <div className="home-brand">
              <img src={HOME_BRAND_LOGO_SRC} alt="동네사이" className="home-brand-logo" />
            </div>
            {/* 지역 선택 드롭다운 */}
            <div className="rp-wrap">
              <button type="button" className="home-region-pill" onClick={() => setShowRegionPicker(v=>!v)}>
                <LocationPinIcon className="rp-pin" />
                <span className="rp-label">{regionShortLabel}</span>
                <svg className="rp-arrow" viewBox="0 0 10 6" fill="none" style={{transform:showRegionPicker?'rotate(180deg)':'none',transition:'transform 0.18s'}}>
                  <path d="M1 1l4 4 4-4" stroke="#555" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
              {showRegionPicker && (
                <div className="rp-dropdown" onClick={e=>e.stopPropagation()}>
                  {regionOptions.map(r => (
                    <button key={r.val} type="button" className={`rp-opt ${selectedRegion===r.val?'active':''}`}
                      onClick={() => { setSelectedRegion(r.val); setShowRegionPicker(false); }}>
                      <span className="rp-opt-label">{r.label}</span>
                      {selectedRegion===r.val && <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7l3.5 3.5L12 3" stroke="#43A047" strokeWidth="2" strokeLinecap="round"/></svg>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* 우측 아이콘 */}
            <div className="home-header-actions">
              <button type="button" className="hdr-icon" onClick={() => onNav('search')} aria-label="모임 찾기">
                <svg className="hdr-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-4.2-4.2" />
                </svg>
              </button>
              <button type="button" className="hdr-icon" onClick={() => onNav('notif')} aria-label="알림">
                <svg className="hdr-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 20a2 2 0 0 1-4 0" />
                  <path d="M18 16H6l1.1-1.8c.55-.9.9-1.95.9-3.02V9a4 4 0 1 1 8 0v2.18c0 1.07.35 2.12.9 3.02L18 16Z" />
                </svg>
                {homeNotifUnreadTotal > 0 && (
                  <span className="hdr-unread-badge">{homeNotifUnreadTotal > 99 ? '99+' : homeNotifUnreadTotal}</span>
                )}
              </button>
              <button type="button" className="hdr-avatar" onClick={() => onNav('profile')} aria-label="내 정보">
                <img src={user?.profileImageUrl || DEFAULT_PROFILE_ICON} alt="" />
              </button>
            </div>
          </div>
          <div className="home-region-sub">{selectedRegionMeta?.sub || '인천·서울·경기'}</div>
          <div
            className={`category-tabs${catTabDragging ? ' is-dragging' : ''}`}
            onMouseDown={onCategoryMouseDown}
            onMouseMove={onCategoryMouseMove}
            onMouseUp={stopCategoryDrag}
            onMouseLeave={stopCategoryDrag}
          >
            {categories.map(c => <button key={c} className={`category-tab ${activeCategory===c?'active':''}`} onClick={() => setActiveCategory(c)}>{c}</button>)}
          </div>
        </div>
      </div>

      <div className="fixed-scroll" style={{padding:'12px 14px'}}>
        {/* 배너 */}
        <div
          className="home-banner-promo"
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/banner1.png)` }}
        >
          <div className="hbp-copy">
            <p className="hbp-kicker">가까운 동네, 비슷한 나이</p>
            <p className="hbp-title">
              지금 바로
              <br />
              <span className="hbp-title-em">대화 시작</span>해보세요
            </p>
            <button
              className="hbp-cta"
              onClick={() => {
                updateState('fromClub', false);
                updateState('ratio', null);
                updateState('times', []);
                updateState('hostingTitle', '');
                updateState('hostingIntro', '');
                updateState('hostingCoverDataUrl', null);
                onNav('hobby');
              }}
            >
              모임 만들기
            </button>
          </div>
        </div>

        <div className="section-header">
          <div className="section-title">{selectedRegion === 'all' ? '지금 매칭중인 모임' : `${regionShortLabel} 모집중인 모임`}</div>
          <button className="section-more" onClick={() => setShowAllClubs(true)}>더보기 ›</button>
        </div>

        {sortedVisibleClubs.length === 0
          ? <div style={{textAlign:'center',padding:'40px 0',color:'var(--gray-400)',fontSize:14,background:'white',borderRadius:14,border:'0.5px solid var(--gray-200)'}}>
              모집중인 모임이 없어요
            </div>
          : <div className="mcard-list">{sortedVisibleClubs.slice(0,4).map(c => <ClubCard key={c.id} c={c}/>)}</div>
        }

        {sortedActiveRooms.length > 0 && (
          <>
            <div className="section-header" style={{marginTop:20}}>
              <div className="section-title">현재 채팅중인 모임</div>
              <button className="section-more" onClick={() => setShowAllActiveRooms(true)}>더보기 ›</button>
            </div>
            <div className="mcard-list">{sortedActiveRooms.slice(0,4).map(c => <ActiveClubCard key={c.id} c={c}/>)}</div>
          </>
        )}
        {activeCategory === '전체' && (
          <>
            <div style={{height:14}}/>
            <div className="biz-footer biz-footer--compact">
              <p className="biz-footer__name">동네사이</p>
              <dl className="biz-footer__info">
                <div><dt>대표자</dt><dd>백진욱</dd></div>
                <div><dt>사업자등록번호</dt><dd>453-49-01202</dd></div>
                <div><dt>통신판매업 신고번호</dt><dd>제 2026-인천연수구-1277</dd></div>
                <div><dt>고객센터</dt><dd>dongnesai@gmail.com</dd></div>
              </dl>
            </div>
            <div style={{textAlign:'center',fontSize:12,color:'var(--gray-400)',marginTop:6}}>
              <span style={{cursor:'pointer'}} onClick={() => onNav('terms')}>이용약관</span>
              <span style={{margin:'0 6px',opacity:0.6}}>|</span>
              <span style={{cursor:'pointer'}} onClick={() => onNav('privacy')}>개인정보처리방침</span>
              <span style={{margin:'0 6px',opacity:0.6}}>|</span>
              <span style={{cursor:'pointer'}} onClick={() => onNav('refund_policy')}>환불정책</span>
            </div>
            <div style={{fontSize:12,color:'var(--gray-400)',textAlign:'center',padding:'12px 0 6px'}}>동네사이 v1.0.0</div>
          </>
        )}
      </div>
      <TabBar active="home" onNav={onNav} updateState={updateState} unreadTotal={unreadTotal}/>
    </div>
  );
}

/* ── 취미 선택 ─────────────────────────── */
function HobbyScreen({ onNav, appState, updateState }) {
  const selected = Array.isArray(appState.hobbies) && appState.hobbies.length > 0
    ? appState.hobbies
    : (appState.hobby ? [appState.hobby] : []);
  const toggleHobby = (id) => {
    if (selected.includes(id)) {
      updateState('hobbies', selected.filter(v => v !== id));
      return;
    }
    if (selected.length >= 2) {
      alert('모임 주제는 최대 2개까지 선택할 수 있어요.');
      return;
    }
    updateState('hobbies', [...selected, id]);
  };
  return (
    <div className="screen">
      <div className="top-bar"><button className="back-btn" onClick={() => onNav('home', { replace: true })}>←</button><div className="screen-title">모임 주제 선택</div></div>
      <div className="progress-bar"><div className="progress-fill" style={{width:'20%'}}/></div>
      <div className="content">
        <div className="page-title">어떤 모임을 원하세요?</div>
        <div className="page-sub">관심 주제를 선택해주세요 (최대 2개, 1개 이상 필수)</div>
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {HOBBIES.map(h => (
            <div key={h.id} className={`ratio-card ${selected.includes(h.id)?'selected':''}`} onClick={() => toggleHobby(h.id)} style={{marginBottom:0}}>
              <div style={{fontSize:17,fontWeight:700,color:'#111827'}}>{h.label}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="bottom-area">
        <button
          className="btn-primary"
          disabled={selected.length === 0}
          onClick={() => {
            updateState('hobbies', selected);
            updateState('hobby', selected[0] || null);
            onNav('time');
          }}
        >
          다음 → {selected.length > 0 && `(${selected.length}개 선택)`}
        </button>
      </div>
    </div>
  );
}

/* ── 시간 선택 ─────────────────────────── */
function TimeScreen({ onNav, appState, updateState }) {
  const times = [
    { id:'weekday_morning', label:'평일 오전', sub:'월~금 오전 시간대' },
    { id:'weekday_evening', label:'평일 저녁', sub:'월~금 퇴근 후 저녁 시간' },
    { id:'weekend_day',     label:'주말 낮',   sub:'토~일 낮 시간대' },
    { id:'weekend_evening', label:'주말 저녁', sub:'토~일 저녁 시간대' },
  ];
  const selected = appState.times || [];
  const toggle = (id) => selected.includes(id)
    ? updateState('times', selected.filter(t => t !== id))
    : updateState('times', [...selected, id]);
  return (
    <div className="screen">
      <div className="top-bar"><button className="back-btn" onClick={() => onNav('hobby', { replace: true })}>←</button><div className="screen-title">시간 선택</div></div>
      <div className="progress-bar"><div className="progress-fill" style={{width:'40%'}}/></div>
      <div className="content">
        <div className="page-title">언제 모임이 가능한가요?</div>
        <div className="page-sub">복수 선택 가능해요 ✓</div>
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {times.map(t => (
            <div key={t.id} className={`ratio-card ${selected.includes(t.id)?'selected':''}`} onClick={() => toggle(t.id)} style={{marginBottom:0}}>
              <div className="ratio-card-top">
                <div className="ratio-card-title" style={{fontSize:16}}>{t.label}</div>
                {selected.includes(t.id) && <div style={{color:'var(--green)',fontWeight:800,fontSize:18}}>✓</div>}
              </div>
              <div className="ratio-card-desc">{t.sub}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="bottom-area">
        <button className="btn-primary" disabled={selected.length===0} onClick={() => onNav('form')}>
          다음 → {selected.length>0 && `(${selected.length}개 선택)`}
        </button>
      </div>
    </div>
  );
}

/* ── 방 제목 & 모임장 가이드 ──────────────── */
function TitleScreen({ user, onNav, appState, updateState }) {
  const coverInputRef = useRef(null);
  const [title, setTitle]         = useState(appState.hostingTitle || '');
  const [intro, setIntro]         = useState(appState.hostingIntro || '');
  const [showGuide, setShowGuide] = useState(false);

  const hostAgeRange = resolveHostAgeRange(user);

  const handleNext = () => {
    if (title.trim().length < 2) { alert('방 제목을 2자 이상 입력해주세요.'); return; }
    updateState('hostingTitle', title.trim());
    updateState('hostingIntro', intro.trim().slice(0, 100));
    updateState('roomType', 'age_limited');
    setShowGuide(true);
  };

  if (showGuide) return (
    <div className="screen">
      <div className="top-bar">
        <button className="back-btn" onClick={() => setShowGuide(false)}>←</button>
        <div className="screen-title">모임장 가이드</div>
      </div>
      <div className="content">
        <div className="page-title">모임장 역할 안내 </div>
        <div style={{background:'#EFF6FF',borderRadius:12,padding:'16px',marginBottom:14,fontSize:14,color:'#1E40AF',lineHeight:1.9}}>
          <div style={{fontWeight:800,marginBottom:6}}>모임장 권한</div>
          · 방 정보 수정 (제목·대표 사진·소개 — 제목은 일주일에 1번)<br/>
          · 멤버 이탈 시 방 잠금 / 공개 여부 결정
        </div>
        <div style={{background:'#FEF2F2',borderRadius:12,padding:'16px',marginBottom:14,fontSize:14,color:'#991B1B',lineHeight:1.9}}>
          <div style={{fontWeight:800,marginBottom:6}}>모임장 규칙</div>
          · 욕설, 비하, 성적 발언 절대 금지<br/>
          · 모임원들과 매너 있게 대화해주세요<br/>
          · 무단 탈퇴 시 포인트 환불 없습니다<br/>
          · 방 운영 변경 시 반드시 방 인원과 상의해주세요
        </div>
        <div style={{background:'#F0FDF4',borderRadius:12,padding:'16px',marginBottom:14,fontSize:14,color:'#065F46',lineHeight:1.9}}>
          <div style={{fontWeight:800,marginBottom:6}}>방 생성 비용 </div>
          · 비용: <strong>유료 포인트 {HOST_COST}포인트</strong> (무료 포인트 사용 불가)<br/>
          · 48시간 이후 이탈 시 전액 환급<br/>
          · 48시간 이내 방 삭제 시 환급 없음
        </div>
        {hostAgeRange?.minAge === MINOR_ROOM_MIN && hostAgeRange?.maxAge === MINOR_ROOM_MAX && (
          <div style={{background:'#FFFBEB',borderRadius:12,padding:'16px',marginBottom:14,fontSize:13,color:'#92400E',lineHeight:1.85,border:'1px solid #F59E0B'}}>
            <div style={{fontWeight:800,marginBottom:8}}>청소년 모임장 · 연령 제한(생년 기준)</div>
            이 방은 <strong>{MINOR_ROOM_MIN}~{MINOR_ROOM_MAX}세</strong>(생년 기준)만 신청·참여할 수 있습니다.<br/>
            · 법정대리인 지도 하에 운영·참여하세요. 성인 전용 모임과 섞이지 않습니다.<br/>
            · 「정보통신망법」「청소년보호법」 준수, 음란·폭력·유인·금품 요구 등 <strong>즉시 신고·차단</strong> 대상입니다.
          </div>
        )}
        <div className="notice blue" style={{fontSize:13}}>위 내용을 충분히 확인했습니다.</div>
      </div>
      <div className="bottom-area">
        <button className="btn-primary" onClick={() => onNav('confirm')}>동의하고 방 만들기 →</button>
      </div>
    </div>
  );

  return (
    <div className="screen">
      <div className="top-bar">
        <button className="back-btn" onClick={() => onNav('ratio', { replace: true })}>←</button>
        <div className="screen-title">방 제목 설정</div>
      </div>
      <div className="progress-bar"><div className="progress-fill" style={{width:'88%'}}/></div>
      <div className="content">
        <div className="page-title">방 제목을 정해주세요</div>
        <div className="page-sub">홈화면에 표시되는 제목입니다</div>
        <div style={{ background: '#F0FDF4', borderRadius: 10, padding: '12px 14px', fontSize: 13, color: '#166534', lineHeight: 1.75, marginBottom: 14 }}>
          활동 지역은 시·도만 선택돼요. <strong>사는 동네·만나고 싶은 구역</strong>은 제목이나 소개에 꼭 적어주세요.
          <br />
          예) 제목: 「송도 직장인 브런치」 · 소개: 「연수구 거주, 주말 오전 인천·부천 쪽 모임 희망」
        </div>
        <div className="form-group">
          <label className="form-label">방 제목 <span style={{color:'#991B1B',fontSize:12}}>*필수</span></label>
          <input
            className="form-input"
            style={{background:'white',color:'#111827',fontSize:16}}
            value={title}
            onChange={e => setTitle(e.target.value.slice(0,24))}
            placeholder="예: 분당 직장인 산책 (거주·모이는 동네를 제목에)"
            maxLength={24}
          />
          <div style={{fontSize:12,color:'var(--gray-400)',marginTop:5}}>{title.length}/24자 (최소 2자)</div>
        </div>
        <div className="form-group">
          <label className="form-label">채팅방 소개 <span className="cover-field-optional">최대 100자</span></label>
          <textarea
            className="form-input"
            style={{background:'white',color:'#111827',fontSize:15,minHeight:92,resize:'none',paddingTop:12,lineHeight:1.6}}
            value={intro}
            onChange={e => setIntro((e.target.value || '').slice(0, 100))}
            placeholder="예: 저는 수원 영통구에 살아요. 평일 저녁 수원·용인 근처에서 천천히 친해지고 싶어요."
            maxLength={100}
          />
          <div style={{fontSize:12,color:'var(--gray-400)',marginTop:5}}>{intro.length}/100자</div>
        </div>
        <div className="form-group cover-field">
          <label className="form-label">
            모임 대표 사진{' '}
            <span className="cover-field-optional">선택 · HEIC 포함 · 자동 압축(최대 1080px, ~1MB)</span>
          </label>
          <div className="cover-field-hint">
            사진을 올리지 않으면 <strong>모임 주제에 맞는 기본 배경</strong>이 방 썸네일·채팅 상단 등에 자동으로 쓰여요.
          </div>
          <div className="cover-field-row">
            <button type="button" className="cover-field-preview" onClick={() => coverInputRef.current?.click()}>
              {appState.hostingCoverDataUrl ? (
                <img src={appState.hostingCoverDataUrl} alt="미리보기" className="cover-field-preview-img" />
              ) : (
                <img
                  src={defaultClubCoverSrc(appState.hobby || 'wine')}
                  alt=""
                  className="cover-field-preview-img cover-field-preview-img--default"
                  onError={(e) => {
                    const t = e.currentTarget;
                    if (String(t.src || '').replace(/\\/g, '/').includes('/chatroom.png') && !String(t.src || '').includes('/default-covers/')) return;
                    t.onerror = null;
                    t.src = defaultClubCoverFallbackSrc();
                  }}
                />
              )}
            </button>
            <div className="cover-field-actions">
              <button type="button" className="btn-cover-pick" onClick={() => coverInputRef.current?.click()}>앨범에서 선택</button>
              {appState.hostingCoverDataUrl && (
                <button type="button" className="btn-cover-clear" onClick={() => updateState('hostingCoverDataUrl', null)}>제거</button>
              )}
            </div>
          </div>
          <input
            ref={coverInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
            style={{ display: 'none' }}
            onChange={async (e) => {
              const raw = e.target.files?.[0];
              e.target.value = '';
              if (!raw) return;
              if (!raw.type?.startsWith('image/') && !isLikelyHeicFile(raw)) {
                alert('이미지 파일만 선택할 수 있어요.');
                return;
              }
              if (raw.size > IMAGE_UPLOAD_MAX_INPUT_BYTES) {
                alert('원본이 너무 큽니다(최대 약 30MB). 다른 사진을 선택해주세요.');
                return;
              }
              try {
                const cf = await prepareImageFileForUpload(raw);
                if (cf.size > CLUB_COVER_MAX_BYTES * 1.12) {
                  alert('압축 후에도 용량이 큽니다. 다른 이미지를 선택해주세요.');
                  return;
                }
                const reader = new FileReader();
                reader.onload = () => updateState('hostingCoverDataUrl', reader.result);
                reader.readAsDataURL(cf);
              } catch (err) {
                alertImagePrepareFailure('hostingCoverPick', err, '이미지를 불러오지 못했습니다. 형식·용량을 확인해주세요.');
              }
            }}
          />
        </div>
        <div className="form-group" style={{marginTop:20}}>
          <label className="form-label">참여 연령 설정</label>
          <div style={{display:'flex',gap:10,marginTop:6}}>
            <div
              style={{flex:1,padding:'13px 10px',borderRadius:12,border:'2px solid var(--green)',background:'var(--green-light)',fontFamily:'inherit',textAlign:'center'}}>
              <div style={{fontSize:14,fontWeight:700,color:'var(--green)'}}>연령 제한</div>
              <div style={{fontSize:12,color:'var(--gray-500)',marginTop:3}}>
                {hostAgeRange ? `내 생년 기준 (${hostAgeRange.minAge}~${hostAgeRange.maxAge}세)` : '내 생년 기준 나이대 제한'}
              </div>
            </div>
          </div>
          <div style={{fontSize:12,color:'var(--gray-400)',marginTop:6}}>
            자유방은 운영되지 않으며, 모임장 방은 자동으로 연령 제한 방으로 생성됩니다.
          </div>
          {hostAgeRange && hostAgeRange.minAge === MINOR_ROOM_MIN && hostAgeRange.maxAge === MINOR_ROOM_MAX && (
            <div style={{fontSize:12,color:'#92400E',marginTop:10,lineHeight:1.78,background:'#FFFBEB',borderRadius:10,padding:'12px 14px',border:'1px solid #F59E0B'}}>
              <strong>생년 기준 19세 이하 모임장(청소년)</strong>는 참여 가능 연령이 <strong>{MINOR_ROOM_MIN}~{MINOR_ROOM_MAX}세</strong>(생년 기준)로 고정됩니다.<br/>
              보호자 동의·지도 하에 이용하고, 허위 나이·타인 사칭 시 <strong>영구 제재</strong>됩니다.
            </div>
          )}
        </div>
        <div style={{background:'#EFF6FF',borderRadius:10,padding:'13px 15px',fontSize:13,color:'#1D4ED8',lineHeight:1.8,marginTop:8}}>
          <strong>방 제목 안내</strong><br/>
          · 방 제목 수정은 일주일에 1번만 가능합니다<br/>
          · 홈화면 모임 목록에 표시됩니다
        </div>
      </div>
      <div className="bottom-area">
        <button className="btn-primary" disabled={title.trim().length < 2} onClick={handleNext}>
          다음 → (모임장 가이드 확인)
        </button>
      </div>
    </div>
  );
}

/* ── 정보 확인 ─────────────────────────── */
function FormScreen({ user, onNav, appState, updateState }) {
  const regions = ['수도권 전체', '인천', '서울', '경기'];
  const profileAgeRange = user?.birthYear ? calcAgeRange(user.birthYear, user?.birthMonth, user?.birthDay) : null;
  const canNext = !!appState.region;
  const handleNext = () => {
    if (appState.fromClub) { onNav('confirm'); }
    else { onNav('ratio'); }
  };
  return (
    <div className="screen">
      <div className="top-bar"><button className="back-btn" onClick={() => onNav('time', { replace: true })}>←</button><div className="screen-title">신청 정보 확인</div></div>
      <div className="progress-bar"><div className="progress-fill" style={{width:'60%'}}/></div>
      <div className="content">
        <div className="page-title">정보를 확인해주세요</div>
        <div className="page-sub">가입 시 등록한 정보예요</div>
        <div className="user-chip">
          <div className="user-chip-avatar">{user?.name?.charAt(0)||'?'}</div>
          <div>
            <div className="user-chip-name">{user?.name||'회원'}</div>
            <div className="user-chip-age">전화번호 인증 · {user?.age}</div>
          </div>
        </div>
        {user?.name && user.name !== '회원' && (
          <div className="form-group"><label className="form-label">이름 <span className="verified">✓ 인증됨</span></label><input className="form-input" value={user.name} disabled/></div>
        )}
        <div className="form-group">
          <label className="form-label">나이대 <span className="verified">✓ 인증됨</span></label>
          <input className="form-input"
            value={profileAgeRange
              ? `${profileAgeRange.minAge}~${profileAgeRange.maxAge}세`
              : (AGE_GROUP_LABELS[user?.ageGroup]||'인증됨')}
            disabled/>
          <div style={{fontSize:12,color:'var(--gray-400)',marginTop:5}}>
            {profileAgeRange && profileAgeRange.minAge === MINOR_ROOM_MIN && profileAgeRange.maxAge === MINOR_ROOM_MAX
              ? `청소년 회원: 연령 제한 모임은 ${MINOR_ROOM_MIN}~${MINOR_ROOM_MAX}세(생년 기준)로만 고정됩니다. ${signupAgeRuleFootnote()}`
              : '내 생년 기준으로 참여 나이대가 제한됩니다.'}
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">성별 <span className="verified">✓ 인증됨</span></label>
          <input className="form-input" value={user?.gender==='남'?'남성':'여성'} disabled/>
        </div>
        <div className="form-group">
          <label className="form-label">활동 지역</label>
          <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 10, lineHeight: 1.65 }}>
            시·도 단위로 선택해요. 구체적인 동네는 방을 만든 뒤 <strong>제목·채팅방 소개</strong>에 적어주시면 참여자가 알기 쉬워요.
          </div>
          <div className="gender-grid">
            {regions.map(r => (
              <button key={r} className={`gender-card ${appState.region===r?'selected':''}`} onClick={() => updateState('region',r)}>
                <div className="gender-label" style={{fontSize:16}}>{r}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="bottom-area"><button className="btn-primary" disabled={!canNext} onClick={handleNext}>다음 →</button></div>
    </div>
  );
}

/* ── 모임 타입 ─────────────────────────── */
function RatioScreen({ user, onNav, appState, updateState }) {
  const ratios = [
    { id:'mixed',  title:' 혼합 모임',   badge:'4~6명', desc:'다양한 사람들과 편하게 대화할 수 있는 모임이에요.' },
    ...(user?.gender === '여' ? [{ id:'female', title:' 여성전용 모임', badge:'4~6명', desc:'여성분들만 편하게 모이는 모임이에요.' }] : []),
    ...(user?.gender === '남' ? [{ id:'male', title:' 남성전용 모임', badge:'4~6명', desc:'남성분들만 편하게 모이는 모임이에요.' }] : []),
  ];
  return (
    <div className="screen">
      <div className="top-bar"><button className="back-btn" onClick={() => onNav('form', { replace: true })}>←</button><div className="screen-title">모임 타입 선택</div></div>
      <div className="progress-bar"><div className="progress-fill" style={{width:'80%'}}/></div>
      <div className="content">
        <div className="page-title">어떤 모임을 원하세요?</div>
        <div className="page-sub">모임 타입을 선택해주세요</div>
        {ratios.map(r => (
          <div key={r.id} className={`ratio-card ${appState.ratio===r.id?'selected':''}`} onClick={() => updateState('ratio',r.id)}>
            <div className="ratio-card-top"><div className="ratio-card-title" style={{fontSize:16}}>{r.title}</div><div className="ratio-badge">{r.badge}</div></div>
            <div className="ratio-card-desc" style={{fontSize:14}}>{r.desc}</div>
          </div>
        ))}
      </div>
      <div className="bottom-area"><button className="btn-primary" disabled={!appState.ratio} onClick={() => onNav(appState.fromClub ? 'confirm' : 'host_title')}>다음 →</button></div>
    </div>
  );
}

/* ── 신청 확인 ─────────────────────────── */
function ConfirmScreen({ user, onNav, appState, submitApp, clubs, showChargeInfo }) {
  const [agreed, setAgreed]             = useState(false);
  const [showDupModal, setShowDupModal] = useState(false);
  const [showModal, setShowModal]       = useState(false);
  const isHosting    = !appState.fromClub;
  const targetClub   = appState.fromClub ? clubs.find(c => c.id === appState.clubId) : null;
  const effectiveRoomType = isHosting ? (appState.roomType || 'age_limited') : (targetClub?.roomType || 'age_limited');
  const userAgeRange = user?.birthYear ? calcAgeRange(user.birthYear, user?.birthMonth, user?.birthDay) : null;
  const isMinorHosting = isHosting && userAgeRange
    && userAgeRange.minAge === MINOR_ROOM_MIN && userAgeRange.maxAge === MINOR_ROOM_MAX;
  const ageDisplay   = (!isHosting && appState.fromClub)
    ? (user?.age || (userAgeRange ? `${userAgeRange.minAge}~${userAgeRange.maxAge}세` : (AGE_GROUP_LABELS[user?.ageGroup] || '')))
    : (effectiveRoomType === 'age_limited'
      ? (userAgeRange ? `${userAgeRange.minAge}~${userAgeRange.maxAge}세` : (AGE_GROUP_LABELS[user?.ageGroup] || ''))
      : '자유');
  const cost         = isHosting ? HOST_COST : JOIN_COST;
  const useFree      = !isHosting && (user?.freeCoins || 0) >= cost;
  const gender       = user?.gender;
  const backScreen   = appState.fromClub ? 'home' : 'host_title';
  const displayRatio = appState.ratio || 'mixed';

  const handleSubmit = () => {
    const hasFree = !isHosting && (user?.freeCoins || 0) >= cost;
    const hasPaid = (user?.paidCoins || 0) >= cost;
    if (!hasFree && !hasPaid) { showChargeInfo?.(); return; }
    if (!isRatioAllowedForGender(appState.ratio, gender)) { alert(getRatioDenyMessage(appState.ratio)); onNav('home', { resetHistory: true }); return; }
    if (!appState.fromClub) {
      const dup = clubs.find(c => c.hobby===appState.hobby && c.region===appState.region &&
        (c.times||[]).some(t => (appState.times||[]).includes(t)));
      if (dup) { setShowDupModal(true); return; }
    }
    setShowModal(true);
  };

  return (
    <div className="screen">
      {showDupModal && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 24px'}}>
          <div style={{background:'white',borderRadius:16,padding:24,width:'100%',maxWidth:340}}>
            <div style={{fontSize:17,fontWeight:800,color:'#111827',marginBottom:8}}>비슷한 모임이 있어요!</div>
            <div style={{fontSize:14,color:'var(--gray-600)',lineHeight:1.8,marginBottom:20}}>같은 취미·지역·시간대의 모집중인 방이 있어요.<br/>기존 방에 합류하시겠어요?</div>
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              <button onClick={() => { setShowDupModal(false); onNav('search'); }} style={{width:'100%',padding:14,background:'var(--green-light)',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'inherit',color:'var(--green)'}}>모집중인 방 찾으러 가기</button>
              <button onClick={() => { setShowDupModal(false); setShowModal(true); }} style={{width:'100%',padding:14,background:'var(--green)',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'inherit',color:'white'}}>새로 만들기</button>
            </div>
          </div>
        </div>
      )}
      {showModal && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 24px'}}>
          <div style={{background:'white',borderRadius:16,padding:24,width:'100%',maxWidth:340}}>
            <div style={{fontSize:18,fontWeight:800,color:'#111827',marginBottom:12}}>📋 모임 이용 안내</div>
            <div style={{fontSize:14,color:'#374151',lineHeight:1.9,marginBottom:20}}>
              <div style={{background:'#FEF2F2',borderRadius:8,padding:'10px 13px',marginBottom:10,color:'#991B1B',fontWeight:700}}>⚠️ 매칭 완료 즉시 취소 불가</div>
              {isMinorHosting && (
                <div style={{background:'#FFFBEB',borderRadius:8,padding:'12px 14px',marginBottom:10,color:'#92400E',fontWeight:600,lineHeight:1.72,border:'1px solid #F59E0B'}}>
                  <strong>청소년 모임장</strong>: 개설 방은 <strong>만 {MINOR_ROOM_MIN}~{MINOR_ROOM_MAX}세</strong>만 참여 가능합니다.<br/>
                  보호자 지도·동의 하에 이용하세요. 허위 정보·성인 사칭 시 영구 제재됩니다.
                </div>
              )}
              · 욕설, 비하, 성적 발언은 <strong>법적 제재</strong> 대상입니다.<br/>
              · 무단 탈퇴 시 <strong>포인트 환불 없습니다.</strong><br/>
                · <strong>매칭 완료 후 취소/나가기는 환급이 없습니다.</strong><br/>
              · 입장 후 <strong>24시간 내 첫 메시지 없으면 자동 퇴장</strong>됩니다.<br/>
              · 매너 있는 만남으로 좋은 인연 만들어가요 🌿
            </div>
            <div style={{display:'flex',gap:10}}>
              <button onClick={() => setShowModal(false)} style={{flex:1,padding:14,background:'var(--gray-100)',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'inherit',color:'#374151'}}>취소</button>
              <button onClick={() => { setShowModal(false); submitApp(); }} style={{flex:1,padding:14,background:'var(--green)',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'inherit',color:'white'}}>동의 후 신청</button>
            </div>
          </div>
        </div>
      )}
      <div className="top-bar"><button className="back-btn" onClick={() => onNav(backScreen, { replace: true })}>←</button><div className="screen-title">신청 확인</div></div>
      <div className="progress-bar"><div className="progress-fill" style={{width:'100%'}}/></div>
      <div className="content">
        <div className="page-title">이렇게 신청할게요</div>
        <div className="page-sub">내용을 꼭 확인해주세요</div>
        <div className="card">
          <div className="confirm-hobby-row">
            <div className="confirm-hobby-name" style={{fontSize:16}}>
              {(Array.isArray(appState.hobbies) && appState.hobbies.length > 0
                ? appState.hobbies
                : [appState.hobby]
              ).filter(Boolean).map(h => HOBBY_MAP[h]).join(' · ')}
            </div>
            <div className={`coin-badge ${useFree?'free':'paid'}`}>🪙 {useFree?'무료':'유료'}포인트 {cost}</div>
          </div>
          {isHosting && appState.hostingTitle && (
            <div className="info-row" style={{fontSize:15}}><span className="lbl">방 제목</span><span className="val" style={{fontWeight:700}}>{appState.hostingTitle}</span></div>
          )}
          {isHosting && appState.hostingIntro && (
            <div className="info-row" style={{fontSize:14,alignItems:'flex-start'}}>
              <span className="lbl">채팅방 소개</span>
              <span className="val" style={{whiteSpace:'pre-line',lineHeight:1.55,maxWidth:220,textAlign:'right'}}>{appState.hostingIntro}</span>
            </div>
          )}
          {isHosting && (
            <div className="confirm-cover-row">
              <span className="lbl">대표 사진</span>
              {appState.hostingCoverDataUrl ? (
                <img src={appState.hostingCoverDataUrl} alt="" className="confirm-cover-img" />
              ) : (
                <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 }}>
                  <img
                    src={defaultClubCoverSrc(appState.hobby || 'wine')}
                    alt=""
                    className="confirm-cover-img"
                    onError={(e) => {
                      const t = e.currentTarget;
                      if (String(t.src || '').replace(/\\/g, '/').includes('/chatroom.png') && !String(t.src || '').includes('/default-covers/')) return;
                      t.onerror = null;
                      t.src = defaultClubCoverFallbackSrc();
                    }}
                  />
                  <span style={{ fontSize:11, color:'#6B7280', fontWeight:600 }}>선택 안 함 · 주제별 기본 배경</span>
                </div>
              )}
            </div>
          )}
          {isHosting && (
            <div className="info-row" style={{fontSize:13}}>
              <span className="lbl">역할</span>
              <span className="val" style={{color:'var(--green)',fontWeight:700}}>모임장</span>
            </div>
          )}
          {user?.name && user.name!=='회원' && <div className="info-row" style={{fontSize:15}}><span className="lbl">이름</span><span className="val">{user.name}</span></div>}
          <div className="info-row" style={{fontSize:15}}><span className="lbl">나이대</span><span className="val">{ageDisplay}</span></div>
          {isMinorHosting && (
            <div className="info-row" style={{fontSize:13}}>
              <span className="lbl">연령 제한</span>
              <span className="val" style={{color:'#B45309',fontWeight:700}}>모임장 기준 만 {MINOR_ROOM_MIN}~{MINOR_ROOM_MAX}세 고정</span>
            </div>
          )}
          <div className="info-row" style={{fontSize:15}}><span className="lbl">성별</span><span className="val">{gender==='남'?'남성':'여성'}</span></div>
          <div className="info-row" style={{fontSize:15}}><span className="lbl">지역</span><span className="val">{appState.region}</span></div>
          {displayRatio !== 'mixed' && (
            <div className="info-row" style={{fontSize:15}}><span className="lbl">모임 타입</span><span className="val">{RATIO_MAP[displayRatio] || '전용 모임'}</span></div>
          )}
          <div className="info-row" style={{fontSize:15}}><span className="lbl">가능 시간</span><span className="val">{(appState.times||[]).map(t=>TIME_LABELS[t]).join(' · ')}</span></div>
          <div className="divider"/>
          <div className="info-row" style={{fontSize:14}}>
            <span className="lbl">신청 후 잔여 포인트</span>
            <span className="val">무료 🪙 {useFree?Math.max(0,(user?.freeCoins||0)-cost):(user?.freeCoins||0)} · 유료 🪙 {!useFree?Math.max(0,(user?.paidCoins||0)-cost):(user?.paidCoins||0)}</span>
          </div>
        </div>
        <div style={{background:'#FEF2F2',borderRadius:10,padding:'13px 15px',marginBottom:12,border:'0.5px solid #FECACA'}}>
          <div style={{fontSize:14,fontWeight:700,color:'#991B1B',marginBottom:6}}>📋 취소 및 환불 정책</div>
          <div style={{fontSize:13,color:'#7F1D1D',lineHeight:1.8}}>
            {isHosting ? (
              <>
                <strong>[모임장 — 유료 포인트 {HOST_COST}포인트]</strong><br/>
                · 방 나가기 (48시간 이내): 환급 없음<br/>
                · 방 나가기 (48시간 이후, 미매칭): 전액 환급<br/>
                · 매칭 완료 후 나가기: 환급 없음<br/>
                · 무료 포인트 사용 불가
              </>
            ) : (
              <>
                <strong>[무료 포인트]</strong><br/>
                · 취소 가능하나 환급 없음<br/><br/>
                <strong>[유료 포인트]</strong><br/>
                · 24시간 이후 취소: 전액 환급<br/>
                · 24시간 이내 취소: 10포인트 패널티 (20포인트 환급)<br/>
                · 매칭 완료 후 취소/나가기: 환급 없음<br/>
                · 미사용 유료 포인트: 구매일로부터 7일 이내 환불 신청 가능<br/>
                · 환불 신청: 카카오채널 @동네사이
              </>
            )}
          </div>
        </div>
        {isHosting && (
          <div className="notice cancel" style={{fontSize:13,marginBottom:12}}>
            모임장 방 생성 비용: <strong>유료 포인트 {HOST_COST}포인트</strong> (무료 포인트 사용 불가)
          </div>
        )}
        {!isHosting && (
          <div className={`notice ${useFree?'coin-free':'coin-paid'}`} style={{fontSize:13}}>
            <strong>🪙 {useFree?'무료':'유료'} 포인트 사용 안내</strong><br/>
            {useFree
              ? '무료 포인트는 취소 시 환급되지 않으며, 매칭 완료 후에도 환급되지 않습니다.'
              : '유료 포인트는 24시간 이내 취소 시 10포인트 패널티(20포인트 환급), 24시간 이후 전액 환급됩니다. 단, 매칭 완료 후 취소/나가기는 환급되지 않습니다.'}
          </div>
        )}
      </div>
      <div className="bottom-area">
        <div className="agree-row">
          <input type="checkbox" id="agree" checked={agreed} onChange={e => setAgreed(e.target.checked)}/>
          <label htmlFor="agree" style={{fontSize:14}}>취소 정책 및 포인트 환급·초기화 정책을 확인했습니다.</label>
        </div>
        {!useFree && !((user?.paidCoins||0)>=cost) && (
          <div style={{background:'#FEF2F2',borderRadius:9,padding:'11px 14px',marginBottom:12,fontSize:14,color:'#991B1B',fontWeight:600}}>🪙 포인트가 부족해요! {cost}포인트가 필요합니다.</div>
        )}
        <button className="btn-primary" style={{fontSize:16}} disabled={!agreed} onClick={handleSubmit}>신청 완료하기 🪙</button>
      </div>
    </div>
  );
}

/* ── 매칭 현황 ─────────────────────────── */
function MatchScreen({ user, onNav, appState, setUser, updateState, clubs, setClubs, unreadCounts }) {
  const [selectedMatch, setSelectedMatch] = useState(null);
  const unreadTotal = readUnreadTotal(unreadCounts);
  const formatUnread = (n) => (n > 99 ? '+99' : String(n));

  // DB 기준 참여 중인 방 목록 구성
  // appState.matches(DB 재조회) + clubs(DB) 교차 참조
  // 모임장인 경우: clubs.hostId === user.userId 로 DB에서 직접 판별
  const myClubIds = new Set(appState.matches.map(m => m.clubId));
  // 내가 모임장인 방도 포함 (matches 동기 전 방어)
  const myMatchOrHostClubs = clubs.filter(c => myClubIds.has(c.id) || sameUserId(c.hostId, user?.userId));

  const activeMatchClubs = myMatchOrHostClubs
    .filter(c => isClubOpenForChat(c))
    .map(c => {
      const m = appState.matches.find(x => x.clubId === c.id);
      return {
        ...c,
        matchId:         m?.id || 'host_' + c.id,
        matchIsHost:     sameUserId(c.hostId, user?.userId),
        matchCreatedAt:  m?.createdAt || 0,
        matchCoinType:   m?.coinType  || 'paid',
        matchSpentCoins: m?.spentCoins || (!!m?.isHost ? HOST_COST : JOIN_COST),
      };
    });

  const waitingMatches = myMatchOrHostClubs
    .filter(c => !isClubOpenForChat(c))
    .map(c => {
      const m = appState.matches.find(x => x.clubId === c.id);
      return m ? {
        ...m,
        isViewerInRoom: true,
        applicantWasHost: !!m.isHost,
        roomIntro: m.roomIntro ?? c.roomIntro ?? null,
        roomType: m.roomType ?? c.roomType ?? 'age_limited',
        minAge: m.minAge ?? c.minAge ?? null,
        maxAge: m.maxAge ?? c.maxAge ?? null,
        createdAt: cancelPolicyAnchorMs(m, c, user?.userId) ?? m.createdAt,
      } : {
        id:         'host_' + c.id,
        clubId:     c.id,
        hobby:      c.hobby,
        roomIntro:  c.roomIntro || null,
        region:     c.region,
        ratio:      c.ratio,
        times:      c.times || [],
        title:      c.title || null,
        roomType:   c.roomType || 'age_limited',
        minAge:     c.minAge ?? null,
        maxAge:     c.maxAge ?? null,
        /** club_members↔matches 동기 전: 방장만 남은 경우 — 방 생성 시각·모임장 비용으로 홈/모임찾기 상세와 동일한 정책선 */
        applicantWasHost: true,
        isViewerInRoom: true,
        isHost:     true,
        status:     'waiting',
        createdAt:  (() => {
          const t = new Date(c.createdAt ?? c.created_at).getTime();
          return Number.isFinite(t) ? t : Date.now();
        })(),
        coinType:   'paid',
        spentCoins: HOST_COST,
      };
    });

  const handleCancelConfirmed = async (match) => {
    await executeMembershipWithdrawal({
      match,
      user,
      appState,
      clubs,
      setUser,
      updateState,
      setClubs,
    });
    setSelectedMatch(null);
  };

  if (selectedMatch) return (
    <MatchDetail
      match={selectedMatch}
      user={user}
      clubs={clubs}
      onBack={() => setSelectedMatch(null)}
      onNav={onNav}
      onCancel={() => handleCancelConfirmed(selectedMatch)}
      onEnterChat={(clubId) => { updateState('currentClubId', clubId); onNav('chat'); }}
      onClubUpdated={(updatedClub) => {
        if (!updatedClub?.id) return;
        setClubs(prev => (prev || []).map(item => item.id === updatedClub.id ? { ...item, ...updatedClub } : item));
      }}
      onBoostApplied={async () => {
        if (user?.userId) {
          const p = await fetchProfile(user.userId);
          if (p) {
            setUser(prev => prev ? ({
              ...prev,
              freeCoins: p.free_coins ?? prev.freeCoins,
              paidCoins: p.paid_coins ?? prev.paidCoins,
            }) : prev);
          }
        }
        setClubs(await fetchClubs());
      }}
    />
  );

  return (
    <div className="page-fixed">
      <div className="fixed-header" style={{padding:'18px 16px 14px'}}><div style={{fontSize:20,fontWeight:800,color:'#111827'}}>채팅방</div></div>
      <div className="fixed-scroll">
        <div className="sec-label" style={{marginBottom:10}}>⏳ 매칭 대기중 ({waitingMatches.length})</div>
        {waitingMatches.length===0
          ? <div style={{background:'white',borderRadius:12,border:'0.5px solid var(--gray-200)',padding:'20px',marginBottom:12,textAlign:'center',color:'var(--gray-400)',fontSize:14}}>신청한 모임이 없어요</div>
          : waitingMatches.map(match => (
            <div key={match.id} onClick={() => setSelectedMatch(match)} style={{background:'white',borderRadius:12,border:'0.5px solid var(--gray-200)',padding:'16px',marginBottom:9,cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center',gap:10}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8}}>
                  <div style={{fontSize:15,fontWeight:700,color:'#111827',lineHeight:1.35,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden',textOverflow:'ellipsis',wordBreak:'keep-all',overflowWrap:'anywhere'}}>
                    {match.title || HOBBY_MAP[match.hobby]}
                  </div>
                </div>
                <div style={{fontSize:13,color:'var(--gray-600)',marginTop:3}}>{match.region} · {RATIO_MAP[match.ratio]||'혼합 모임'}</div>
                <div style={{fontSize:12,color:'var(--gray-400)',marginTop:2}}>{(match.times||[]).map(t=>TIME_LABELS[t]).join(' · ')}</div>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <div className="pill waiting-mine" style={{fontSize:12}}><span className="pulse"/> 매칭중</div>
                <span style={{color:'var(--gray-400)',fontSize:18}}>›</span>
              </div>
            </div>
          ))
        }
        <div className="sec-label" style={{marginBottom:10,marginTop:18}}>✅ 진행중인 모임 ({activeMatchClubs.length})</div>
        {activeMatchClubs.length === 0
          ? <div style={{background:'white',borderRadius:12,border:'0.5px solid var(--gray-200)',padding:'20px',marginBottom:12,textAlign:'center',color:'var(--gray-400)',fontSize:14}}>아직 오픈된 모임이 없어요</div>
          : activeMatchClubs.map(club => {
            const membership = appState.matches.find(x => x.clubId === club.id);
            const policyAt = cancelPolicyAnchorMs(membership, club, user?.userId) ?? club.matchCreatedAt;
            return (
          <div
            key={club.id}
            className="match-row-card"
            onClick={() => {
              setSelectedMatch({
                id: club.matchId,
                clubId: club.id,
                hobby: club.hobby,
                title: club.title || null,
                roomIntro: club.roomIntro || null,
                region: club.region,
                ratio: club.ratio,
                times: club.times || [],
                applicantWasHost: !!membership?.isHost,
                isViewerInRoom: true,
                isHost: club.matchIsHost,
                status: 'active',
                createdAt: policyAt || 0,
                coinType: club.matchCoinType || 'paid',
                spentCoins: club.matchSpentCoins || (club.matchIsHost ? HOST_COST : JOIN_COST),
              });
            }}
            style={{cursor:'pointer'}}
          >
            <ClubListThumb club={club} compact />
            <div className="match-row-body">
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8}}>
                <div
                  className="match-row-title"
                  style={{display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden',textOverflow:'ellipsis',wordBreak:'keep-all',overflowWrap:'anywhere'}}
                >
                  {club.title || HOBBY_MAP[club.hobby] || club.hobby}
                </div>
              </div>
              <div className="match-row-sub">
                {club.region}{club.ratio !== 'mixed' ? ` · ${RATIO_MAP[club.ratio] || '전용 모임'}` : ''} · {club.members}명
              </div>
            </div>
            <div className="match-row-actions">
              <div className="pill done" style={{fontSize:12}}>진행중</div>
              <button type="button" className="match-row-chat-btn" onClick={(e) => { e.stopPropagation(); updateState('currentClubId', club.id); onNav('chat'); }}>
                💬
                {(unreadCounts?.[club.id] || 0) > 0 && (
                  <span className="unread-badge">{formatUnread(unreadCounts[club.id])}</span>
                )}
              </button>
            </div>
          </div>
            );
          })}
        <div style={{textAlign:'center',padding:'20px 0'}}>
          <button className="btn-outline" onClick={() => { updateState('fromClub',false); updateState('ratio',null); updateState('times',[]); updateState('hostingTitle',''); updateState('hostingIntro',''); updateState('hostingCoverDataUrl', null); onNav('hobby'); }}>새 모임 신청하기</button>
        </div>
      </div>
      <TabBar active="match" onNav={onNav} updateState={updateState} unreadTotal={unreadTotal}/>
    </div>
  );
}

function MatchDetail({
  match,
  user,
  onBack,
  onCancel,
  clubs,
  onEnterChat,
  hideCancelAction = false,
  primaryAction = null,
  onClubUpdated = null,
  onBoostApplied = null,
  onNav: _onNav,
  onAgeMismatchFromDetail = null,
  onJoinActiveRoomFromDetail = null,
}) {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelStep, setCancelStep] = useState(1);
  const [hostProfile, setHostProfile] = useState(null);
  const [memberList, setMemberList] = useState([]);
  const [detailClub, setDetailClub] = useState(null);
  const [showEditRoomModal, setShowEditRoomModal] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editIntro, setEditIntro] = useState('');
  const [editRoomCoverDataUrl, setEditRoomCoverDataUrl] = useState(null);
  const [editRoomCoverRemoved, setEditRoomCoverRemoved] = useState(false);
  const editRoomCoverInputRef = useRef(null);
  const [savingRoomEdit, setSavingRoomEdit] = useState(false);
  const [boostingNow, setBoostingNow] = useState(false);
  const applicantWasCreatorHost = !!match.applicantWasHost;
  const spentCoins   = match.spentCoins || (applicantWasCreatorHost ? HOST_COST : JOIN_COST);
  const coinType     = match.coinType || 'free';

  // 방 오픈 여부
  const currentClub  = clubs?.find(c => c.id === match.clubId);
  const policyAnchorMs = parseCancelPolicyAnchorMs(match, currentClub, user?.userId);
  const hoursPassed = Math.floor((Date.now() - policyAnchorMs) / 3600000);
  const detailClubBase = detailClub || currentClub || null;
  const effectiveClub = detailClubBase && currentClub && detailClubBase.id === currentClub.id
    ? {
        ...detailClubBase,
        /** 채팅/목록과 동일하게 앱이 들고 있는 host_id 우선 — 상세 단독 조회가 구버전이어도 맞춤 */
        hostId: currentClub.hostId ?? detailClubBase.hostId,
        hostName: currentClub.hostName ?? detailClubBase.hostName,
        hostProfileImageUrl: currentClub.hostProfileImageUrl ?? detailClubBase.hostProfileImageUrl,
        members: currentClub.members ?? detailClubBase.members,
        maxMembers: currentClub.maxMembers ?? detailClubBase.maxMembers,
        isLocked: currentClub.isLocked ?? detailClubBase.isLocked,
        status: currentClub.status ?? detailClubBase.status,
        title: currentClub.title ?? detailClubBase.title,
        roomIntro: currentClub.roomIntro ?? detailClubBase.roomIntro,
        lastTitleUpdate: currentClub.lastTitleUpdate ?? detailClubBase.lastTitleUpdate,
      }
    : detailClubBase;
  const isCurrentHost = sameUserId(effectiveClub?.hostId, user?.userId);
  const isMatched    = effectiveClub?.status === 'matched';
  const currentMembers = Math.max(1, effectiveClub?.members || 0);
  const maxMembers = effectiveClub?.maxMembers || 6;
  const isActiveClub = isClubOpenForChat(effectiveClub);
  const roomSummary = (effectiveClub?.roomIntro || match?.roomIntro || '').trim();
  const genderOk = isRatioAllowedForGender(effectiveClub?.ratio || match?.ratio, user?.gender);
  const ageOk = !user?.birthYear || canJoinClub(user, effectiveClub || match || {});
  const isRoomFull = (effectiveClub?.members || 0) >= (effectiveClub?.maxMembers || 6);
  const genderBreakdownMembers = enrichMembersForGenderCount(memberList, {
    viewer: user,
    hostId: effectiveClub?.hostId,
    hostProfile,
  });
  const genderMemberBreakdownText = formatGenderMemberBreakdown(genderBreakdownMembers);
  /** 원 신청으로 방을 연 모임장에게만 48시간 규칙 — 승계만 받은 방장은 참가자(24시간) 기준 유지 */
  const useCreatorHostHourWindow = isCurrentHost && applicantWasCreatorHost;
  const freeCancel   = !isMatched && (useCreatorHostHourWindow ? (hoursPassed >= 48) : hoursPassed >= 24);
  const hoursLeftFree = useCreatorHostHourWindow ? Math.max(0, 48 - hoursPassed) : Math.max(0, 24 - hoursPassed);
  /** 방장 라인 — 본인이 모임장이이면 세션 user를 우선(프로필 수정 직후 즉시 반영) */
  const hostDisplayName = (() => {
    if (isCurrentHost) {
      return String(user?.nickname || user?.name || hostProfile?.nickname || hostProfile?.name || effectiveClub?.hostName || '모임장').trim() || '모임장';
    }
    return (hostProfile?.nickname || hostProfile?.name || effectiveClub?.hostName || '모임장').trim() || '모임장';
  })();
  const hostAvatarSrc = (() => {
    if (isCurrentHost && user?.profileImageUrl) {
      return withUrlCacheBust(user.profileImageUrl);
    }
    return hostProfile?.avatar || effectiveClub?.hostProfileImageUrl || DEFAULT_PROFILE_ICON;
  })();
  const hostDetailAge = isCurrentHost ? resolveYearDiffAge(user) : hostProfile?.age;
  const rawMinAge = effectiveClub?.minAge ?? match?.minAge ?? null;
  const rawMaxAge = effectiveClub?.maxAge ?? match?.maxAge ?? null;
  const fallbackAgeRange = (hostDetailAge ?? hostProfile?.age)
    ? calcAgeRangeFromAge(hostDetailAge ?? hostProfile?.age)
    : null;
  const useFallbackRange = (
    (!Number.isFinite(Number(rawMinAge)) || !Number.isFinite(Number(rawMaxAge))) ||
    (Number(rawMinAge) === 30 && Number(rawMaxAge) === 50)
  ) && !!fallbackAgeRange;
  const displayMinAge = useFallbackRange ? fallbackAgeRange.minAge : rawMinAge;
  const displayMaxAge = useFallbackRange ? fallbackAgeRange.maxAge : rawMaxAge;
  const displayAgeRangeText = (
    Number.isFinite(Number(displayMinAge)) && Number.isFinite(Number(displayMaxAge))
      ? `${displayMinAge}~${displayMaxAge}세`
      : ''
  );
  const TITLE_EDIT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
  const lastTitleUpdateMs = effectiveClub?.lastTitleUpdate ? new Date(effectiveClub.lastTitleUpdate).getTime() : NaN;
  const titleCooldownLeftDays = Number.isFinite(lastTitleUpdateMs)
    ? Math.max(0, Math.ceil((TITLE_EDIT_COOLDOWN_MS - (Date.now() - lastTitleUpdateMs)) / (24 * 60 * 60 * 1000)))
    : 0;
  const canEditTitleNow = !Number.isFinite(lastTitleUpdateMs) || (Date.now() - lastTitleUpdateMs) >= TITLE_EDIT_COOLDOWN_MS;
  const viewerIsParticipant = !!memberList.find(m => sameUserId(m.id, user?.userId)) || isCurrentHost || !!match?.isViewerInRoom;
  // 방 생성일 = 모임장이 최초 매칭 방 생성한 시점(clubs.created_at)
  const roomCreatedAtRaw = effectiveClub?.createdAt ?? effectiveClub?.created_at ?? currentClub?.createdAt ?? currentClub?.created_at ?? null;
  const roomCreatedAtText = (() => {
    if (!roomCreatedAtRaw) return '생성일 정보 없음';
    const d = new Date(roomCreatedAtRaw);
    if (Number.isNaN(d.getTime())) return '생성일 정보 없음';
    return d.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).replace(/\.\s/g, '.').replace(/\.$/, '');
  })();
  const lastResolvedHostRef = useRef(null);

  const boostDescriptionText =
    '10포인트를 사용해 모임을 2시간 동안 상단에 노출합니다.\n'
    + '상단 노출 중인 모임은 최근 등록 순으로 표시되며,\n'
    + '노출 종료 후 기존 위치로 자동 복귀됩니다.';

  const handleBoost2Hours = async () => {
    const clubId = effectiveClub?.id || match?.clubId;
    if (!clubId) return;
    if (!isCurrentHost) {
      alert('모임장만 사용할 수 있습니다.');
      return;
    }
    if (isBoostActive(effectiveClub)) {
      alert('이미 상단 노출 중인 모임입니다.\n(연장은 현재 막혀 있어요)');
      return;
    }
    if ((user?.paidCoins || 0) < 10) {
      alert('유료 포인트가 부족합니다.');
      return;
    }
    const ok = window.confirm(`2시간 상단 노출을 사용하시겠습니까?\n\n${boostDescriptionText}\n\n- 비용: 10포인트`);
    if (!ok) return;
    setBoostingNow(true);
    try {
      const { data, error } = await supabase.rpc('boost_my_club_2h', { p_club_id: clubId });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      const updated = {
        id: clubId,
        isBoosted: true,
        boostedAt: row?.boosted_at || new Date().toISOString(),
        boostExpiresAt: row?.boost_expires_at || null,
      };
      if (typeof onClubUpdated === 'function') onClubUpdated(updated);
      if (typeof onBoostApplied === 'function') await onBoostApplied(clubId, row || null);
      alert('상단 노출이 등록되었습니다.');
    } catch (e) {
      const msg = String(e?.message || e || '').trim();
      if (msg.includes('포인트') || msg.includes('부족')) {
        alert('포인트가 부족합니다.');
      } else if (msg.includes('이미') && msg.includes('상단')) {
        alert('이미 상단 노출 중인 모임입니다.\n(연장은 현재 막혀 있어요)');
      } else {
        alert('상단 노출 처리에 실패했습니다. 잠시 후 다시 시도해주세요.');
      }
      console.warn('boost_my_club_2h 실패:', e?.message || e);
    } finally {
      setBoostingNow(false);
    }
  };

  const handleCancelBoost = async () => {
    const clubId = effectiveClub?.id || match?.clubId;
    if (!clubId) return;
    if (!isCurrentHost) {
      alert('모임장만 사용할 수 있습니다.');
      return;
    }
    if (!isBoostActive(effectiveClub)) {
      alert('상단 노출 중인 모임이 아닙니다.');
      return;
    }
    const ok = window.confirm(
      '지금 취소하시면 포인트 환급이 불가하며 상단 노출 기능이 종료됩니다.\n그래도 하시겠습니까?'
    );
    if (!ok) return;
    setBoostingNow(true);
    try {
      const { error } = await supabase.rpc('cancel_my_club_boost', { p_club_id: clubId });
      if (error) throw error;
      const updated = {
        id: clubId,
        isBoosted: false,
        boostedAt: null,
        boostExpiresAt: null,
      };
      if (typeof onClubUpdated === 'function') onClubUpdated(updated);
      if (typeof onBoostApplied === 'function') await onBoostApplied(clubId, null);
      alert('상단 노출이 취소되었습니다.');
    } catch (e) {
      console.warn('cancel_my_club_boost 실패:', e?.message || e);
      alert('상단 노출 취소에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setBoostingNow(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const loadDetailProfiles = async () => {
      const clubId = match?.clubId;
      if (!clubId) {
        if (mounted) {
          setDetailClub(null);
          setHostProfile(null);
          setMemberList([]);
        }
        return;
      }
      const { data: clubRow } = await supabase
        .from('clubs')
        .select('*')
        .eq('id', clubId)
        .maybeSingle();
      const fetchedClub = clubRow ? mapClubRow(clubRow) : null;
      const listHostId = currentClub?.hostId ?? null;
      const rowHostId = fetchedClub?.hostId ?? null;
      let canonicalHostId = listHostId || rowHostId || null;
      if (listHostId && rowHostId && !sameUserId(listHostId, rowHostId)) {
        canonicalHostId = listHostId;
      }
      const mergedDetail = fetchedClub
        ? { ...fetchedClub, hostId: canonicalHostId || fetchedClub.hostId }
        : null;
      if (mounted) setDetailClub(mergedDetail);
      const hostId = canonicalHostId;
      if (!sameUserId(lastResolvedHostRef.current, hostId)) {
        lastResolvedHostRef.current = hostId;
        if (mounted) setHostProfile(null);
      }
      const hostIdForProfile = isUuidLike(hostId) ? String(hostId).trim() : null;
      const snapshotMatchesHost = fetchedClub && sameUserId(fetchedClub.hostId, hostId);
      if (mounted && snapshotMatchesHost && (fetchedClub?.hostName || fetchedClub?.hostProfileImageUrl)) {
        const clubFallbackHost = {
          id: hostId,
          name: String(fetchedClub?.hostName || '모임장').trim() || '모임장',
          nickname: null,
          region: '',
          age: null,
          avatar: fetchedClub?.hostProfileImageUrl || DEFAULT_PROFILE_ICON,
          isHost: true,
        };
        setHostProfile(prev => mergeHostDetailProfile(prev, clubFallbackHost));
      }
      let hostGenderFromDb = null;
      if (hostIdForProfile) {
        const { data: hostData } = await supabase
          .from('profiles')
          .select('user_id, name, nickname, profile_image_url, region, birth_year, gender')
          .eq('user_id', hostIdForProfile)
          .maybeSingle();
        if (hostData?.gender) hostGenderFromDb = hostData.gender;
        if (mounted && hostData) {
          const directHost = {
            id: hostData.user_id,
            name: String(hostData.nickname || hostData.name || '모임장').trim() || '모임장',
            nickname: hostData.nickname || null,
            region: hostData.region || '',
            age: hostData.birth_year ? calcYearDiffAgeFromBirthYear(hostData.birth_year) : null,
            gender: hostData.gender || null,
            avatar: hostData.profile_image_url ? withUrlCacheBust(hostData.profile_image_url) : DEFAULT_PROFILE_ICON,
            isHost: true,
          };
          setHostProfile(prev => mergeHostDetailProfile(prev, directHost));
        }
      }
      const hostGenderHint = hostGenderFromDb
        || (sameUserId(hostId, user?.userId) ? user?.gender : null)
        || null;
      const { data: membersData, error: membersErr } = await supabase
        .from('club_members')
        .select('user_id, joined_at, member_name, member_profile_image_url')
        .eq('club_id', clubId)
        .order('joined_at', { ascending: true });
      if (membersErr) return;
      const ids = Array.from(new Set((membersData || []).map(x => String(x.user_id || '').trim()).filter(Boolean)));
      if (ids.length === 0) {
        if (mounted) {
          setMemberList([]);
        }
        return;
      }
      const validProfileIds = ids.filter(isUuidLike);
      let { data: profilesData, error: profilesErr } = await supabase
        .from('profiles')
        .select('user_id, name, nickname, profile_image_url, region, birth_year, gender')
        .in('user_id', validProfileIds.length > 0 ? validProfileIds : ['00000000-0000-0000-0000-000000000000']);
      if (profilesErr) {
        const fallback = await supabase
          .from('profiles')
          .select('user_id, name, profile_image_url, region, birth_year, gender')
          .in('user_id', validProfileIds.length > 0 ? validProfileIds : ['00000000-0000-0000-0000-000000000000']);
        profilesData = fallback.data || [];
      }
      const byId = {};
      (profilesData || []).forEach(p => { if (p?.user_id) byId[p.user_id] = p; });
      const mapped = (membersData || []).map((m) => {
        const p = byId[m.user_id] || {};
        const isRowHost = sameUserId(m.user_id, hostId);
        const rawLabel = String(p.nickname || p.name || m.member_name || '').trim();
        const displayNameRaw = rawLabel || (isRowHost ? '모임장' : '회원');
        let memberGender = p.gender || null;
        if (!memberGender && sameUserId(m.user_id, user?.userId) && user?.gender) {
          memberGender = user.gender;
        }
        if (!memberGender && isRowHost && hostGenderHint) {
          memberGender = hostGenderHint;
        }
        return {
          id: m.user_id,
          name: displayNameRaw,
          nickname: p.nickname || null,
          region: '',
          age: p.birth_year ? calcYearDiffAgeFromBirthYear(p.birth_year) : null,
          gender: memberGender,
          avatar: (p.profile_image_url || m.member_profile_image_url)
            ? withUrlCacheBust(p.profile_image_url || m.member_profile_image_url)
            : DEFAULT_PROFILE_ICON,
          isHost: isRowHost,
        };
      });
      const hostUser = mapped.find(m => m.isHost) || null;
      if (mounted) {
        if (hostUser && isUuidLike(hostUser.id)) {
          const fromMembersHost = {
            id: hostUser.id,
            name: hostUser.name,
            nickname: hostUser.nickname || null,
            region: hostUser.region || '',
            age: hostUser.age,
            gender: hostUser.gender || hostGenderHint || null,
            avatar: hostUser.avatar || DEFAULT_PROFILE_ICON,
            isHost: true,
          };
          setHostProfile(prev => mergeHostDetailProfile(prev, fromMembersHost));
        }
        setMemberList(mapped);
      }
    };
    loadDetailProfiles();
    const profileSync = setInterval(() => { loadDetailProfiles(); }, 7000);
    const onWinFocus = () => { loadDetailProfiles(); };
    window.addEventListener('focus', onWinFocus);
    const clubId = match?.clubId;
    let clubChannel = null;
    if (clubId) {
      clubChannel = supabase.channel(`match_detail_club_${clubId}`)
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'clubs', filter: `id=eq.${clubId}` }, () => {
          loadDetailProfiles();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'club_members', filter: `club_id=eq.${clubId}` }, () => {
          loadDetailProfiles();
        })
        .subscribe();
    }
    return () => {
      mounted = false;
      clearInterval(profileSync);
      window.removeEventListener('focus', onWinFocus);
      if (clubChannel) supabase.removeChannel(clubChannel);
    };
  }, [
    match?.clubId,
    currentClub?.hostId,
    user?.userId,
    user?.profileImageUrl,
    user?.nickname,
    user?.name,
    user?.birthYear,
    user?.birthMonth,
    user?.birthDay,
    user?.gender,
  ]);


  const handleCancelConfirm = () => {
    if (cancelStep === 1) { setCancelStep(2); return; }
    setShowCancelModal(false);
    onCancel();
  };
  const openRoomEditModal = () => {
    setEditTitle(String(effectiveClub?.title || match?.title || '').slice(0, 24));
    setEditIntro(String(effectiveClub?.roomIntro || match?.roomIntro || '').slice(0, 100));
    setEditRoomCoverDataUrl(null);
    setEditRoomCoverRemoved(false);
    setShowEditRoomModal(true);
  };
  const saveRoomEdit = async () => {
    if (!effectiveClub?.id) return;
    const nextTitle = String(editTitle || '').trim();
    const nextIntro = String(editIntro || '').trim();
    if (nextTitle.length < 2) { alert('방 제목을 2자 이상 입력해주세요.'); return; }
    const prevTitle = String(effectiveClub?.title || '').trim();
    const titleChanged = nextTitle !== prevTitle;
    if (titleChanged && !canEditTitleNow) {
      alert(`방 제목은 일주일에 1번만 수정할 수 있어요. (${titleCooldownLeftDays}일 남음)`);
      return;
    }
    setSavingRoomEdit(true);
    try {
      let pCoverImageUrl;
      if (editRoomCoverRemoved) {
        pCoverImageUrl = '';
      } else if (editRoomCoverDataUrl && String(editRoomCoverDataUrl).startsWith('data:')) {
        const uid = user?.userId;
        if (!uid) {
          alert('로그인이 필요합니다.');
          return;
        }
        const uploaded = await uploadClubCoverPublicUrl(uid, String(effectiveClub.id), editRoomCoverDataUrl);
        if (!uploaded) {
          alert('이미지 업로드에 실패했습니다. JPG·PNG·WEBP·HEIC 등 이미지를 선택했는지, 네트워크 상태를 확인해주세요.');
          return;
        }
        pCoverImageUrl = withUrlCacheBust(uploaded);
      }
      const hostNameVal = user?.nickname || user?.name || effectiveClub?.hostName || '모임장';
      const hostImgVal = user?.profileImageUrl || effectiveClub?.hostProfileImageUrl || null;
      const rpcArgs = {
        p_club_id: effectiveClub.id,
        p_room_intro: nextIntro || null,
        p_host_name: hostNameVal,
        p_host_profile_image_url: hostImgVal,
        p_title: titleChanged ? nextTitle : null,
        p_last_title_update: titleChanged ? new Date().toISOString() : null,
        ...(typeof pCoverImageUrl !== 'undefined' ? { p_cover_image_url: pCoverImageUrl } : {}),
      };
      let { data, error } = await supabase.rpc('update_my_club_room', rpcArgs);
      const rpcMissing =
        error && (
          error.code === 'PGRST202'
          || /could not find the function/i.test(String(error.message || ''))
          || /function.*does not exist/i.test(String(error.message || ''))
        );
      if (!error && data != null) {
        const row = Array.isArray(data) ? data[0] : data;
        const mapped = row ? mapClubRow(row) : { ...effectiveClub, title: nextTitle, roomIntro: nextIntro || null };
        setDetailClub(mapped);
        if (onClubUpdated) onClubUpdated(mapped);
        setEditRoomCoverDataUrl(null);
        setEditRoomCoverRemoved(false);
        setShowEditRoomModal(false);
        alert('방 정보가 수정되었습니다.');
        return;
      }
      if (error && !rpcMissing) {
        alertDbFailure('updateClubRpc', '방 정보 수정에 실패했어요. 잠시 후 다시 시도해주세요.', error);
        return;
      }
      const payload = {
        room_intro: nextIntro || null,
        host_name: hostNameVal,
        host_profile_image_url: hostImgVal,
      };
      if (titleChanged) {
        payload.title = nextTitle;
        payload.last_title_update = new Date().toISOString();
      }
      if (typeof pCoverImageUrl !== 'undefined') {
        payload.cover_image_url = pCoverImageUrl === '' ? null : pCoverImageUrl;
      }
      ({ data, error } = await supabase
        .from('clubs')
        .update(payload)
        .eq('id', effectiveClub.id)
        .select('*')
        .maybeSingle());
      if (error && isMissingProfileColumnError(error)) {
        const fallback = { ...payload };
        delete fallback.host_name;
        delete fallback.host_profile_image_url;
        ({ data, error } = await supabase
          .from('clubs')
          .update(fallback)
          .eq('id', effectiveClub.id)
          .select('*')
          .maybeSingle());
      }
      if (error) {
        alertDbFailure('updateClubDirect', '방 정보 수정에 실패했어요. 잠시 후 다시 시도해주세요.', error);
        return;
      }
      const mapped = data ? mapClubRow(data) : { ...effectiveClub, title: nextTitle, roomIntro: nextIntro || null };
      setDetailClub(mapped);
      if (onClubUpdated) onClubUpdated(mapped);
      setEditRoomCoverDataUrl(null);
      setEditRoomCoverRemoved(false);
      setShowEditRoomModal(false);
      alert('방 정보가 수정되었습니다.');
    } finally {
      setSavingRoomEdit(false);
    }
  };

  return (
    <div className="screen">
      {showEditRoomModal && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:210,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 20px'}}>
          <div style={{background:'#fff',borderRadius:14,padding:18,width:'100%',maxWidth:360}}>
            <div style={{fontSize:18,fontWeight:800,color:'#111827',marginBottom:12}}>방 정보 수정</div>
            <div style={{marginBottom:10}}>
              <label style={{display:'block',fontSize:13,fontWeight:700,color:'#374151',marginBottom:6}}>방 제목 (최대 24자)</label>
              <input
                value={editTitle}
                onChange={(e) => setEditTitle((e.target.value || '').slice(0, 24))}
                className="form-input"
                style={{background:'#fff'}}
                maxLength={24}
              />
              <div style={{fontSize:12,color:'#9CA3AF',marginTop:4}}>
                {editTitle.length}/24자 · 제목 수정은 1주 1회{!canEditTitleNow ? ` (${titleCooldownLeftDays}일 남음)` : ''}
              </div>
            </div>
            <div style={{marginBottom:14}}>
              <label style={{display:'block',fontSize:13,fontWeight:700,color:'#374151',marginBottom:6}}>모임 소개 (최대 100자)</label>
              <textarea
                value={editIntro}
                onChange={(e) => setEditIntro((e.target.value || '').slice(0, 100))}
                className="form-input"
                style={{minHeight:86,resize:'none',paddingTop:10,lineHeight:1.6,background:'#fff'}}
                maxLength={100}
              />
              <div style={{fontSize:12,color:'#9CA3AF',marginTop:4}}>{editIntro.length}/100자</div>
            </div>
            <div style={{marginBottom:14}}>
              <label style={{display:'block',fontSize:13,fontWeight:700,color:'#374151',marginBottom:6}}>
                채팅방 대표 사진 <span style={{fontWeight:500,color:'#9CA3AF',fontSize:11}}>선택 · HEIC 포함 · 자동 압축(~1MB)</span>
              </label>
              <div style={{ fontSize:12, color:'#6B7280', lineHeight:1.55, marginBottom:8 }}>
                새로 올리지 않으면 <strong style={{ color:'#374151' }}>주제별 기본 배경</strong>이 그대로 표시됩니다.
              </div>
              <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
                <button
                  type="button"
                  onClick={() => editRoomCoverInputRef.current?.click()}
                  style={{
                    width:112,
                    height:112,
                    borderRadius:12,
                    border:'1px dashed #D1D5DB',
                    background:'#F9FAFB',
                    padding:0,
                    overflow:'hidden',
                    cursor:'pointer',
                    flexShrink:0,
                  }}
                >
                  {editRoomCoverDataUrl ? (
                    <img src={editRoomCoverDataUrl} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />
                  ) : !editRoomCoverRemoved && effectiveClub?.coverImageUrl ? (
                    <img src={effectiveClub.coverImageUrl} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />
                  ) : (
                    <img
                      src={defaultClubCoverSrc(match.hobby)}
                      alt=""
                      style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'center', transform:'scale(1.06)' }}
                      onError={(e) => {
                        const t = e.currentTarget;
                        if (String(t.src || '').replace(/\\/g, '/').includes('/chatroom.png') && !String(t.src || '').includes('/default-covers/')) return;
                        t.onerror = null;
                        t.src = defaultClubCoverFallbackSrc();
                      }}
                    />
                  )}
                </button>
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  <button
                    type="button"
                    onClick={() => editRoomCoverInputRef.current?.click()}
                    style={{padding:'8px 14px',border:'1px solid #D1D5DB',background:'#fff',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}
                  >
                    앨범에서 선택
                  </button>
                  {(effectiveClub?.coverImageUrl || editRoomCoverDataUrl) && !editRoomCoverRemoved && (
                    <button
                      type="button"
                      onClick={() => { setEditRoomCoverDataUrl(null); setEditRoomCoverRemoved(true); }}
                      style={{padding:'8px 14px',border:'1px solid #FECACA',background:'#FEF2F2',color:'#B91C1C',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}
                    >
                      사진 제거
                    </button>
                  )}
                </div>
              </div>
              <input
                ref={editRoomCoverInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const raw = e.target.files?.[0];
                  e.target.value = '';
                  if (!raw) return;
                  if (!raw.type?.startsWith('image/') && !isLikelyHeicFile(raw)) {
                    alert('이미지 파일만 선택할 수 있어요.');
                    return;
                  }
                  if (raw.size > IMAGE_UPLOAD_MAX_INPUT_BYTES) {
                    alert('원본이 너무 큽니다(최대 약 30MB). 다른 사진을 선택해주세요.');
                    return;
                  }
                  try {
                    const cf = await prepareImageFileForUpload(raw);
                    if (cf.size > CLUB_COVER_MAX_BYTES * 1.12) {
                      alert('압축 후에도 용량이 큽니다. 다른 이미지를 선택해주세요.');
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = () => {
                      setEditRoomCoverDataUrl(reader.result);
                      setEditRoomCoverRemoved(false);
                    };
                    reader.readAsDataURL(cf);
                  } catch (err) {
                    alertImagePrepareFailure('editRoomCoverPick', err, '이미지를 불러오지 못했습니다. 형식·용량을 확인해주세요.');
                  }
                }}
              />
            </div>
            <div style={{display:'flex',gap:8}}>
              <button type="button" onClick={() => setShowEditRoomModal(false)} style={{flex:1,height:42,border:'1px solid #D1D5DB',background:'#fff',borderRadius:10,fontSize:14,fontWeight:700,cursor:'pointer'}}>취소</button>
              <button type="button" disabled={savingRoomEdit} onClick={saveRoomEdit} style={{flex:1,height:42,border:'none',background:'#16A34A',color:'#fff',borderRadius:10,fontSize:14,fontWeight:700,cursor:'pointer'}}>{savingRoomEdit ? '저장 중...' : '저장'}</button>
            </div>
          </div>
        </div>
      )}
      {showCancelModal && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.55)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 24px'}}>
          <div style={{background:'white',borderRadius:16,padding:24,width:'100%',maxWidth:340}}>
            {isCurrentHost ? (
              // 모임장: 매칭 대기 중에는 문구를 신청 취소로 통일
              <>
                <div style={{fontSize:18,fontWeight:800,color:'#111827',marginBottom:8}}>
                  {!isActiveClub ? '매칭 신청을 취소하시겠어요?' : '모임에서 나가시겠어요?'}
                </div>
                <div style={{fontSize:14,color:'var(--gray-600)',lineHeight:1.8,marginBottom:16}}>
                  나가면 다음 입장자에게 모임장 권한이 자동으로 승계됩니다.<br/>
                  혼자 남은 경우 방이 종료됩니다.<br/>
                  {coinType === 'paid' && (useCreatorHostHourWindow ? (freeCancel
                    ? <strong style={{color:'var(--green)'}}>유료포인트 {spentCoins}포인트 전액 환급됩니다.</strong>
                    : <strong style={{color:'#991B1B'}}>{isMatched ? '매칭 완료 후 취소: 포인트 환급 없음' : '방 생성 48시간 이내: 포인트 환급 없음'}</strong>
                  ) : (freeCancel
                    ? <strong style={{color:'var(--green)'}}>유료포인트 {spentCoins}포인트 전액 환급됩니다.</strong>
                    : <strong style={{color:'#991B1B'}}>{isMatched ? '매칭 완료 후 취소: 포인트 환급 없음' : '24시간 이내 취소: 10포인트 패널티 적용 (20포인트 환급)'}</strong>
                  ))}
                </div>
                <div style={{display:'flex',gap:10}}>
                  <button onClick={() => setShowCancelModal(false)}
                    style={{flex:1,padding:14,background:'var(--green)',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'inherit',color:'white'}}>
                    유지하기
                  </button>
                  <button onClick={() => { setShowCancelModal(false); onCancel(); }}
                    style={{flex:1,padding:14,background:'var(--gray-100)',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'inherit',color:'var(--gray-600)'}}>
                      {!isActiveClub ? '신청 취소하기' : '나가기'}
                  </button>
                </div>
              </>
            ) : cancelStep === 1 ? (
              <>
                <div style={{fontSize:18,fontWeight:800,color:'#111827',marginBottom:8}}>신청을 취소하시겠어요?</div>
                <div style={{fontSize:14,color:'var(--gray-600)',lineHeight:1.8,marginBottom:16}}>
                  {coinType !== 'paid' ? (
                    <>무료 포인트 매칭은 취소 가능하지만 <strong style={{color:'#991B1B'}}>환급이 없습니다.</strong></>
                  ) : freeCancel ? (
                    <><strong style={{color:'var(--green)'}}>유료포인트 {spentCoins}포인트 전액 환급</strong>됩니다.</>
                  ) : (
                    <>{isMatched
                      ? <strong style={{color:'#991B1B'}}>매칭 완료 후 취소는 환급되지 않습니다.</strong>
                      : <>24시간 이내 취소: <strong style={{color:'#991B1B'}}>10포인트 패널티</strong> → <strong style={{color:'var(--green)'}}>20포인트 환급</strong></>
                    }</>
                  )}
                </div>
                <div style={{display:'flex',gap:10}}>
                  <button onClick={() => setShowCancelModal(false)} style={{flex:1,padding:14,background:'var(--green)',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'inherit',color:'white'}}>유지하기</button>
                  <button onClick={() => handleCancelConfirm(false)} style={{flex:1,padding:14,background:'var(--gray-100)',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'inherit',color:'var(--gray-600)'}}>취소하기</button>
                </div>
              </>
            ) : (
              <>
                <div style={{fontSize:18,fontWeight:800,color:'#991B1B',marginBottom:8}}>정말 취소하시겠어요?</div>
                <div style={{fontSize:14,color:'var(--gray-600)',lineHeight:1.8,marginBottom:16}}><strong style={{color:'#991B1B'}}>취소 후에는 되돌릴 수 없습니다.</strong></div>
                <div style={{display:'flex',gap:10}}>
                  <button onClick={() => setShowCancelModal(false)} style={{flex:1,padding:14,background:'var(--green)',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'inherit',color:'white'}}>유지하기</button>
                  <button onClick={() => handleCancelConfirm(false)} style={{flex:1,padding:14,background:'#991B1B',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'inherit',color:'white'}}>최종 취소</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <div className="top-bar"><button className="back-btn" onClick={onBack}>←</button><div className="screen-title">{isActiveClub ? '채팅방 상세' : '매칭 상세'}</div></div>
      <div className="content">
        <div className="match-card detail-view-shell" style={{padding:14}}>
          <div className="member-box detail-section detail-section--head">
            <div style={{display:'flex',gap:12,alignItems:'center'}}>
              <ClubListThumb club={currentClub || match} compact />
              <div style={{flex:1,minWidth:0}}>
                <div className="detail-room-title">
                  {match.title || effectiveClub?.title || HOBBY_MAP[match.hobby]}
                </div>
              </div>
            </div>
          </div>
          <div className="member-box detail-section detail-section--host">
            <div className="detail-section-title">모임장</div>
            <div style={{display:'flex',alignItems:'center',gap:12}}>
              <img src={hostAvatarSrc} alt="" style={{width:58,height:58,borderRadius:'50%',objectFit:'cover',border:'1px solid #E5E7EB'}} />
              <div style={{minWidth:0}}>
                <div style={{fontSize:18,fontWeight:800,color:'#111827'}}>{hostDisplayName} {isCurrentHost && <span style={{fontSize:11,color:'#059669',background:'#ECFDF5',padding:'2px 7px',borderRadius:999,verticalAlign:'middle',marginLeft:6}}>모임장</span>}</div>
                {(hostDetailAge != null || hostProfile?.region) && (
                  <div style={{fontSize:13,color:'#6B7280',marginTop:2}}>
                    {hostDetailAge != null ? `${hostDetailAge}세` : ''}{hostDetailAge != null && hostProfile?.region ? ' · ' : ''}{hostProfile?.region ? hostProfile.region : ''}
                  </div>
                )}
              </div>
            </div>
            {isCurrentHost && (
              <button
                type="button"
                onClick={openRoomEditModal}
                className="detail-edit-btn"
              >
                방 정보 수정
              </button>
            )}
          </div>
          <div className="member-box detail-section detail-section--intro">
            <div className="detail-section-title">모임 소개</div>
            <div className="detail-intro-text">
              {roomSummary || '아직 등록된 채팅방 소개가 없어요.'}
            </div>
          </div>
          {isCurrentHost && (
            <div className="member-box detail-section" style={{padding:'12px 13px'}}>
              <div className="detail-section-title">상단 노출</div>
              <div style={{fontSize:13,color:'#4B5563',lineHeight:1.6,whiteSpace:'pre-line'}}>
                {boostDescriptionText}
              </div>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,marginTop:10}}>
                <div style={{fontSize:12,fontWeight:800,color:isBoostActive(effectiveClub)?'#C2410C':'#6B7280'}}>
                  {isBoostActive(effectiveClub)
                    ? `🔥 상단 노출중 · 남은 시간 ${formatBoostRemaining(boostRemainingMs(effectiveClub))}`
                    : '현재 상단 노출 중이 아닙니다'}
                </div>
                <div style={{display:'flex',gap:8,alignItems:'center',flexShrink:0}}>
                  {isBoostActive(effectiveClub) ? (
                    <button
                      type="button"
                      className="btn-outline"
                      disabled={boostingNow}
                      onClick={handleCancelBoost}
                      style={{
                        padding:'10px 12px',
                        borderRadius:10,
                        fontWeight:900,
                        borderColor:'#FCA5A5',
                        color:'#B91C1C',
                        opacity: boostingNow ? 0.6 : 1,
                        cursor: boostingNow ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {boostingNow ? '처리중…' : '상단 노출 취소'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-outline"
                      disabled={boostingNow}
                      onClick={handleBoost2Hours}
                      style={{
                        padding:'10px 12px',
                        borderRadius:10,
                        fontWeight:900,
                        opacity: boostingNow ? 0.6 : 1,
                        cursor: boostingNow ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {boostingNow ? '처리중…' : '2시간 상단 노출'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="member-box detail-section detail-quick-info">
            <div className="detail-quick-item">
              <div className="detail-quick-label">모임 일정</div>
              <div className="detail-quick-value">{(effectiveClub?.times || match?.times || []).map(t => TIME_LABELS[t] || t).join(' · ') || '미정'}</div>
            </div>
            <div className="detail-quick-item">
              <div className="detail-quick-label">모임 장소</div>
              <div className="detail-quick-value">{effectiveClub?.region || match?.region || '미정'}</div>
            </div>
            <div className="detail-quick-item">
              <div className="detail-quick-label">모집 인원</div>
              <div className="detail-quick-value">{currentMembers}명 참여</div>
            </div>
          </div>
          <div className="member-box detail-section detail-section--members">
            <div className="detail-section-title">참여 중인 사람 {currentMembers}</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(5, minmax(0,1fr))',gap:10}}>
              {(isActiveClub ? memberList : memberList.slice(0, Math.min(3, memberList.length))).map((raw) => {
                const m = sameUserId(raw.id, user?.userId) && user
                  ? {
                    ...raw,
                    name: String(user.nickname || user.name || raw.name || '회원').trim(),
                    avatar: user.profileImageUrl ? withUrlCacheBust(user.profileImageUrl) : raw.avatar,
                  }
                  : raw;
                return (
                <div key={m.id} style={{textAlign:'center'}}>
                  <img src={m.avatar} alt="" style={{width:48,height:48,borderRadius:'50%',objectFit:'cover',border:'1px solid #E5E7EB'}} />
                  <div style={{fontSize:12,fontWeight:700,color:'#111827',marginTop:5,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {isActiveClub && viewerIsParticipant
                      ? (String(m.name || '').trim() || (sameUserId(m.id, effectiveClub?.hostId) ? '모임장' : '회원'))
                      : maskMemberNameForViewer(m.name, user?.userId, m.id, effectiveClub?.hostId)}
                  </div>
                </div>
              );})}
              {!isActiveClub && (
                <div style={{textAlign:'center'}}>
                  <div style={{width:48,height:48,borderRadius:'50%',border:'1px dashed #D1D5DB',display:'grid',placeItems:'center',color:'#9CA3AF',margin:'0 auto'}}>+</div>
                  <div style={{fontSize:13,fontWeight:700,color:'#111827',marginTop:5}}>매칭 후 공개</div>
                </div>
              )}
              {isActiveClub && !viewerIsParticipant && (
                <div style={{textAlign:'center'}}>
                  <div style={{width:48,height:48,borderRadius:'50%',border:'1px dashed #D1D5DB',display:'grid',placeItems:'center',color:'#9CA3AF',margin:'0 auto'}}>+</div>
                  <div style={{fontSize:13,fontWeight:700,color:'#111827',marginTop:5}}>
                    {(currentMembers - Math.min(4, memberList.length)) > 0 ? `${currentMembers - Math.min(4, memberList.length)}명 더` : '자리 있음'}
                  </div>
                </div>
              )}
            </div>
          </div>
          {!isActiveClub && viewerIsParticipant && (
            <div className="countdown-box" style={{marginTop:10,marginBottom:0}}>
              <div className="countdown-top">
                <span className="countdown-label">⏳ {useCreatorHostHourWindow ? '모임장' : '참가자'} 취소 정책</span>
                <span className="countdown-time">{isMatched ? '매칭 완료 · 환급 없음' : (freeCancel ? '무료 취소 가능' : `${useCreatorHostHourWindow ? 48 : 24}시간까지 ${hoursLeftFree}시간 남음`)}</span>
              </div>
              <div className="countdown-bar-wrap"><div className="countdown-bar" style={{width:`${Math.max(0,Math.min(100,(hoursPassed/(useCreatorHostHourWindow ? 48 : 24))*100))}%`}}/></div>
              <div className="countdown-sub">
                {useCreatorHostHourWindow
                  ? coinType !== 'paid'
                    ? '무료 포인트 사용: 환급 없음'
                    : freeCancel
                      ? `지금 취소 시 유료포인트 ${spentCoins}포인트 전액 환급`
                      : (isMatched ? '매칭 완료 후 나가기: 포인트 환급 없음' : '48시간 이내 나가기: 포인트 환급 없음')
                  : coinType !== 'paid'
                    ? '무료 포인트 사용: 취소 가능 / 환급 없음'
                    : freeCancel
                      ? `지금 취소 시 유료포인트 ${spentCoins}포인트 전액 환급`
                      : (isMatched ? '매칭 완료 후 취소/나가기: 포인트 환급 없음' : '24시간 이내 취소 시 10포인트 패널티 (20포인트 환급)')
                }
              </div>
            </div>
          )}
          <div className="member-box detail-section detail-info-section" style={{padding:'12px 13px'}}>
            <div className="detail-section-title">모임 정보</div>
            <div style={{display:'flex',justifyContent:'space-between',gap:12,fontSize:13,color:'#374151',padding:'4px 0'}}>
              <span style={{fontWeight:700,color:'#6B7280'}}>카테고리</span>
              <span style={{textAlign:'right'}}>{clubHobbyCategoryText(effectiveClub || match)}</span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',gap:12,fontSize:13,color:'#374151',padding:'4px 0'}}>
              <span style={{fontWeight:700,color:'#6B7280'}}>지역</span>
              <span style={{textAlign:'right'}}>{effectiveClub?.region || match.region}</span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',gap:12,fontSize:13,color:'#374151',padding:'4px 0'}}>
              <span style={{fontWeight:700,color:'#6B7280'}}>연령</span>
              <span style={{textAlign:'right'}}>{displayAgeRangeText || '모임장 나이대 기준'}</span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',gap:12,fontSize:13,color:'#374151',padding:'4px 0'}}>
              <span style={{fontWeight:700,color:'#6B7280'}}>성별</span>
              <span style={{textAlign:'right'}}>{RATIO_MAP[effectiveClub?.ratio || match?.ratio] || '혼합 모임'}</span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',gap:12,fontSize:13,color:'#374151',padding:'4px 0',alignItems:'flex-start'}}>
              <span style={{fontWeight:700,color:'#6B7280',flexShrink:0}}>활동 시간</span>
              <span style={{textAlign:'right',lineHeight:1.5,whiteSpace:'normal'}}>
                {(effectiveClub?.times || match?.times || []).map(t => TIME_LABELS[t]).filter(Boolean).join(' · ') || '시간 미정'}
              </span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',gap:12,fontSize:13,color:'#374151',padding:'4px 0'}}>
              <span style={{fontWeight:700,color:'#6B7280'}}>인원</span>
              <span style={{textAlign:'right'}}>
                {currentMembers} / {maxMembers}명
                <span style={{ display:'block', fontSize:12, color:'#6B7280', marginTop:2, fontWeight:600 }}>
                  {genderMemberBreakdownText}
                </span>
              </span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',gap:12,fontSize:13,color:'#374151',padding:'4px 0'}}>
              <span style={{fontWeight:700,color:'#6B7280'}}>방 생성일</span>
              <span style={{textAlign:'right'}}>{roomCreatedAtText}</span>
            </div>
          </div>
          {isCurrentHost && (
            <div style={{background:'#EFF6FF',borderRadius:10,padding:'10px 13px',marginTop:12,fontSize:13,color:'#1D4ED8'}}>
              모임장 나가기 시 다음 입장자에게 자동 승계됩니다
            </div>
          )}
          {!primaryAction && isActiveClub && effectiveClub?.id && viewerIsParticipant && (
            <button
              onClick={() => onEnterChat && onEnterChat(effectiveClub.id)}
              style={{width:'100%',marginTop:14,padding:'14px',background:'#16A34A',border:'none',borderRadius:10,fontSize:18,fontWeight:800,cursor:'pointer',fontFamily:'inherit',color:'white'}}
            >
              💬 채팅방 입장하기
            </button>
          )}
          {!primaryAction && isActiveClub && !viewerIsParticipant && (
            <button
              type="button"
              disabled={!genderOk || isRoomFull}
              onClick={() => {
                if (isRoomFull || !genderOk) return;
                if (!ageOk) {
                  if (typeof onAgeMismatchFromDetail === 'function') onAgeMismatchFromDetail();
                  else alert(clubAgeApplyOnlyMessage(effectiveClub || match));
                  return;
                }
                if (typeof onJoinActiveRoomFromDetail === 'function') onJoinActiveRoomFromDetail();
              }}
              style={{
                width:'100%',
                marginTop:14,
                padding:'14px',
                background: (!genderOk || isRoomFull) ? '#E5E7EB' : '#16A34A',
                border:'none',
                borderRadius:10,
                fontSize:18,
                fontWeight:800,
                cursor: (!genderOk || isRoomFull) ? 'not-allowed' : 'pointer',
                fontFamily:'inherit',
                color: (!genderOk || isRoomFull) ? '#6B7280' : 'white',
              }}
            >
              {!genderOk
                ? `${joinEligibilityBlockLabel(user, effectiveClub || match || {})} 불가`
                : (isRoomFull ? '자리 없음' : '채팅방 참여하기')}
            </button>
          )}
          {primaryAction && (
            <button
              onClick={primaryAction.onClick}
              disabled={!!primaryAction.disabled}
              style={{
                width:'100%',
                marginTop:14,
                padding:'14px',
                background: primaryAction.disabled ? '#E5E7EB' : (primaryAction.background || '#6366F1'),
                border:'none',
                borderRadius:10,
                fontSize:18,
                fontWeight:800,
                cursor: primaryAction.disabled ? 'not-allowed' : 'pointer',
                fontFamily:'inherit',
                color: primaryAction.disabled ? '#6B7280' : '#fff'
              }}
            >
              {primaryAction.label || '확인'}
            </button>
          )}
          {!hideCancelAction && (
            <button onClick={() => setShowCancelModal(true)} style={{width:'100%',marginTop:14,padding:'13px',background:'white',border:'1px solid var(--gray-200)',borderRadius:10,fontSize:15,fontWeight:600,cursor:'pointer',fontFamily:'inherit',color:'var(--gray-600)'}}>
              {!isActiveClub ? '신청 취소하기' : (isCurrentHost ? '방 나가기' : '신청 취소하기')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── 채팅 (신고·모임장·시스템메시지 포함) ──── */
function ChatScreen({ user, onNav, appState, updateState, clubs, setClubs, screen,
  mutedRooms, onResetUnread, onToggleMute, suppressHostNoticeForClub }) {
  const chatTabVisibleRef = useRef(false);
  chatTabVisibleRef.current = screen === 'chat';
  const currentClub   = clubs?.find(c => c.id === appState.currentClubId);
  const isHost        = sameUserId(currentClub?.hostId, user?.userId);
  const myMatch       = appState.matches.find(m => m.clubId === appState.currentClubId);
  const wasOrigHost   = myMatch?.isHost || false;
  const isNewHost     = isHost && !wasOrigHost; // 승계로 새 모임장이 됨
  const clubLabel     = currentClub ? (currentClub.title || clubHobbyFullText(currentClub) || HOBBY_MAP[currentClub.hobby] || '모임') : '와인·맛집 모임';
  const roomCapacity  = currentClub?.maxMembers || 6;

  const SYSTEM_MSGS = [
    { id:'sys1', type:'system', text:'🎉 모임방이 열렸습니다!\n지금부터 자유롭게 대화를 시작해보세요.' },
    { id:'sys2', type:'system', text:'간단한 자기소개 부탁드려요.' },
    ...(isNewHost ? [{ id:'sys_host', type:'system', text:'기존 모임장이 이탈하여 회원님이 새 모임장이 되었습니다.' }] : []),
  ];

  const [messages, setMessages] = useState([...SYSTEM_MSGS]);
  const [input, setInput]               = useState('');
  const [showExitModal, setShowExitModal]   = useState(false);
  const [expandedImg, setExpandedImg]       = useState(null);
  const [isLocked, setIsLocked]             = useState(currentClub?.isLocked || false);
  // 신고 관련
  const [profileTarget, setProfileTarget]   = useState(null);
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportReason, setReportReason]     = useState('');
  const [reportScreenshot, setReportScreenshot] = useState(null);
  const [avatarMap, setAvatarMap] = useState(() => {
    if (!user?.userId || !user?.profileImageUrl) return {};
    const b = withUrlCacheBust(user.profileImageUrl);
    return b ? { [user.userId]: b } : {};
  });
  const [roomMembers, setRoomMembers] = useState([]);
  const [showRoomPanel, setShowRoomPanel] = useState(false);
  const [showChatSearch, setShowChatSearch] = useState(false);
  const [chatSearchQuery, setChatSearchQuery] = useState('');
  const [activeChatRestriction, setActiveChatRestriction] = useState(null);
  const [memberActionMenuUserId, setMemberActionMenuUserId] = useState(null);
  /** 같은 방에서 내가 차단(또는 과거 차단)한 상대 — 메시지 숨김 윈도우[{start,end?}] */
  const [blockedSinceByUserId, setBlockedSinceByUserId] = useState(() => new Map());
  const [showAllSharedPhotos, setShowAllSharedPhotos] = useState(false);
  /** 모임장 강제퇴장: 사유·캡처 필수 (Edge `host-kick-member`) */
  const [showHostKickModal, setShowHostKickModal] = useState(false);
  const [hostKickTarget, setHostKickTarget] = useState(null);
  const [hostKickReason, setHostKickReason] = useState('');
  const [hostKickScreenshotPreview, setHostKickScreenshotPreview] = useState(null);
  const [hostKickBusy, setHostKickBusy] = useState(false);

  const memberCount   = roomMembers.length > 0 ? roomMembers.length : (currentClub?.members || 0);
  const isRoomFull    = memberCount >= roomCapacity;

  const messagesEndRef    = useRef(null);
  const fileInputRef      = useRef(null);
  const screenshotInputRef = useRef(null);
  const hostKickFileInputRef = useRef(null);
  /** @type {React.MutableRefObject<File|null>} */
  const hostKickPreparedFileRef = useRef(null);
  /** @type {React.MutableRefObject<Set<string>|null>} */
  const roomMemberIdsRef = useRef(null);
  const memberNamesByIdRef = useRef(new Map());
  const roomMemberListKeyRef = useRef('');
  const kickedExitNotifiedRef = useRef(false);
  /** 정책 강퇴 안내(chat_messages) 직후 일반 퇴장 시스템 문구 중복 방지 */
  const skipLeaveForIdsRef = useRef(new Set());
  const lastRestrictionTypeRef = useRef(null);
  const seenRestrictionIdsRef = useRef(new Set());
  const chatVisibleSinceRef = useRef(null);
  const memberEventDedupRef = useRef(new Map());

  const joinSystemText = (name) =>
    `${name}님이 입장하셨습니다.\n안녕하세요! 가볍게 자기소개 부탁드려요 😊`;
  const leaveSystemText = (name) => `${name}님이 퇴장하셨습니다.`;
  const colors = ['#4B6FA0','#5B8A68','#8B6F52','#7A5A8A'];
  // user_id 기반 일관된 색상 (같은 유저는 항상 같은 색)
  const getUserColor = (uid) => { let h=0; for(const c of (uid||'')) h=c.charCodeAt(0)+((h<<5)-h); return colors[Math.abs(h)%colors.length]; };
  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return '';
    const h = d.getHours();
    const period = h < 12 ? '오전' : '오후';
    const hour12 = h % 12 || 12;
    return `${period} ${hour12}:${String(d.getMinutes()).padStart(2, '0')}`;
  };
  const getAvatarSrc = (uid) => avatarMap[uid] || DEFAULT_PROFILE_ICON;
  const getDisplayName = (uid, fallbackName = '회원') => {
    const fromRoom = roomMembers.find(r => sameUserId(r.userId, uid))?.name;
    const fromRef = memberNamesByIdRef.current.get(uid);
    const raw = String(fromRoom || fromRef || fallbackName || '회원').trim() || '회원';
    return raw;
  };
  const loadAvatarMap = async (userIds = []) => {
    const ids = Array.from(new Set((userIds || []).filter(Boolean)));
    if (ids.length === 0) return;
    const { data, error } = await supabase.from('profiles').select('user_id, profile_image_url').in('user_id', ids);
    if (error) return;
    const next = {};
    (data || []).forEach(r => {
      if (!r?.user_id || !r?.profile_image_url) return;
      const bust = withUrlCacheBust(r.profile_image_url);
      if (bust) next[r.user_id] = bust;
    });
    if (Object.keys(next).length > 0) setAvatarMap(prev => ({ ...prev, ...next }));
  };
  const loadRoomMembers = async (roomId) => {
    if (!roomId) { setRoomMembers([]); return; }
    const exitAsKicked = () => {
      if (!kickedExitNotifiedRef.current) {
        kickedExitNotifiedRef.current = true;
        alert(CHAT_POLICY_KICK_EXIT_MESSAGE);
      }
      updateState('matches', (appState.matches || []).filter(m => m.clubId !== roomId));
      updateState('currentClubId', null);
      setRoomMembers([]);
      onNav('match', { replace: true });
    };
    const { data: membersData, error: membersErr } = await supabase
      .from('club_members')
      .select('user_id, joined_at')
      .eq('club_id', roomId)
      .order('joined_at', { ascending: true });
    if (membersErr) {
      console.warn('room members load error:', membersErr.message);
      const msg = String(membersErr?.message || '').toLowerCase();
      // 강퇴(또는 접근권한 상실) 직후에는 club_members SELECT가 RLS로 막힐 수 있음.
      // 이 경우 기존 화면에 남아있는 문제를 막기 위해 "강퇴 처리"로 간주하고 즉시 퇴장 처리한다.
      if (
        msg.includes('permission denied') ||
        msg.includes('not allowed') ||
        msg.includes('row-level security') ||
        msg.includes('rls')
      ) {
        exitAsKicked();
      }
      return;
    }
    const ids = Array.from(new Set((membersData || []).map(m => m.user_id).filter(Boolean)));
    const myId = user?.userId;
    const stillMember = myId ? ids.some(id => sameUserId(id, myId)) : true;
    if (myId && !stillMember) {
      exitAsKicked();
      return;
    }
    const nameBefore = new Map(memberNamesByIdRef.current);
    const oldIdSet = roomMemberIdsRef.current;
    const shouldEmitMemberEvent = (kind, memberId) => {
      const key = `${kind}:${roomId}:${memberId}`;
      const now = Date.now();
      const prev = memberEventDedupRef.current.get(key) || 0;
      if (now - prev < 5000) return false;
      memberEventDedupRef.current.set(key, now);
      return true;
    };
    const applyMemberDiff = (newIdSet, newNameById) => {
      if (oldIdSet === null) {
        memberNamesByIdRef.current = newNameById;
        roomMemberIdsRef.current = newIdSet;
        roomMemberListKeyRef.current = [...newIdSet].sort().join(',');
        return;
      }
      memberNamesByIdRef.current = newNameById;
      for (const id of newIdSet) {
        if (!oldIdSet.has(id)) {
          if (!shouldEmitMemberEvent('join', id)) continue;
          const joinName = String(newNameById.get(id) || '회원').trim() || '회원';
          setMessages(prev => [...prev, { id: `sys_join_${id}_${Date.now()}`, type: 'system', text: joinSystemText(joinName) }]);
        }
      }
      for (const id of oldIdSet) {
        if (!newIdSet.has(id)) {
          if (!shouldEmitMemberEvent('leave', id)) continue;
          if (skipLeaveForIdsRef.current.has(id)) {
            skipLeaveForIdsRef.current.delete(id);
            continue;
          }
          const leftName = String(nameBefore.get(id) || '회원').trim() || '회원';
          setMessages(prev => [...prev, { id: `sys_leave_${id}_${Date.now()}`, type: 'system', text: leaveSystemText(leftName) }]);
        }
      }
      roomMemberIdsRef.current = newIdSet;
      roomMemberListKeyRef.current = [...newIdSet].sort().join(',');
    };
    if (ids.length === 0) {
      const empty = new Set();
      const emptyNames = new Map();
      applyMemberDiff(empty, emptyNames);
      setRoomMembers([]);
      return;
    }
    let { data: profilesData, error: profilesErr } = await supabase
      .from('profiles')
      .select('user_id, name, nickname, profile_image_url')
      .in('user_id', ids);
    if (profilesErr) {
      const fallback = await supabase
        .from('profiles')
        .select('user_id, name, profile_image_url')
        .in('user_id', ids);
      profilesData = fallback.data || [];
      profilesErr = fallback.error;
    }
    if (profilesErr) {
      console.warn('room member profiles load error:', profilesErr.message);
    }
    const byId = {};
    (profilesData || []).forEach(p => {
      byId[p.user_id] = p;
    });
    const nextAvatar = {};
    const mapped = (membersData || []).map(m => {
      const p = byId[m.user_id] || {};
      if (p.profile_image_url) {
        const bust = withUrlCacheBust(p.profile_image_url);
        if (bust) nextAvatar[m.user_id] = bust;
      }
      return {
        userId: m.user_id,
        name: p.nickname || p.name || (m.user_id === currentClub?.hostId ? '모임장' : '회원'),
        mine: sameUserId(m.user_id, user?.userId),
        joinedAt: m.joined_at || '',
      };
    });
    const newNameById = new Map(mapped.map(m => [m.userId, m.name]));
    const newIdSet = new Set(mapped.map(m => m.userId));
    applyMemberDiff(newIdSet, newNameById);
    if (Object.keys(nextAvatar).length > 0) {
      setAvatarMap(prev => ({ ...prev, ...nextAvatar }));
    }
    setRoomMembers(mapped);
  };
  const participants = (() => {
    if (roomMembers.length > 0) {
      const sorted = [...roomMembers].sort((a, b) => {
        if (a.userId === currentClub?.hostId) return -1;
        if (b.userId === currentClub?.hostId) return 1;
        return String(a.joinedAt || '').localeCompare(String(b.joinedAt || ''));
      });
      return sorted;
    }
    const byId = {};
    if (user?.userId) {
      byId[user.userId] = { userId: user.userId, name: user?.nickname || user?.name || '회원', mine: true };
    }
    messages.forEach(m => {
      if (m.type === 'system' || !m.userId) return;
      if (!byId[m.userId]) byId[m.userId] = { userId: m.userId, name: m.name || '회원', mine: m.mine };
    });
    if (currentClub?.hostId && !byId[currentClub.hostId]) {
      byId[currentClub.hostId] = { userId: currentClub.hostId, name: '모임장', mine: sameUserId(currentClub.hostId, user?.userId) };
    }
    return Object.values(byId);
  })();
  const normalizeParticipantName = (name) => {
    const raw = String(name || '').trim();
    if (!raw) return '회원';
    if (raw === '모임장') return '모임장';
    return raw.replace(/\s*(·\s*)?모임장\s*$/g, '').trim() || '회원';
  };
  const openMemberProfile = (rawName, userId) => {
    if (!userId || userId === user?.userId) return;
    /** 표시 시 getDisplayName에서 마스크 — 저장은 DB 기준 원문(닉네임) */
    setProfileTarget({ name: String(rawName || '').trim() || '회원', userId });
    setShowReportForm(false);
    setMemberActionMenuUserId(null);
    setShowRoomPanel(false);
    setShowHostKickModal(false);
    setHostKickTarget(null);
    setHostKickReason('');
    setHostKickScreenshotPreview(null);
    hostKickPreparedFileRef.current = null;
  };
  const effectiveLocked = isLocked;
  const canHostToggleLock = isHost && memberCount <= 5;
  const lockStateLabel = effectiveLocked ? '🔒 비공개 모임방' : '🔓 공개 모임방';
  const lockStateDesc = isRoomFull
    ? '정원 6명이라 신규 참여는 마감되고, 공개 목록에서는 자동으로 숨겨져요.'
    : effectiveLocked
      ? '새 인원 입장이 차단된 상태예요.'
      : '새 인원이 자유롭게 입장할 수 있어요.';
  /** 채팅에 등장한 순서(중복 URL은 첫 등장 기준 한 번만) */
  const sharedImagesChrono = messages
    .filter(m => !!m.image)
    .map(m => m.image)
    .filter((u, i, arr) => arr.indexOf(u) === i);
  const recentSharedImages = sharedImagesChrono.slice(-3);
  const normalizedSearch = chatSearchQuery.trim().toLowerCase();
  const visibleMessages = messages.filter(m => {
    if (m.type !== 'system' && m.userId && !sameUserId(m.userId, user?.userId)) {
      const windows = blockedSinceByUserId.get(String(m.userId));
      if (Array.isArray(windows) && windows.length > 0 && m.createdAt) {
        const msgMs = new Date(m.createdAt).getTime();
        if (Number.isFinite(msgMs) && msgMs > 0) {
          for (const w of windows) {
            const sMs = w?.start ? new Date(w.start).getTime() : NaN;
            const eMs = w?.end ? new Date(w.end).getTime() : Infinity;
            if (!Number.isFinite(sMs) || sMs <= 0) continue;
            const end = Number.isFinite(eMs) && eMs > 0 ? eMs : Infinity;
            if (msgMs >= sMs && msgMs <= end) return false;
          }
        }
      }
    }
    if (!normalizedSearch) return true;
    const t = `${m.text || ''} ${m.image ? '사진' : ''}`.toLowerCase();
    return t.includes(normalizedSearch);
  });

  useEffect(() => {
    setIsLocked(currentClub?.isLocked || false);
  }, [currentClub?.isLocked]);

  useEffect(() => {
    setShowAllSharedPhotos(false);
  }, [appState.currentClubId]);

  // 같은 방 참여자 모두가 잠금/자유 전환을 즉시 보도록 room 단위 실시간 구독
  useEffect(() => {
    const roomId = appState.currentClubId;
    if (!roomId) return;
    let prevLocked = currentClub?.isLocked || false;
    const channel = supabase.channel('club_lock_watch_' + roomId)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'clubs', filter: `id=eq.${roomId}` }, (payload) => {
        const oldH = payload?.old?.host_id;
        const newH = payload?.new?.host_id;
        if (oldH != null && newH != null && String(oldH) !== String(newH)) {
          fetchClubs().then(setClubs);
          setMessages(prev => [...prev, {
            id: `sys_host_change_${Date.now()}`,
            type: 'system',
            text: '모임장이 변경되었습니다.',
          }]);
        }
        const nextLocked = !!payload?.new?.is_locked;
        setIsLocked(nextLocked);
        if (nextLocked !== prevLocked) {
          setMessages(prev => [...prev, {
            id: 'sys_lock_' + Date.now(),
            type: 'system',
            text: nextLocked
              ? '🔒 모임장이 모임방을 비공개로 바꿨어요. 새 인원이 입장할 수 없습니다.'
              : '🔓 모임장이 모임방을 공개했어요. 새 인원이 입장할 수 있습니다.'
          }]);
        }
        prevLocked = nextLocked;
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [appState.currentClubId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 방이 오픈 조건을 한번 만족하면 status를 active로 고정해,
  // 이후 인원 변동(예: 6 -> 5)이 있어도 채팅중 방 목록에서 유지되도록 한다.
  useEffect(() => {
    if (!currentClub?.id) return;
    if (currentClub.status === 'active') return;
    if (!isClubOpenForChat(currentClub)) return;
    maybeActivateClubForChat(currentClub.id).then((ok) => {
      if (!ok) return;
      setClubs(prev => (prev || []).map(c => (
        c.id === currentClub.id ? { ...c, status: 'active' } : c
      )));
      fetchClubs().then(setClubs);
    });
  }, [currentClub?.id, currentClub?.status, currentClub?.members, currentClub?.createdAt]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const roomId = appState.currentClubId;
    if (!roomId) {
      kickedExitNotifiedRef.current = false;
      skipLeaveForIdsRef.current.clear();
      setRoomMembers([]);
      roomMemberIdsRef.current = null;
      memberNamesByIdRef.current = new Map();
      roomMemberListKeyRef.current = '';
      return;
    }
    kickedExitNotifiedRef.current = false;
    skipLeaveForIdsRef.current.clear();
    roomMemberIdsRef.current = null;
    memberNamesByIdRef.current = new Map();
    roomMemberListKeyRef.current = '';
    loadRoomMembers(roomId);
    const channel = supabase.channel('room_members_' + roomId)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'club_members', filter: `club_id=eq.${roomId}` }, () => {
        loadRoomMembers(roomId);
      })
      .subscribe();
    // Realtime 미노출/미구독 환경에서도 멤버 변화 감지(입퇴장 시스템 문구)
    const poll = setInterval(async () => {
      const { data, error } = await supabase
        .from('club_members')
        .select('user_id')
        .eq('club_id', roomId);
      if (error) return;
      const key = (data || []).map(r => r.user_id).filter(Boolean).sort().join(',');
      if (key !== roomMemberListKeyRef.current) loadRoomMembers(roomId);
    }, 20000);
    return () => {
      clearInterval(poll);
      supabase.removeChannel(channel);
    };
  }, [appState.currentClubId, currentClub?.hostId, user?.userId, user?.profileImageUrl, user?.nickname, user?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  // 내 프로필 사진 변경 시 채팅 말풍선/멤버 아바타 맵 즉시 동기화
  useEffect(() => {
    if (!user?.userId || !user?.profileImageUrl) return;
    const bust = withUrlCacheBust(user.profileImageUrl);
    if (!bust) return;
    setAvatarMap((prev) => ({
      ...prev,
      [user.userId]: bust,
    }));
  }, [user?.userId, user?.profileImageUrl]);

  // 채팅 진입/갱신 시 화면을 즉시 최신 메시지(맨 아래)로 맞춤
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior:'auto' }); }, [messages]);

  useEffect(() => {
    if (!memberActionMenuUserId) return;
    const close = () => setMemberActionMenuUserId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [memberActionMenuUserId]);

  useEffect(() => {
    const roomId = appState.currentClubId;
    const uid = user?.userId;
    if (!roomId || !uid) {
      setBlockedSinceByUserId(new Map());
      return undefined;
    }
    let alive = true;
    const loadBlocks = async () => {
      const { data, error } = await supabase
        .from('club_user_chat_blocks')
        .select('blocked_user_id, created_at, ended_at')
        .eq('club_id', roomId)
        .eq('blocker_user_id', uid);
      if (!alive) return;
      if (error) {
        logClientError('loadClubChatBlocks', error?.message, error);
        return;
      }
      const next = new Map();
      (data || []).forEach((r) => {
        const bid = String(r.blocked_user_id || '').trim();
        if (!bid) return;
        const prev = next.get(bid) || [];
        next.set(bid, [...prev, { start: r.created_at, end: r.ended_at || null }]);
      });
      setBlockedSinceByUserId(next);
    };
    loadBlocks();
    const channel = supabase.channel(`club_user_chat_blocks_${roomId}_${uid}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'club_user_chat_blocks',
        filter: `blocker_user_id=eq.${uid}`,
      }, () => { loadBlocks(); })
      .subscribe();
    return () => {
      alive = false;
      supabase.removeChannel(channel);
    };
  }, [appState.currentClubId, user?.userId]);

  // DB 채팅 로드 + Realtime 구독
  useEffect(() => {
    const roomId = appState.currentClubId;
    const myId = user?.userId;
    chatVisibleSinceRef.current = null;
    if (!roomId || !myId) return;
    const mapMsg = (m) => {
      const uid = String(m.user_id || '');
      if (uid.toLowerCase() === GYEOL_CHAT_SYSTEM_USER_ID.toLowerCase()) {
        const { kickedUserId, displayText } = parsePolicyKickChatText(m.text || '');
        if (kickedUserId) skipLeaveForIdsRef.current.add(kickedUserId);
        return {
          id: m.id,
          type: 'system',
          text: displayText,
          mine: false,
          createdAt: m.created_at,
          userId: m.user_id,
          image: null,
        };
      }
      return {
        id: m.id,
        name: m.user_name,
        text: m.text || null,
        mine: sameUserId(m.user_id, myId),
        createdAt: m.created_at,
        userId: m.user_id,
        image: sanitizeUserImageUrl(m.image_url) || null,
      };
    };

    const setupAndLoad = async () => {
      // 모든 유저는 본인이 이 방에 입장한 시점 이후 메시지만 본다.
      // 메시지는 DB에 계속 누적 저장하고, 표시할 때만 membership.joined_at으로 필터링한다.
      const matchJoinedAt = myMatch?.createdAt && Number(myMatch.createdAt) > 0
        ? new Date(myMatch.createdAt).toISOString()
        : null;
      const hostJoinedAt = isHost && currentClub?.createdAt ? currentClub.createdAt : null;
      const visibleSince = await ensureClubMemberJoinedAt(roomId, myId, matchJoinedAt || hostJoinedAt);
      if (!visibleSince) {
        setMessages(prev => prev.filter(m => m.type === 'system'));
        return;
      }
      chatVisibleSinceRef.current = visibleSince;
      const q = supabase.from('chat_messages').select('*').eq('club_id', roomId)
        .gte('created_at', visibleSince)
        .order('created_at', { ascending: false }).limit(CHAT_FETCH_LIMIT);
      const { data, error } = await q;
      if (error) { console.error('채팅 로드 오류:', error.message); return; }
      if (data) {
        const ordered = [...data].reverse();
        setMessages(prev => [...prev.filter(m => m.type === 'system'), ...ordered.map(mapMsg)]);
        loadAvatarMap(data.map(m => m.user_id));
        const newestReadAt = ordered[ordered.length - 1]?.created_at || new Date().toISOString();
        if (chatTabVisibleRef.current) onResetUnread?.(roomId, newestReadAt);
        // 채팅방 진입 직후에는 항상 최신 메시지(맨 아래)에서 시작
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
        });
      }
    };
    setupAndLoad();

    // 실시간 구독
    const channel = supabase.channel('chat_' + roomId)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messages', filter: `club_id=eq.${roomId}` }, (payload) => {
        const visibleSince = chatVisibleSinceRef.current;
        if (!visibleSince) return;
        if (payload?.new?.created_at && payload.new.created_at < visibleSince) return;
        const msg = mapMsg(payload.new);
        if (payload?.new?.user_id) loadAvatarMap([payload.new.user_id]);
        if (chatTabVisibleRef.current) {
          onResetUnread?.(roomId, payload?.new?.created_at || new Date().toISOString());
        }
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          if (msg.mine) {
            const tmpIdx = prev.findIndex(m => m.id?.startsWith('tmp_') && m.text === msg.text);
            if (tmpIdx !== -1) return prev.map((m, i) => i === tmpIdx ? msg : m);
          }
          return [...prev, msg];
        });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [appState.currentClubId, user?.userId, myMatch?.createdAt, isHost, currentClub?.createdAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // 채팅방이 화면에 표시되면 미읽은 카운트 초기화
  useEffect(() => {
    if (screen === 'chat' && appState.currentClubId) {
      onResetUnread?.(appState.currentClubId);
      // 같은 방 재진입 시에도 무조건 최신 메시지 위치로 이동
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
      });
    }
  }, [screen, appState.currentClubId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExit = async () => {
    const clubId = appState.currentClubId;
    if (clubId) {
      // 이미 채팅 오픈된 방만 active 고정(모집 중 방을 모임장이 닫을 때는 건너뜀 — 잘못 active 고정 방지)
      if (currentClub && isClubOpenForChat(currentClub)) {
        await pinClubAsActive(clubId);
      }
      if (isHost) {
        const currentMemberCount = roomMembers.length > 0 ? roomMembers.length : (currentClub?.members || 0);
        if (currentMemberCount > 1) {
          alert('모임장을 먼저 다른 사용자에게 넘겨주세요.');
          return;
        }
        // 모임장 단독일 때만 바로 나가기 허용
        await supabase.from('club_members').delete()
          .eq('club_id', clubId).eq('user_id', user?.userId);
        await clearClubMemberHistory(clubId, user?.userId);
        await syncClubMembersCount(clubId);
        await deactivateClub(clubId);
      } else {
        await supabase.from('club_members').delete()
          .eq('club_id', clubId).eq('user_id', user?.userId);
        await clearClubMemberHistory(clubId, user?.userId);
        await syncClubMembersCount(clubId);
      }
      // matches 테이블 정합성 유지
      await supabase.from('matches')
        .update({ status: 'cancelled' })
        .eq('user_id', user?.userId)
        .eq('status', 'waiting');
      updateState('matches', appState.matches.filter(m => m.clubId !== clubId));
      fetchClubs().then(setClubs);
    }
    updateState('currentClubId', null);
    setShowExitModal(false);
    onNav('match', { replace: true });
  };

  const refreshRestrictionNow = async () => {
    const row = await getActiveChatRestriction();
    if (row) setActiveChatRestriction(row);
    return row || null;
  };

  // 모더레이션 호출은 큐로 직렬화해, 빠르게 연속 전송해도 유실/경합 없이 처리한다.
  const moderationQueueRef = useRef([]);
  const moderationBusyRef = useRef(false);
  const moderationSeenMessageIdsRef = useRef(new Set());

  const runAiModerationForMessage = async ({ roomId, messageId, text }) => {
    try {
      // NOTE: functions.invoke는 기본적으로 현재 세션 JWT를 자동 첨부한다.
      // 헤더를 직접 덮어쓰면(특히 토큰이 비어있을 때) 다른 유저에서 401로 조용히 실패할 수 있어
      // 기본 동작을 우선 사용하고, 401이면 1회만 재시도한다.
      const body = {
        room_id: roomId,
        message_id: messageId || null,
        text,
      };

      let { data: modData, error: modError } = await supabase.functions.invoke('moderation-auto-sanction', { body });
      const modErrMsg = String(modError?.message || '');
      const looksLikeAuthError =
        !!modError && (
          modErrMsg.toLowerCase().includes('jwt')
          || modErrMsg.includes('401')
          || modErrMsg.toLowerCase().includes('unauthorized')
        );
      if (looksLikeAuthError) {
        // 세션 복구/리프레시가 늦는 케이스 대비
        await new Promise(r => setTimeout(r, 250));
        ({ data: modData, error: modError } = await supabase.functions.invoke('moderation-auto-sanction', { body }));
      }
      if (modError) {
        logClientError('moderation:auto-sanction:invoke', modError?.message, modError?.context);
        // 모더레이션 실패는 사용자에게 노출하지 않고 내부 로그로만 추적
        return;
      }
      if (modData?.success === false) {
        logClientError('moderation:auto-sanction:result', modData?.error, modData);
        // 모더레이션 실패는 사용자에게 노출하지 않고 내부 로그로만 추적
        return;
      }
      const action = String(modData?.action || '').toLowerCase();
      // 빠르게 타이핑해도 즉시 안내가 뜨도록, Edge 응답에 포함된 popup을 우선 표시한다.
      const popup = modData?.popup;
      if (popup?.title && popup?.message) {
        markModSanctionPopupShown();
        alert(`${popup.title}\n\n${popup.message}`);
      } else if (action === 'room_kick') {
        alert(`채팅방 강제 퇴장 안내\n\n${CHAT_POLICY_KICK_EXIT_MESSAGE}`);
      }

      // 제재가 걸린 경우(또는 강제퇴장) 즉시 상태 재조회 → 입력창 차단/퇴장 처리에 바로 반영
      if (action === 'warning' || action === 'chat_ban_10m' || action === 'chat_ban_24h' || action === 'room_kick') {
        const blockedNow = await refreshRestrictionNow();
        if (blockedNow && (action === 'chat_ban_10m' || action === 'chat_ban_24h')) {
          // 추가로 한번 더 확실히 안내(팝업이 없거나 사용자가 놓친 경우)
          // popup이 이미 떴다면 중복이 될 수 있어, popup이 없을 때만 표시
          if (!(popup?.title && popup?.message)) {
            alert(toRestrictionText(blockedNow));
          }
        }
      }
    } catch (e) {
      logClientError('moderation:auto-sanction:catch', e?.message, e);
    }
  };

  const enqueueAiModeration = ({ roomId, messageId, text }) => {
    const mid = messageId ? String(messageId) : '';
    if (mid) {
      if (moderationSeenMessageIdsRef.current.has(mid)) return;
      moderationSeenMessageIdsRef.current.add(mid);
    }
    moderationQueueRef.current.push({ roomId, messageId, text });
    if (moderationBusyRef.current) return;
    moderationBusyRef.current = true;
    (async () => {
      try {
        while (moderationQueueRef.current.length > 0) {
          const job = moderationQueueRef.current.shift();
          if (!job) continue;
          // 네트워크/레이트리밋 등 순간 오류에 대비해 짧은 재시도
          let attempt = 0;
          while (attempt < 3) {
            attempt += 1;
            try {
              await runAiModerationForMessage(job);
              break;
            } catch {
              const waitMs = attempt === 1 ? 350 : attempt === 2 ? 800 : 1400;
              await new Promise(r => setTimeout(r, waitMs));
            }
          }
        }
      } finally {
        moderationBusyRef.current = false;
      }
    })();
  };

  const getActiveChatRestriction = async () => {
    if (!user?.userId) return null;
    const roomId = appState.currentClubId || null;
    const nowIso = new Date().toISOString();
    const { data, error } = await supabase
      .from('user_sanctions_v2')
      .select('id,sanction_type,club_id,start_at,end_at,is_active,reason')
      .eq('user_id', user.userId)
      .eq('is_active', true)
      .or(`end_at.is.null,end_at.gt.${nowIso}`)
      .in('sanction_type', [
        'ROOM_KICK',
        'CHAT_BAN_10M',
        'CHAT_BAN_24H',
        'ACCOUNT_SUSPEND_1D',
        'ACCOUNT_SUSPEND_7D',
        'PERMANENT_BAN',
      ]);
    if (error) {
      logClientError('getActiveChatRestriction', error?.message, error);
      return null;
    }
    const rows = data || [];
    const accountBanTypes = new Set(['ACCOUNT_SUSPEND_1D', 'ACCOUNT_SUSPEND_7D', 'PERMANENT_BAN']);
    const active = rows.find((r) => {
      if (accountBanTypes.has(r.sanction_type)) return true;
      if (!roomId) return false;
      const clubMatch = String(r.club_id || '') === String(roomId);
      // ROOM_KICK은 "해당 채팅방(클럽) 단위"만 적용. club_id 없는 ROOM_KICK은 무시한다.
      if (r.sanction_type === 'ROOM_KICK') return clubMatch;
      // 챗밴은 전역 제한: 방과 무관하게 막음
      if (r.sanction_type === 'CHAT_BAN_10M' || r.sanction_type === 'CHAT_BAN_24H') return true;
      return false;
    });
    return active || null;
  };

  const toRestrictionText = (row) => {
    if (!row) return '';
    // 안내 문구는 정책 텍스트로 통일 (시간 표시는 별도 UI에서 필요 시 추가)
    if (row.sanction_type === 'ROOM_KICK') {
      return '채팅방 강제 퇴장 안내\n\n'
        + '운영 정책 위반 또는 신고 접수로 인해 관리자에 의해 채팅방에서 퇴장 처리되었습니다.\n\n'
        + '안전한 채팅 환경 유지를 위한 조치이며, 반복 위반 시 추가 이용 제한 또는 계정 정지 조치가 적용될 수 있습니다.\n\n'
        + '조치에 대한 문의 또는 재검토가 필요한 경우 카카오채널 @동네사이 로 문의해주세요.';
    }
    if (row.sanction_type === 'CHAT_BAN_10M') return '10분 채팅 제한 안내\n\n운영 정책에 위반될 수 있는 대화 내용이 감지되어 10분 동안 채팅 이용이 제한되었습니다.\n\n안전한 채팅 환경 유지를 위한 조치이며, 반복 위반 시 추가 이용 제한 또는 계정 정지 조치가 적용될 수 있습니다.\n\n조치에 대한 문의 또는 재검토가 필요한 경우 카카오채널 @동네사이 로 문의해주세요..';
    if (row.sanction_type === 'CHAT_BAN_24H') return '24시간 채팅 제한 안내\n\n운영 정책에 위반될 수 있는 대화 내용이 감지되어 24시간 동안 채팅 이용이 제한되었습니다.\n\n안전한 채팅 환경 유지를 위한 조치이며, 반복 위반 시 추가 이용 제한 또는 계정 정지 조치가 적용될 수 있습니다.\n\n조치에 대한 문의 또는 재검토가 필요한 경우 카카오채널 @동네사이 로 문의해주세요.';
    if (row.sanction_type === 'ACCOUNT_SUSPEND_1D') return '1일 계정 정지 안내\n\n운영 정책에 위반될 수 있는 활동이 확인되어 계정 이용이 1일 동안 제한되었습니다.\n\n안전한 채팅 환경 유지를 위한 조치이며, 반복 위반 시 추가 이용 제한 또는 장기 정지 조치가 적용될 수 있습니다.\n\n조치에 대한 문의 또는 재검토가 필요한 경우 카카오채널 @동네사이 로 문의해주세요.';
    if (row.sanction_type === 'ACCOUNT_SUSPEND_7D') return '7일 계정 정지 안내\n\n운영 정책에 위반될 수 있는 활동이 반복적으로 확인되어 계정 이용이 7일 동안 제한되었습니다.\n\n안전한 서비스 환경 유지를 위한 조치이며, 추가 위반 시 영구 정지 조치가 적용될 수 있습니다.\n\n조치에 대한 문의 또는 재검토가 필요한 경우 카카오채널 @동네사이 로 문의해주세요.';
    if (row.sanction_type === 'PERMANENT_BAN') return '영구 정지 안내\n\n운영 정책에 위반되는 활동이 반복적으로 확인되어 계정 이용이 영구적으로 제한되었습니다.\n\n안전한 서비스 환경 유지를 위한 조치이며, 서비스 이용이 더 이상 불가능할 수 있습니다.\n\n조치에 대한 문의 또는 재검토가 필요한 경우 카카오채널 @동네사이 로 문의해주세요.';
    return '현재 채팅이 제한되었습니다.';
  };

  const buildReleaseNoticeMessage = (kind) => {
    if (kind === '6') {
      return '채팅 제한이 해제되었습니다.\n같은 행동이 지속될 경우 더 긴 이용 제한이 적용될 수 있습니다.';
    }
    if (kind === '10') {
      return '채팅 제한이 해제되었습니다.\n같은 행동이 지속될 경우 계정 정지가 적용될 수 있습니다.';
    }
    if (kind === '15') {
      return '계정 이용이 다시 가능합니다.\n추가 위반 시 더 긴 정지 또는 강퇴 조치가 이루어질 수 있습니다';
    }
    if (kind === '20') {
      return '계정 이용이 재개되었습니다.\n추가 위반 시 계정 영구 제한이 이루어질 수 있습니다.';
    }
    if (kind === '25') {
      return '현재 계정은 제재 위험 상태입니다.\n추가적인 규칙 위반이 발생할 경우 계정이 영구적으로 제한될 수 있습니다.';
    }
    return '';
  };

  const tryShowSanctionReleaseNotice = async () => {
    if (!user?.userId) return;
    try {
      const nowIso = new Date().toISOString();
      const { data: endedRows, error: endedErr } = await supabase
        .from('user_sanctions_v2')
        .select('id,sanction_type,end_at')
        .eq('user_id', user.userId)
        .not('end_at', 'is', null)
        .lte('end_at', nowIso)
        .in('sanction_type', ['CHAT_BAN_10M', 'CHAT_BAN_24H', 'ACCOUNT_SUSPEND_1D', 'ACCOUNT_SUSPEND_7D'])
        .order('end_at', { ascending: false })
        .limit(5);
      if (endedErr) return;
      const rows = endedRows || [];
      if (rows.length === 0) return;

      try {
        await supabase.rpc('refresh_my_global_risk_scores');
      } catch {
        // best-effort — RPC 미적용 환경에서는 캐시값 사용
      }
      const { data: riskRow } = await supabase
        .from('user_global_risk_scores')
        .select('ai_flag_count_7d')
        .eq('user_id', user.userId)
        .maybeSingle();
      const total7d = Number(riskRow?.ai_flag_count_7d ?? 0);
      const band =
        total7d >= 25 ? '25'
          : total7d >= 20 ? '20'
            : total7d >= 15 ? '15'
              : '10';

      for (const s of rows) {
        const sanctionId = String(s?.id || '').trim();
        if (!sanctionId) continue;

        // 중복 노출 방지: DB insert 성공 시에만 띄움 (unique 제약으로 1회 보장)
        const { error: insErr } = await supabase
          .from('user_sanction_notices_v2')
          .insert({ user_id: user.userId, sanction_id: sanctionId, notice_type: 'release' });
        if (insErr) continue;

        const st = String(s?.sanction_type || '');
        if (st === 'CHAT_BAN_10M') {
          const msg = buildReleaseNoticeMessage('6');
          if (msg) alert(msg);
          break;
        }
        if (st === 'CHAT_BAN_24H') {
          const msg = buildReleaseNoticeMessage(band);
          if (msg) alert(msg);
          break;
        }
        if (st === 'ACCOUNT_SUSPEND_1D' || st === 'ACCOUNT_SUSPEND_7D') {
          const msg = buildReleaseNoticeMessage(band);
          if (msg) alert(msg);
          break;
        }
      }
    } catch (e) {
      logClientError('tryShowSanctionReleaseNotice', e?.message, e);
    }
  };

  useEffect(() => {
    let alive = true;
    let channel = null;
    const loadRestriction = async () => {
      const row = await getActiveChatRestriction();
      if (!alive) return;
      const prevType = lastRestrictionTypeRef.current;
      const nextType = row?.sanction_type || null;
      const nextId = row?.id ? String(row.id) : '';
      if (!prevType && nextType && nextId && !seenRestrictionIdsRef.current.has(nextId)) {
        seenRestrictionIdsRef.current.add(nextId);
        // 챗밴 안내는 단계별 user_warnings_v2로 먼저 노출되므로 여기서는 중복 팝업을 띄우지 않음.
        if (nextType !== 'CHAT_BAN_10M' && nextType !== 'CHAT_BAN_24H') {
          alert(toRestrictionText(row));
        }
      }
      // 해제 안내: 여기서 한 번만 처리 (10분 챗밴은 예전에 직접 alert + tryShow… 이중 호출로 창이 2개 뜸)
      const releasedActiveSanction = !!(prevType && !nextType);
      if (releasedActiveSanction) {
        await tryShowSanctionReleaseNotice();
      }
      lastRestrictionTypeRef.current = nextType;
      setActiveChatRestriction(row);

      // 앱을 나중에 켰을 때(이미 해제만 남은 상태) 1회 노출 — 방금 해제 전환에서 이미 호출한 틱에서는 생략
      if (!nextType && !releasedActiveSanction) {
        await tryShowSanctionReleaseNotice();
      }
    };
    loadRestriction();
    const t = setInterval(loadRestriction, 10000);

    // 실시간 반영: 계정정지/영구정지/챗밴/강퇴가 적용되는 즉시 restriction 재조회
    // (폴링만 의존하면 사용자가 “로딩해야 반영”으로 느낄 수 있음)
    if (user?.userId) {
      channel = supabase.channel('my-sanctions-v2-' + user.userId)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'user_sanctions_v2',
          filter: `user_id=eq.${user.userId}`,
        }, () => {
          // 이벤트 연속 발생 시에도 최종 상태만 반영되면 되므로 즉시 1회 호출
          loadRestriction();
        })
        .subscribe();
    }

    const onFocus = () => loadRestriction();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      alive = false;
      clearInterval(t);
      if (channel) supabase.removeChannel(channel);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [user?.userId, appState.currentClubId]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (!input.trim() || !appState.currentClubId) return;
    if (!user?.userId) {
      alert('로그인 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }
    const blocked = await getActiveChatRestriction();
    if (blocked) {
      setActiveChatRestriction(blocked);
      alert(toRestrictionText(blocked));
      return;
    }
    const text = input.trim();
    setInput('');
    // 낙관적 업데이트
    const tempId = 'tmp_' + Date.now();
    setMessages(prev => [...prev, { id: tempId, name: user?.nickname || user?.name || '회원', text, mine: true, createdAt: new Date().toISOString(), userId: user?.userId }]);
    // DB 직접 저장 (club_id 컬럼 사용)
    const { data, error } = await supabase.from('chat_messages')
      .insert({ club_id: appState.currentClubId, user_id: user?.userId, user_name: user?.nickname || user?.name || '회원', text })
      .select();
    if (error) {
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setInput(text);
      alertDbFailure('chatMessageInsert', '메시지를 보내지 못했어요. 잠시 후 다시 시도해주세요.', error);
      return;
    }
    if (data?.[0]) {
      const m = data[0];
      setMessages(prev => prev.map(x => x.id === tempId
        ? { id: m.id, name: m.user_name, text: m.text, mine: true, createdAt: m.created_at, userId: m.user_id }
        : x));
      // 모더레이션은 큐로 비동기 처리(빠른 연속 전송에서도 유실/버벅임 방지)
      enqueueAiModeration({
        roomId: appState.currentClubId,
        messageId: m.id,
        text: m.text || text,
      });
    }
  };

  const handleImageUpload = async (e) => {
    if (!user?.userId || !appState.currentClubId) {
      alert('채팅방 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      e.target.value = '';
      return;
    }
    const blocked = await getActiveChatRestriction();
    if (blocked) {
      setActiveChatRestriction(blocked);
      alert(toRestrictionText(blocked));
      e.target.value = '';
      return;
    }
    const files = Array.from(e.target.files).slice(0, 3);
    e.target.value = '';
    for (const rawFile of files) {
      if (!rawFile.type?.startsWith('image/') && !isLikelyHeicFile(rawFile)) continue;
      if (rawFile.size > IMAGE_UPLOAD_MAX_INPUT_BYTES) {
        alert('원본이 너무 큽니다(최대 약 30MB). 다른 이미지를 선택해주세요.');
        continue;
      }
      let file;
      try {
        file = await prepareImageFileForUpload(rawFile);
      } catch (err) {
        alertImagePrepareFailure('chatImagePrepare', err);
        continue;
      }
      if (file.size > CHAT_IMAGE_MAX_UPLOAD_BYTES * 1.12) {
        alert('압축 후에도 용량이 큽니다. 다른 이미지를 선택해주세요.');
        continue;
      }
      // 로컬 미리보기 (업로드 전 즉시 표시)
      const tempId = 'tmp_img_' + Date.now();
      await new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = (ev) => { setMessages(prev => [...prev, { id: tempId, name: user?.nickname || user?.name || '회원', mine: true, image: ev.target.result, imageNote: '업로드 중...' }]); resolve(); };
        reader.readAsDataURL(file);
      });
      // Storage 업로드
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `${appState.currentClubId}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from('chat-images').upload(path, file);
      if (upErr) {
        alertDbFailure('chatImageUpload', '이미지 업로드에 실패했어요. 잠시 후 다시 시도해주세요.', upErr);
        setMessages(prev => prev.filter(m => m.id !== tempId));
        continue;
      }
      const { data: { publicUrl } } = supabase.storage.from('chat-images').getPublicUrl(path);
      // DB 저장
      const { data: msgData, error: msgErr } = await supabase.from('chat_messages')
        .insert({ club_id: appState.currentClubId, user_id: user?.userId, user_name: user?.nickname || user?.name || '회원', text: null, image_url: publicUrl })
        .select();
      if (msgErr) {
        alertDbFailure('chatImageMessageInsert', '이미지 메시지를 저장하지 못했어요. 잠시 후 다시 시도해주세요.', msgErr);
        continue;
      }
      if (msgData?.[0]) {
        const m = msgData[0];
        setMessages(prev => prev.map(x => x.id === tempId
          ? { id: m.id, name: m.user_name, mine: true, image: publicUrl, createdAt: m.created_at, userId: m.user_id }
          : x));
        // 채팅 이미지는 방 커버 이미지에 반영하지 않음
      }
    }
  };

  const handleScreenshotUpload = async (e) => {
    const raw = e.target.files?.[0];
    e.target.value = '';
    if (!raw) return;
    if (!raw.type?.startsWith('image/') && !isLikelyHeicFile(raw)) { alert('이미지 파일만 첨부 가능해요.'); return; }
    if (raw.size > IMAGE_UPLOAD_MAX_INPUT_BYTES) { alert('원본이 너무 큽니다(최대 약 30MB).'); return; }
    try {
      const file = await prepareImageFileForUpload(raw);
      const reader = new FileReader();
      reader.onload = (ev) => setReportScreenshot(ev.target.result);
      reader.readAsDataURL(file);
    } catch (err) {
      alertImagePrepareFailure('reportScreenshotPrepare', err);
    }
  };

  const submitReport = async () => {
    const reason = String(reportReason || '').trim();
    if (reason.length < 20 || reason.length >= 50) {
      alert('신고 사유는 20자 이상 50자 미만으로 입력해주세요.');
      return;
    }
    const targetUserId = String(profileTarget?.userId || '').trim();
    const reporterUserId = String(user?.userId || '').trim();
    const clubId = String(appState.currentClubId || '').trim();
    if (!targetUserId || !reporterUserId || !clubId) {
      alert('신고 대상 또는 방 정보를 확인할 수 없습니다.');
      return;
    }
    try {
      let screenshotUrl = null;
      if (reportScreenshot) {
        const file = dataUrlToFile(reportScreenshot, `report_${Date.now()}.jpg`);
        const path = `${clubId}/report_v2/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.jpg`;
        const { error: upErr } = await supabase.storage.from('chat-images').upload(path, file);
        if (upErr) {
          alertDbFailure('reportScreenshotUpload', '신고 첨부 이미지 업로드에 실패했습니다.', upErr);
          return;
        }
        const { data: { publicUrl } } = supabase.storage.from('chat-images').getPublicUrl(path);
        screenshotUrl = publicUrl;
      }
      const { error } = await supabase.from('report_cases_v2').insert({
        reporter_user_id: reporterUserId,
        target_user_id: targetUserId,
        club_id: clubId,
        category: 'etc',
        reason,
        screenshot_url: screenshotUrl,
      });
      if (error) {
        const msg = String(error?.message || '').trim();
        if (msg.includes('같은 회원에게는 24시간 내 1회만 신고')) {
          alert('같은 회원에게는 24시간 내 1회만 신고할 수 있습니다.');
          return;
        }
        alertDbFailure('submitReportV2', '신고 접수에 실패했습니다. 잠시 후 다시 시도해주세요.', error);
        return;
      }
      alert('신고가 접수되었습니다. 관리자가 검토 후 조치합니다.');
      setShowReportForm(false);
      setProfileTarget(null);
      setReportReason('');
      setReportScreenshot(null);
    } catch (e) {
      logClientError('submitReportV2', e?.message, e);
      alert('신고 접수 중 오류가 발생했습니다.');
    }
  };

  const toggleBlockMember = async (targetUserId) => {
    const roomId = appState.currentClubId;
    const rid = String(targetUserId || '').trim();
    if (!roomId || !user?.userId || !rid || sameUserId(rid, user.userId)) return;
    const blocked = blockedSinceByUserId.has(rid) && (blockedSinceByUserId.get(rid) || []).some((w) => !w?.end);
    try {
      if (blocked) {
        const { error } = await supabase
          .from('club_user_chat_blocks')
          .update({ ended_at: new Date().toISOString() })
          .eq('club_id', roomId)
          .eq('blocker_user_id', user.userId)
          .eq('blocked_user_id', rid)
          .is('ended_at', null);
        if (error) {
          alertDbFailure('unblockClubChatUser', '차단 해제에 실패했어요. 잠시 후 다시 시도해주세요.', error);
          return;
        }
        alert('차단을 해제했습니다.');
      } else {
        const { error } = await supabase.from('club_user_chat_blocks').insert({
          club_id: roomId,
          blocker_user_id: user.userId,
          blocked_user_id: rid,
        });
        if (error) {
          alertDbFailure('blockClubChatUser', '차단 처리에 실패했어요. 잠시 후 다시 시도해주세요.', error);
          return;
        }
        alert('차단 시점 이후에 보낸 메시지만 보이지 않습니다. 이전 대화는 그대로 보여요.');
      }
      setMemberActionMenuUserId(null);
      setShowRoomPanel(false);
      const { data } = await supabase
        .from('club_user_chat_blocks')
        .select('blocked_user_id, created_at, ended_at')
        .eq('club_id', roomId)
        .eq('blocker_user_id', user.userId);
      const next = new Map();
      (data || []).forEach((r) => {
        const bid = String(r.blocked_user_id || '').trim();
        if (!bid) return;
        const prev = next.get(bid) || [];
        next.set(bid, [...prev, { start: r.created_at, end: r.ended_at || null }]);
      });
      setBlockedSinceByUserId(next);
    } catch (e) {
      logClientError('toggleBlockMember', e?.message, e);
      alert('차단 처리 중 오류가 발생했습니다.');
    }
  };

  const toggleLock = async () => {
    if (!appState.currentClubId) return;
    if (!isHost) return;
    if (!canHostToggleLock) {
      alert('모임방 공개·비공개 전환은 참여 인원 5명 이하일 때만 할 수 있어요.');
      return;
    }
    const newLocked = !isLocked;
    setIsLocked(newLocked);
    // 홈 목록 반영 지연을 줄이기 위해 로컬 clubs 상태를 즉시 동기화
    setClubs(prev => (prev || []).map(c => (
      c.id === appState.currentClubId ? { ...c, isLocked: newLocked } : c
    )));
    await supabase.from('clubs').update({ is_locked: newLocked }).eq('id', appState.currentClubId);
  };
  const handleHostKickScreenshot = async (e) => {
    const raw = e.target.files?.[0];
    e.target.value = '';
    if (!raw) return;
    if (!raw.type?.startsWith('image/') && !isLikelyHeicFile(raw)) {
      alert('이미지 파일만 첨부 가능해요.');
      return;
    }
    if (raw.size > IMAGE_UPLOAD_MAX_INPUT_BYTES) {
      alert('원본이 너무 큽니다(최대 약 30MB).');
      return;
    }
    try {
      const file = await prepareImageFileForUpload(raw);
      if (file.size > CHAT_IMAGE_MAX_UPLOAD_BYTES * 1.12) {
        alert('압축 후에도 용량이 큽니다. 다른 이미지를 선택해주세요.');
        return;
      }
      hostKickPreparedFileRef.current = file;
      const reader = new FileReader();
      reader.onload = (ev) => setHostKickScreenshotPreview(ev.target.result);
      reader.readAsDataURL(file);
    } catch (err) {
      hostKickPreparedFileRef.current = null;
      setHostKickScreenshotPreview(null);
      alertImagePrepareFailure('hostKickScreenshotPrepare', err);
    }
  };

  const closeHostKickModal = () => {
    setShowHostKickModal(false);
    setHostKickTarget(null);
    setHostKickReason('');
    setHostKickScreenshotPreview(null);
    hostKickPreparedFileRef.current = null;
  };

  const submitHostKick = async () => {
    const roomId = appState.currentClubId;
    const targetId = hostKickTarget?.userId;
    const detail = hostKickReason.trim();
    if (!roomId || !targetId || !user?.userId) {
      alert('방 정보를 확인할 수 없습니다.');
      return;
    }
    if (detail.length < 30) {
      alert('강제퇴장 사유를 30자 이상 구체적으로 입력해 주세요.');
      return;
    }
    if (!hostKickPreparedFileRef.current) {
      alert('캡처(스크린샷) 이미지를 반드시 첨부해 주세요.');
      return;
    }
    setHostKickBusy(true);
    try {
      const file = hostKickPreparedFileRef.current;
      const ext = (file.name && file.name.split('.').pop()) || 'jpg';
      const path = `${roomId}/host_kick_evidence/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from('chat-images').upload(path, file);
      if (upErr) {
        alertDbFailure('hostKickEvidenceUpload', '캡처 업로드에 실패했어요. 잠시 후 다시 시도해주세요.', upErr);
        return;
      }
      const { data: { publicUrl } } = supabase.storage.from('chat-images').getPublicUrl(path);
      const { data, error } = await supabase.functions.invoke('host-kick-member', {
        body: {
          club_id: roomId,
          target_user_id: targetId,
          kick_detail: detail,
          screenshot_url: publicUrl,
        },
      });
      let serverErr = typeof data?.error === 'string' ? data.error.trim() : '';
      if (!serverErr && error?.context?.json) {
        try {
          const b = await error.context.json();
          serverErr = typeof b?.error === 'string' ? b.error.trim() : '';
        } catch { /* non-JSON body */ }
      }
      if (error || !data?.success) {
        if (serverErr) {
          if (error) logClientError('host-kick-member:invoke', error.message);
          alert(serverErr);
        } else {
          alertFromEdgeInvoke('host-kick-member', '강제퇴장 처리에 실패했어요. 잠시 후 다시 시도해주세요.', { data, error });
        }
        return;
      }
      closeHostKickModal();
      await fetchClubs().then(setClubs);
      loadRoomMembers(roomId);
      alert('강제퇴장 처리되었습니다.');
    } catch (e) {
      logClientError('host-kick-member-catch', e?.message, e);
      alert('강제퇴장 처리에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setHostKickBusy(false);
    }
  };

  const transferHost = async () => {
    const roomId = appState.currentClubId;
    const targetId = profileTarget?.userId;
    if (!roomId || !targetId) return;
    if (!isHost) return;
    if (sameUserId(targetId, user?.userId)) return;
    const targetName = getDisplayName(targetId, profileTarget?.name || '해당 회원');
    const ok = window.confirm(`정말 ${targetName}님에게 모임장을 넘길까요?\n이후에는 모임방 공개·비공개 설정 권한이 해당 사용자에게 넘어가요.`);
    if (!ok) return;
    // 모임장을 넘겨주는 본인 클라이언트에서는 승계 안내 토스트를 잠시 억제한다.
    suppressHostNoticeForClub?.(roomId, 15000);
    const { error } = await supabase.from('clubs').update({ host_id: targetId }).eq('id', roomId);
    if (error) {
      alertDbFailure('transferHost', '모임장 넘기기에 실패했어요. 잠시 후 다시 시도해주세요.', error);
      return;
    }
    setClubs(prev => (prev || []).map(c => (
      c.id === roomId ? { ...c, hostId: targetId } : c
    )));
    setMessages(prev => [...prev, { id: `sys_host_transfer_${Date.now()}`, type:'system', text:`모임장이 ${targetName}님에게 변경되었습니다.` }]);
    setMemberActionMenuUserId(null);
    setProfileTarget(null);
    alert(`${targetName}님에게 모임장 권한을 넘겼습니다.`);
  };

  return (
    <div className="page-fixed chat-screen">
      {/* 이미지 전체화면 */}
      {expandedImg && (
        <div onClick={() => setExpandedImg(null)} style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.9)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <img src={expandedImg} alt="" style={{maxWidth:'100%',maxHeight:'100%',borderRadius:8,objectFit:'contain'}}/>
          <div style={{position:'absolute',top:20,right:20,color:'white',fontSize:26,cursor:'pointer'}}>✕</div>
        </div>
      )}

      {showAllSharedPhotos && (
        <div className="chat-shared-gallery" role="dialog" aria-modal="true" aria-label="대화 사진 전체">
          <header className="chat-shared-gallery__header">
            <button type="button" className="chat-shared-gallery__back" onClick={() => setShowAllSharedPhotos(false)} aria-label="뒤로">←</button>
            <div className="chat-shared-gallery__title">대화 사진</div>
            <span className="chat-shared-gallery__spacer" aria-hidden />
          </header>
          <div className="chat-shared-gallery__scroll">
            {sharedImagesChrono.map((img, idx) => (
              <button
                key={`gallery_${idx}_${img}`}
                type="button"
                className="chat-shared-gallery__item"
                onClick={() => setExpandedImg(img)}
              >
                <img src={img} alt="" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 나가기 모달 */}
      {showExitModal && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 24px'}}>
          <div style={{background:'white',borderRadius:16,padding:24,width:'100%',maxWidth:340}}>
            <div style={{fontSize:18,fontWeight:800,color:'#111827',marginBottom:8}}>채팅방을 나가시겠어요?</div>
            <div style={{fontSize:14,color:'var(--gray-600)',lineHeight:1.7,marginBottom:20}}>나가시면 <strong style={{color:'#991B1B'}}>사용한 포인트는 환불되지 않으며</strong> 인원 수가 줄어듭니다.</div>
            <div style={{display:'flex',gap:10}}>
              <button onClick={() => setShowExitModal(false)} style={{flex:1,padding:14,background:'var(--gray-100)',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'inherit',color:'#374151'}}>취소</button>
              <button onClick={handleExit} style={{flex:1,padding:14,background:'#991B1B',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'inherit',color:'white'}}>나가기</button>
            </div>
          </div>
        </div>
      )}

      {/* 유저 프로필 + 신고 모달 */}
      {profileTarget && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 24px'}}>
          <div style={{background:'white',borderRadius:16,padding:24,width:'100%',maxWidth:320}}>
            {!showReportForm ? (
              <>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
                  <div style={{display:'flex',alignItems:'center',gap:14}}>
                    <div style={{width:52,height:52,borderRadius:'50%',overflow:'hidden',background:'#E5E7EB',display:'flex',alignItems:'center',justifyContent:'center'}}>
                      <img src={getAvatarSrc(profileTarget?.userId)} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />
                    </div>
                    <div>
                      <div style={{fontSize:17,fontWeight:800,color:'#111827'}}>{getDisplayName(profileTarget?.userId, profileTarget?.name || '알 수 없음')}</div>
                      <div style={{fontSize:13,color:'var(--gray-400)',marginTop:2}}>같은 모임방 멤버</div>
                    </div>
                  </div>
                </div>
                <div style={{background:'var(--gray-50)',borderRadius:10,padding:'12px',fontSize:13,color:'var(--gray-400)',marginBottom:16,textAlign:'center'}}>
                  🔒 개인정보 보호를 위해 상세 정보는 표시되지 않아요
                </div>
                {isHost && !sameUserId(profileTarget?.userId, user?.userId) && (
                  <button
                    type="button"
                    onClick={transferHost}
                    style={{width:'100%',padding:'12px 14px',border:'none',borderRadius:12,background:'#EEF2FF',cursor:'pointer',fontSize:14,fontWeight:800,color:'#1D4ED8',fontFamily:'inherit',marginBottom:10}}
                  >
                    모임장 넘기기
                  </button>
                )}
                <button onClick={() => { setProfileTarget(null); setShowReportForm(false); }}
                  style={{width:'100%',padding:12,background:'var(--gray-100)',border:'none',borderRadius:10,cursor:'pointer',fontSize:14,fontWeight:600,fontFamily:'inherit',color:'var(--gray-600)'}}>
                  닫기
                </button>
              </>
            ) : (
              <>
                <div style={{fontSize:16,fontWeight:800,color:'#991B1B',marginBottom:4}}>🚨 신고하기</div>
                <div style={{fontSize:13,color:'var(--gray-400)',marginBottom:12}}>{getDisplayName(profileTarget?.userId, profileTarget?.name || '해당 회원')} 님을 신고합니다</div>
                <textarea
                  value={reportReason}
                  onChange={e => setReportReason(e.target.value)}
                  placeholder="어떤 발언·행동이 문제였는지 구체적으로 적어주세요 (20자 이상, 50자 미만)"
                  rows={3}
                  style={{width:'100%',padding:'10px 13px',border:'1px solid var(--gray-200)',borderRadius:9,fontSize:14,fontFamily:'inherit',outline:'none',boxSizing:'border-box',resize:'none',marginBottom:10}}
                />
                <div style={{fontSize:11,color:'var(--gray-400)',marginBottom:10}}>
                  {reportReason.trim().length}/20자 이상, 50자 미만
                </div>
                {/* 스크린샷 첨부 */}
                <input type="file" ref={screenshotInputRef} accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" style={{display:'none'}} onChange={handleScreenshotUpload}/>
                {reportScreenshot ? (
                  <div style={{marginBottom:10,position:'relative'}}>
                    <img src={reportScreenshot} alt="첨부 스크린샷" style={{width:'100%',maxHeight:120,objectFit:'cover',borderRadius:8}}/>
                    <button onClick={() => setReportScreenshot(null)} style={{position:'absolute',top:4,right:4,background:'rgba(0,0,0,0.5)',border:'none',borderRadius:'50%',width:22,height:22,cursor:'pointer',color:'white',fontSize:12,display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
                  </div>
                ) : (
                  <button onClick={() => screenshotInputRef.current?.click()} style={{width:'100%',padding:'8px',background:'var(--gray-50)',border:'1px dashed var(--gray-300)',borderRadius:8,cursor:'pointer',fontSize:13,fontFamily:'inherit',color:'var(--gray-500)',marginBottom:10}}>
                    📎 스크린샷 첨부 (선택)
                  </button>
                )}
                <div style={{fontSize:11,color:'var(--gray-400)',marginBottom:12}}>※ 신고는 자동 강퇴되지 않으며, 관리자 검토 후 정지 여부가 결정됩니다</div>
                <div style={{display:'flex',gap:10}}>
                  <button onClick={() => { setShowReportForm(false); setReportScreenshot(null); }}
                    style={{flex:1,padding:12,background:'var(--gray-100)',border:'none',borderRadius:10,cursor:'pointer',fontSize:14,fontWeight:600,fontFamily:'inherit',color:'var(--gray-600)'}}>뒤로</button>
                  <button onClick={submitReport}
                    style={{flex:1,padding:12,background:'#991B1B',border:'none',borderRadius:10,cursor:'pointer',fontSize:14,fontWeight:700,fontFamily:'inherit',color:'white'}}>신고 접수</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showHostKickModal && hostKickTarget?.userId && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.55)',zIndex:110,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 20px'}}>
          <div style={{background:'white',borderRadius:16,padding:22,width:'100%',maxWidth:380,maxHeight:'90vh',overflow:'auto'}}>
            <div style={{fontSize:17,fontWeight:800,color:'#9A3412',marginBottom:6}}>⛔ 강제퇴장</div>
            <div style={{fontSize:13,color:'var(--gray-600)',marginBottom:14,lineHeight:1.5}}>
              <strong>{String(hostKickTarget.name || '회원').trim() || '회원'}</strong>님을 이 모임에서 보냅니다. <strong style={{color:'#991B1B'}}>사유 30자 이상</strong>과 <strong style={{color:'#991B1B'}}>캡처 이미지</strong>는 필수입니다.
              <div style={{ marginTop: 10, fontSize: 12, color: 'var(--gray-500)' }}>허위 신고 시 서비스 이용이 제한될 수 있습니다.</div>
            </div>
            <textarea
              value={hostKickReason}
              onChange={e => setHostKickReason(e.target.value)}
              placeholder="어떤 발언·행동이 문제였는지 시점·맥락을 포함해 구체적으로 적어주세요 (30자 이상)"
              rows={5}
              style={{width:'100%',padding:'10px 13px',border:'1px solid var(--gray-200)',borderRadius:9,fontSize:14,fontFamily:'inherit',outline:'none',boxSizing:'border-box',resize:'vertical',marginBottom:10}}
            />
            <div style={{fontSize:11,color:hostKickReason.trim().length >= 30 ? 'var(--gray-400)' : '#B91C1C',marginBottom:10}}>
              {hostKickReason.trim().length}/30자 이상 필요
            </div>
            <input type="file" ref={hostKickFileInputRef} accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" style={{display:'none'}} onChange={handleHostKickScreenshot} />
            {hostKickScreenshotPreview ? (
              <div style={{marginBottom:12,position:'relative'}}>
                <img src={hostKickScreenshotPreview} alt="증빙 캡처" style={{width:'100%',maxHeight:140,objectFit:'cover',borderRadius:8}} />
                <button type="button" onClick={() => { setHostKickScreenshotPreview(null); hostKickPreparedFileRef.current = null; }} style={{position:'absolute',top:4,right:4,background:'rgba(0,0,0,0.55)',border:'none',borderRadius:'50%',width:24,height:24,cursor:'pointer',color:'white',fontSize:12}}>✕</button>
              </div>
            ) : (
              <button type="button" onClick={() => hostKickFileInputRef.current?.click()} style={{width:'100%',padding:'10px',background:'#FFFBEB',border:'1px dashed #F59E0B',borderRadius:8,cursor:'pointer',fontSize:13,fontFamily:'inherit',color:'#92400E',marginBottom:12,fontWeight:600}}>
                📎 캡처 이미지 첨부 (필수)
              </button>
            )}
            <div style={{display:'flex',gap:10}}>
              <button type="button" onClick={() => !hostKickBusy && closeHostKickModal()} disabled={hostKickBusy}
                style={{flex:1,padding:12,background:'var(--gray-100)',border:'none',borderRadius:10,cursor:hostKickBusy?'default':'pointer',fontSize:14,fontWeight:600,fontFamily:'inherit',color:'var(--gray-600)',opacity:hostKickBusy?0.6:1}}>취소</button>
              <button type="button" onClick={submitHostKick} disabled={hostKickBusy}
                style={{flex:1,padding:12,background:'#C2410C',border:'none',borderRadius:10,cursor:hostKickBusy?'default':'pointer',fontSize:14,fontWeight:700,fontFamily:'inherit',color:'white',opacity:hostKickBusy?0.7:1}}>
                {hostKickBusy ? '처리 중…' : '강제퇴장 실행'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRoomPanel && (
        <div
          className="chat-drawer-overlay"
          onClick={() => setShowRoomPanel(false)}>
          <div
            className="chat-drawer-panel"
            onClick={e => e.stopPropagation()}>
            <div className="chat-drawer-top">
              <div style={{fontSize:17,fontWeight:800,color:'#111827'}}>방 정보</div>
              <button type="button" onClick={() => setShowRoomPanel(false)}
                style={{border:'none',background:'none',fontSize:20,cursor:'pointer',color:'#6B7280',lineHeight:1}}>×</button>
            </div>
            <div className="chat-drawer-meta">
              <div className="chat-drawer-chip">{lockStateLabel}</div>
              <div style={{fontSize:12,color:'#6B7280',lineHeight:1.45,marginBottom:10}}>{lockStateDesc}</div>
              <div style={{fontSize:12,color:'#4B5563',fontWeight:700}}>참여 인원 {memberCount}/{roomCapacity}명</div>
            </div>
            <div className="chat-drawer-section-head">
              <div className="chat-drawer-section-title chat-drawer-section-title--inline">대화 사진</div>
              {sharedImagesChrono.length > 3 && (
                <button
                  type="button"
                  className="chat-drawer-more-link"
                  onClick={() => { setShowRoomPanel(false); setShowAllSharedPhotos(true); }}
                >
                  더보기 ({sharedImagesChrono.length}장)
                </button>
              )}
            </div>
            {sharedImagesChrono.length === 0 ? (
              <div style={{fontSize:12,color:'#9CA3AF',marginBottom:12,background:'#F9FAFB',borderRadius:10,padding:'10px 12px'}}>
                아직 업로드된 사진이 없어요.
              </div>
            ) : (
              <div className="chat-drawer-image-grid">
                {recentSharedImages.map((img, idx) => (
                  <button
                    key={`${img}_${idx}`}
                    type="button"
                    onClick={() => setExpandedImg(img)}
                    style={{padding:0,border:'1px solid #E5E7EB',borderRadius:8,overflow:'hidden',cursor:'pointer',background:'#fff',aspectRatio:'1/1'}}
                  >
                    <img src={img} alt="" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} />
                  </button>
                ))}
              </div>
            )}
            <div className="chat-drawer-section-title">참여 멤버</div>
            <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:12}}>
              {participants.map(p => (
                <div
                  key={p.userId}
                  className={`chat-drawer-member-row ${p.mine ? '' : 'chat-drawer-member-row--clickable'}`}
                  role={p.mine ? undefined : 'button'}
                  tabIndex={p.mine ? undefined : 0}
                  onClick={() => { if (!p.mine) openMemberProfile(p.name, p.userId); }}
                  onKeyDown={(e) => {
                    if (p.mine) return;
                    if (e.key === 'Enter' || e.key === ' ') openMemberProfile(p.name, p.userId);
                  }}
                  aria-label={p.mine ? '내 프로필' : `${normalizeParticipantName(getDisplayName(p.userId, p.name))} 프로필 보기`}
                  style={{display:'flex',alignItems:'center',gap:10}}
                >
                    <div style={{width:34,height:34,borderRadius:'50%',overflow:'hidden',background:'#E5E7EB',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,color:'#374151',flexShrink:0}}>
                      <img src={getAvatarSrc(p.userId)} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />
                    </div>
                    <div className="chat-drawer-member-name">
                      {(() => {
                        const cleanName = normalizeParticipantName(getDisplayName(p.userId, p.name));
                        const hostSuffix = p.userId === currentClub?.hostId && cleanName !== '모임장' ? ' · 모임장' : '';
                        return `${cleanName}${p.mine ? ' (나)' : ''}${hostSuffix}`;
                      })()}
                    </div>
                  {!p.mine && (
                    <div style={{position:'relative',marginLeft:'auto',flexShrink:0}} onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        aria-label="더보기"
                        onClick={() => setMemberActionMenuUserId((prev) => (sameUserId(prev, p.userId) ? null : p.userId))}
                        style={{width:34,height:34,background:'#F3F4F6',border:'none',borderRadius:10,cursor:'pointer',fontSize:18,color:'#4B5563',fontFamily:'inherit',lineHeight:1,display:'flex',alignItems:'center',justifyContent:'center'}}
                      >
                        ⋯
                      </button>
                      {sameUserId(memberActionMenuUserId, p.userId) && (
                        <div
                          style={{position:'absolute',right:0,top:38,background:'#fff',borderRadius:10,boxShadow:'0 6px 22px rgba(0,0,0,0.12)',border:'1px solid #E5E7EB',zIndex:20,minWidth:144,overflow:'hidden'}}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setMemberActionMenuUserId(null);
                              setShowRoomPanel(false);
                              setProfileTarget({ name: String(p.name || '').trim() || '회원', userId: p.userId });
                              setShowReportForm(true);
                            }}
                            style={{display:'block',width:'100%',padding:'12px 16px',border:'none',background:'none',cursor:'pointer',fontSize:14,fontWeight:800,color:'#991B1B',textAlign:'left',fontFamily:'inherit'}}
                          >
                            🚨 신고하기
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              void toggleBlockMember(p.userId);
                            }}
                            style={{display:'block',width:'100%',padding:'12px 16px',border:'none',borderTop:'1px solid #E5E7EB',background:'none',cursor:'pointer',fontSize:14,fontWeight:800,color:'#374151',textAlign:'left',fontFamily:'inherit'}}
                          >
                            {blockedSinceByUserId.has(String(p.userId)) ? '차단 해제' : '차단하기'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="chat-drawer-actions">
            {isHost ? (
              <button type="button" onClick={toggleLock}
                disabled={!canHostToggleLock}
                style={{width:'100%',height:42,border:'none',borderRadius:10,background:effectiveLocked?'#1E40AF':'var(--green)',color:'#fff',fontWeight:700,cursor:canHostToggleLock?'pointer':'not-allowed',opacity:canHostToggleLock?1:0.72}}>
                {canHostToggleLock
                  ? (effectiveLocked ? '모임방 공개하기' : '모임방 비공개하기')
                  : '인원 5명 이하에서만 공개·비공개 전환 가능'}
              </button>
            ) : (
              <div style={{fontSize:12,color:'#6B7280',background:'#F3F4F6',padding:'10px 12px',borderRadius:10}}>
                모임방 공개·비공개 변경은 모임장만 할 수 있어요.
              </div>
            )}
            <button
              type="button"
              onClick={() => { setShowRoomPanel(false); setShowExitModal(true); }}
              style={{width:'100%',height:42,border:'none',borderRadius:10,marginTop:10,background:'#991B1B',color:'#fff',fontWeight:700,cursor:'pointer'}}
            >
              채팅방 나가기
            </button>
            </div>
          </div>
        </div>
      )}

      <div className="chat-header">
        <button className="chat-back" onClick={() => onNav('match', { replace: true })}>←</button>
        {currentClub && (
          <div className="chat-room-thumb" aria-hidden>
            <img
              src={currentClub.coverImageUrl || defaultClubCoverSrc(currentClub.hobby)}
              alt=""
              loading="eager"
              decoding="sync"
              className={currentClub.coverImageUrl ? '' : 'chat-room-thumb-img--default'}
              onError={(e) => {
                const t = e.currentTarget;
                if (String(t.src || '').replace(/\\/g, '/').includes('/chatroom.png') && !String(t.src || '').includes('/default-covers/')) return;
                t.onerror = null;
                t.src = defaultClubCoverFallbackSrc();
              }}
            />
          </div>
        )}
        <div style={{flex:1,minWidth:0}}>
          <div
            className="chat-title"
            style={{
              fontSize: 10,
              fontWeight: 800,
              lineHeight: 1.35,
              whiteSpace: 'normal',
              wordBreak: 'keep-all',
              overflowWrap: 'anywhere'
            }}
          >
            {currentClub?.title || clubHobbyFullText(currentClub) || HOBBY_MAP[currentClub?.hobby] || clubLabel}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowChatSearch(s => !s)}
          aria-label="검색"
          className={`chat-header-action ${showChatSearch ? 'chat-header-action--active' : ''}`}
        >
          <svg className="chat-header-action-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-4.2-4.2" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => onToggleMute?.(appState.currentClubId)}
          aria-label={mutedRooms?.has(appState.currentClubId) ? '알림 켜기' : '알림 끄기'}
          className="chat-header-action"
          title={mutedRooms?.has(appState.currentClubId) ? '알림 꺼짐' : '알림 켜짐'}
        >
          {mutedRooms?.has(appState.currentClubId) ? (
            <svg className="chat-header-action-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M14 20a2 2 0 0 1-4 0" />
              <path d="M18 16H6l1.1-1.8c.55-.9.9-1.95.9-3.02V9a4 4 0 1 1 8 0v2.18c0 1.07.35 2.12.9 3.02L18 16Z" />
              <path d="M2 2l20 20" />
            </svg>
          ) : (
            <svg className="chat-header-action-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M14 20a2 2 0 0 1-4 0" />
              <path d="M18 16H6l1.1-1.8c.55-.9.9-1.95.9-3.02V9a4 4 0 1 1 8 0v2.18c0 1.07.35 2.12.9 3.02L18 16Z" />
            </svg>
          )}
        </button>
        <button
          type="button"
          onClick={() => setShowRoomPanel(true)}
          aria-label="메뉴"
          className={`chat-header-action ${showRoomPanel ? 'chat-header-action--active' : ''}`}
        >
          ☰
        </button>
      </div>
      {showChatSearch && (
        <div style={{padding:'10px 12px',background:'#FFF',borderBottom:'0.5px solid var(--gray-200)'}}>
          <input
            className="chat-input"
            style={{fontSize:14,borderRadius:10,background:'#F8FAFC'}}
            value={chatSearchQuery}
            onChange={e => setChatSearchQuery(e.target.value)}
            placeholder="대화내용 검색"
          />
          {normalizedSearch && <div style={{fontSize:12,color:'var(--gray-400)',marginTop:6}}>검색 결과 {visibleMessages.length}개</div>}
        </div>
      )}

      <div className="fixed-scroll" style={{background:'var(--gray-100)',gap:10,display:'flex',flexDirection:'column'}}>
        <div style={{background:effectiveLocked?'#EFF6FF':'#ECFDF3',borderRadius:10,padding:'10px 12px',margin:'8px 0 0',fontSize:12,color:effectiveLocked?'#1E40AF':'#166534',fontWeight:700}}>
          {lockStateLabel} · {lockStateDesc}
        </div>
        <div className="chat-announce">
          <div className="chat-announce-tag">📌 공지사항</div>
          동네사이 채팅방에 오신 것을 환영합니다 🌿<br/>
          <strong>만날 장소와 시간은 자유롭게 정해주세요.</strong>
        </div>
        <div
          style={{
            background: '#FFF7ED',
            borderRadius: 10,
            padding: '10px 12px',
            margin: '0',
            fontSize: 12,
            color: '#9A3412',
            fontWeight: 700,
            lineHeight: 1.6,
            border: '1px solid #FED7AA',
          }}
        >
          편하게 대화하되, 욕설이나 불쾌한 표현은 피해 주세요 🙂<br />
          문제가 반복되면 채팅이 제한될 수 있어요.
        </div>
        {visibleMessages.map((m, i) => {
          if (m.type === 'system') return (
            <div key={m.id} style={{textAlign:'center',padding:'6px 16px'}}>
              <div style={{display:'inline-block',background:'rgba(0,0,0,0.06)',borderRadius:10,padding:'8px 14px',fontSize:13,color:'#374151',whiteSpace:'pre-line',lineHeight:1.7}}>
                {m.text}
              </div>
            </div>
          );
          return (
            <div key={m.id} className={`msg-row ${m.mine?'mine':''}`}>
              <div className="msg-avatar"
                style={{background:getUserColor(m.userId),cursor:m.mine?'default':'pointer'}}
                onClick={() => {
                  if (m.mine) return;
                  openMemberProfile(m.name, m.userId);
                }}>
                <img src={getAvatarSrc(m.userId)} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
              </div>
              <div>
                {!m.mine && <div className="msg-meta">{getDisplayName(m.userId, m.name)}</div>}
                <div style={{display:'flex',alignItems:'flex-end',gap:5,flexDirection:m.mine?'row-reverse':'row'}}>
                  {m.image
                    ? <div>
                        <img src={m.image} alt="" onClick={() => setExpandedImg(m.image)} style={{maxWidth:180,maxHeight:180,borderRadius:12,cursor:'pointer',objectFit:'cover',display:'block'}}/>
                      </div>
                    : <div className={`msg-bubble ${m.mine?'mine':'other'}`} style={{fontSize:15}}>{m.text}</div>
                  }
                  <div className="msg-time">{formatTime(m.createdAt)}</div>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef}/>
      </div>

      <div className="chat-input-area">
        <input type="file" ref={fileInputRef} accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" multiple style={{display:'none'}} onChange={handleImageUpload}/>
        <button onClick={() => fileInputRef.current?.click()} style={{width:38,height:38,borderRadius:'50%',background:'var(--gray-100)',border:'0.5px solid var(--gray-200)',cursor:'pointer',fontSize:17,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center'}}>📷</button>
        <button
          type="button"
          onClick={() => alert('안내: 채팅 기록과 채팅 이미지는 등록 후 30일(1개월) 보관되며, 이후 자동 삭제됩니다.')}
          aria-label="이미지 보관 안내"
          title="이미지 보관 안내"
          style={{width:30,height:30,borderRadius:'50%',background:'transparent',border:'none',cursor:'pointer',fontSize:14,color:'var(--gray-400)',flexShrink:0}}
        >
          ⓘ
        </button>
        <input
          className="chat-input"
          style={{fontSize:15, opacity: activeChatRestriction ? 0.55 : 1}}
          value={input}
          disabled={!!activeChatRestriction}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key==='Enter'&&send()}
          placeholder={activeChatRestriction ? toRestrictionText(activeChatRestriction) : "메시지를 입력하세요..."}
        />
        <button className="chat-send" onClick={send} disabled={!!activeChatRestriction} style={{opacity: activeChatRestriction ? 0.5 : 1}}>
          전송
        </button>
      </div>
    </div>
  );
}

/* ── 매칭 포인트 ───────────────────────── */
function WalletScreen({ user, onNav, updateState, unreadCounts }) {
  const [coinHistory, setCoinHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const unreadTotal = readUnreadTotal(unreadCounts);
  const kstParts = (() => {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Seoul',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      }).formatToParts(new Date());
      const n = (t) => Number(parts.find((p) => p.type === t)?.value || 0);
      const y = n('year');
      const m = n('month');
      const day = n('day');
      const lastDay = new Date(y, m, 0).getDate();
      const remain = lastDay - day;
      const pct = Math.round((day / lastDay) * 100);
      return { y, m, day, lastDay, remain, pct };
    } catch {
      const d = new Date();
      const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
      const day = d.getDate();
      return {
        y: d.getFullYear(),
        m: d.getMonth() + 1,
        day,
        lastDay,
        remain: lastDay - day,
        pct: Math.round((day / lastDay) * 100),
      };
    }
  })();

  useEffect(() => {
    let alive = true;
    const loadHistory = async () => {
      if (!user?.userId) return;
      setHistoryLoading(true);
      try {
        const { data, error } = await supabase
          .from('coin_adjustments')
          .select('id,amount,reason,created_at')
          .eq('user_id', user.userId)
          .order('created_at', { ascending: false })
          .limit(200);
        if (error) throw error;
        if (alive) setCoinHistory(data || []);
      } catch (e) {
        console.warn('포인트 내역 조회 실패:', e?.message || e);
        if (alive) setCoinHistory([]);
      } finally {
        if (alive) setHistoryLoading(false);
      }
    };
    loadHistory();
    return () => { alive = false; };
  }, [user?.userId]);

  const normalizedHistory = (coinHistory || []).map(item => {
    const amount = Number(item?.amount || 0);
    const reasonText = String(item?.reason || '포인트 지급');
    // UI 표시용: 내부 식별자(orderId 등) 노출 방지
    const displayReasonText = (() => {
      // 토스 결제: "... - dnsi_xxx" → 뒤 주문ID 숨김
      const stripped = reasonText.replace(/\s*-\s*dnsi_[a-z0-9]+$/i, '').trim();
      return stripped || reasonText;
    })();
    const isAdminAdjust = reasonText.includes('[관리자') || reasonText.includes('관리자');
    const isCharge =
      reasonText.includes('유료 포인트 충전') || reasonText.includes('포인트 충전');
    const isCreateOrJoin = reasonText.includes('방 생성') || reasonText.includes('모임 참여');
    const isLegacyMonthlyStub = amount > 0 && reasonText.includes('무료 기본 지급');
    const isMonthlyGrant =
      amount > 0
      && (isLegacyMonthlyStub
        || /^월간 무료 포인트 지급\s*\(\d{4}-\d{2}\)/.test(reasonText.trim()));
    const isMonthlyReset =
      amount < 0 && reasonText.includes('월간 무료 포인트 미사용 분 초기화');
    const isSignupOpenEvent = amount > 0 && isSignupOpenEventReason(reasonText);
    const adminReason = reasonText.replace(/^\[관리자\s*(지급|차감)\]\s*/,'').trim();
    let rowLabel = isAdminAdjust
      ? (adminReason || (amount > 0 ? '관리자 포인트 지급' : '관리자 포인트 차감'))
      : isSignupOpenEvent
        ? '오픈 이벤트 지급'
        : isMonthlyGrant
          ? '월간 무료 포인트 지급'
          : isMonthlyReset
            ? '월간 무료 포인트 초기화'
            : isCharge
              ? '유료 포인트 충전'
              : isCreateOrJoin
                ? '모임 포인트 사용'
                : (amount > 0 ? '포인트 환급' : '포인트 차감');
    let historySub = null;
    if (isSignupOpenEvent) {
      historySub = `${amount}코인 지급`;
    } else if (isMonthlyGrant) {
      historySub = `${amount}코인 지급`;
    } else if (isMonthlyReset) {
      historySub = `${Math.abs(amount)}코인 차감`;
    } else if (isCharge && amount > 0) {
      historySub = `${amount}코인 충전 완료`;
    }
    return {
      ...item,
      amount,
      reasonText,
      displayReasonText,
      isAdminAdjust,
      isCharge,
      isMonthlyGrant,
      isMonthlyReset,
      rowLabel,
      historySub,
    };
  });
  const hasMonthlyFreeGrant = normalizedHistory.some((i) => i.isMonthlyGrant);
  const monthlyFreeGrantRow = hasMonthlyFreeGrant ? [] : [{
    id: `virtual_monthly_free_${kstParts.y}_${kstParts.m}`,
    amount: MONTHLY_FREE_COINS,
    reasonText: monthlyFreeGrantReason(kstYearMonthKey()),
    rowLabel: '월간 무료 포인트 지급',
    historySub: `${MONTHLY_FREE_COINS}코인 지급`,
    created_at: kstMonthStartIso(),
  }];
  const displayHistory = [...monthlyFreeGrantRow, ...normalizedHistory]
    .sort((a, b) => new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime());

  const renderHistoryRow = (item) => {
    const plus = item.amount > 0;
    const amountText = `${plus ? '+' : ''}${item.amount.toLocaleString('ko-KR')}`;
    const dateText = item.created_at
      ? new Date(item.created_at).toLocaleString('ko-KR', { month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit' })
      : '';
    const subLine = item.historySub != null
      ? `${item.historySub}${dateText ? ` · ${dateText}` : ''}`
      : `${item.displayReasonText || item.reasonText}${dateText ? ` · ${dateText}` : ''}`;
    return (
      <div key={item.id || `${item.created_at}_${item.amount}`} className="coin-history-row">
        <div>
          <div className="coin-history-label">{item.rowLabel}</div>
          <div className="coin-history-sub">{subLine}</div>
        </div>
        <div className={`coin-amount ${plus ? 'plus' : 'minus'}`}>{amountText}</div>
      </div>
    );
  };

  return (
    <div className="page-fixed">
      <div className="fixed-header" style={{padding:'18px 16px 14px'}}><div style={{fontSize:20,fontWeight:800,color:'#111827'}}>매칭 포인트</div></div>
      <div className="fixed-scroll">
        <div className="sec-label" style={{marginBottom:8}}>무료 포인트 — 이번 달 말일 초기화</div>
        <div className="coin-wallet-card">
          <div className="coin-wallet-label">보유 무료 포인트</div>
          <div className="coin-wallet-amount" style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:12,lineHeight:1}}>
            <CoinOnlyIcon size={41} />
            <span style={{lineHeight:1,display:'inline-block'}}>{user?.freeCoins}</span>
          </div>
          <div className="month-bar-wrap"><div className="month-bar-fill" style={{width:`${kstParts.pct}%`}}/></div>
          <div className="month-bar-info"><span>{kstParts.y}년 {kstParts.m}월 {kstParts.lastDay}일 초기화</span><span>{kstParts.remain}일 남음</span></div>
          <div className="coin-wallet-desc">매달 1일 자동 지급 · 무료 포인트 매칭 취소 시 환급 없음</div>
        </div>
        <div className="sec-label" style={{marginBottom:8,marginTop:16}}>유료 포인트 — 무기한 사용</div>
        <div className="card">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <div>
              <div style={{fontSize:13,color:'var(--gray-600)',marginBottom:3}}>보유 유료 포인트</div>
              <div style={{fontSize:30,fontWeight:800,color:'var(--navy)',display:'inline-flex',alignItems:'center',justifyContent:'center',gap:14,lineHeight:1}}>
                <CoinOnlyIcon size={48} />
                <span style={{lineHeight:1,display:'inline-block'}}>{user?.paidCoins}</span>
              </div>
            </div>
            <button
              className="btn-primary"
              style={{width:'auto',padding:'11px 20px',fontSize:14}}
              onClick={() => onNav('charge')}
            >
              충전하기
            </button>
          </div>
          <div className="notice coin-paid" style={{fontSize:13}}>
            <strong>유료 포인트 환급 정책</strong><br/>
            · 유효기간 없음<br/>
            · 미사용 포인트 7일 이내 현금 환불 가능 (수수료 10% 공제)
          </div>
        </div>
        <div className="card">
          <div className="card-title" style={{marginBottom:12}}>포인트 내역</div>
          {historyLoading ? (
            <div style={{fontSize:13,color:'var(--gray-400)',padding:'10px 0'}}>내역을 불러오는 중...</div>
          ) : displayHistory.length === 0 ? (
            <div className="coin-history-row">
              <div>
                <div className="coin-history-label">월간 무료 포인트 지급</div>
                <div className="coin-history-sub">{MONTHLY_FREE_COINS}코인 지급</div>
              </div>
              <div className="coin-amount plus">+{MONTHLY_FREE_COINS.toLocaleString('ko-KR')}</div>
            </div>
          ) : (
            <div className="coin-history-list" role="region" aria-label="포인트 내역 목록">
              {displayHistory.map(renderHistoryRow)}
            </div>
          )}
        </div>
      </div>
      <TabBar active="wallet" onNav={onNav} updateState={updateState} unreadTotal={unreadTotal}/>
    </div>
  );
}

/* ── 충전 (베타: 유료 구매 제한) ───────────────────────────────── */
function ChargeScreen({ onNav }) {
  return (
    <div className="screen">
      <div className="top-bar">
        <button type="button" className="back-btn" onClick={() => onNav('wallet', { replace: true })}>←</button>
        <div className="screen-title">유료 포인트 충전</div>
      </div>
      <div className="content" style={{ paddingTop: 24 }}>
        <div className="page-title" style={{ fontSize: 17 }}>베타 테스트 안내</div>
        <div className="notice coin-paid" style={{ fontSize: 14, lineHeight: 1.75, marginTop: 14, whiteSpace: 'pre-line' }}>
          {BETA_PAID_CHARGE_NOTICE}
        </div>
      </div>
      <div className="bottom-area">
        <button type="button" className="btn-primary" style={{ fontSize: 16 }} onClick={() => onNav('wallet', { replace: true })}>
          돌아가기
        </button>
      </div>
    </div>
  );
}

/* ═══ [베타 비활성화] 토스페이먼츠 결제 — 정식 오픈 시 아래 블록 주석 해제 ═══
function TossCheckoutScreen({ user, onNav, appState, updateState }) {
  const draft = appState?.tossChargeDraft || null;
  const draftId = draft?.id || null;
  const draftPrice = Number(draft?.price || 0);
  const clientKey = String(process.env.REACT_APP_TOSS_CLIENT_KEY || '').trim();
  const [busy, setBusy] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const paymentRef = useRef(null);
  const autoStartedRef = useRef(false);

  useEffect(() => {
    if (!draftId) return;
    if (!clientKey) return;
    let cancelled = false;

    const loadScript = () =>
      new Promise((resolve, reject) => {
        if (window.TossPayments) return resolve(true);
        const s = document.createElement('script');
        s.src = 'https://js.tosspayments.com/v2/standard';
        s.async = true;
        s.onload = () => resolve(true);
        s.onerror = () => reject(new Error('토스페이먼츠 SDK 로드 실패'));
        document.head.appendChild(s);
      });

    const mount = async () => {
      try {
        await loadScript();
        if (cancelled) return;
        if (typeof window.TossPayments !== 'function') {
          throw new Error('TossPayments SDK가 로드되지 않았습니다.');
        }
        // 결제창(payment)은 API 개별 연동 키(test_ck_/live_ck_)를 사용합니다.
        // 결제위젯(widgets)은 gck 키가 필요하므로, 여기서는 payment 플로우로 동작.
        const ck = String(clientKey || '').trim();
        const looksLikePaymentClientKey = ck.startsWith('test_ck_') || ck.startsWith('live_ck_');
        if (!looksLikePaymentClientKey) {
          throw new Error('결제창 클라이언트 키 형식이 올바르지 않습니다. (예: test_ck_... / live_ck_...)');
        }

        const tossPayments = window.TossPayments(ck);
        const payment = tossPayments.payment({ customerKey: user?.userId || window.TossPayments.ANONYMOUS });
        paymentRef.current = payment;
        if (!cancelled) setSdkReady(true);
      } catch (e) {
        const msg = String(e?.message || e || '');
        logClientError('toss-checkout-mount', msg, e);
        if (!cancelled) {
          alert(
            msg.includes('클라이언트 키')
              ? `결제창 설정 오류\n\n${msg}\n\n(Vercel 환경변수 REACT_APP_TOSS_CLIENT_KEY에 결제창 클라이언트 키를 넣어주세요)`
              : '결제창을 불러오지 못했어요. 잠시 후 다시 시도해주세요.'
          );
        }
      }
    };
    mount();
    return () => { cancelled = true; };
  }, [draftId, draftPrice, user?.userId, clientKey]);

  const startPayment = async () => {
    if (!draft || !sdkReady || busy) return;
    if (!clientKey) { alert('결제 클라이언트 키가 설정되지 않았습니다.'); return; }
    setBusy(true);
    try {
      const orderName = `유료 포인트 충전 (${draft.coins}포인트)`;
      const { data, error } = await supabase.functions.invoke('toss-create-session', {
        body: { amount: Number(draft.price || 0), coins: Number(draft.coins || 0), order_name: orderName },
      });
      if (error || !data?.success) {
        alertFromEdgeInvoke('toss-create-session', '결제 세션 생성에 실패했어요. 잠시 후 다시 시도해주세요.', { data, error });
        return;
      }
      const orderId = String(data.orderId || '').trim();
      if (!orderId) throw new Error('orderId missing');

      const successUrl = `${window.location.origin}/pay/success`;
      const failUrl = `${window.location.origin}/pay/fail`;
      await paymentRef.current.requestPayment({
        method: 'CARD',
        amount: { currency: 'KRW', value: Number(draft.price || 0) },
        orderId,
        orderName,
        successUrl,
        failUrl,
        customerName: user?.nickname || user?.name || undefined,
        customerMobilePhone: (user?.phone || '').replace('+82', '0') || undefined,
      });
    } catch (e) {
      logClientError('toss-checkout-start', e?.message, e);
      alert('결제를 시작하지 못했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setBusy(false);
    }
  };

  // "토스 결제" 중간 화면 없이, 진입 즉시 결제창을 열기
  useEffect(() => {
    if (!draft) return;
    if (!clientKey) return;
    if (!sdkReady) return;
    if (busy) return;
    if (!paymentRef.current) return;
    if (autoStartedRef.current) return;
    autoStartedRef.current = true;
    void startPayment();
  }, [draftId, sdkReady, busy, clientKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!draft) {
    return (
      <div className="screen">
        <div className="top-bar">
          <button className="back-btn" onClick={() => onNav('wallet', { replace: true })}>←</button>
          <div className="screen-title">결제</div>
        </div>
        <div className="content" style={{ paddingTop: 18 }}>
          <div className="page-title">결제 정보가 없습니다</div>
          <div className="page-sub">충전 화면에서 다시 시도해주세요.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="top-bar">
        <button className="back-btn" onClick={() => { updateState('tossChargeDraft', null); onNav('charge', { replace: true }); }}>←</button>
        <div className="screen-title">결제하기</div>
      </div>
      <div className="content" style={{ paddingTop: 18 }}>
        {!clientKey ? (
          <div className="notice coin-paid" style={{ fontSize: 13 }}>
            설정 필요: <strong>REACT_APP_TOSS_CLIENT_KEY</strong>
          </div>
        ) : (
          <div style={{ fontSize: 14, color: 'var(--gray-600)', lineHeight: 1.6 }}>
            결제창을 여는 중입니다. 잠시만 기다려주세요.
          </div>
        )}
      </div>
    </div>
  );
}

function TossPaySuccessScreen({ user, setUser, onNav, appState, updateState }) {
  const [status, setStatus] = useState('processing'); // processing | done | fail
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search || '');
    const paymentKey = qs.get('paymentKey') || '';
    const orderId = qs.get('orderId') || '';
    const amount = Number(qs.get('amount') || 0);
    if (!paymentKey || !orderId || !Number.isFinite(amount) || amount <= 0) {
      setStatus('fail');
      setMsg('결제 정보를 확인할 수 없습니다.');
      return;
    }
    let alive = true;
    (async () => {
      try {
        // 리다이렉트 직후 user state가 비어도 Supabase 세션은 복원될 수 있음
        const { data: sess } = await supabase.auth.getSession();
        if (!alive) return;
        if (!sess?.session?.access_token) {
          setStatus('fail');
          setMsg('로그인 정보를 확인할 수 없습니다. 로그인 후 결제 내역을 다시 확인해주세요.');
          return;
        }
        const { data, error } = await supabase.functions.invoke('toss-confirm', {
          body: { paymentKey, orderId, amount },
        });
        if (!alive) return;
        if (error || !data?.success) {
          setStatus('fail');
          setMsg(String(data?.error || '결제 승인에 실패했습니다.'));
          return;
        }
        // 프로필 포인트 재동기화
        const p = await fetchProfile(user?.userId);
        if (p) {
          setUser(prev => prev ? ({ ...prev, paidCoins: p.paid_coins ?? prev.paidCoins }) : prev);
        }
        setStatus('done');
        setMsg(`🪙 ${Number(data?.coins || 0)}포인트가 충전되었습니다.`);
        updateState('tossChargeDraft', null);
      } catch (e) {
        if (!alive) return;
        setStatus('fail');
        setMsg('결제 승인에 실패했습니다.');
      }
    })();
    return () => { alive = false; };
  }, [user?.userId, setUser, updateState]);

  return (
    <div className="screen">
      <div className="top-bar">
        <button className="back-btn" onClick={() => { window.history.replaceState(null, '', '/'); onNav('wallet', { replace: true }); }}>←</button>
        <div className="screen-title">결제 결과</div>
      </div>
      <div className="content" style={{ paddingTop: 18 }}>
        <div className="page-title">
          {status === 'processing' ? '결제 확인 중...' : status === 'done' ? '결제 완료' : '결제 실패'}
        </div>
        <div className="page-sub" style={{ whiteSpace: 'pre-line' }}>{msg}</div>
        <div style={{ height: 16 }} />
        <button className="btn-primary" onClick={() => { window.history.replaceState(null, '', '/'); onNav('wallet', { replace: true }); }}>
          매칭 포인트로
        </button>
      </div>
    </div>
  );
}

function TossPayFailScreen({ onNav, updateState }) {
  const qs = new URLSearchParams(window.location.search || '');
  const code = qs.get('code') || '';
  const message = qs.get('message') || '';
  const orderId = qs.get('orderId') || '';
  const body = `결제가 취소되었거나 실패했습니다.\n\n${code ? `코드: ${code}\n` : ''}${message ? `메시지: ${message}\n` : ''}${orderId ? `주문번호: ${orderId}` : ''}`.trim();
  return (
    <div className="screen">
      <div className="top-bar">
        <button className="back-btn" onClick={() => { window.history.replaceState(null, '', '/'); updateState('tossChargeDraft', null); onNav('wallet', { replace: true }); }}>←</button>
        <div className="screen-title">결제 실패</div>
      </div>
      <div className="content" style={{ paddingTop: 18 }}>
        <div className="page-title">결제 실패</div>
        <div className="page-sub" style={{ whiteSpace: 'pre-line' }}>{body}</div>
        <div style={{ height: 16 }} />
        <button className="btn-primary" onClick={() => { window.history.replaceState(null, '', '/'); updateState('tossChargeDraft', null); onNav('charge', { replace: true }); }}>
          다시 시도하기
        </button>
      </div>
    </div>
  );
}
*/

/* ── 내 정보 ────────────────────────────── */
function ProfileScreen({ user, onNav, setUser, updateState, unreadCounts }) {
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawPassword, setWithdrawPassword] = useState('');
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  const [nicknameInput, setNicknameInput] = useState(user?.nickname || '');
  const [nicknameChecked, setNicknameChecked] = useState(true);
  const [nicknameChecking, setNicknameChecking] = useState(false);
  const [profilePreviewDataUrl, setProfilePreviewDataUrl] = useState(user?.profileImageUrl || null);
  const [savingProfile, setSavingProfile] = useState(false);
  const unreadTotal = readUnreadTotal(unreadCounts);
  const cooldown = nicknameCooldown(user?.nicknameUpdatedAt);
  const nicknameChanged = normalizeNickname(nicknameInput) !== normalizeNickname(user?.nickname || '');
  const profileChanged = (profilePreviewDataUrl || null) !== (user?.profileImageUrl || null);

  const handleLogout = async () => {
    const { signOut } = await import('./auth');
    await signOut();
    if (user?.userId) localStorage.removeItem(`gyeol_unread_${user.userId}`);
    localStorage.removeItem('gyeol_user_setup');
    localStorage.removeItem('gyeol_last_active');
    setUser(null);
    onNav('login', { resetHistory: true });
  };

  const handleWithdraw = async () => {
    if (!withdrawPassword) {
      alert('비밀번호를 입력해주세요.');
      return;
    }
    setWithdrawBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('withdraw-self', {
        body: { password: withdrawPassword },
      });
      if (error || !data?.success) {
        alertFromEdgeInvoke('withdraw-self', '회원탈퇴에 실패했어요. 잠시 후 다시 시도해주세요.', { data, error });
        return;
      }
      await supabase.auth.signOut();
      if (user?.userId) localStorage.removeItem(`gyeol_unread_${user.userId}`);
      localStorage.removeItem('gyeol_user_setup');
      localStorage.removeItem('gyeol_last_active');
      setUser(null);
      alert('회원탈퇴가 완료되었습니다.');
      setShowWithdrawModal(false);
      setWithdrawPassword('');
      onNav('login', { resetHistory: true });
    } catch (e) {
      logClientError('withdraw-self-catch', e?.message, e);
      alert('회원탈퇴에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setWithdrawBusy(false);
    }
  };

  const checkNicknameNow = async () => {
    const nick = normalizeNickname(nicknameInput);
    if (!isValidNicknameFormat(nick)) {
      alert(`닉네임은 ${NICKNAME_MIN_LEN}~${NICKNAME_MAX_LEN}자, 한글/영문/숫자/_만 가능합니다.`);
      return;
    }
    setNicknameChecking(true);
    try {
      const ok = await isNicknameAvailable(nick, user?.userId || null);
      if (ok == null) { alert('닉네임 기능을 위해 DB 컬럼 업데이트가 필요해요.'); return; }
      if (!ok) { setNicknameChecked(false); alert('이미 사용 중인 닉네임입니다.'); return; }
      setNicknameInput(nick);
      setNicknameChecked(true);
      alert('사용 가능한 닉네임입니다.');
    } catch (e) {
      logClientError('nicknameCheck', e?.message, e);
      alert('닉네임 확인에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setNicknameChecking(false);
    }
  };

  const saveProfileEdits = async () => {
    if (!nicknameChanged && !profileChanged) { alert('변경된 내용이 없어요.'); return; }
    if (nicknameChanged) {
      if (!cooldown.canChange) { alert(`닉네임은 한 달에 한 번만 변경할 수 있어요. (${cooldown.leftDays}일 남음)`); return; }
      if (!nicknameChecked) { alert('닉네임 확인을 눌러 중복 체크를 완료해주세요.'); return; }
    }
    setSavingProfile(true);
    try {
      const payload = { updated_at: new Date().toISOString() };
      let uploadedProfileUrl = user?.profileImageUrl || null;
      if (profileChanged) {
        if (profilePreviewDataUrl) uploadedProfileUrl = await uploadProfileImageFromDataUrl(user?.userId, profilePreviewDataUrl);
        else uploadedProfileUrl = null;
        payload.profile_image_url = uploadedProfileUrl;
      }
      if (nicknameChanged) {
        payload.nickname = normalizeNickname(nicknameInput);
        payload.nickname_updated_at = new Date().toISOString();
      }
      const upsertPayload = {
        user_id: user?.userId,
        name: user?.name || null,
        ...payload,
      };
      let { error } = await supabase
        .from('profiles')
        .upsert(upsertPayload, { onConflict: 'user_id' });
      if (error && isMissingProfileColumnError(error)) {
        const fallbackPayload = { updated_at: payload.updated_at };
        ({ error } = await supabase
          .from('profiles')
          .upsert({ user_id: user?.userId, name: user?.name || null, ...fallbackPayload }, { onConflict: 'user_id' }));
        if (!error) {
          alert('DB 업데이트가 필요해 닉네임/사진 저장은 적용되지 않았어요.');
          return;
        }
      }
      if (error) {
        alertDbFailure('profileScreenSave', '프로필 저장에 실패했어요. 잠시 후 다시 시도해주세요.', error);
        return;
      }
      setUser(prev => ({
        ...prev,
        nickname: nicknameChanged ? normalizeNickname(nicknameInput) : prev?.nickname,
        profileImageUrl: profileChanged ? uploadedProfileUrl : prev?.profileImageUrl,
        nicknameUpdatedAt: nicknameChanged ? new Date().toISOString() : prev?.nicknameUpdatedAt,
      }));
      setNicknameChecked(true);
      alert('프로필이 저장되었습니다.');
    } catch (e) {
      logClientError('profileScreenSaveCatch', e?.message, e);
      alert('프로필 저장에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div className="page-fixed">
      <div className="fixed-header" style={{padding:'18px 16px 14px'}}><div style={{fontSize:20,fontWeight:800,color:'#111827'}}>내 정보</div></div>
      <div className="fixed-scroll">
        <div className="profile-hero">
          <div className="profile-avatar" style={{overflow:'hidden',padding:0}}>
            {user?.profileImageUrl
              ? <img src={user.profileImageUrl} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
              : <img src={DEFAULT_PROFILE_ICON} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>}
          </div>
          <div>
            <div className="profile-name">{user?.nickname || user?.name || '회원'}</div>
            {!!user?.nickname && user?.name && user.name !== user.nickname && (
              <div style={{fontSize:12,color:'var(--gray-400)',marginTop:2}}>실명 {user.name}</div>
            )}
            <div className="profile-age">{user?.age} · {user?.gender==='남'?'남성':'여성'}</div>
            <div className="profile-badges">
              <div className="profile-badge">전화번호 인증</div>
              <div className="profile-badge" style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:7,lineHeight:1}}>
                무료 <CoinOnlyIcon size={18} /> <span style={{lineHeight:1,display:'inline-block'}}>{user?.freeCoins}</span>
              </div>
              <div className="profile-badge" style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:7,lineHeight:1}}>
                유료 <CoinOnlyIcon size={18} /> <span style={{lineHeight:1,display:'inline-block'}}>{user?.paidCoins}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="card" style={{marginTop:12,marginBottom:14}}>
          <div className="card-title" style={{marginBottom:10}}>프로필 설정</div>
          <div style={{fontSize:12,color:'var(--gray-400)',marginBottom:10}}>
            닉네임은 <strong>한 달에 한 번</strong>만 변경할 수 있어요. ({nicknameNextLabel(user?.nicknameUpdatedAt)})
          </div>
          <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:12}}>
            <label style={{width:62,height:62,borderRadius:'50%',overflow:'hidden',border:'1px solid var(--gray-200)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',background:'#F8FAFC'}}>
              {profilePreviewDataUrl
                ? <img src={profilePreviewDataUrl} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                : <img src={DEFAULT_PROFILE_ICON} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>}
              <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" style={{display:'none'}}
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  if (!f.type?.startsWith('image/') && !isLikelyHeicFile(f)) { alert('이미지 파일만 선택할 수 있어요.'); e.target.value=''; return; }
                  if (f.size > IMAGE_UPLOAD_MAX_INPUT_BYTES) { alert('원본이 너무 큽니다(최대 약 30MB).'); e.target.value=''; return; }
                  try {
                    const cf = await prepareImageFileForUpload(f);
                    const reader = new FileReader();
                    reader.onload = () => setProfilePreviewDataUrl(reader.result);
                    reader.readAsDataURL(cf);
                  } catch (err) {
                    alertImagePrepareFailure('profileScreenPreviewImage', err);
                  }
                  e.target.value = '';
                }}
              />
            </label>
            <button type="button" className="btn-outline" style={{width:'auto',padding:'10px 12px'}} onClick={() => setProfilePreviewDataUrl(null)}>기본 아이콘</button>
          </div>
          <div style={{display:'flex',gap:8}}>
            <input className="form-input" style={{flex:1}} value={nicknameInput} maxLength={NICKNAME_MAX_LEN}
              onChange={e => { setNicknameInput(e.target.value); setNicknameChecked(false); }}
              placeholder="닉네임 2~12자" />
            <button type="button" className="btn-primary" style={{width:'auto',padding:'0 14px',fontSize:13}} onClick={checkNicknameNow} disabled={nicknameChecking || (!cooldown.canChange && nicknameChanged)}>
              {nicknameChecking ? '확인중' : '닉네임 확인'}
            </button>
          </div>
          {!cooldown.canChange && nicknameChanged && <div style={{fontSize:12,color:'#991B1B',marginTop:8}}>닉네임 변경은 {cooldown.leftDays}일 후 가능합니다.</div>}
          {nicknameChecked && !nicknameChanged && <div style={{fontSize:12,color:'var(--gray-400)',marginTop:8}}>현재 닉네임 유지</div>}
          {nicknameChecked && nicknameChanged && <div style={{fontSize:12,color:'var(--green)',marginTop:8}}>✓ 사용 가능한 닉네임</div>}
          <button type="button" className="btn-primary" style={{marginTop:12,fontSize:14}} onClick={saveProfileEdits} disabled={savingProfile || (!nicknameChanged && !profileChanged)}>
            {savingProfile ? '저장 중...' : '프로필 저장'}
          </button>
        </div>
        <div className="divider" style={{margin:'8px 0 16px'}}/>
        <div className="sec-title" style={{marginTop:0,marginBottom:12}}>설정 &amp; 지원</div>
        <div className="setting-row" onClick={() => onNav('wallet')}>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <div className="setting-icon">
              <img
                src={`${process.env.PUBLIC_URL || ''}/myicon/coinwallet.png`}
                alt=""
                aria-hidden="true"
                style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain', background: 'transparent' }}
              />
            </div>
            <div><div className="setting-label">매칭 포인트</div><div className="setting-sub">무료 · 유료 포인트 및 충전</div></div>
          </div>
          <span style={{color:'var(--gray-400)',fontSize:18}}>›</span>
        </div>
        <div className="setting-row" onClick={() => window.open(KAKAO_CHANNEL_URL,'_blank','noopener,noreferrer')}>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <div className="setting-icon">
              <img src={`${process.env.PUBLIC_URL || ''}/myicon/kakao.png`} alt="" aria-hidden="true" />
            </div>
            <div><div className="setting-label">카카오채널 문의</div><div className="setting-sub">@동네사이</div></div>
          </div>
          <span style={{color:'var(--gray-400)',fontSize:18}}>›</span>
        </div>
        <div className="setting-row" onClick={() => onNav('terms')}>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <div className="setting-icon">
              <img src={`${process.env.PUBLIC_URL || ''}/myicon/document.png`} alt="" aria-hidden="true" />
            </div>
            <div><div className="setting-label">이용약관</div></div>
          </div>
          <span style={{color:'var(--gray-400)',fontSize:18}}>›</span>
        </div>
        <div className="setting-row" onClick={() => onNav('refund_policy')}>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <div className="setting-icon">
              <img src={`${process.env.PUBLIC_URL || ''}/myicon/refund.png`} alt="" aria-hidden="true" />
            </div>
            <div><div className="setting-label">환불정책</div></div>
          </div>
          <span style={{color:'var(--gray-400)',fontSize:18}}>›</span>
        </div>
        <div className="setting-row" onClick={() => onNav('privacy')}>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <div className="setting-icon">
              <img src={`${process.env.PUBLIC_URL || ''}/myicon/personal.png`} alt="" aria-hidden="true" />
            </div>
            <div><div className="setting-label">개인정보처리방침</div></div>
          </div>
          <span style={{color:'var(--gray-400)',fontSize:18}}>›</span>
        </div>
        <div className="setting-row" onClick={handleLogout}>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <div className="setting-icon">
              <img src={`${process.env.PUBLIC_URL || ''}/myicon/logout.png`} alt="" aria-hidden="true" />
            </div>
            <div><div className="setting-label" style={{color:'#991B1B'}}>로그아웃</div></div>
          </div>
          <span style={{color:'var(--gray-400)',fontSize:18}}>›</span>
        </div>
        <div style={{marginTop:8,paddingLeft:2}}>
          <button
            onClick={() => {
              setWithdrawPassword('');
              setShowWithdrawModal(true);
            }}
            style={{background:'none',border:'none',padding:0,cursor:'pointer',fontSize:12,color:'#9CA3AF',fontFamily:'inherit',textDecoration:'underline'}}>
            회원탈퇴
          </button>
        </div>
      </div>
      {showWithdrawModal && (
        <div
          style={{
            position:'fixed', inset:0, background:'rgba(17,24,39,0.55)', zIndex:120,
            display:'flex', alignItems:'center', justifyContent:'center', padding:'20px',
          }}
          onClick={() => !withdrawBusy && setShowWithdrawModal(false)}
        >
          <div
            style={{background:'#fff', borderRadius:14, width:'100%', maxWidth:360, padding:'18px 16px'}}
            onClick={e => e.stopPropagation()}
          >
            <div style={{fontSize:17,fontWeight:800,color:'#111827',marginBottom:8}}>회원탈퇴 확인</div>
            <div style={{fontSize:13,color:'#4B5563',lineHeight:1.65,marginBottom:12}}>
              동네사이는 안전한 운영을 위해 <strong>탈퇴 후 7일 이내 재가입이 불가능</strong>합니다.
              <br />
              정말 탈퇴하시겠습니까?
            </div>
            <input
              type="password"
              value={withdrawPassword}
              onChange={e => setWithdrawPassword(e.target.value)}
              placeholder="비밀번호 입력"
              autoComplete="current-password"
              style={{
                width:'100%', height:44, border:'1px solid #D1D5DB', borderRadius:10,
                padding:'0 12px', fontSize:14, outline:'none', boxSizing:'border-box',
              }}
              disabled={withdrawBusy}
            />
            <div style={{display:'flex',gap:8,marginTop:12}}>
              <button
                onClick={() => setShowWithdrawModal(false)}
                disabled={withdrawBusy}
                style={{
                  flex:1, height:42, border:'1px solid #D1D5DB', background:'#fff',
                  borderRadius:10, fontSize:14, fontWeight:700, cursor:'pointer',
                }}
              >
                취소
              </button>
              <button
                onClick={handleWithdraw}
                disabled={withdrawBusy}
                style={{
                  flex:1, height:42, border:'none', background:'#DC2626', color:'#fff',
                  borderRadius:10, fontSize:14, fontWeight:700, cursor:'pointer',
                }}
              >
                {withdrawBusy ? '처리 중...' : '탈퇴하기'}
              </button>
            </div>
          </div>
        </div>
      )}
      <TabBar active="profile" onNav={onNav} updateState={updateState} unreadTotal={unreadTotal}/>
    </div>
  );
}

/* ── 모임 찾기 ─────────────────────────── */
function SearchScreen({ onNav, updateState, appState, user, setUser, clubs, setClubs, unreadCounts }) {
  const [hobbyFilter, setHobbyFilter]   = useState(
    (Array.isArray(appState.hobbies) && appState.hobbies[0]) || appState.hobby || ''
  );
  const [regionFilter, setRegionFilter] = useState('');
  const [timeFilter, setTimeFilter]     = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [titleQuery, setTitleQuery]     = useState('');
  const [searchHistory, setSearchHistory] = useState(() => loadSearchHistory(user?.userId));
  const [ageWarnClub, setAgeWarnClub]   = useState(null);
  const [selectedDetailClub, setSelectedDetailClub] = useState(null);
  const unreadTotal = readUnreadTotal(unreadCounts);
  const normalizedTitleQuery = titleQuery.trim().toLowerCase();
  useEffect(() => {
    setSearchHistory(loadSearchHistory(user?.userId));
  }, [user?.userId]);
  const saveKeyword = (value) => {
    const keyword = String(value || '').trim();
    if (!keyword) return;
    setSearchHistory(prev => {
      const next = pushSearchHistory(prev, keyword);
      saveSearchHistory(user?.userId, next);
      return next;
    });
  };
  const clearSearchHistory = () => {
    setSearchHistory([]);
    saveSearchHistory(user?.userId, []);
  };
  const removeSearchKeyword = (keyword) => {
    setSearchHistory(prev => {
      const next = (prev || []).filter(v => v !== keyword);
      saveSearchHistory(user?.userId, next);
      return next;
    });
  };

  // clubs 테이블이 단일 소스 (직접 만든 방도 clubs에 INSERT됨)
  // userClubs(로컬 matches → 가짜 클럽 변환) 제거 → 중복 표시 방지
  const allSearch = clubs;
  const filtered  = allSearch.filter(c => {
    // 홈 '채팅중' 섹션과 동일: 잠금·정원 마감된 오픈 채팅방은 모임 찾기에서도 노출하지 않음
    if (isClubOpenForChat(c)) {
      if ((c.members || 0) >= (c.maxMembers || 6)) return false;
      if (c.isLocked) return false;
    }
    const clubHobbies = normalizeClubHobbies(c);
    const hm = !hobbyFilter || clubHobbies.includes(hobbyFilter) || (hobbyFilter === 'book' && clubHobbies.includes('talk'));
    const rm = !regionFilter || clubMatchesMetroRegionFilter(c.region, regionFilter);
    const tm = !timeFilter  || (c.times||[]).includes(timeFilter);
    const sm = !statusFilter
      || (statusFilter === 'waiting' && !isClubOpenForChat(c))
      || (statusFilter === 'active' && isClubOpenForChat(c));
    const km = !normalizedTitleQuery || String(c.title || '').toLowerCase().includes(normalizedTitleQuery);
    return hm && rm && tm && sm && km;
  });

  const hobbyFilters  = [{ val:'',label:'전체' }, ...HOBBIES.map(h => ({ val:h.id, label:h.label.split(' & ')[0].replace(/^[^ ]+ /,'').trim() }))];
  const regionFilters = [
    { val: '', label: '전체' },
    { val: '인천', label: '인천' },
    { val: '서울', label: '서울' },
    { val: '경기', label: '경기' },
  ];
  const timeFilters   = [{ val:'',label:'전체' },{ val:'weekday_morning',label:'평일 오전' },{ val:'weekday_evening',label:'평일 저녁' },{ val:'weekend_day',label:'주말 낮' },{ val:'weekend_evening',label:'주말 저녁' }];
  const statusFilters = [{ val:'',label:'전체' },{ val:'waiting',label:'매칭중' },{ val:'active',label:'채팅중' }];
  const sortedFiltered = [...filtered].sort((a, b) => {
    const aActive = isClubOpenForChat(a);
    const bActive = isClubOpenForChat(b);
    const aDenied = aActive
      ? !isRatioAllowedForGender(a.ratio, user?.gender)
      : (!isRatioAllowedForGender(a.ratio, user?.gender) || (user?.birthYear && !canJoinClub(user, a)));
    const bDenied = bActive
      ? !isRatioAllowedForGender(b.ratio, user?.gender)
      : (!isRatioAllowedForGender(b.ratio, user?.gender) || (user?.birthYear && !canJoinClub(user, b)));
    return Number(aDenied) - Number(bDenied);
  });

  const handleApply = async (c) => {
    if (!user?.userId) { if (window.confirm('로그인하시겠습니까?')) onNav('login'); return; }
    if (!isRatioAllowedForGender(c.ratio, user?.gender)) { alert(getRatioDenyMessage(c.ratio)); return; }
    if (user?.birthYear && !canJoinClub(user, c)) { setAgeWarnClub(c); return; }
    if (await wasKickedFromClub(c.id, user?.userId)) { alert('강퇴된 모임에는 다시 참여할 수 없습니다.'); return; }
    const targetHobbies = normalizeClubHobbies(c);
    updateState('hobby',targetHobbies[0] || c.hobby || null); updateState('hobbies', targetHobbies); updateState('region',c.region);
    updateState('ratio',c.ratio); updateState('times',c.times);
    updateState('fromClub',true); updateState('clubId',c.id);
    onNav('confirm');
  };

  const handleJoinActive = async (c) => {
    if (!user?.userId) { if (window.confirm('로그인하시겠습니까?')) onNav('login'); return; }
    const isMember = appState?.matches?.some(m => m.clubId === c.id) || c.hostId === user?.userId;
    if (isMember) { updateState('currentClubId', c.id); onNav('chat'); return; }
    if (!isRatioAllowedForGender(c.ratio, user?.gender)) { alert(getRatioDenyMessage(c.ratio)); return; }
    if (user?.birthYear && !canJoinClub(user, c)) { setAgeWarnClub(c); return; }
    if (await wasKickedFromClub(c.id, user?.userId)) { alert('강퇴된 모임에는 다시 참여할 수 없습니다.'); return; }
    const targetHobbies = normalizeClubHobbies(c);
    updateState('hobby',targetHobbies[0] || c.hobby || null); updateState('hobbies', targetHobbies); updateState('region',c.region);
    updateState('ratio',c.ratio); updateState('times',c.times);
    updateState('fromClub',true); updateState('clubId',c.id);
    onNav('confirm');
  };

  const clubAgeText = (c) => (
    c.minAge != null && c.maxAge != null
      ? `${c.minAge}~${c.maxAge}세`
      : '모임장 나이대 기준'
  );

  if (selectedDetailClub) {
    const c = selectedDetailClub;
    const isActive = isClubOpenForChat(c);
    const isMember = appState?.matches?.some(m => m.clubId === c.id) || c.hostId === user?.userId;
    const isHostViewer = sameUserId(c.hostId, user?.userId);
    const genderOk = isRatioAllowedForGender(c.ratio, user?.gender);
    const ageOk = !user?.birthYear || canJoinClub(user, c);
    const full = !isMember && (c.members || 0) >= (c.maxMembers || 6);
    let primaryAction = null;
    if (isHostViewer && !isActive) {
      primaryAction = { label: '내가 만든 방 (매칭 진행중)', disabled: true, onClick: () => {}, background: '#9CA3AF' };
    } else if (isMember && isActive) {
      primaryAction = { label: '💬 채팅방 입장하기', onClick: () => { updateState('currentClubId', c.id); onNav('chat'); } };
    } else if (isMember && !isActive) {
      primaryAction = null;
    } else if (!genderOk) {
      const lab = joinEligibilityBlockLabel(user, c);
      primaryAction = { label: lab ? `${lab} 조건` : '참여 불가', disabled: true, onClick: () => {}, background: '#9CA3AF' };
    } else if (!ageOk) {
      primaryAction = isActive
        ? { label: '참여하기', onClick: () => handleJoinActive(c), background: 'var(--green)' }
        : { label: '신청하기', onClick: () => handleApply(c), background: 'var(--green)' };
    } else if (full) {
      primaryAction = { label: '마감된 모임', disabled: true, onClick: () => {}, background: '#9CA3AF' };
    } else if (isActive) {
      primaryAction = { label: '참여하기', onClick: () => handleJoinActive(c) };
    } else {
      primaryAction = { label: '신청하기', onClick: () => handleApply(c), background: 'var(--green)' };
    }
    const myMembership = appState?.matches?.find(m => m.clubId === c.id) || null;
    const searchAnchor = cancelPolicyAnchorMs(
      myMembership || { clubId: c.id },
      c,
      user?.userId
    );
    const searchDetailMatch = {
      id: myMembership?.id || `search_${c.id}`,
      clubId: c.id,
      hobby: c.hobby,
      title: c.title || null,
      roomIntro: c.roomIntro || null,
      region: c.region,
      ratio: c.ratio,
      times: c.times || [],
      applicantWasHost: !!myMembership?.isHost,
      isViewerInRoom: isMember,
      isHost: sameUserId(c.hostId, user?.userId),
      status: isActive ? 'active' : 'waiting',
      createdAt: searchAnchor ?? myMembership?.createdAt ?? Date.now(),
      coinType: myMembership?.coinType || 'paid',
      spentCoins: myMembership?.spentCoins ?? (!!myMembership?.isHost ? HOST_COST : JOIN_COST),
    };
    const waitingMyApplication = isMember && !isActive;
    return (
      <MatchDetail
        match={searchDetailMatch}
        user={user}
        clubs={clubs}
        onBack={() => setSelectedDetailClub(null)}
        onCancel={waitingMyApplication
          ? () => executeMembershipWithdrawal({
            match: searchDetailMatch,
            user,
            appState,
            clubs,
            setUser,
            updateState,
            setClubs,
          }).then(() => setSelectedDetailClub(null))
          : () => setSelectedDetailClub(null)}
        onNav={onNav}
        hideCancelAction={!waitingMyApplication}
        primaryAction={primaryAction}
        onEnterChat={(clubId) => { updateState('currentClubId', clubId); onNav('chat'); }}
        onClubUpdated={(updatedClub) => {
          if (!updatedClub?.id || !setClubs) return;
          setClubs(prev => (prev || []).map(item => item.id === updatedClub.id ? { ...item, ...updatedClub } : item));
          setSelectedDetailClub(prev => (prev?.id === updatedClub.id ? { ...prev, ...updatedClub } : prev));
        }}
        onAgeMismatchFromDetail={() => setAgeWarnClub(c)}
        onJoinActiveRoomFromDetail={() => handleJoinActive(c)}
      />
    );
  }

  return (
    <div className="page-fixed">
      {ageWarnClub && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 24px'}}>
          <div style={{background:'white',borderRadius:16,padding:24,width:'100%',maxWidth:340}}>
            <div style={{fontSize:18,fontWeight:800,color:'#111827',marginBottom:10}}>신청 가능 연령 안내</div>
            <div style={{fontSize:14,color:'var(--gray-600)',lineHeight:1.8,marginBottom:20}}>
              <div style={{fontWeight:800,color:'#111827'}}>{clubAgeApplyOnlyMessage(ageWarnClub)}</div>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              <button onClick={() => setAgeWarnClub(null)}
                style={{width:'100%',padding:14,background:'var(--green)',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'inherit',color:'white'}}>확인</button>
            </div>
          </div>
        </div>
      )}
      <div className="fixed-header" style={{padding:'14px 16px 12px'}}>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
          <button className="back-btn" onClick={() => onNav('home', { replace: true })}>←</button>
          <div style={{fontSize:18,fontWeight:800,color:'var(--gray-900)'}}>모임 찾기</div>
        </div>
        <div style={{marginBottom:12}}>
          <input
            className="search-input"
            type="text"
            inputMode="search"
            value={titleQuery}
            onChange={e => setTitleQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') saveKeyword(titleQuery); }}
            onBlur={() => saveKeyword(titleQuery)}
            placeholder="모임 제목 검색"
          />
          {searchHistory.length > 0 && (
            <>
              <div className="search-history-head">
                <span>최근 검색</span>
                <button type="button" className="search-history-clear" onClick={clearSearchHistory}>전체삭제</button>
              </div>
              <div className="search-history-wrap">
                {searchHistory.map((q, idx) => (
                  <div key={`${q}_${idx}`} className="search-history-chip">
                    <button type="button" className="search-history-chip__text" onClick={() => setTitleQuery(q)}>
                      {q}
                    </button>
                    <button type="button" className="search-history-chip__del" onClick={() => removeSearchKeyword(q)} aria-label="검색기록 삭제">
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div style={{marginBottom:10}}>
          <div className="filter-label">상태</div>
          <div className="filter-chips">
            {statusFilters.map(f => (
              <button
                key={f.val || 'all'}
                type="button"
                className={`filter-chip ${statusFilter === f.val ? 'active' : ''}`}
                onClick={() => setStatusFilter(f.val)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="search-filter-grid">
          <label className="search-filter-item">
            <span className="filter-label">주제</span>
            <select className="search-filter-select" value={hobbyFilter} onChange={e => setHobbyFilter(e.target.value)}>
              {hobbyFilters.map(f => <option key={f.val} value={f.val}>{f.label}</option>)}
            </select>
          </label>
          <label className="search-filter-item">
            <span className="filter-label">지역</span>
            <select className="search-filter-select" value={regionFilter} onChange={e => setRegionFilter(e.target.value)}>
              {regionFilters.map(f => <option key={f.val} value={f.val}>{f.label}</option>)}
            </select>
          </label>
          <label className="search-filter-item">
            <span className="filter-label">시간</span>
            <select className="search-filter-select" value={timeFilter} onChange={e => setTimeFilter(e.target.value)}>
              {timeFilters.map(f => <option key={f.val} value={f.val}>{f.label}</option>)}
            </select>
          </label>
        </div>
      </div>
      <div className="fixed-scroll" style={{padding:0}}>
        <div className="search-result-summary">
          결과 {sortedFiltered.length}개
          {(hobbyFilter || regionFilter || timeFilter || statusFilter || normalizedTitleQuery) && ' · 필터 적용됨'}
        </div>
        {sortedFiltered.length===0
          ? <div style={{textAlign:'center',padding:40,color:'var(--gray-400)',fontSize:15}}>조건에 맞는 모임이 없어요</div>
          : sortedFiltered.map(c => {
            const isActive = isClubOpenForChat(c);
            const myMem = appState?.matches?.find(m => m.clubId === c.id) || null;
            const isMember = !!myMem || sameUserId(c.hostId, user?.userId);
            const genderOk = isRatioAllowedForGender(c.ratio, user?.gender);
            const alreadyApplied = !isActive && appState?.matches?.some(m => m.hobby===c.hobby && m.region===c.region);
            const full = !isMember && (c.members||0) >= (c.maxMembers||6);
            const ageText = clubAgeText(c);
            const titleLine = c.title || (clubHobbyCategoryText(c) || HOBBY_MAP[c.hobby] || c.hobby);
            const timeText = (c.times||[]).map(t=>TIME_LABELS[t]||t).join(' · ');
            const mem = c.members || 0;
            const maxM = c.maxMembers || 6;
            return (
              <div
                key={c.id}
                className="search-result-row"
                style={{borderLeft: isActive ? '3px solid #3B82F6' : 'none', cursor:'pointer'}}
                onClick={() => setSelectedDetailClub(c)}
              >
                <div>
                  <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                    <div className="club-name" style={{fontSize:15,fontWeight:800,color:'var(--gray-900)'}}>
                      {titleLine}
                    </div>
                    {isActive && <span className="mcard-badge mcard-badge--chat">채팅중</span>}
                  </div>
                  <div style={{fontSize:13,color:'var(--gray-600)',marginTop:4,fontWeight:700}}>
                    {clubHobbyCategoryText(c)}{ageText ? ` · ${ageText}` : ''}
                  </div>
                  <div className="mcard-loc" style={{marginTop:4}}>
                    <LocationPinIcon className="mcard-loc-pin" />
                    <span>{c.region}</span>
                  </div>
                  <div style={{fontSize:12,color:'var(--gray-400)',marginTop:3}}>{timeText}</div>
                </div>
                {isActive
                  ? !genderOk
                    ? <span className="mcard-btn mcard-btn--warn">성별 불가</span>
                    : full
                      ? <span className="mcard-btn mcard-btn--muted">마감</span>
                      : <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:10}}>
                          <div className={`mcard-mem mcard-mem--chat${!genderOk && !isMember ? ' mcard-mem--warn' : ''}`}>
                            <img className="mcard-mem-icon" src={`${process.env.PUBLIC_URL || ''}/humanicon.png`} alt="" aria-hidden="true" />
                            <span className="mcard-mem-num">{mem}</span>
                            <span className="mcard-mem-slash">/</span>
                            <span className="mcard-mem-total">{maxM}</span>
                          </div>
                          <button
                            type="button"
                            className="mcard-btn mcard-btn--blue"
                            onClick={(e) => { e.stopPropagation(); handleJoinActive(c); }}
                          >
                            {isMember ? '입장' : '참여'}
                          </button>
                        </div>
                  : full
                    ? <span className="mcard-btn mcard-btn--muted">마감</span>
                    : !genderOk
                      ? <span className="mcard-btn mcard-btn--warn">{joinEligibilityBlockLabel(user, c)} 불가</span>
                      : alreadyApplied
                        ? <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:10}}>
                            <div className={`mcard-mem${!genderOk && !isMember ? ' mcard-mem--warn' : ''}`}>
                              <img className="mcard-mem-icon" src={`${process.env.PUBLIC_URL || ''}/humanicon.png`} alt="" aria-hidden="true" />
                              <span className="mcard-mem-num">{mem}</span>
                              <span className="mcard-mem-slash">/</span>
                              <span className="mcard-mem-total">{maxM}</span>
                            </div>
                            <span className="mcard-btn mcard-btn--wait">매칭중</span>
                          </div>
                        : <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:10}}>
                            <div className={`mcard-mem${!genderOk && !isMember ? ' mcard-mem--warn' : ''}`}>
                              <img className="mcard-mem-icon" src={`${process.env.PUBLIC_URL || ''}/humanicon.png`} alt="" aria-hidden="true" />
                              <span className="mcard-mem-num">{mem}</span>
                              <span className="mcard-mem-slash">/</span>
                              <span className="mcard-mem-total">{maxM}</span>
                            </div>
                            <button
                              type="button"
                              className="mcard-btn mcard-btn--green"
                              onClick={(e) => { e.stopPropagation(); handleApply(c); }}
                            >
                              신청
                            </button>
                          </div>}
              </div>
            );
          })
        }
      </div>
      <TabBar active="search" onNav={onNav} updateState={updateState} unreadTotal={unreadTotal}/>
    </div>
  );
}

/* ── 알림 ───────────────────────────────── */
function NotifScreen({ onNav, user, updateState, roomOpenNotifs = [] }) {
  const [warningNotifs, setWarningNotifs] = useState([]);
  const warnDefaultBody = '운영정책 위반 행위가 확인되었습니다. 채팅 이용에 유의해주세요.';
  const alertUserWarning = (w) => {
    const body = String(w?.message || '').trim() || warnDefaultBody;
    alert(`${USER_WARN_NOTICE_TITLE}\n\n${body}`);
  };
  useEffect(() => {
    if (!user?.userId) { setWarningNotifs([]); return; }
    const loadWarnings = async () => {
      const { data, error } = await supabase
        .from('user_warnings')
        .select('*')
        .eq('user_id', user.userId)
        .order('created_at', { ascending: false })
        .limit(30);
      if (error) return;
      setWarningNotifs(data || []);
      await supabase.from('user_warnings')
        .update({ is_read: true })
        .eq('user_id', user.userId)
        .eq('is_read', false);
    };
    loadWarnings();
    const channel = supabase.channel('warnings-' + user.userId)
      // read 처리(update)가 다시 loadWarnings를 트리거하는 루프를 줄이기 위해 INSERT만 감지
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'user_warnings', filter: `user_id=eq.${user.userId}` }, () => loadWarnings())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.userId]);

  const openRoomFromNotif = async (clubId) => {
    if (!clubId || !user?.userId) return;
    const { data: club, error: clubErr } = await supabase
      .from('clubs')
      .select('id,is_active,status')
      .eq('id', clubId)
      .maybeSingle();
    if (clubErr || !club?.id || club.is_active === false || String(club.status || '') === 'cancelled') {
      alert('해당 방은 종료되어 더 이상 입장할 수 없습니다.');
      return;
    }
    const { data: member, error: memberErr } = await supabase
      .from('club_members')
      .select('user_id')
      .eq('club_id', clubId)
      .eq('user_id', user.userId)
      .maybeSingle();
    if (memberErr || !member?.user_id) {
      alert('해당 방에 참여 중인 사용자만 입장할 수 있습니다.');
      return;
    }
    updateState('currentClubId', clubId);
    onNav('chat');
  };

  const roomOpenItems = (roomOpenNotifs || []).map((n) => ({
    id: n.id,
    clubId: n.clubId,
    unread: !n.isRead,
    icon: '🎉',
    bg: '#DCFCE7',
    title: '모임방 오픈',
    desc: `"${n.title || '모임'}" 방이 오픈되어 채팅이 시작됐어요.`,
    time: formatNotifListTime(n.createdAt),
  }));
  const monthStart = new Date();
  monthStart.setDate(1);
  monthStart.setHours(0, 0, 0, 0);
  const notifs = [
    {
      id: 'coin_monthly',
      icon: '🪙',
      bg: '#FEF3C7',
      title: '이번 달 포인트 지급 완료',
      desc: '무료 포인트 30개가 지급됐어요. 이번 달 말일 초기화됩니다.',
      time: formatNotifListTime(monthStart),
      unread: false,
    },
  ];
  return (
    <div className="page-fixed">
      <div className="fixed-header" style={{padding:'18px 16px 14px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <button className="back-btn" onClick={() => onNav('home', { replace: true })}>←</button>
          <div style={{fontSize:20,fontWeight:800,color:'#111827'}}>알림</div>
        </div>
      </div>
      <div className="fixed-scroll" style={{padding:0}}>
        {warningNotifs.map((w, i) => (
          <div
            key={`warn-${w.id || i}`}
            className={`notif-item ${w.is_read ? '' : 'unread'}`}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onClick={() => alertUserWarning(w)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                alertUserWarning(w);
              }
            }}
          >
            <div className="notif-icon" style={{background:'#FEF3C7'}}>⚠️</div>
            <div style={{flex:1}}>
              <div className="notif-title" style={{fontSize:15,color:'#92400E'}}>{USER_WARN_NOTICE_TITLE}</div>
              <div className="notif-desc" style={{color:'#7C2D12'}}>{w.message}</div>
              <div className="notif-time">{formatNotifListTime(w.created_at)}</div>
            </div>
          </div>
        ))}
        {roomOpenItems.map((n) => (
          <div
            key={n.id}
            className={`notif-item ${n.unread?'unread':''}`}
            onClick={() => { openRoomFromNotif(n.clubId); }}
          >
            <div className="notif-icon" style={{background:n.bg}}>{n.icon}</div>
            <div style={{flex:1}}>
              <div className="notif-title" style={{fontSize:15}}>{n.title}</div>
              <div className="notif-desc">{n.desc}</div>
              <div className="notif-time">{n.time}</div>
            </div>
          </div>
        ))}
        {notifs.map((n) => (
          <div key={n.id} className={`notif-item ${n.unread?'unread':''}`}>
            <div className="notif-icon" style={{background:n.bg}}>{n.icon}</div>
            <div style={{flex:1}}>
              <div className="notif-title" style={{fontSize:15}}>{n.title}</div>
              <div className="notif-desc">{n.desc}</div>
              <div className="notif-time">{n.time}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 이용약관 ───────────────────────────── */
function TermsScreen({ onNav, user }) {
  return (
    <div className="screen">
      <div className="top-bar">
        <button className="back-btn" onClick={() => onNav(user ? 'profile' : 'login', { replace: true })}>←</button>
        <div className="screen-title">이용약관</div>
      </div>
      <div className="content" style={{fontSize:14,color:'#374151',lineHeight:2}}>
        <div style={{fontSize:17,fontWeight:800,color:'#111827',marginBottom:12}}>동네사이 서비스 이용약관</div>
        <div style={{ marginBottom: 18, padding: '12px 14px', border: '1px solid #E5E7EB', borderRadius: 12, background: '#F9FAFB' }}>
          <div style={{ fontSize: 12, fontWeight: 900, color: '#111827', marginBottom: 6 }}>사업자/고객지원 정보</div>
          <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
            {[
              '서비스명: 동네사이',
              '대표자: 백진욱',
              '사업자등록번호: 453-49-01202',
              '통신판매업 신고번호: 제 2026-인천연수구-1277',
              '고객지원 이메일: dongnesai@gmail.com',
              '고객지원: 카카오채널 @동네사이',
            ].join('\n')}
          </div>
        </div>
        {[
          ['제1조 (목적)','본 약관은 동네사이 서비스(이하 “서비스”)의 이용 조건 및 절차, 회사와 회원의 권리·의무 및 책임사항을 규정함을 목적으로 합니다.'],
          ['제2조 (정의)','① “회원”이란 본 약관에 동의하고 회사가 제공하는 서비스를 이용하는 자를 말합니다.\n② “모임(모임방)”이란 회원이 참여하여 대화·교류할 수 있도록 서비스 내에서 제공되는 공간(채팅방 포함)을 말합니다.\n③ “모임장(방장)”이란 모임을 개설했거나 회사가 정한 절차에 따라 대표 권한을 승계받은 회원을 말합니다.\n④ “게시물”이란 서비스 내에 게시되는 글, 이미지, 채팅 메시지 등 일체의 정보를 말합니다.'],
          ['제3조 (서비스 내용)','동네사이는 수도권(인천·서울·경기) 지역을 중심으로 4~6명 규모의 소모임을 만들고 매칭/참여할 수 있는 기능과, 모임방 내 채팅 기능을 제공합니다. 회사는 운영상 필요에 따라 서비스의 전부 또는 일부를 변경할 수 있습니다.'],
          ['제4조 (회원 자격)',`전화번호 인증을 완료한 만 ${MIN_SIGNUP_MAN_AGE}세 이상(생년월일에 따른 법정 만 나이) 회원으로 가입할 수 있습니다. 생년 기준 ${MINOR_ROOM_MAX}세 이하 회원(「개인정보처리방침」의 청소년 회원)은 법정대리인의 동의·지도·감독 하에 이용해야 하며, 연령 제한 모임의 개설·참여 가능 범위는 ${MINOR_ROOM_MIN}세 이상 ${MINOR_ROOM_MAX}세 이하(생년 기준)로 자동 고정될 수 있습니다. 생년 기준 ${ADULT_ROOM_POLICY_BASE}세 이상 회원이 개설·참여하는 연령 제한 모임은 방장의 출생연도 기준 나이를 바탕으로 참여 나이대가 산정됩니다.`],
          ['제5조 (포인트 및 결제)','· 무료 포인트: 매월 1일(한국시간 기준) 소정 포인트가 지급·조정됩니다. 월별 이월·정리 및 사용 조건은 앱 내 매칭 포인트 안내 및 「환불정책」을 따릅니다.\n· 유료 포인트: 충전 후 정책 범위에서 사용하며, 미사용 잔액 환급 등은 「환불정책」 및 전자상거래 관련 법령을 따릅니다.'],
          ['제6조 (취소 및 환불 정책)','환불·취소 기준은 앱 내 별도 "환불정책" 화면을 따르며, 최신 기준이 우선 적용됩니다. 매칭 완료 후 취소/나가기는 환급되지 않습니다.'],
          ['제7조 (금지행위)','회원은 다음 행위를 하여서는 안 됩니다.\n\n· 타인의 정보 도용/사칭, 허위 정보 기재\n· 욕설/비하/혐오/괴롭힘/협박 등 타인에게 불쾌감 또는 위협을 주는 행위\n· 성희롱, 음란물/불법 촬영물/아동·청소년 관련 불법 콘텐츠\n· 스팸, 사기, 피싱, 불법 광고\n· 조건만남/성매매/선불만남 유도, 금품 요구, 불법 거래/홍보\n· 타인의 개인정보 무단 수집/유출/공유 강요\n· 저작권·초상권·명예훼손 등 타인의 권리를 침해하는 행위\n· 다중 계정/자동화 등 서비스 악용 및 제재 회피\n\n회사는 안전한 서비스 운영 및 부정 이용 방지를 위해 회원에게 본인확인, 추가 인증 또는 관련 정보 제출을 요청할 수 있습니다.'],
          ['제8조 (자동 모더레이션 및 운영조치)','회사는 안전한 서비스 운영 및 운영정책 위반 탐지를 위해 일부 채팅 내용이 자동화된 방식(로컬 필터 및 AI 기반 분석)으로 처리될 수 있습니다. 분석 결과 또는 신고/정황에 따라 경고, 채팅 이용 제한(예: 10분/24시간), 강제 퇴장(모임방 단위), 계정 정지(1일/7일) 또는 영구 정지 등의 조치를 취할 수 있습니다.\n\n중대한 위반(예: 불법·유해 유도, 조건만남/성매매 유도 등)은 누적과 무관하게 즉시 조치될 수 있습니다. 오탐 가능성이 있어 회사는 필요 시 재검토할 수 있습니다.'],
          ['제9조 (신고 및 차단)','회원은 서비스 내 기능을 통해 다른 회원 또는 게시물을 신고할 수 있으며, 회사는 사실관계 확인 및 조치를 위해 필요한 범위에서 신고 사유, 관련 채팅 내용(일부 미리보기 포함), 증빙(스크린샷 등), 처리 기록을 검토할 수 있습니다.\n\n차단은 개인의 이용 경험 보호를 위한 기능으로, 차단 대상과의 상호작용(노출/채팅 등)이 제한될 수 있습니다. 또한 회원이 차단한 상대가 차단 기간 중 작성한 콘텐츠(채팅 메시지 등)는 차단 해제 후에도 다시 노출되지 않을 수 있습니다.\n\n허위 신고 또는 악의적 신고는 서비스 악용으로 간주되어 이용 제한 또는 제재 대상이 될 수 있습니다.'],
          ['제10조 (채팅 기록 및 보관)','서비스는 신고 처리, 운영정책 위반 대응, 분쟁 처리 및 법령 준수를 위해 필요한 범위에서 채팅/게시물 기록(일부 내용, 시각, 방 정보, 조치 이력 등)을 저장·열람할 수 있습니다. 채팅 메시지 및 채팅 이미지는 등록 시점부터 30일(1개월) 보관 후 자동 삭제될 수 있으며, 법령에 따라 별도 보관이 필요한 경우는 예외로 합니다.'],
          ['제11조 (강제 조치 및 환불)','운영정책 위반으로 강제 퇴장 또는 이용 제한 조치될 경우 결제/포인트/이용권 환불은 제공되지 않을 수 있습니다.'],
          ['제12조 (면책)','회사는 매칭 플랫폼을 제공하며, 회원 간 분쟁/손해에 대해 회사의 고의 또는 중대한 과실이 없는 한 책임을 지지 않습니다.'],
          ['제13조 (청소년 보호 및 준수사항)','서비스는 「정보통신망 이용촉진 및 정보보호 등에 관한 법률」, 「청소년 보호법」 등 관계 법령이 정하는 범위에서 청소년 유해정보 차단·연령 구분·이용 행위 모니터링 등을 운영할 수 있습니다. 청소년 회원은 법정대리인의 동의 및 일상적인 지도·감독 하에 이용해야 하며, 허위 생년월일·보호자 동의 없는 이용은 이용 제한·계약 해지 사유가 될 수 있습니다. 회사는 개인정보처리방침에 따른 범위에서 채팅·활동 내역·신고 기록 등을 활용해 이상 징후 발견 시 사전 통지 없이 이용을 제한할 수 있습니다. 본 조항은 일반적인 고지이며 개별 사건에 대한 법률 자문을 구성하지 않습니다.'],
          ['제14조 (회원 정보의 진위 및 책임)','회원은 가입·이용 과정에서 제공하는 모든 정보(이름, 생년월일 등)를 사실에 맞게 정확히 제공해야 합니다. 타인 정보 도용·허위 기재 시 관련 법령에 따른 민·형사상 책임이 있을 수 있으며, 회사는 사전 통지 없이 이용 제한·영구 정지를 할 수 있습니다. 허위 정보로 인한 분쟁·손해에 대해서는 회원 본인이 책임지며, 회사는 고의 또는 중대한 과실이 없는 한 책임지지 않습니다. 안전한 운영을 위해 회사가 정보 확인을 요청하거나 이용을 제한할 수 있는 구체적 사항은 「개인정보처리방침」 제8조(개인정보의 진위 및 책임)에 따릅니다.'],
          ['제15조 (제재 및 이의제기)','회원은 제재에 대해 앱 내 안내 또는 고객지원 채널을 통해 이의제기를 할 수 있으며, 회사는 합리적인 범위에서 재검토할 수 있습니다. 다만 서비스 안전을 위해 긴급히 필요한 경우 사전 통지 없이 조치가 우선 적용될 수 있습니다.'],
          ['제16조 (정책 변경)','회사는 서비스 운영상 필요에 따라 운영정책/제재 기준 및 본 약관을 변경할 수 있으며, 중요한 변경 사항은 앱 내 공지 등 합리적인 방법으로 안내합니다.'],
        ].map(([title,desc]) => (
          <div key={title} style={{marginBottom:16}}>
            <div style={{fontWeight:700,color:'#111827',marginBottom:5}}>{title}</div>
            <div style={{fontSize:13,color:'#374151',whiteSpace:'pre-line'}}>{desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 환불정책 ───────────────────────────── */
function RefundPolicyScreen({ onNav, user }) {
  return (
    <div className="screen">
      <div className="top-bar">
        <button className="back-btn" onClick={() => onNav(user ? 'profile' : 'login', { replace: true })}>←</button>
        <div className="screen-title">환불정책</div>
      </div>
      <div className="content" style={{fontSize:14,color:'#374151',lineHeight:1.9}}>
        <div style={{fontSize:11,color:'var(--gray-400)',marginBottom:16}}>최종 수정: 2026년 5월 13일</div>
        {[
          {
            title: '1) 무료 포인트',
            content: '· 매월 1일(한국시간 기준) 소정 포인트가 지급·조정됩니다(앱 로직 및 월별 정리 기준은 매칭 포인트 안내를 따릅니다).\n· 무료 포인트로 매칭을 신청한 뒤 취소하더라도 포인트는 환급되지 않습니다.',
          },
          {
            title: '2) 모임장(방 개설) — 유료 포인트 50',
            content: '· 모임장은 방 생성 시 유료 포인트 50을 사용합니다(무료 포인트 사용 불가).\n· 매칭이 완료되기 전에 모임장이 방에서 나가 방이 비활성화되는 경우: 기준 시각으로부터 48시간이 지나기 전에는 유료 포인트 환급이 없고, 48시간이 지난 뒤에는 사용한 유료 포인트 50이 전액 환급됩니다.\n· 매칭이 완료된 뒤 취소·나가기: 환급 없습니다.',
          },
          {
            title: '3) 참가자(매칭 신청) — 유료 포인트 30',
            content: '· 참가자는 매칭 신청 시 유료 포인트 30(또는 무료 포인트 30)을 사용할 수 있습니다.\n· 유료 포인트 30을 사용한 경우: 신청 후 24시간 이내 취소 시 10포인트 패널티가 적용되어 20포인트가 환급되고, 24시간이 지난 뒤 취소 시 30포인트가 전액 환급됩니다.\n· 매칭이 완료된 뒤 취소·나가기: 환급 없습니다.',
          },
          {
            title: '4) 참가자 — 무료 포인트 30',
            content: '· 무료 포인트로 신청한 경우 취소는 가능하나 포인트는 환급되지 않습니다.',
          },
          {
            title: '5) 모임장 승계',
            content: '· 최초 방을 연 사람이 아니라, 기존 모임장으로부터 권한을 승계받아 모임장이 된 경우에는 위 「모임장 50」 규칙 대신 참가자와 동일하게 유료 30·24시간 기준(또는 무료 30)이 적용됩니다.\n· 무료 포인트는 어떤 경우에도 환급되지 않습니다.',
          },
          {
            title: '6) 결제 취소/현금 환불',
            content: '· 미사용 유료 포인트(충전분)는 구매일로부터 7일 이내 환불 신청이 가능합니다(결제 경로·수단에 따라 신청 절차가 달라질 수 있습니다).\n· 회사가 직접 환급을 처리하는 결제에 대해서는 내부 정책·정산 조건 및 관련 법령에 따라 결제·정산 수수료 등이 공제될 수 있으며, 구체적 금액·비율은 환불 처리 시점에 안내합니다.',
          },
          {
            title: '7) Google Play 등 앱스토어 결제',
            content: '※ Google Play를 통한 인앱 결제는 Google Play 결제 정책 및 환불 정책이 함께 적용될 수 있습니다.\n\nGoogle Play에서 결제한 일부 상품의 환불은 Google의 정책 및 결제 수단 정책에 따라 처리될 수 있으며, 회사는 관련 정책 범위 내에서 환불 요청을 검토합니다.\n\n이용자는 Google Play 스토어 또는 고객지원 채널을 통해 환불 관련 문의를 진행할 수 있습니다.',
          },
          {
            title: '8) 문의',
            content: '· 환불 신청 및 문의: 카카오채널 @동네사이 또는 dongnesai@gmail.com',
          },
        ].map(({ title, content }) => (
          <div key={title} style={{marginBottom:18}}>
            <div style={{fontWeight:700,color:'#111827',marginBottom:6}}>{title}</div>
            <div style={{whiteSpace:'pre-line'}}>{content}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 개인정보처리방침 ─────────────────────── */
function PrivacyScreen({ onNav, user }) {
  return (
    <div className="screen">
      <div className="top-bar">
        <button className="back-btn" onClick={() => onNav(user ? 'profile' : 'login', { replace: true })}>←</button>
        <div className="screen-title">개인정보처리방침</div>
      </div>
      <div className="content" style={{fontSize:13,color:'#374151',lineHeight:1.9}}>
        <div style={{fontSize:11,color:'var(--gray-400)',marginBottom:20}}>시행일: 2025년 1월 1일 · 최종 수정: 2026년 5월 13일</div>
        {[
          { title:'제1조 (총칙)', content:'동네사이(이하 “서비스”)는 개인정보보호법 등 관련 법령을 준수하며, 이용자의 개인정보를 안전하게 보호하기 위해 본 개인정보처리방침을 수립·공개합니다.\n회사는 회원가입 및 서비스 이용 과정에서 관련 법령에 따라 이용자의 동의를 받아 개인정보를 수집·이용합니다.' },
          { title:'제2조 (수집하는 개인정보의 항목 및 수집 방법)', content:'회사는 서비스 제공 및 안전한 운영을 위해 필요한 범위에서 개인정보를 수집합니다.\n\n[필수 수집 항목]\n· 전화번호 인증: 전화번호\n· 회원 입력 정보: 이름, 생년월일(연·월·일), 성별\n\n[서비스 이용 과정에서 생성·수집]\n· 활동 지역, 모임 신청/참여 내역\n· 채팅 메시지 및 이미지(운영정책 위반 대응, 분쟁 처리 목적 포함)\n· 자동 모더레이션(로컬 필터/AI 분석) 결과 및 처리 이력(일부 미리보기, 시각, 방 정보, 경고/제재/챗밴 적용 내역 등)\n· 신고 접수/처리 기록(신고 사유, 대상, 처리 상태 등) 및 증빙(스크린샷 등)\n· 차단 기록(차단/해제 시각 등) 및 제재/경고 기록\n· 접속 로그(IP 등), 기기/브라우저 정보(보안·부정이용 방지)\n\n[결제 관련(해당 시)]\n· 주문/결제 식별값, 결제 금액, 결제 상태 등\n※ 카드번호 등 민감 결제정보는 결제대행사가 처리하며 회사가 직접 저장하지 않습니다.\n\n[수집 방법]\n· 회원가입/서비스 이용 과정에서 이용자가 직접 입력 또는 업로드\n· 서비스 이용 과정에서 자동 생성·수집(접속 로그 등)\n· 신고/문의 과정에서 이용자가 제공(스크린샷, 문의 내용 등)' },
          { title:'제3조 (개인정보의 수집 및 이용 목적)', content:`· 회원 식별 및 서비스 제공(매칭/채팅/알림)\n· 가입 시 법정 만 나이 검증, 모임 연령은 출생연도·성별 기반 매칭\n· 생년 기준 ${MINOR_MAX_MAN_AGE}세 이하 회원(청소년 회원)에 대한 연령 제한 모임 범위(${MINOR_ROOM_MIN}~${MINOR_ROOM_MAX}세, 생년 기준) 자동 적용 및 「정보통신망법」「청소년 보호법」 등에 따른 보호·제한·모니터링\n· 신고 처리, 운영정책 위반 대응, 서비스 안전 운영(자동 모더레이션 포함)\n· 결제 및 포인트 관리(해당 시)\n· 서비스 공지 및 고객 지원\n· 부정 이용 방지 및 법적 분쟁 해결` },
          { title:'제4조 (개인정보의 제3자 제공)', content:'회사는 이용자의 동의 없이 개인정보를 제3자에게 제공하지 않습니다.\n\n다만, 다음의 경우에는 예외로 합니다.\n· 이용자가 사전에 동의한 경우\n· 법령의 규정에 의거하거나, 수사 목적으로 법령에 정해진 절차와 방법에 따라 수사기관의 요구가 있는 경우' },
          { title:'제5조 (개인정보 처리 위탁)', content:'회사는 원활한 서비스 제공을 위해 아래와 같이 개인정보 처리 업무를 위탁할 수 있습니다.\n\n· Supabase Inc. — 데이터베이스 및 인증 서비스\n· 토스페이먼츠(또는 결제대행사) — 결제 처리 서비스\n\n회사는 위탁받는 자와 위탁업무 내용, 보유·이용기간, 보호조치 등을 계약으로 정하고 관리·감독합니다.' },
          { title:'제6조 (개인정보의 보유 및 이용 기간)', content:'회사는 원칙적으로 개인정보의 수집 및 이용목적이 달성되면 지체 없이 파기합니다.\n다만, 다음의 정보는 아래 기간 동안 보관할 수 있습니다.\n\n[서비스 운영상 보관]\n· 신고/제재/차단/자동 모더레이션 등 안전 운영 기록: 분쟁 대응 및 부정 이용 방지 목적 범위 내에서 필요한 기간\n\n[서비스 내 기록]\n· 채팅 메시지 및 채팅 이미지: 등록 후 30일(1개월) 보관 후 자동 삭제(운영정책 위반 조사/분쟁 처리 또는 법령상 보관 필요 시 예외)\n\n[관련 법령에 의한 보관]\n· 계약·청약철회 기록: 5년(전자상거래법)\n· 대금결제 및 재화 공급 기록: 5년(전자상거래법)\n· 소비자 불만·분쟁처리 기록: 3년(전자상거래법)\n· 접속 로그: 3개월(통신비밀보호법)' },
          { title:'제7조 (개인정보의 파기절차 및 방법)', content:'개인정보는 목적 달성 후 내부 방침 및 관련 법령에 따른 보관기간이 경과하면 지체 없이 파기합니다.\n\n· 전자적 파일 형태: 복구·재생이 불가능한 기술적 방법으로 삭제\n· 종이 출력물(해당 시): 분쇄 또는 소각' },
          { title:'제8조 (이용자의 권리와 행사 방법)', content:'이용자는 언제든지 개인정보 열람·정정·삭제·처리정지를 요청할 수 있습니다.\n\n· 앱 내 “내 프로필”에서 일부 정보 조회/수정 가능\n· 기타 요청/문의: 카카오채널 @동네사이 또는 dongnesai@gmail.com\n\n회사는 이용자 요청을 받은 경우 관련 법령에 따라 지체 없이 조치합니다.' },
          { title:'제9조 (개인정보의 진위 및 책임)', content:'회원은 서비스 이용 시 입력하는 이름, 생년월일 등 모든 정보에 대해 사실에 기반하여 정확하게 제공하여야 합니다.\n\n타인의 정보를 도용하거나 허위 정보를 입력한 경우, 관련 법령에 따라 민·형사상 책임을 질 수 있으며 회사는 서비스 이용을 제한하거나 영구 정지할 수 있습니다.' },
          { title:'제10조 (안전성 확보 조치)', content:'회사는 개인정보보호법 등 관계 법령에 따라 개인정보의 안전성 확보를 위해 관리적·기술적·물리적 조치를 시행합니다.\n예) 접근권한 관리, 인증정보 보호, 전송구간 암호화, 접속기록 보관 및 점검, 취약점 대응 등' },
          { title:'제11조 (자동화된 처리 및 안전 운영)', content:'회사는 안전한 서비스 운영 및 운영정책 위반 탐지를 위해 일부 채팅 내용이 자동화된 방식(로컬 필터 및 AI 기반 분석)으로 처리될 수 있습니다.' },
          { title:'제12조 (개인정보보호 책임자 및 고충처리)', content:'· 상호·서비스명: 동네사이\n· 대표자: 백진욱\n· 사업자등록번호: 453-49-01202\n· 통신판매업 신고번호: 제 2026-인천연수구-1277\n· 이메일: dongnesai@gmail.com\n· 카카오채널: @동네사이\n\n기타 침해 신고: 개인정보보호위원회(privacy.go.kr, 182) / 한국인터넷진흥원(118)' },
          { title:'제13조 (고지의 의무)', content:'회사는 법률이나 서비스의 변경사항을 반영하기 위한 목적 등으로 개인정보처리방침을 수정할 수 있습니다.\n내용 추가/삭제/수정이 있을 시 앱 내 공지 등 합리적인 방법으로 사전 고지합니다.' },
        ].map(({ title, content }) => (
          <div key={title} style={{marginBottom:20}}>
            <div style={{fontWeight:700,color:'#111827',marginBottom:6,fontSize:14}}>{title}</div>
            <div style={{whiteSpace:'pre-line',color:'#374151'}}>{content}</div>
          </div>
        ))}
        <div style={{height:30}}/>
      </div>
    </div>
  );
}

/* ── 탭바 (홈 · 모임찾기 · 매칭포인트 · 채팅 · 마이) ─── */
function TabBar({ active, onNav, unreadTotal = 0 }) {
  const goRoot = (target) => (typeof onNav === 'function' ? onNav(target, { root: true }) : undefined);
  const formatUnread = (n) => (n > 99 ? '+99' : String(n));
  const IcHome = () => (
    <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 11L12 3l9 8v9a1 1 0 0 1-1 1h-5v-5H9v5H4a1 1 0 0 1-1-1v-9z"
        stroke="currentColor" strokeWidth="1.9" fill={active==='home'?'currentColor':'none'} fillOpacity={active==='home'?.15:0}/>
    </svg>
  );
  const IcSearch = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
      <circle cx="11" cy="11" r="7"/><path d="m17 17 4 4"/>
    </svg>
  );
  const IcWallet = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7.5h14.5A2.5 2.5 0 0 1 21 10v7a2.5 2.5 0 0 1-2.5 2.5h-12A2.5 2.5 0 0 1 4 17V7.5z"/>
      <path d="M4 8l12.2-3.2A2 2 0 0 1 18.7 6v1.5"/>
      <path d="M16 13.5h5"/>
      <circle cx="16.5" cy="13.5" r=".7" fill="currentColor" stroke="none"/>
    </svg>
  );
  const IcChat = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  );
  const IcMy = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
    </svg>
  );
  const tab = (id, Icon, label, nav, badgeCount = 0) => (
    <button key={id} type="button" className={`tab ${active===id?'active':''}`} onClick={() => goRoot(nav || id)}>
      <span className="tab-icon">
        <Icon/>
        {badgeCount > 0 && <span className="tab-unread-badge">{formatUnread(badgeCount)}</span>}
      </span>
      <span className="tab-label">{label}</span>
    </button>
  );
  const walletCta = () => (
    <button key="wallet" type="button" className={`tab tab--make ${active==='wallet'?'active':''}`}
      onClick={() => goRoot('wallet')}>
      <span className="tab-icon tab-icon--make"><IcWallet/></span>
      <span className="tab-label">매칭 포인트</span>
    </button>
  );
  return (
    <nav className="tab-bar" aria-label="하단 메뉴">
      {tab('home', IcHome, '홈')}
      {tab('search', IcSearch, '모임 찾기')}
      {walletCta()}
      {tab('match', IcChat, '채팅', undefined, unreadTotal)}
      {tab('profile', IcMy, '마이')}
    </nav>
  );
}